import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import { Home, TreeDeciduous, Flame, Coffee, Globe, Heart, BookOpen, Users, User, ChevronRight, MapPin, ArrowLeft, Plus, Download, MessageSquare, Clock, BookMarked, Layers, ShieldCheck, Mail, Phone, MapPinned, LockKeyhole, CheckCircle2, UserRound, Eye, EyeOff, Search, X, Sparkles, Trophy, MessageCircleHeart, Award, LogOut, Music, CalendarCheck2, Medal, Star, Crown, Bell } from "lucide-react";
import { TRAILS_DATA as TRAILS, DEVOTIONALS_MOCK as DAYS_MOCK, ensureCuratedTrailsLoaded } from "./data/TrailsData";
import EBOOKS_MOCK from "./data/ebooks.json";
import { SITE_URL, DEFAULT_IMAGE, setMeta, setCanonical } from "./institutional/useSeo";
import { createAvatar } from "@dicebear/core";
import * as avataaarsStyle from "@dicebear/avataaars";

const FAMILIA_EBOOK_SLUG_RE = /^\/familia\/ebooks\/([a-z0-9-]+)\/?$/;
function getInitialFamiliaEbookFromUrl() {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(FAMILIA_EBOOK_SLUG_RE);
  if (!match) return null;
  return EBOOKS_MOCK.find((ebook) => ebook.id === match[1]) || null;
}

const FAMILIA_TRILHA_SLUG_RE = /^\/familia\/trilhas\/([a-z0-9-]+)\/?$/;
function getInitialFamiliaTrailSlugFromUrl() {
  if (typeof window === "undefined") return null;
  const match = window.location.pathname.match(FAMILIA_TRILHA_SLUG_RE);
  return match ? match[1] : null;
}
function findTrailBySlug(list, slug) {
  if (!slug || !Array.isArray(list)) return null;
  // editorialSlug (ex.: "deus-como-plataforma") e o slug publico/canonico,
  // mais descritivo que o id curto interno ("plataforma") -- ver
  // trailUrlSlug() abaixo, que decide qual usar ao montar a URL.
  return list.find((item) => item.editorialSlug === slug || item.id === slug || item.slug === slug) || null;
}
function trailUrlSlug(trail) {
  return (trail && (trail.editorialSlug || trail.slug || trail.id)) || "";
}

// "Entrar" no site institucional leva pra "/entrar" (ver rota em index.js) --
// esse modulo nao roteia por sub-rota (navega por estado "activeTab"), então
// decidimos a aba inicial pelo caminho: só em "/entrar" comeca direto na aba
// "profile" (onde fica o formulario de login), pra nao cair na home sem
// nenhuma pista de como entrar.
//
// O mesmo vale pro avatar "RM" do topbar institucional (InstitutionalLayout):
// ele linka pra "/familia?tab=perfil" -- sem esse query param, o clique caia
// sempre na aba "home" (o feed devocional), quebrando a expectativa universal
// de que clicar no próprio avatar leva pro perfil/conta, não pra home de um
// outro app.
function getInitialActiveTabFromUrl() {
  if (typeof window === "undefined") return "home";
  if (window.location.pathname === "/entrar") return "profile";
  const params = new URLSearchParams(window.location.search);
  if (params.get("tab") === "perfil") return "profile";
  // "planos" -- usado pelo link do convite devocional (shareSlimDevotional),
  // pra quem recebe o convite cair direto na tela de escolher uma trilha,
  // em vez da home do feed devocional sem nenhum caminho claro.
  if (params.get("tab") === "planos") return "library";
  // "cuidado" -- usado pelo link "Abrir agenda completa" no painel
  // /admin/acolhimento (bloco "Conversas de acolhimento"), pra quem tem
  // perfil de acolhedor/admin cair direto na Agenda de cuidado em vez de
  // ficar preso numa lista so-leitura sem nenhuma acao possivel.
  if (params.get("tab") === "cuidado") return "conecta";
  // "ebooks" e "mesa" -- links diretos pras abas Ebooks e Mesa (usados, por
  // exemplo, pelo agente virtual ao orientar quem pergunta onde fica cada
  // coisa). Sem login o app ja trata o que exige conta.
  if (params.get("tab") === "ebooks") return "ebooks";
  if (params.get("tab") === "mesa") return "family";
  return "home";
}
import { DS, UI, cardStyle, buttonBase, buttonStyles } from "./designSystem";
import DayScreen from "./screens/DayScreen";
import EbookScreen from "./screens/EbookScreen";
import EbookLanding from "./screens/EbookLanding";
import TrailLanding from "./screens/TrailLanding";
import DiaryScreen from "./screens/DiaryScreen";
import { subscribeToPushReminders, unsubscribeFromPushReminders, isPushSupported } from "./pushNotifications";
import HomeBlockPao from "./components/HomeBlockPao";
import HomeBlockSaudacao from "./components/HomeBlockSaudacao.jsx";
import HomeBlockSemana from "./components/HomeBlockSemana.jsx";
import HomeBlockBiblioteca from "./components/HomeBlockBiblioteca.jsx";
import { MosaicSeedIcon, MosaicBridgeIcon, MosaicOrbitIcon } from "./brandIcons";
import { apiFetch, apiPost, apiPatch, apiPut, apiDelete, ApiError, setSessionExpiredHandler } from "./api/client";
import { getEffectiveRoles, getSimulatedHasMemberRecord, getSimulatedIsMusician, getSimulatedMinistryFlag } from "./roleSimulator";
import { trackEvent } from "./institutional/trackEvent";


const MOSAIC_BG = DS.gradients.appBg;

const ICON_MAP = {
  Home: <Home size={24} strokeWidth={1.5} />,
  Tree: <TreeDeciduous size={24} strokeWidth={1.5} />,
  Flame: <Flame size={24} strokeWidth={1.5} />,
  Coffee: <Coffee size={24} strokeWidth={1.5} />,
  Globe: <Globe size={24} strokeWidth={1.5} />,
  Heart: <Heart size={24} strokeWidth={1.5} />,
  BookOpen: <BookOpen size={24} strokeWidth={1.5} />,
  Users: <Users size={24} strokeWidth={1.5} />,
  MapPin: <MapPin size={24} strokeWidth={1.5} />
};

// Gamificacao leve (conquistas) -- mapeia o nome de icone que vem do
// catalogo do backend (src/badges/catalog.js, so' manda a string) pro
// componente lucide-react certo. Guarda o COMPONENTE (nao o JSX ja montado,
// diferente do ICON_MAP acima) porque o tamanho muda conforme onde a
// conquista aparece (teaser pequeno na Home vs. card grande no Perfil).
const BADGE_ICON_MAP = { Sparkles, Trophy, Flame, BookMarked, MessageCircleHeart, Medal, Star, Award, Crown };

const TRAIL_MOMENT_OPTIONS = [
  {
    id: "todos",
    label: "Todos",
    title: "Mapa completo",
    hint: "Todas as trilhas revisadas, prontas para começar ou continuar."
  },
  {
    id: "comecar",
    label: "Começar",
    title: "Primeiros passos",
    hint: "Para quem chegou agora, está retomando a fé ou precisa reorganizar fundamentos."
  },
  {
    id: "interior",
    label: "Vida interior",
    title: "Alma e maturidade",
    hint: "Oração, perseverança, ansiedade, dor, fruto e cuidado do coração."
  },
  {
    id: "comunidade",
    label: "Comunidade",
    title: "Mesa e vínculos",
    hint: "Igreja, família, perdão, aliança e relações curadas pelo evangelho."
  },
  {
    id: "missao",
    label: "Missão",
    title: "Fé no mundo real",
    hint: "Propósito, trabalho, cidade, testemunho e evangelização no cotidiano."
  }
];

const TRAIL_MOMENT_BY_ID = {
  comecar: ["plataforma", "fundamentos-da-fe", "biblia-formacao-e-entendimento", "formados-pela-palavra", "enraizados-em-amor", "fe-sem-barganha", "fe-revitalizada", "identidade-em-batalha", "advento-a-paz-chegou", "adoracao-que-forma-a-alma", "casa-30", "nova-natureza-em-cristo", "conhecer-cristo-de-verdade", "coracao-que-da-fruto"],
  interior: ["vida-interior", "alegria-que-permanece", "quando-tudo-nao-basta", "quando-ninguem-parece-ver-voce", "sabedoria-para-viver-o-tempo", "livre-do-medo-cheio-de-fonte", "folego-para-continuar", "identidade-antes-da-tarefa", "chamados-para-sair", "fe-inteira-no-deserto", "oracao-no-secreto", "oracao-que-amadurece", "maturidade-para-decidir", "liberdade-para-obedecer", "maturidade-sem-retroceder", "emocoes-formadas-por-deus", "cuidado-da-alma", "esperanca-em-tempos-de-crise", "quando-o-mundo-parece-acabar"],
  comunidade: ["igreja-mesa", "igreja-sem-aparencia", "comunhao-que-vira-vida", "comunhao-no-caminho", "a-alegria-que-reconstroi", "juntos-de-verdade", "culto-que-vira-cultura", "casa-alianca-e-familia", "casa-relacoes", "corpo-alianca-e-ternura", "intimidade-com-reverencia", "relacoes-redimidas"],
  missao: ["proposito", "reino-publico", "reino-poder-e-servico", "fe-para-pensar-o-mundo", "fe-publica-sem-idolos", "fe-que-pensa-e-acontece", "evangelho-em-um-mundo-ferido", "missao-no-cotidiano", "trabalho-vocacao-e-contentamento", "bencao-que-se-reparte", "vida-inteira-em-missao"]
};

const NEWLY_CURATED_TRAIL_IDS = ["comunhao-no-caminho", "emocoes-formadas-por-deus", "fe-que-pensa-e-acontece", "vida-inteira-em-missao", "chamados-para-sair", "identidade-antes-da-tarefa"];

// Teto de quantos cards aparecem no feed "Trilhas novas para aprofundar a
// semana" (/familia/planos) -- com os 6 ids acima a fileira nao cabia na
// largura e sobrava uma barra de rolagem horizontal feia no fim (reportado
// pelo Rodrigo); com 5 a fileira cabe inteira sem cortar nenhum card.
const NEWLY_CURATED_TRAIL_LIMIT = 5;

const FEATURED_CURATED_TRAIL_COPY = {
  "comunhao-no-caminho": "Para transformar grupo, trilha e encontro em vínculo, acolhimento e cuidado durante a semana.",
  "emocoes-formadas-por-deus": "Para reconhecer a alma, atravessar processos e amadurecer emoções diante de Deus.",
  "fe-que-pensa-e-acontece": "Para crer com entendimento, discernir a vontade do Pai e agir no mundo com responsabilidade.",
  "vida-inteira-em-missao": "Para viver por convicção, fundamento e envio, não por medo, falta ou plano pessoal.",
  "chamados-para-sair": "Para sair do controle, da pressa por resultado e da fé sem relação.",
  "identidade-antes-da-tarefa": "Para reencontrar quem você é em Deus antes de medir a fé por tarefa, controle ou resultado.",
  "culto-que-vira-cultura": "Para transformar culto em vida, fruto, testemunho e interesse real por pessoas.",
  "liberdade-para-obedecer": "Para trocar autonomia sem direção por escolhas profundas, limites e amor concreto.",
  "folego-para-continuar": "Para quem precisa parar, respirar e voltar para a vida sem endurecer a alma.",
  "fe-sem-barganha": "Para trocar fé de interesse por entrega, descanso, transformação e testemunho.",
  "juntos-de-verdade": "Para trocar ajuntamento sem envolvimento por presença, vínculo e cuidado real.",
  "enraizados-em-amor": "Para trocar religiosidade orgulhosa por uma vida fortalecida por Cristo no íntimo.",
  "a-alegria-que-reconstroi": "Para transformar ruínas em oração, responsabilidade, Palavra e celebração compartilhada.",
  "quando-ninguem-parece-ver-voce": "Para quem está presente nos ambientes, mas ainda sente que não pertence de verdade.",
  "evangelho-em-um-mundo-ferido": "Para enxergar pessoas de novo quando medo, violência e descarte adoecem a alma.",
  "quando-tudo-nao-basta": "Para confrontar conquistas, aparência e controle quando nada disso consegue preencher a alma.",
  "adoracao-que-forma-a-alma": "Para transformar música, culto e sensibilidade em vida inteira diante de Deus.",
  "bencao-que-se-reparte": "Para deixar de apenas receber de Deus e se tornar bênção para pessoas reais.",
  "advento-a-paz-chegou": "Para viver o Natal como presença, promessa e paz, não apenas agenda e consumo.",
  "identidade-em-batalha": "Para discernir batalha espiritual sem medo, fuga ou terceirização da responsabilidade.",
  "intimidade-com-reverencia": "Para pensar corpo, desejo e intimidade sem culpa, consumo ou moralismo.",
  "fe-inteira-no-deserto": "Para reconstruir confiança, abandonar escapes e atravessar processos com Deus.",
  "fe-revitalizada": "Para abandonar aparências religiosas e viver como morada consciente de Deus.",
  "quando-o-mundo-parece-acabar": "Para reencontrar Cristo quando crise, dor e medo parecem ocupar toda a visão.",
  "fe-publica-sem-idolos": "Para viver a fé na cidade sem transformar opinião, ideologia ou poder em evangelho.",
  "reino-poder-e-servico": "Para trocar domínio por mesa, caráter e serviço como sinais do Reino.",
  "casa-alianca-e-familia": "Para viver casa, intimidade e família como lugar de aliança, não de carência.",
  "livre-do-medo-cheio-de-fonte": "Para sair da defesa, vencer o medo e viver a partir da fonte em Deus.",
  "sabedoria-para-viver-o-tempo": "Para discernir o tempo de Deus sem viver atrasado, apressado ou fora de compasso.",
  "igreja-sem-aparencia": "Para voltar ao Evangelho sem medo, personagem religioso ou controle.",
  "fe-para-pensar-o-mundo": "Para discernir cultura, história e sociedade sem reduzir Cristo às nossas ideias.",
  "formados-pela-palavra": "Para ler a Bíblia como caminho de formação, não como frase solta.",
  "trabalho-vocacao-e-contentamento": "Para viver trabalho, estudo e rotina como lugar de justiça e missão.",
  "maturidade-para-decidir": "Para quem precisa buscar direção antes de pedir força para continuar.",
  "alegria-que-permanece": "Para quem precisa reencontrar alegria sem fingir que a vida está leve.",
  "comunhao-que-vira-vida": "Para quem precisa sair da fé isolada e reaprender a caminhar com gente de verdade.",
  "oracao-que-amadurece": "Para transformar necessidade em escuta, presença e resposta prática diante de Deus.",
  "conhecer-cristo-de-verdade": "Para passar da admiração por Jesus para uma vida formada por Ele."
};

const PASTORAL_TEAM = [
  { id: "local-rodrigo", name: "Rodrigo Moraes", email: "rodrigo.moraes@batistamosaico.com.br" },
  { id: "local-ezequiel", name: "Ezequiel Naftali", email: "ezequiel.naftali@batistamosaico.com.br" },
  { id: "local-caio", name: "Caio Bianchi", email: "caio.bianchi@batistamosaico.com.br" }
];

const buildPastoralAvailability = (pastors = PASTORAL_TEAM) => {
  const slotTemplates = [
    { dayOffset: 1, hour: 9, minute: 30, duration: 45, mode: "online" },
    { dayOffset: 2, hour: 15, minute: 0, duration: 50, mode: "presencial" },
    { dayOffset: 4, hour: 19, minute: 30, duration: 45, mode: "online" }
  ];

  return pastors.flatMap((pastor, pastorIndex) => (
    slotTemplates.map((slot, slotIndex) => {
      const startAt = new Date();
      startAt.setDate(startAt.getDate() + slot.dayOffset + pastorIndex);
      startAt.setHours(slot.hour, slot.minute, 0, 0);
      const endAt = new Date(startAt);
      endAt.setMinutes(endAt.getMinutes() + slot.duration);
      return {
        id: `${pastor.id}-${startAt.toISOString()}-${slot.mode}`,
        pastorId: pastor.id,
        pastorName: pastor.name,
        startAt,
        endAt,
        duration: slot.duration,
        mode: slot.mode,
        label: slot.mode === "online" ? "Online" : "Presencial",
        helper: slot.mode === "online" ? "Videochamada pelo app" : "Na igreja, com local confirmado pela equipe",
        featured: pastorIndex === 0 && slotIndex === 0
      };
    })
  )).sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
};

const JOURNEY_PARTNERS = [
  { id: "p1", name: "Ana", color: "#60A5FA" },
  { id: "p2", name: "Lucas", color: "#34D399" },
  { id: "p3", name: "Mari", color: "#F472B6" }
];

const COMMUNITY_USERS_MOCK = [
  { id: "u1", name: "Felipe Almeida", email: "felipe@mosaico.app", avatarId: "m-03" },
  { id: "u2", name: "Ana Clara", email: "ana@mosaico.app", avatarId: "f-06" },
  { id: "u3", name: "Lucas Ribeiro", email: "lucas@mosaico.app", avatarId: "m-02" },
  { id: "u4", name: "Mariana Souza", email: "mariana@mosaico.app", avatarId: "f-03" },
  { id: "u5", name: "Bruno Costa", email: "bruno@mosaico.app", avatarId: "m-04" },
  { id: "u6", name: "Nayara Lima", email: "nayara@mosaico.app", avatarId: "f-10" }
];

const PROFILE_CONNECTION_OPTIONS = [
  {
    id: "member",
    label: "Sou membro",
    description: "Faço parte da Mosaico e quero organizar minha caminhada."
  },
  {
    id: "remote",
    label: "Acompanho de longe",
    description: "Quero caminhar com a comunidade mesmo à distância."
  },
  {
    id: "newcomer",
    label: "Ainda não sou membro",
    description: "Estou conhecendo a Mosaico de perto, mas ainda não sou membro."
  },
  // Legado -- nao oferecido mais como botao (ver filtro em PROFILE_CONNECTION_OPTIONS.filter),
  // so mantido aqui pra quem ja tinha escolhido "Cheguei por convite" antes
  // dessa mudanca continuar vendo o rotulo certo no resumo do perfil.
  {
    id: "invite",
    label: "Cheguei por convite",
    description: "Vim por um devocional, e-book ou indicação de alguém.",
    legacy: true
  }
];

// Segunda pergunta, nova: como a pessoa chegou ate a Mosaico (canal de
// descoberta), separado de qual e' a relacao dela com a igreja acima.
const DISCOVERY_CHANNEL_OPTIONS = [
  {
    id: "invite",
    label: "Convite de alguém",
    description: "Vim por indicação de um amigo, familiar ou alguém da igreja."
  },
  {
    id: "search",
    label: "Busca online",
    description: "Encontrei pelo Google, redes sociais ou no site."
  },
  {
    id: "walkby",
    label: "Passando em frente",
    description: "Conheci de perto, passando na rua ou no bairro."
  },
  {
    id: "other",
    label: "Outro jeito",
    description: "Cheguei até aqui de outra forma."
  }
];

const PROFILE_FORM_DEFAULT = {
  name: "",
  birthDate: "",
  email: "",
  whatsapp: "",
  state: "",
  city: "",
  connectionType: "newcomer",
  discoveryChannel: "other",
  invitedBy: "",
  privacyAccepted: false
};

// Formulario de "Meu cadastro" (membresia) na aba Perfil -- mesmos campos de
// CadastroMembro.jsx (institucional/pages), sem status/linkedUserId/householdId
// (decididos pelo backend em PATCH /me/member, nao pelo formulario).
const SELF_MEMBER_FORM_DEFAULT = {
  fullName: "",
  email: "",
  birthDate: "",
  cpf: "",
  mobilePhone: "",
  homePhone: "",
  emergencyContactName: "",
  emergencyPhone: "",
  address: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
  zipCode: "",
  spouseName: "",
  fatherName: "",
  motherName: "",
  specialNeeds: "",
  baptismDate: "",
  notBaptized: false,
  baptismChurch: "",
  churchRole: "",
  howMetChurch: "",
  attendingSince: "",
  notes: ""
};

// Formulario de "Meus filhos" na aba Perfil -- mesmos campos de
// CadastroCrianca.jsx, sem status/staffNotes (decididos pelo backend/equipe,
// nao pelo formulario do responsavel).
const SELF_CHILD_FORM_DEFAULT = {
  fullName: "",
  birthDate: "",
  address: "",
  addressComplement: "",
  neighborhood: "",
  city: "",
  state: "",
  zipCode: "",
  fatherName: "",
  fatherPhone: "",
  fatherEmail: "",
  motherName: "",
  motherPhone: "",
  motherEmail: "",
  guardianName: "",
  guardianPhone: "",
  guardianRelationship: "",
  livesWith: "",
  hasAllergies: false,
  allergyDetails: "",
  medications: "",
  imageAuthorization: true,
  importantNotes: ""
};

const AVATAR_OPTIONS = [
  { id: "m-01", label: "Branco loiro", gender: "male", skin: "#f4d7b7", hair: "#d9b24c", style: "male-short", tone: "#1d4ed8" },
  { id: "m-02", label: "Branco preto", gender: "male", skin: "#f4d7b7", hair: "#1c1b1a", style: "male-short", tone: "#2563eb" },
  { id: "m-03", label: "Moreno claro", gender: "male", skin: "#d9ab84", hair: "#1f1d1c", style: "male-short", tone: "#0ea5e9" },
  { id: "m-04", label: "Moreno escuro", gender: "male", skin: "#8d5d3c", hair: "#151413", style: "male-short", tone: "#0284c7" },
  { id: "m-05", label: "Branco careca", gender: "male", skin: "#f4d7b7", hair: "#f4d7b7", style: "male-bald", tone: "#3b82f6" },
  { id: "m-06", label: "Moreno claro careca", gender: "male", skin: "#d9ab84", hair: "#d9ab84", style: "male-bald", tone: "#0ea5e9" },
  { id: "m-07", label: "Moreno escuro careca", gender: "male", skin: "#8d5d3c", hair: "#8d5d3c", style: "male-bald", tone: "#0369a1" },
  { id: "f-01", label: "Loira longa lisa", gender: "female", skin: "#f4d7b7", hair: "#dfbe63", style: "female-long-straight", tone: "#7c3aed" },
  { id: "f-02", label: "Loira curta lisa", gender: "female", skin: "#f4d7b7", hair: "#dfbe63", style: "female-short-straight", tone: "#8b5cf6" },
  { id: "f-03", label: "Loira longa cacheada", gender: "female", skin: "#f4d7b7", hair: "#d9b24c", style: "female-long-curly", tone: "#a855f7" },
  { id: "f-04", label: "Loira curta cacheada", gender: "female", skin: "#f4d7b7", hair: "#d9b24c", style: "female-short-curly", tone: "#9333ea" },
  { id: "f-05", label: "Morena clara longa lisa", gender: "female", skin: "#d9ab84", hair: "#2b2119", style: "female-long-straight", tone: "#2563eb" },
  { id: "f-06", label: "Morena clara curta lisa", gender: "female", skin: "#d9ab84", hair: "#2b2119", style: "female-short-straight", tone: "#0ea5e9" },
  { id: "f-07", label: "Morena clara longa cacheada", gender: "female", skin: "#d9ab84", hair: "#2a211a", style: "female-long-curly", tone: "#0284c7" },
  { id: "f-08", label: "Morena clara curta cacheada", gender: "female", skin: "#d9ab84", hair: "#2a211a", style: "female-short-curly", tone: "#0891b2" },
  { id: "f-09", label: "Negra longa cacheada", gender: "female", skin: "#6d432a", hair: "#191513", style: "female-long-curly", tone: "#7c3aed" },
  { id: "f-10", label: "Negra curta cacheada", gender: "female", skin: "#6d432a", hair: "#191513", style: "female-short-curly", tone: "#6d28d9" },
  { id: "f-11", label: "Negra longa lisa", gender: "female", skin: "#6d432a", hair: "#171311", style: "female-long-straight", tone: "#4f46e5" },
  { id: "f-12", label: "Negra curta lisa", gender: "female", skin: "#6d432a", hair: "#171311", style: "female-short-straight", tone: "#4338ca" },
  { id: "m-08", label: "Branco boné", gender: "male", skin: "#f4d7b7", hair: "#1c1b1a", style: "male-cap", tone: "#0284c7" },
  { id: "m-09", label: "Negro boné", gender: "male", skin: "#6d432a", hair: "#151413", style: "male-cap", tone: "#4f46e5", capColor: "#94a3b8", capShadow: "#475569" },
  { id: "f-13", label: "Branca boné", gender: "female", skin: "#f4d7b7", hair: "#1c1b1a", style: "female-cap", tone: "#9333ea" },
  { id: "f-14", label: "Negra boné", gender: "female", skin: "#6d432a", hair: "#191513", style: "female-cap", tone: "#6d28d9" },
  { id: "f-15", label: "Loira boné", gender: "female", skin: "#f4d7b7", hair: "#dfbe63", style: "female-cap", tone: "#ec4899" }
];

// Sistema novo de avatar "montavel": ao inves de uma lista fixa de avatares
// prontos (uma combinacao pra cada cor), a pessoa escolhe separadamente o
// formato do cabelo, a cor da pele, a cor do cabelo e os acessorios. Tudo
// isso vira um unico avatarId (ex: "custom:afro|negra|preto|azul|none|full"),
// que continua sendo so uma string -- o mesmo campo avatarId de sempre, sem
// mudanca nenhuma no backend. Os avatares antigos (lista AVATAR_OPTIONS
// acima) continuam funcionando exatamente como antes pra quem ja escolheu
// um deles.
const AVATAR_STYLE_CATALOG = [
  { id: "curto", label: "Cabelo curto", renderStyle: "male-short" },
  { id: "careca", label: "Careca", renderStyle: "male-bald" },
  { id: "medio-liso", label: "Médio liso", renderStyle: "medium-straight" },
  { id: "longo-liso", label: "Longo liso", renderStyle: "female-long-straight" },
  { id: "longo-sem-franja", label: "Longo sem franja", renderStyle: "female-long-straight-no-bangs" },
  { id: "longo-cacheado", label: "Longo cacheado", renderStyle: "female-long-curly" },
  { id: "curto-cacheado", label: "Curto cacheado", renderStyle: "female-short-curly" },
  { id: "afro", label: "Afro", renderStyle: "afro" },
  { id: "trancas", label: "Tranças", renderStyle: "braids" },
  { id: "coque", label: "Coque", renderStyle: "bun" },
  { id: "chanel", label: "Chanel", renderStyle: "female-short-straight" },
  { id: "moicano", label: "Moicano", renderStyle: "moicano" },
  { id: "topete", label: "Topete", renderStyle: "topete" },
  { id: "rabo-cavalo", label: "Rabo de cavalo", renderStyle: "rabo-cavalo" },
  { id: "samurai", label: "Samurai", renderStyle: "samurai" }
];

// Boné e lenço nao sao "formato de cabelo" -- sao acessorios que entram por
// cima de qualquer penteado (como oculos/barba), por isso moraram pra
// secao Acessorios em vez do grid de cabelo.
const AVATAR_HEADWEAR_OPTIONS = [
  { id: "none", label: "Nenhum" },
  { id: "bone", label: "Boné" },
  { id: "lenco", label: "Lenço" }
];

const AVATAR_SKIN_TONES = [
  { id: "clara", hex: "#f4d7b7", label: "Clara" },
  { id: "morena-clara", hex: "#d9ab84", label: "Morena clara" },
  { id: "morena-escura", hex: "#8d5d3c", label: "Morena escura" },
  { id: "negra", hex: "#6d432a", label: "Negra" }
];

const AVATAR_HAIR_COLORS = [
  { id: "loiro", hex: "#d9b24c", label: "Loiro" },
  { id: "castanho-claro", hex: "#8a6a4a", label: "Castanho claro" },
  { id: "castanho-escuro", hex: "#3a2a1a", label: "Castanho escuro" },
  { id: "preto", hex: "#1c1b1a", label: "Preto" },
  { id: "ruivo", hex: "#a6432c", label: "Ruivo" },
  { id: "grisalho", hex: "#c7c9cc", label: "Grisalho" },
  { id: "azul", hex: "#3b82f6", label: "Azul" }
];

const AVATAR_EYE_COLORS = [
  { id: "castanho-escuro", hex: "#3b2a1a", label: "Castanho escuro" },
  { id: "castanho-claro", hex: "#8a6a4a", label: "Castanho claro" },
  { id: "verde", hex: "#4f7a5c", label: "Verde" },
  { id: "azul", hex: "#5b83b0", label: "Azul" },
  { id: "mel", hex: "#a67b3f", label: "Mel" },
  { id: "preto", hex: "#17110c", label: "Preto" }
];

const AVATAR_ACCESSORY_COLORS = [
  { id: "azul", hex: "#3b6fd1", label: "Azul" },
  { id: "vinho", hex: "#7c2d3a", label: "Vinho" },
  { id: "verde", hex: "#0f6e6e", label: "Verde" },
  { id: "mostarda", hex: "#b9871f", label: "Mostarda" },
  { id: "cinza", hex: "#475569", label: "Cinza" },
  { id: "roxo", hex: "#7c3aed", label: "Roxo" }
];

const AVATAR_GLASSES_OPTIONS = [
  { id: "none", label: "Nenhum" },
  { id: "dark", label: "Clássico" },
  { id: "round", label: "Redondo" }
];

const AVATAR_BEARD_OPTIONS = [
  { id: "none", label: "Nenhuma" },
  { id: "goatee", label: "Curta" },
  { id: "full", label: "Cheia" },
  { id: "bigode", label: "Bigode" },
  { id: "cavanhaque", label: "Cavanhaque" },
  { id: "rala", label: "Rala" }
];

const CUSTOM_AVATAR_PREFIX = "custom:";

function encodeCustomAvatarId({ style, skin, hair, accessoryColor, glasses, beard, eyeColor, headwear }) {
  return `${CUSTOM_AVATAR_PREFIX}${style}|${skin}|${hair}|${accessoryColor}|${glasses}|${beard}|${eyeColor}|${headwear || "none"}`;
}

function decodeCustomAvatarId(avatarId) {
  if (!avatarId || typeof avatarId !== "string" || !avatarId.startsWith(CUSTOM_AVATAR_PREFIX)) return null;
  const parts = avatarId.slice(CUSTOM_AVATAR_PREFIX.length).split("|");
  const [styleId, skinId, hairId, accessoryColorId, glasses, beard, eyeColorId, headwearId] = parts;
  // Boné/lenço eram um "formato de cabelo" antes -- avatarId salvo assim
  // continua decodificando igual, so que agora vira o campo headwear novo,
  // com o penteado caindo pro padrao "curto" por baixo do boné/lenço.
  const legacyHeadwear = (styleId === "bone" || styleId === "lenco") ? styleId : null;
  const effectiveStyleId = legacyHeadwear ? "curto" : styleId;
  const styleDef = AVATAR_STYLE_CATALOG.find((item) => item.id === effectiveStyleId);
  const skinDef = AVATAR_SKIN_TONES.find((item) => item.id === skinId);
  const hairDef = AVATAR_HAIR_COLORS.find((item) => item.id === hairId);
  if (!styleDef || !skinDef || !hairDef) return null;
  const accessoryDef = AVATAR_ACCESSORY_COLORS.find((item) => item.id === accessoryColorId);
  const headwear = legacyHeadwear || (headwearId && headwearId !== "none" ? headwearId : null);
  // avatarId salvo antes da cor de olho existir nao tem esse 7o campo --
  // cai no primeiro tom da paleta em vez de quebrar a decodificacao.
  const eyeDef = AVATAR_EYE_COLORS.find((item) => item.id === eyeColorId) || AVATAR_EYE_COLORS[0];
  return {
    id: avatarId,
    label: styleDef.label,
    gender: null,
    skin: skinDef.hex,
    hair: hairDef.hex,
    eyeColor: eyeDef.hex,
    tone: "#3b6fd1",
    style: styleDef.renderStyle,
    headwear,
    capColor: accessoryDef?.hex,
    capShadow: "#1f2937",
    hasGlasses: glasses && glasses !== "none" ? glasses : null,
    hasBeard: beard && beard !== "none" ? beard : null,
    custom: true
  };
}

function parseCustomAvatarId(avatarId) {
  if (!avatarId || typeof avatarId !== "string" || !avatarId.startsWith(CUSTOM_AVATAR_PREFIX)) return null;
  const [style, skin, hair, accessoryColor, glasses, beard, eyeColor, headwear] = avatarId.slice(CUSTOM_AVATAR_PREFIX.length).split("|");
  // Compat com avatarId antigo, de quando boné/lenço eram um "formato de
  // cabelo" (ver decodeCustomAvatarId) -- migra pro campo headwear novo.
  const legacyHeadwear = (style === "bone" || style === "lenco") ? style : null;
  return {
    style: legacyHeadwear ? "curto" : style,
    skin,
    hair,
    accessoryColor,
    glasses,
    beard,
    eyeColor,
    headwear: legacyHeadwear || headwear
  };
}

// ---------------------------------------------------------------------------
// Avatares "Avataaars" (DiceBear, open-source/MIT) -- motor novo, escolhido
// pra ficar mais realista que o SVG desenhado a mao acima. Os avatares
// antigos (AVATAR_OPTIONS fixos e os "custom:..." desenhados a mao)
// continuam funcionando exatamente como antes -- o id novo usa o prefixo
// "dicebear:" pra nao colidir com nenhum dos dois formatos anteriores.
// ---------------------------------------------------------------------------
// "length" categoriza cada estilo pra dar pra filtrar na tela de avatar --
// "curto"/"longo" sao os dois grupos principais, mas nem todo estilo e'
// sobre comprimento de cabelo (careca, raspados, chapeu/gorro/hijab/turbante),
// entao esses entram no terceiro grupo "acessorios" em vez de forcar os dois
// extremos. Pedido do Rodrigo: com 34 estilos em 3 fileiras, um filtro ajuda
// a achar o estilo mais rapido (nao substitui o preview fixo -- ver abaixo).
const DICEBEAR_TOP_OPTIONS = [
  { id: "bald", label: "Careca", length: "curto" },
  { id: "shortFlat", label: "Curto liso", length: "curto" },
  { id: "shortRound", label: "Curto arredondado", length: "curto" },
  { id: "shortWaved", label: "Curto ondulado", length: "curto" },
  { id: "shortCurly", label: "Curto cacheado", length: "curto" },
  { id: "sides", label: "Raspado nas laterais", length: "curto" },
  { id: "shavedSides", label: "Moicano", length: "curto" },
  { id: "theCaesar", label: "César", length: "curto" },
  { id: "theCaesarAndSidePart", label: "César com risca", length: "curto" },
  { id: "shaggy", label: "Desalinhado", length: "curto" },
  { id: "frizzle", label: "Frisado", length: "curto" },
  { id: "shaggyMullet", label: "Mullet", length: "longo" },
  { id: "straight01", label: "Liso longo", length: "longo" },
  { id: "straight02", label: "Liso médio", length: "longo" },
  { id: "straightAndStrand", label: "Liso com mecha", length: "longo" },
  { id: "longButNotTooLong", label: "Longo médio", length: "longo" },
  { id: "bigHair", label: "Black power", length: "longo" },
  { id: "fro", label: "Afro", length: "longo" },
  { id: "froBand", label: "Afro com faixa", length: "longo" },
  { id: "dreads", label: "Dreads", length: "longo" },
  { id: "dreads01", label: "Dreads presos", length: "longo" },
  { id: "dreads02", label: "Dreads soltos", length: "longo" },
  { id: "curly", label: "Cacheado", length: "longo" },
  { id: "curvy", label: "Ondulado", length: "longo" },
  { id: "bun", label: "Coque", length: "longo" },
  { id: "bob", label: "Chanel", length: "longo" },
  { id: "miaWallace", label: "Chanel com franja", length: "longo" },
  { id: "frida", label: "Tranças com flor", length: "longo" },
  { id: "hat", label: "Chapéu", length: "acessorios" },
  { id: "hijab", label: "Hijab", length: "acessorios" },
  { id: "turban", label: "Turbante", length: "acessorios" },
  { id: "winterHat1", label: "Gorro", length: "acessorios" },
  { id: "winterHat02", label: "Gorro com pompom", length: "acessorios" },
  { id: "winterHat03", label: "Gorro listrado", length: "acessorios" },
  { id: "winterHat04", label: "Gorro dobrado", length: "acessorios" }
];

const AVATAR_HAIR_LENGTH_OPTIONS = [
  { id: "todos", label: "Todos" },
  { id: "curto", label: "Curto" },
  { id: "longo", label: "Longo" },
  { id: "acessorios", label: "Chapéu/acessório" }
];

const DICEBEAR_SKIN_COLORS = [
  { id: "614335", hex: "#614335", label: "Pele escura" },
  { id: "d08b5b", hex: "#d08b5b", label: "Pele morena escura" },
  { id: "ae5d29", hex: "#ae5d29", label: "Pele morena" },
  { id: "edb98a", hex: "#edb98a", label: "Pele média" },
  { id: "ffdbb4", hex: "#ffdbb4", label: "Pele clara" },
  { id: "fd9841", hex: "#fd9841", label: "Pele dourada" },
  { id: "f8d25c", hex: "#f8d25c", label: "Pele amarelada" }
];

const DICEBEAR_HAIR_COLORS = [
  { id: "2c1b18", hex: "#2c1b18", label: "Preto" },
  { id: "4a312c", hex: "#4a312c", label: "Castanho escuro" },
  { id: "724133", hex: "#724133", label: "Castanho" },
  { id: "a55728", hex: "#a55728", label: "Castanho avermelhado" },
  { id: "b58143", hex: "#b58143", label: "Castanho claro" },
  { id: "d6b370", hex: "#d6b370", label: "Loiro escuro" },
  { id: "ecdcbf", hex: "#ecdcbf", label: "Loiro claro" },
  { id: "c93305", hex: "#c93305", label: "Ruivo" },
  { id: "e8e1e1", hex: "#e8e1e1", label: "Grisalho" },
  { id: "f59797", hex: "#f59797", label: "Rosa" },
  { id: "3b82f6", hex: "#3b82f6", label: "Azul" }
];

const DICEBEAR_BG_COLORS = [
  { id: "b6e3f4", hex: "#b6e3f4", label: "Azul claro" },
  { id: "c0aede", hex: "#c0aede", label: "Lilás" },
  { id: "d1d4f9", hex: "#d1d4f9", label: "Lavanda" },
  { id: "ffd5dc", hex: "#ffd5dc", label: "Rosa claro" },
  { id: "ffdfbf", hex: "#ffdfbf", label: "Pêssego" },
  { id: "e8f0fb", hex: "#e8f0fb", label: "Cinza claro" }
];

const DICEBEAR_GLASSES_OPTIONS = [
  { id: "none", label: "Nenhum" },
  { id: "prescription01", label: "Redondo" },
  { id: "prescription02", label: "Quadrado" },
  { id: "round", label: "Retrô" },
  { id: "wayfarers", label: "Clássico" },
  { id: "sunglasses", label: "Escuro" },
  { id: "kurt", label: "Pequeno" },
  { id: "eyepatch", label: "Tapa-olho" }
];

const DICEBEAR_BEARD_OPTIONS = [
  { id: "none", label: "Nenhuma" },
  { id: "beardLight", label: "Leve" },
  { id: "beardMedium", label: "Média" },
  { id: "beardMajestic", label: "Cheia" },
  { id: "moustacheFancy", label: "Bigode fino" },
  { id: "moustacheMagnum", label: "Bigode grosso" }
];

const DICEBEAR_CLOTHING_OPTIONS = [
  { id: "shirtCrewNeck", label: "Camiseta gola redonda" },
  { id: "shirtVNeck", label: "Camiseta gola V" },
  { id: "shirtScoopNeck", label: "Camiseta gola canoa" },
  { id: "collarAndSweater", label: "Gola e suéter" },
  { id: "hoodie", label: "Moletom" },
  { id: "overall", label: "Macacão" },
  { id: "blazerAndShirt", label: "Blazer e camisa" },
  { id: "blazerAndSweater", label: "Blazer e suéter" }
];

const DICEBEAR_CLOTHES_COLORS = [
  { id: "262e33", hex: "#262e33", label: "Preto" },
  { id: "3c4f5c", hex: "#3c4f5c", label: "Cinza escuro" },
  { id: "929598", hex: "#929598", label: "Cinza" },
  { id: "e6e6e6", hex: "#e6e6e6", label: "Cinza claro" },
  { id: "25557c", hex: "#25557c", label: "Azul escuro" },
  { id: "5199e4", hex: "#5199e4", label: "Azul" },
  { id: "65c9ff", hex: "#65c9ff", label: "Azul claro" },
  { id: "b1e2ff", hex: "#b1e2ff", label: "Azul bebê" },
  { id: "a7ffc4", hex: "#a7ffc4", label: "Verde claro" },
  { id: "ffafb9", hex: "#ffafb9", label: "Rosa" },
  { id: "ffffb1", hex: "#ffffb1", label: "Amarelo claro" },
  { id: "ff488e", hex: "#ff488e", label: "Pink" },
  { id: "ff5c5c", hex: "#ff5c5c", label: "Vermelho" },
  { id: "ffffff", hex: "#ffffff", label: "Branco" }
];

const DICEBEAR_MOUTH_OPTIONS = [
  { id: "default", label: "Neutra" },
  { id: "smile", label: "Sorriso" },
  { id: "serious", label: "Séria" },
  { id: "twinkle", label: "Animada" },
  { id: "tongue", label: "Língua de fora" }
];

const DICEBEAR_EYE_COLORS = [
  { id: "090a0a", hex: "#090a0a", label: "Preto" },
  { id: "3d2b1f", hex: "#3d2b1f", label: "Castanho escuro" },
  { id: "6b4226", hex: "#6b4226", label: "Castanho" },
  { id: "4a6fa5", hex: "#4a6fa5", label: "Azul" },
  { id: "6b8e23", hex: "#6b8e23", label: "Verde" },
  { id: "7d8491", hex: "#7d8491", label: "Cinza" },
  { id: "b08d57", hex: "#b08d57", label: "Mel" }
];

const DICEBEAR_EYES_OPTIONS = [
  { id: "default", label: "Neutro" },
  { id: "happy", label: "Feliz" },
  { id: "squint", label: "Sorridente" },
  { id: "surprised", label: "Surpreso" },
  { id: "wink", label: "Piscando" },
  { id: "side", label: "De lado" }
];

const DICEBEAR_GLASSES_COLORS = [
  { id: "262e33", hex: "#262e33", label: "Preto" },
  { id: "3c4f5c", hex: "#3c4f5c", label: "Cinza escuro" },
  { id: "929598", hex: "#929598", label: "Cinza" },
  { id: "e6e6e6", hex: "#e6e6e6", label: "Cinza claro" },
  { id: "25557c", hex: "#25557c", label: "Azul escuro" },
  { id: "5199e4", hex: "#5199e4", label: "Azul" },
  { id: "65c9ff", hex: "#65c9ff", label: "Azul claro" },
  { id: "b1e2ff", hex: "#b1e2ff", label: "Azul bebê" },
  { id: "a7ffc4", hex: "#a7ffc4", label: "Verde claro" },
  { id: "ffdeb5", hex: "#ffdeb5", label: "Pêssego" },
  { id: "ffafb9", hex: "#ffafb9", label: "Rosa" },
  { id: "ffffb1", hex: "#ffffb1", label: "Amarelo claro" },
  { id: "ff488e", hex: "#ff488e", label: "Pink" },
  { id: "ff5c5c", hex: "#ff5c5c", label: "Vermelho" },
  { id: "ffffff", hex: "#ffffff", label: "Branco" }
];

const DICEBEAR_AVATAR_PREFIX = "dicebear:";

function encodeDicebearAvatarId({ top, skinColor, hairColor, bgColor, eyes, eyeColor, mouth, glasses, glassesColor, beard, beardColor, clothing, clothesColor }) {
  const payload = {
    top,
    skinColor,
    hairColor,
    bgColor,
    eyes: eyes || "default",
    eyeColor: eyeColor || "090a0a",
    mouth: mouth || "default",
    glasses: glasses || "none",
    glassesColor: glassesColor || "262e33",
    beard: beard || "none",
    beardColor: beardColor || hairColor,
    clothing: clothing || "shirtCrewNeck",
    clothesColor: clothesColor || "3c4f5c"
  };
  const json = JSON.stringify(payload);
  const base64 = typeof window !== "undefined" && window.btoa
    ? window.btoa(unescape(encodeURIComponent(json)))
    : Buffer.from(json, "utf-8").toString("base64");
  return `${DICEBEAR_AVATAR_PREFIX}${base64}`;
}

function buildDicebearOptions(parsed) {
  const isBald = parsed.top === "bald";
  const options = {
    top: [isBald ? "shortFlat" : parsed.top],
    topProbability: isBald ? 0 : 100,
    skinColor: [parsed.skinColor],
    hairColor: [parsed.hairColor],
    backgroundColor: [parsed.bgColor],
    backgroundType: ["solid"],
    eyes: [parsed.eyes || "default"],
    mouth: [parsed.mouth || "default"],
    clothing: [parsed.clothing || "shirtCrewNeck"],
    clothesColor: [parsed.clothesColor || "3c4f5c"],
    accessoriesProbability: parsed.glasses && parsed.glasses !== "none" ? 100 : 0,
    facialHairProbability: parsed.beard && parsed.beard !== "none" ? 100 : 0
  };
  if (parsed.glasses && parsed.glasses !== "none") {
    options.accessories = [parsed.glasses];
    options.accessoriesColor = [parsed.glassesColor || "262e33"];
  }
  if (parsed.beard && parsed.beard !== "none") {
    options.facialHair = [parsed.beard];
    options.facialHairColor = [parsed.beardColor || parsed.hairColor];
  }
  return options;
}

function svgFillParent(svgString) {
  return svgString.replace("<svg ", '<svg width="100%" height="100%" style="display:block" ');
}

// O estilo Avataaars nao tem opcao de cor de iris no proprio gerador (o
// olho e sempre desenhado em preto) -- entao pintamos a pupila por cima,
// direto no SVG ja gerado. sliceBalancedGroup acha o <g>...</g> dos olhos
// contando aninhamento (algumas expressoes, tipo "wink", tem <g> dentro de
// <g>), e so troca fill="#000" (a marca preta do olho) pela cor escolhida
// -- deixa fill="#fff" (o branco do globo ocular em expressoes tipo
// "surpreso") intocado.
function sliceBalancedGroup(svgString, startIdx) {
  const groupTagRe = /<g\b|<\/g>/g;
  groupTagRe.lastIndex = startIdx;
  let depth = 0;
  let match;
  while ((match = groupTagRe.exec(svgString))) {
    if (match[0] === "</g>") {
      depth -= 1;
      if (depth === 0) return svgString.slice(startIdx, match.index + 4);
    } else {
      depth += 1;
    }
  }
  return null;
}

function recolorEyesInSvg(svgString, hex) {
  const marker = '<g transform="translate(76 90)">';
  const startIdx = svgString.indexOf(marker);
  if (startIdx === -1) return svgString;
  const block = sliceBalancedGroup(svgString, startIdx);
  if (!block) return svgString;
  const recolored = block.split('fill="#000"').join(`fill="${hex}"`);
  if (recolored === block) return svgString;
  return svgString.slice(0, startIdx) + recolored + svgString.slice(startIdx + block.length);
}

// Cache em memória (por sessão de app) pra não regerar o mesmo SVG toda vez
// que o avatarId "dicebear:..." aparece de novo (ex.: mesmo usuário
// renderizado em vários lugares da tela).
const dicebearAvatarCache = new Map();

function decodeDicebearAvatarId(avatarId) {
  if (!avatarId || typeof avatarId !== "string" || !avatarId.startsWith(DICEBEAR_AVATAR_PREFIX)) return null;
  if (dicebearAvatarCache.has(avatarId)) return dicebearAvatarCache.get(avatarId);
  let parsed;
  try {
    const base64 = avatarId.slice(DICEBEAR_AVATAR_PREFIX.length);
    const json = typeof window !== "undefined" && window.atob
      ? decodeURIComponent(escape(window.atob(base64)))
      : Buffer.from(base64, "base64").toString("utf-8");
    parsed = JSON.parse(json);
  } catch (error) {
    return null;
  }
  if (!parsed || !parsed.top || !parsed.skinColor || !parsed.hairColor || !parsed.bgColor) return null;
  let svg;
  try {
    const avatar = createAvatar(avataaarsStyle, buildDicebearOptions(parsed));
    svg = recolorEyesInSvg(avatar.toString(), `#${parsed.eyeColor || "090a0a"}`);
    svg = svgFillParent(svg);
  } catch (error) {
    return null;
  }
  const result = {
    id: avatarId,
    label: "Avatar personalizado",
    gender: null,
    dicebearSvg: svg,
    dicebearOptions: parsed
  };
  dicebearAvatarCache.set(avatarId, result);
  return result;
}

function isValidAvatarId(avatarId) {
  return AVATAR_OPTIONS.some((item) => item.id === avatarId) || Boolean(decodeDicebearAvatarId(avatarId)) || Boolean(decodeCustomAvatarId(avatarId));
}

const AvatarSvg = ({ avatar, size = 34 }) => {
  if (!avatar) return null;
  if (avatar.dicebearSvg) {
    return (
      <span
        style={{ width: size, height: size, borderRadius: "999px", overflow: "hidden", display: "inline-flex", flex: "0 0 auto" }}
        dangerouslySetInnerHTML={{ __html: avatar.dicebearSvg }}
      />
    );
  }
  const skin = avatar.skin || "#d9ab84";
  const hair = avatar.hair || "#1f1f1f";
  const accent = avatar.tone || "#3b82f6";
  const isFemale = avatar.gender === "female";
  const eyeColor = avatar.eyeColor || "#172033";
  const clipId = `avatar-clip-${avatar.id}`;
  const gradId = `avatar-bg-${avatar.id}`;
  // Boné/lenço agora sao acessorios independentes do penteado (avatar.headwear)
  // -- mas os avatares fixos antigos (m-08, f-13...) ainda guardam isso no
  // proprio "style", entao os dois jeitos continuam sendo reconhecidos aqui.
  const headwearRenderStyle = avatar.headwear === "bone" ? "male-cap" : avatar.headwear === "lenco" ? "headscarf" : null;
  const hasCap = Boolean(headwearRenderStyle) || avatar.style.includes("cap") || avatar.style === "headscarf";
  const hairRenderStyle = headwearRenderStyle || avatar.style;
  const shirtColor = isFemale ? "#303760" : "#263142";
  const lipColor = skin === "#6d432a" ? "#b86a5f" : "#c9786f";
  const capColor = avatar.capColor || (isFemale ? "#8b5cf6" : "#475569");
  const capShadow = avatar.capShadow || "#1f2937";

  const hairByStyle = {
    "male-short": <path d="M13 21 C 14 12, 20 9, 25 9 C 32 9, 37 13, 37 22 C 33 17, 28 17, 23 17 C 19 17, 16 18, 13 21 Z" fill={hair} />,
    "male-bald": null,
    "female-long-straight": <path d="M10 22 C 10 12, 16 7, 24 7 C 33 7, 38 12, 38 23 L 36 48 H 30 L 31 24 C 27 22, 21 22, 17 24 L 18 48 H 12 Z" fill={hair} />,
    "female-long-straight-no-bangs": <path d="M10 22 C 10 12, 16 7, 24 7 C 33 7, 38 12, 38 23 L 36 48 H 30 L 31 24 C 27 22, 21 22, 17 24 L 18 48 H 12 Z" fill={hair} />,
    "female-short-straight": <path d="M11 22 C 12 12, 17 8, 24 8 C 31 8, 36 12, 37 22 C 35 27, 31 29, 24 29 C 17 29, 13 27, 11 22 Z" fill={hair} />,
    "female-long-curly": <path d="M9 22 C 9 13, 16 7, 24 7 C 33 7, 39 13, 39 23 C 41 28, 38 31, 39 36 C 40 41, 38 45, 36 48 H 30 L 31 24 C 27 21, 21 21, 17 24 L 18 48 H 12 C 10 45, 8 41, 9 36 C 10 31, 7 28, 9 22 Z" fill={hair} />,
    "female-short-curly": <path d="M9 22 C 9 13, 16 8, 24 8 C 32 8, 39 13, 39 22 C 40 26, 38 29, 36 31 C 32 34, 17 34, 12 31 C 10 29, 8 26, 9 22 Z" fill={hair} />,
    "male-cap": <path d="M13 22 C 15 15, 20 12, 25 12 C 31 12, 35 15, 37 22 C 33 19, 28 18, 24 18 C 20 18, 16 19, 13 22 Z" fill={hair} />,
    "female-cap": <path d="M10 22 C 10 13, 17 8, 24 8 C 32 8, 38 13, 38 23 L 36 48 H 30 L 31 25 C 27 22, 21 22, 17 25 L 18 48 H 12 Z" fill={hair} />,
    "medium-straight": <path d="M10 22 C 10 12, 16 7, 24 7 C 33 7, 38 12, 38 23 L 37 38 C 33 40, 28 41, 24 41 C 20 41, 15 40, 11 38 Z" fill={hair} />,
    "afro": <g fill={hair}><circle cx="24" cy="19" r="17.5" /><circle cx="9" cy="23" r="7.4" /><circle cx="39" cy="23" r="7.4" /><circle cx="13" cy="9" r="6.4" /><circle cx="35" cy="9" r="6.4" /><circle cx="24" cy="4.5" r="7.4" /><circle cx="9.5" cy="34" r="6.2" /><circle cx="38.5" cy="34" r="6.2" /></g>,
    "braids": (
      <>
        <path d="M10 22 C 10 12, 16 7, 24 7 C 33 7, 38 12, 38 23 L 36 48 H 30 L 31 24 C 27 22, 21 22, 17 24 L 18 48 H 12 Z" fill={hair} />
        <g stroke="#000" strokeOpacity="0.22" strokeWidth="1" fill="none">
          <path d="M13 28 L17.3 28" /><path d="M13.3 32 L17.7 32" /><path d="M13.6 36 L18.1 36" /><path d="M13.9 40 L18.5 40" /><path d="M14.2 44 L18.9 44" />
          <path d="M30.7 28 L35 28" /><path d="M30.3 32 L34.7 32" /><path d="M29.9 36 L34.4 36" /><path d="M29.5 40 L34.1 40" /><path d="M29.1 44 L33.8 44" />
        </g>
      </>
    ),
    "bun": (
      <>
        <path d="M11 22 C 12 12, 17 8, 24 8 C 31 8, 36 12, 37 22 C 35 27, 31 29, 24 29 C 17 29, 13 27, 11 22 Z" fill={hair} />
        <circle cx="24" cy="6.5" r="4.3" fill={hair} />
        <rect x="21.3" y="9.6" width="5.4" height="2.1" rx="1" fill="#000" opacity="0.22" />
      </>
    ),
    "headscarf": <path d="M9 23 C 9 12, 16 6, 24 6 C 32 6, 39 12, 39 23 L 40 35 C 40 41, 34 41, 32 37 L 32 25 C 32 22, 28 21, 24 21 C 20 21, 16 22, 16 25 L 16 37 C 14 41, 8 41, 8 35 Z" fill={capColor} />,
    "moicano": null,
    "topete": <path d="M13 21 C 14 12, 20 9, 25 9 C 32 9, 37 13, 37 22 C 33 17, 28 17, 23 17 C 19 17, 16 18, 13 21 Z" fill={hair} />,
    "rabo-cavalo": (
      <>
        <path d="M11 22 C 12 12, 17 8, 24 8 C 31 8, 36 12, 37 22 C 35 27, 31 29, 24 29 C 17 29, 13 27, 11 22 Z" fill={hair} />
        <path d="M35 21 C 39 24, 40 30, 38 36 C 37 39, 36 42, 37 46 L 33 46 C 32 41, 32 36, 33 31 C 33 27, 34 23, 35 21 Z" fill={hair} />
      </>
    ),
    "samurai": (
      <>
        <path d="M12 23 C 13 13, 18 9, 24 9 C 30 9, 35 13, 36 23 C 33 19, 29 17, 24 17 C 19 17, 15 19, 12 23 Z" fill={hair} />
        <path d="M20 8.6 C 20 6.1, 21.7 4.2, 24 4.2 C 26.3 4.2, 28 6.1, 28 8.6 C 28 10.8, 26.3 12.2, 24 12.2 C 21.7 12.2, 20 10.8, 20 8.6 Z" fill={hair} />
        <rect x="20.6" y="10.6" width="6.8" height="2" rx="1" fill="#000" opacity="0.24" />
      </>
    )
  };

  const frontHairByStyle = {
    "male-short": <path d="M14 18 C 17 12, 22 10, 27 11 C 32 12, 35 15, 36 20 C 31 17, 25 16, 20 17 C 18 17, 16 18, 14 18 Z" fill={hair} />,
    "male-bald": null,
    "female-long-straight": <path d="M13 19 C 16 12, 22 9, 29 11 C 34 13, 36 17, 37 22 C 33 19, 29 18, 24 18 C 20 18, 16 18, 13 19 Z" fill={hair} />,
    "female-long-straight-no-bangs": <path d="M22.4 8.4 L 24 20.5 L 25.6 8.4" stroke={hair} strokeWidth="1.1" fill="none" strokeLinecap="round" strokeOpacity="0.45" />,
    "female-short-straight": <path d="M12 20 C 15 12, 21 9, 28 10 C 33 12, 36 16, 37 22 C 31 19, 24 18, 18 19 C 16 19, 14 20, 12 20 Z" fill={hair} />,
    "female-long-curly": <path d="M12 20 C 14 13, 20 9, 25 10 C 29 8, 36 13, 37 21 C 34 19, 31 18, 29 18 C 27 16, 24 17, 22 18 C 18 17, 15 18, 12 20 Z" fill={hair} />,
    "female-short-curly": <path d="M11 21 C 13 13, 20 9, 25 10 C 29 8, 36 13, 38 21 C 34 20, 32 19, 29 18 C 26 17, 23 17, 20 18 C 17 18, 14 19, 11 21 Z" fill={hair} />,
    "male-cap": (
      <g>
        <path d="M12 18 C 13 9, 20 7, 24 7 C 30 7, 36 10, 37 18 C 31 16, 24 15, 17 16 C 15 16, 13 17, 12 18 Z" fill={capColor} />
        <path d="M9 18 C 18 15, 30 15, 40 18 C 31 22, 18 22, 9 18 Z" fill={capColor} />
        <path d="M9 18 C 18 15, 30 15, 40 18 C 31 22, 18 22, 9 18 Z" fill={capShadow} fillOpacity="0.26" />
      </g>
    ),
    "female-cap": (
      <g>
        <path d="M12 18 C 13 9, 20 7, 24 7 C 30 7, 36 10, 37 18 C 31 16, 24 15, 17 16 C 15 16, 13 17, 12 18 Z" fill={capColor} />
        <path d="M9 18 C 18 15, 30 15, 40 18 C 31 22, 18 22, 9 18 Z" fill={capColor} />
        <path d="M9 18 C 18 15, 30 15, 40 18 C 31 22, 18 22, 9 18 Z" fill={capShadow} fillOpacity="0.26" />
      </g>
    ),
    "medium-straight": <path d="M13 19 C 16 12, 22 9, 29 11 C 34 13, 36 17, 37 22 C 33 19, 29 18, 24 18 C 20 18, 16 18, 13 19 Z" fill={hair} />,
    "afro": null,
    "braids": <path d="M13 19 C 16 12, 22 9, 29 11 C 34 13, 36 17, 37 22 C 33 19, 29 18, 24 18 C 20 18, 16 18, 13 19 Z" fill={hair} />,
    "bun": <path d="M12 20 C 15 12, 21 9, 28 10 C 33 12, 36 16, 37 22 C 31 19, 24 18, 18 19 C 16 19, 14 20, 12 20 Z" fill={hair} />,
    "headscarf": (
      <>
        <path d="M14 19 C 16 12, 20 9, 24 9 C 28 9, 32 12, 34 19 C 30 17, 27 16, 24 16 C 21 16, 18 17, 14 19 Z" fill={capColor} opacity="0.88" />
        <path d="M11 21 C 10.3 24, 10.6 27, 12 29" stroke="#000" strokeOpacity="0.12" strokeWidth="1" fill="none" strokeLinecap="round" />
        <path d="M37 21 C 37.7 24, 37.4 27, 36 29" stroke="#fff" strokeOpacity="0.14" strokeWidth="1" fill="none" strokeLinecap="round" />
      </>
    ),
    "moicano": <path d="M20 21 L 21.2 13 L 22.6 17.5 L 24 9 L 25.4 17.5 L 26.8 13 L 28 21 Z" fill={hair} />,
    "topete": (
      <>
        <path d="M14 19 C 15 8, 19 2.5, 24.5 2.5 C 29.5 2.5, 32.5 6, 32 11 C 30.5 8, 27.5 6.5, 24 7 C 20 7.5, 16.5 10.5, 14 19 Z" fill={hair} />
        <path d="M17 8.5 C 20 6, 23.5 5.2, 27 6" stroke="#000" strokeOpacity="0.18" strokeWidth="1" fill="none" strokeLinecap="round" />
      </>
    ),
    "rabo-cavalo": <path d="M12 20 C 15 12, 21 9, 28 10 C 33 12, 36 16, 37 22 C 31 19, 24 18, 18 19 C 16 19, 14 20, 12 20 Z" fill={hair} />,
    "samurai": <path d="M15 19 C 17 13, 21 10, 24 10 C 27 10, 31 13, 33 19 C 29 17, 26 16.4, 24 16.4 C 22 16.4, 19 17, 15 19 Z" fill={hair} />
  };

  const cheekFill = skin === "#6d432a" ? "#c2765d" : "#e7a58e";

  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="9" y1="4" x2="40" y2="45" gradientUnits="userSpaceOnUse">
          <stop stopColor={accent} />
          <stop offset="0.55" stopColor="#263448" />
          <stop offset="1" stopColor="#111827" />
        </linearGradient>
        <clipPath id={clipId}>
          <circle cx="24" cy="24" r="24" />
        </clipPath>
      </defs>
      <circle cx="24" cy="24" r="23.5" fill={`url(#${gradId})`} />
      <circle cx="24" cy="24" r="22.4" fill="none" stroke="rgba(255,255,255,0.28)" strokeWidth="0.8" />
      <g clipPath={`url(#${clipId})`}>
        <path d="M0 34 C 10 28, 18 33, 27 28 C 37 23, 43 28, 48 22 V 48 H 0 Z" fill="rgba(255,255,255,0.08)" />
        <circle cx="36" cy="10" r="12" fill="rgba(255,255,255,0.10)" />
        <path d="M10 48 C 11 39, 17 35, 24 35 C 31 35, 37 39, 38 48 Z" fill={shirtColor} />
        <path d="M17 48 C 18 42, 20 39, 24 39 C 28 39, 30 42, 31 48 Z" fill="rgba(255,255,255,0.08)" />
        <path d="M19 33 V 39 C 21 41, 27 41, 29 39 V 33 Z" fill={skin} />
        <path d="M19 34 V 36 C 21 38, 27 38, 29 36 V 34 Z" fill="#000" opacity="0.09" />
        <g transform={hasCap ? "translate(0 1)" : "translate(0 0)"}>{/* hasCap tambem cobre o lenco, ver definicao acima */}
          {hairByStyle[hairRenderStyle]}
          <circle cx="13.8" cy="24.8" r="2.5" fill={skin} />
          <circle cx="34.2" cy="24.8" r="2.5" fill={skin} />
          <ellipse cx="24" cy="24.8" rx="11.1" ry="12.8" fill={skin} />
          <path d="M17 20.8 C 18.3 20, 20.2 20, 21.5 20.9" stroke={hair} strokeWidth="1.15" fill="none" strokeLinecap="round" strokeOpacity="0.72" />
          <path d="M26.5 20.9 C 27.8 20, 29.7 20, 31 20.8" stroke={hair} strokeWidth="1.15" fill="none" strokeLinecap="round" strokeOpacity="0.72" />
          {frontHairByStyle[hairRenderStyle]}
          <circle cx="19.5" cy="23.3" r="1.55" fill={eyeColor} />
          <circle cx="19.5" cy="23.3" r="0.62" fill="#0d1420" />
          <circle cx="28.5" cy="23.3" r="1.55" fill={eyeColor} />
          <circle cx="28.5" cy="23.3" r="0.62" fill="#0d1420" />
          <circle cx="18.2" cy="27" r="1.35" fill={cheekFill} opacity="0.22" />
          <circle cx="29.8" cy="27" r="1.35" fill={cheekFill} opacity="0.22" />
          <path d="M23.3 24.8 C 22.8 26.6, 23.3 27.7, 25 27.2" stroke="#1f2937" strokeOpacity="0.22" strokeWidth="0.9" fill="none" strokeLinecap="round" />
          <path d="M20.8 30.4 C 22.7 31.9, 25.3 31.9, 27.2 30.4" stroke={lipColor} strokeWidth="1.35" fill="none" strokeLinecap="round" />
          <path d="M15.5 34 C 19.2 38, 28.8 38, 32.5 34" stroke="rgba(255,255,255,0.16)" strokeWidth="0.8" fill="none" strokeLinecap="round" />
          {avatar.hasGlasses === "dark" && (
            <g fill="none" stroke="#2b2b2b" strokeWidth="1.3">
              <rect x="15.8" y="20.9" width="7.1" height="5.4" rx="2.1" fill="rgba(20,20,20,0.12)" />
              <rect x="25.1" y="20.9" width="7.1" height="5.4" rx="2.1" fill="rgba(20,20,20,0.12)" />
              <path d="M22.9 22.9 H 25.1" />
              <path d="M15.8 22.6 L 12.5 21.4" strokeLinecap="round" />
              <path d="M32.2 22.6 L 35.5 21.4" strokeLinecap="round" />
            </g>
          )}
          {avatar.hasGlasses === "round" && (
            <g fill="none" stroke="#3b3f46" strokeWidth="1.25">
              <circle cx="19.4" cy="23.4" r="3.55" fill="rgba(20,20,20,0.10)" />
              <circle cx="28.6" cy="23.4" r="3.55" fill="rgba(20,20,20,0.10)" />
              <path d="M22.95 23.4 H 25.05" />
              <path d="M15.9 22.3 L 12.5 21.2" strokeLinecap="round" />
              <path d="M32.1 22.3 L 35.5 21.2" strokeLinecap="round" />
            </g>
          )}
          {avatar.hasBeard === "full" && (
            <>
              <path d="M14.2 27.5 C 13.8 33.4, 17 39.4, 24 40 C 31 39.4, 34.2 33.4, 33.8 27.5 C 33.2 33, 29.4 36.6, 24 36.7 C 18.6 36.6, 14.8 33, 14.2 27.5 Z" fill={hair} opacity="0.95" />
              <path d="M20.6 30.6 C 22.5 31.6, 25.5 31.6, 27.4 30.6" stroke={hair} strokeWidth="2.6" fill="none" strokeLinecap="round" opacity="0.95" />
            </>
          )}
          {avatar.hasBeard === "goatee" && (
            <path d="M18 31 C 18.5 35.5, 21 38.4, 24 38.6 C 27 38.4, 29.5 35.5, 30 31 C 28.6 34, 26.4 35.8, 24 35.9 C 21.6 35.8, 19.4 34, 18 31 Z" fill={hair} opacity="0.95" />
          )}
          {avatar.hasBeard === "bigode" && (
            <path d="M20.6 30.6 C 22.5 31.6, 25.5 31.6, 27.4 30.6" stroke={hair} strokeWidth="2.6" fill="none" strokeLinecap="round" opacity="0.95" />
          )}
          {avatar.hasBeard === "cavanhaque" && (
            <>
              <path d="M18 31.5 C 18.5 35.8, 21 38.4, 24 38.6 C 27 38.4, 29.5 35.8, 30 31.5 C 28.6 34.3, 26.4 35.8, 24 35.9 C 21.6 35.8, 19.4 34.3, 18 31.5 Z" fill={hair} opacity="0.95" />
              <path d="M19.8 31 C 21.8 32.2, 26.2 32.2, 28.2 31" stroke={hair} strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.95" />
            </>
          )}
          {avatar.hasBeard === "rala" && (
            <path d="M14.2 27.5 C 13.8 33.4, 17 39.4, 24 40 C 31 39.4, 34.2 33.4, 33.8 27.5 C 33.2 33, 29.4 36.6, 24 36.7 C 18.6 36.6, 14.8 33, 14.2 27.5 Z" fill={hair} opacity="0.32" />
          )}
        </g>
      </g>
    </svg>
  );
};

// Campo de senha com botao de mostrar/ocultar (olho). Componente pequeno e
// reutilizavel para nao duplicar o markup do botao em cada um dos varios
// formularios de senha (login, cadastro, trocar senha, recuperar senha).
function PasswordField({ value, onChange, placeholder, style, visible, onToggleVisibility }) {
  return (
    <div style={{ position: "relative" }}>
      <input
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        type={visible ? "text" : "password"}
        style={{ ...style, paddingRight: 40 }}
      />
      <button
        type="button"
        onClick={onToggleVisibility}
        aria-label={visible ? "Ocultar senha" : "Mostrar senha"}
        style={{ position: "absolute", right: 6, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", padding: 4, cursor: "pointer", color: "#94A3B8", display: "flex", alignItems: "center" }}
      >
        {visible ? <EyeOff size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export default function PrototypeApp() {
  const [viewportWidth, setViewportWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 390);
  const [screen, setScreen] = useState(() => {
    if (getInitialFamiliaEbookFromUrl()) return "ebook-landing";
    if (getInitialFamiliaTrailSlugFromUrl()) return "trail-landing";
    return "main";
  }); // "main", "trail", "day", "diary", "ebook", "ebook-landing", "trail-landing"
  const [activeTab, setActiveTab] = useState(getInitialActiveTabFromUrl); // "home", "library", "ebooks", "family", "profile"
  
  const [selectedTrail, setSelectedTrail] = useState(null);
  const [selectedTrailGroup, setSelectedTrailGroup] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedEbook, setSelectedEbook] = useState(() => getInitialFamiliaEbookFromUrl());
  // Trilha resolvida para a landing publica (/familia/trilhas/<slug>). So 6-7
  // trilhas existem sincronamente em TRAILS no primeiro render; as demais (a
  // maioria) só ficam disponíveis depois que ensureCuratedTrailsLoaded() resolve
  // (ver efeito abaixo que reage a curatedTrailsVersion).
  const [selectedTrailLanding, setSelectedTrailLanding] = useState(() => {
    const slug = getInitialFamiliaTrailSlugFromUrl();
    return slug ? findTrailBySlug(TRAILS, slug) : null;
  });
  
  const [dayTab, setDayTab] = useState("devotional");
  // Pra onde o botao "<-" da tela do Dia volta -- antes era sempre fixo em
  // "trail", entao abrir um dia a partir do Meu Diario e depois apertar
  // voltar jogava a pessoa na lista de dias da trilha, perdendo o lugar de
  // onde ela realmente tinha vindo (reparado pelo Rodrigo, 26/10/2026, ao
  // ver que nao tem como "voltar de onde saiu"). Guarda a origem real em
  // vez de montar um breadcrumb -- aqui a navegacao e' so' 1 nivel por vez,
  // entao lembrar o "de onde vim" resolve sem precisar expor hierarquia.
  const [dayScreenBackTarget, setDayScreenBackTarget] = useState("trail");
  const [shareReflection, setShareReflection] = useState(false);
  const [coopActive, setCoopActive] = useState(false);
  const [showPastoralAgenda, setShowPastoralAgenda] = useState(false);
  const [pastors, setPastors] = useState(PASTORAL_TEAM);
  const [pastorsLoading, setPastorsLoading] = useState(false);
  const [pastoralSlots, setPastoralSlots] = useState([]);
  const [pastoralSlotsLoading, setPastoralSlotsLoading] = useState(false);
  const [pastoralSlotsStatus, setPastoralSlotsStatus] = useState("idle");
  const [bookingLoading, setBookingLoading] = useState(false);
  const [bookingFeedback, setBookingFeedback] = useState("");
  const [bookingName, setBookingName] = useState("");
  const [selectedPastorId, setSelectedPastorId] = useState("");
  const [selectedPastoralSlotId, setSelectedPastoralSlotId] = useState("");
  const [bookingNote, setBookingNote] = useState("");
  // "Me avisa quando abrir horario" (POST /pastoral/slot-requests) -- some
  // quando o pastor escolhido nao tem nenhum horario aberto no momento (ver
  // selectedPastorAvailability.length === 0 mais abaixo).
  const [slotRequestLoading, setSlotRequestLoading] = useState(false);
  const [slotRequestFeedback, setSlotRequestFeedback] = useState("");
  // Sininho do app (GET /notifications) -- generico o bastante pra
  // qualquer tipo de aviso, hoje so usado por PASTORAL_SLOTS_OPENED.
  const [notifications, setNotifications] = useState([]);
  const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);
  const [showNotificationsPanel, setShowNotificationsPanel] = useState(false);
  // Hidratacao otimista a partir do cache localStorage ("mosaico_user") --
  // sem isso, viewerUser comeca null em TODO mount deste componente
  // (inclusive ao navegar de uma pagina institucional pra /familia, que e
  // uma troca de rota irma, nao aninhada -- desmonta InstitutionalLayout e
  // monta este componente do zero), entao canSeeFinanceiro/isAdminUser
  // comecam falsos por um instante ate o useEffect abaixo reidratar e
  // reconciliar com /auth/me -- isso fazia o item "Financeiro" (e outros
  // trechos condicionados a papel) aparecerem/desaparecerem um instante
  // depois do primeiro paint, dando a impressao de "menu pulando" ao
  // alternar Inicio -> Devocionais (mesmo padrao ja corrigido em
  // InstitutionalLayout.jsx, replicado aqui pro app Familia Mosaico).
  const [viewerUser, setViewerUser] = useState(() => {
    try {
      const raw = window.localStorage.getItem("mosaico_user");
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  });
  // Marca que uma sessao ja foi confirmada pelo servidor (login, cadastro ou
  // sessao anonima) depois do mount. Existe para resolver uma corrida real:
  // a checagem de boot (/auth/me) pode responder 401 DEPOIS que o usuario ja
  // logou/cadastrou (ex.: cold start do Railway), e sem essa guarda o 401
  // tardio apagava a sessao recem-confirmada, deixando o app "deslogado"
  // mesmo com "Acesso liberado."/"Conta criada." na tela.
  const authConfirmedRef = useRef(false);
  // Guarda de qual usuario os dados atuais do profileForm vieram. Sem isso, ao
  // trocar de conta no mesmo aparelho (ex.: sair da Carla e entrar como o
  // Rodrigo), campos que a conta nova ainda nao tem preenchidos (ex.: whatsapp)
  // "herdavam" o valor que sobrou no formulario da conta anterior, porque o
  // sync usava `campo || prev.campo` sem saber que prev.campo era de OUTRA
  // pessoa. Ver os dois useEffect/handlers que leem esta ref abaixo.
  const profileFormSyncedUserIdRef = useRef(null);
  const [profileForm, setProfileForm] = useState(PROFILE_FORM_DEFAULT);
  const [profileFeedback, setProfileFeedback] = useState("");
  const [profileSaving, setProfileSaving] = useState(false);
  // "Meu cadastro na igreja" (aba Perfil): cadastro de membresia (Member) e
  // filhos (Child) vinculados a propria conta -- ver GET/PATCH /me/member e
  // GET/POST/PATCH /me/children em routes/selfService.js.
  const [myMember, setMyMember] = useState(null);
  const [myMemberLoading, setMyMemberLoading] = useState(false);
  const [myChildren, setMyChildren] = useState([]);
  const [myChildrenLoading, setMyChildrenLoading] = useState(false);
  const [showMemberEditor, setShowMemberEditor] = useState(false);
  const [memberEditorForm, setMemberEditorForm] = useState(SELF_MEMBER_FORM_DEFAULT);
  const [memberEditorSaving, setMemberEditorSaving] = useState(false);
  const [memberEditorFeedback, setMemberEditorFeedback] = useState("");
  const [showChildEditor, setShowChildEditor] = useState(false);
  // null = cadastrando um filho novo; id = editando um filho ja existente
  // (so quem tem guardianUserId igual a propria conta pode editar).
  const [editingChildId, setEditingChildId] = useState(null);
  const [childEditorForm, setChildEditorForm] = useState(SELF_CHILD_FORM_DEFAULT);
  const [childEditorSaving, setChildEditorSaving] = useState(false);
  const [childEditorFeedback, setChildEditorFeedback] = useState("");
  // Dispensa o banner "complete/revise seu cadastro" so nesta sessao de uso
  // (nao persiste entre visitas -- nao ha campo novo no banco so pra isso).
  const [selfCadastroBannerDismissed, setSelfCadastroBannerDismissed] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showMomentGuide, setShowMomentGuide] = useState(false);
  const [showDirectInviteModal, setShowDirectInviteModal] = useState(false);
  const [showProfilePrompt, setShowProfilePrompt] = useState(false);
  const [profilePromptContext, setProfilePromptContext] = useState("general");
  const [momentGuideChoiceId, setMomentGuideChoiceId] = useState("");
  const [adminOverview, setAdminOverview] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminFeedback, setAdminFeedback] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "", currentPassword: "", newPassword: "", confirmPassword: "", resetEmail: "", resetToken: "" });
  const [loginFeedback, setLoginFeedback] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  // Sub-navegacao da aba Perfil (10/2026, pedido do Rodrigo: a aba estava
  // "crescendo e podia ficar confusa" com tudo numa rolagem so). null =
  // ainda nao escolhido pelo usuario -> usa o default esperto calculado em
  // effectiveHubSection (depende de hasCompletedProfile, que so' existe mais
  // abaixo no component). acessoCardTab controla as 3 sub-abas (Entrar /
  // Criar conta / Esqueci a senha) dentro da secao "Conta".
  const [profileHubSection, setProfileHubSection] = useState(null);
  const [acessoCardTab, setAcessoCardTab] = useState("cadastro");
  const [voluntaryPasswordChangeOpen, setVoluntaryPasswordChangeOpen] = useState(false);
  const [adminSlotForm, setAdminSlotForm] = useState({ pastorId: "", date: "", time: "", durationMinutes: "45", mode: "ONLINE", locationNote: "", repeatWeekly: false, repeatUntil: "" });
  const [adminSlotFeedback, setAdminSlotFeedback] = useState("");
  const [adminSlotLoading, setAdminSlotLoading] = useState(false);
  const [showSlotFormModal, setShowSlotFormModal] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [trails, setTrails] = useState(TRAILS);
  // Lista de trilhas em ordem alfabetica -- usada só nos seletores (<select>)
  // de "convidar para uma trilha" / "abrir uma mesa", pra facilitar achar
  // pelo nome. Não mexe em `trails` em si: trails[0] e outras buscas por
  // índice/id continuam de pé (ordem editorial/padrão) em todo o resto do
  // app.
  const trailsAlphabetical = [...trails].sort((a, b) => a.title.localeCompare(b.title, "pt-BR"));
  const [trailMomentFilter, setTrailMomentFilter] = useState("todos");
  // Busca por nome dentro do mapa de trilhas (Planos > Explorar) -- com 56+
  // trilhas so o filtro por momento de vida nao basta pra quem ja sabe o
  // nome da trilha que procura (ouviu falar, foi recomendada) e nao quer
  // adivinhar em qual categoria ela caiu (pedido do Rodrigo).
  const [trailSearchQuery, setTrailSearchQuery] = useState("");
  // "Meus planos" (em andamento + concluidas) vs "Explorar" (grid por tema).
  // Comeca em "explorar" e troca pra "meus" sozinho na primeira vez que os
  // dados de progresso chegam, se o usuario ja tiver alguma trilha comecada
  // -- so' na entrada, pra nao "puxar o tapete" se a pessoa trocar de volta
  // pra Explorar depois.
  const [planosMode, setPlanosMode] = useState("explorar");
  const planosModeAutoSetRef = useRef(false);
  const [trailCompanionMode, setTrailCompanionMode] = useState("solo");
  const [mesaIntent, setMesaIntent] = useState("duo");
  const [mesaInviteTrailId, setMesaInviteTrailId] = useState("comunhao-no-caminho");
  const [mesaInviteName, setMesaInviteName] = useState("");
  const [mesaInviteEmail, setMesaInviteEmail] = useState("");
  const [mesaInviteWhatsapp, setMesaInviteWhatsapp] = useState("");
  const [mesaInviteMessage, setMesaInviteMessage] = useState("");
  const [mesaInviteFeedback, setMesaInviteFeedback] = useState("");
  const [openGroups, setOpenGroups] = useState([]);
  // Comeca "true" (nao "false"): no 1o render, antes do useEffect disparar
  // o fetch de /groups/open, esse estado ainda nao reflete a realidade --
  // comecar em false fazia o hero da Home achar (por um instante) que a
  // pessoa nao tinha nenhuma mesa/convite em andamento, mostrar o texto de
  // "comecar do zero", e so' depois trocar pro texto certo quando os dados
  // chegavam -- o "pulo" visual que a Carla reparou (26/10/2026).
  const [openGroupsLoading, setOpenGroupsLoading] = useState(true);
  const [openGroupsFeedback, setOpenGroupsFeedback] = useState("");
  const [openMesaForm, setOpenMesaForm] = useState({ trailId: "igreja-mesa", name: "", description: "", capacity: "12" });
  const [openMesaSaving, setOpenMesaSaving] = useState(false);
  const [myInvites, setMyInvites] = useState([]);
  const [myInvitesLoading, setMyInvitesLoading] = useState(true); // mesmo motivo do openGroupsLoading acima
  // Gamificacao leve (conquistas) -- GET /badges/me devolve o catalogo
  // inteiro (conquistadas + bloqueadas, com dica de progresso), ver
  // loadBadges mais abaixo.
  const [badgesData, setBadgesData] = useState(null);
  const [badgesLoading, setBadgesLoading] = useState(false);
  // Grade completa de conquistas no Perfil comeca fechada (so' o resumo
  // tipo teaser) -- pedido do Drigo (18/10/2026): "pode por no perfil o
  // teaser pequeno tmb se necessario a pessoa abre". Abre/fecha ao tocar
  // no resumo; estado nao precisa persistir entre sessoes.
  const [badgesExpanded, setBadgesExpanded] = useState(false);
  // Teaser "Escala" na aba Perfil -- em vez de duplicar a lista inteira de
  // /admin/escalas aqui dentro (mais um lugar pra manter sincronizado,
  // sem ganho real, ja que o musico tambem recebe aviso automatico por
  // WhatsApp quando e escalado), mostra so a proxima data em que a pessoa
  // esta escalada + a funcao, com um atalho pra tela de Escalas pra ver o
  // resto (sugestao dada ao Drigo em 23/10/2026, "pode seguir"). null =
  // ainda nao verificou; false = verificou e nao ha escala futura.
  const [nextEscalaSlot, setNextEscalaSlot] = useState(null);
  const [nextEscalaLoading, setNextEscalaLoading] = useState(false);
  const [joiningOpenGroupId, setJoiningOpenGroupId] = useState("");
  const [pendingInvite, setPendingInvite] = useState(null);
  const [pendingInviteFeedback, setPendingInviteFeedback] = useState("");
  const [trailsLoading, setTrailsLoading] = useState(false);
  const [trailsError, setTrailsError] = useState("");
  const [weeklyCompletionMapApi, setWeeklyCompletionMapApi] = useState(null);
  const [progressSyncing, setProgressSyncing] = useState(false);
  const [progressSyncFeedback, setProgressSyncFeedback] = useState("");
  const [selectedCalendarBooking, setSelectedCalendarBooking] = useState(null);
  // Filtro do calendario pastoral do painel de cuidado: mes/ano exibido e
  // acolhedor selecionado ("all" = todos) -- controla o que loadAdminOverview
  // busca (ver query params calendarMonth/calendarYear/calendarPastorId).
  const [calendarFilterMonth, setCalendarFilterMonth] = useState(new Date().getMonth() + 1);
  const [calendarFilterYear, setCalendarFilterYear] = useState(new Date().getFullYear());
  const [calendarFilterPastorId, setCalendarFilterPastorId] = useState("all");
  // Filtro por status/acao dentro do mes/acolhedor ja selecionados --
  // "all" mostra tudo, "OVERDUE" e um filtro sintetico (nao e um status
  // real) pra achar so as conversas pendentes que ja passaram da hora.
  const [calendarStatusFilter, setCalendarStatusFilter] = useState("all");
  // Conversas de acolhimento vencidas (PENDING com data que ja passou),
  // mostradas inline no calendario pastoral: acao de reagendar usa esse
  // mini-form inline; adminBookingActionFeedback e o feedback de
  // cancelar/reagendar, separado do adminFeedback (que e so do
  // carregamento do painel).
  const [rescheduleBookingId, setRescheduleBookingId] = useState("");
  const [rescheduleDate, setRescheduleDate] = useState("");
  const [rescheduleTime, setRescheduleTime] = useState("");
  const [adminBookingActionFeedback, setAdminBookingActionFeedback] = useState("");
  const [selectedAvatarId, setSelectedAvatarId] = useState(AVATAR_OPTIONS[0].id);
  const [avatarBuilderTop, setAvatarBuilderTop] = useState(DICEBEAR_TOP_OPTIONS[1].id);
  const [avatarHairLengthFilter, setAvatarHairLengthFilter] = useState("todos");
  const [avatarBuilderSkinColor, setAvatarBuilderSkinColor] = useState(DICEBEAR_SKIN_COLORS[3].id);
  const [avatarBuilderHairColor, setAvatarBuilderHairColor] = useState(DICEBEAR_HAIR_COLORS[1].id);
  const [avatarBuilderBgColor, setAvatarBuilderBgColor] = useState(DICEBEAR_BG_COLORS[0].id);
  const [avatarBuilderEyes, setAvatarBuilderEyes] = useState(DICEBEAR_EYES_OPTIONS[0].id);
  const [avatarBuilderEyeColor, setAvatarBuilderEyeColor] = useState(DICEBEAR_EYE_COLORS[0].id);
  const [avatarBuilderMouth, setAvatarBuilderMouth] = useState(DICEBEAR_MOUTH_OPTIONS[0].id);
  const [avatarBuilderGlasses, setAvatarBuilderGlasses] = useState("none");
  const [avatarBuilderGlassesColor, setAvatarBuilderGlassesColor] = useState(DICEBEAR_GLASSES_COLORS[0].id);
  const [avatarBuilderBeard, setAvatarBuilderBeard] = useState("none");
  const [avatarBuilderBeardColor, setAvatarBuilderBeardColor] = useState(DICEBEAR_HAIR_COLORS[1].id);
  const [avatarBuilderClothing, setAvatarBuilderClothing] = useState(DICEBEAR_CLOTHING_OPTIONS[0].id);
  const [avatarBuilderClothesColor, setAvatarBuilderClothesColor] = useState(DICEBEAR_CLOTHES_COLORS[1].id);

  const applyAvatarBuilderChange = (patch) => {
    const next = {
      top: avatarBuilderTop,
      skinColor: avatarBuilderSkinColor,
      hairColor: avatarBuilderHairColor,
      bgColor: avatarBuilderBgColor,
      eyes: avatarBuilderEyes,
      eyeColor: avatarBuilderEyeColor,
      mouth: avatarBuilderMouth,
      glasses: avatarBuilderGlasses,
      glassesColor: avatarBuilderGlassesColor,
      beard: avatarBuilderBeard,
      beardColor: avatarBuilderBeardColor,
      clothing: avatarBuilderClothing,
      clothesColor: avatarBuilderClothesColor,
      ...patch
    };
    setAvatarBuilderTop(next.top);
    setAvatarBuilderSkinColor(next.skinColor);
    setAvatarBuilderHairColor(next.hairColor);
    setAvatarBuilderBgColor(next.bgColor);
    setAvatarBuilderEyes(next.eyes);
    setAvatarBuilderEyeColor(next.eyeColor);
    setAvatarBuilderMouth(next.mouth);
    setAvatarBuilderGlasses(next.glasses);
    setAvatarBuilderGlassesColor(next.glassesColor);
    setAvatarBuilderBeard(next.beard);
    setAvatarBuilderBeardColor(next.beardColor);
    setAvatarBuilderClothing(next.clothing);
    setAvatarBuilderClothesColor(next.clothesColor);
    setSelectedAvatarId(encodeDicebearAvatarId(next));
  };

  const hydrateAvatarBuilderFrom = (avatarId) => {
    const decoded = decodeDicebearAvatarId(avatarId);
    if (!decoded) return;
    const parsed = decoded.dicebearOptions;
    if (DICEBEAR_TOP_OPTIONS.some((item) => item.id === parsed.top)) setAvatarBuilderTop(parsed.top);
    if (DICEBEAR_SKIN_COLORS.some((item) => item.id === parsed.skinColor)) setAvatarBuilderSkinColor(parsed.skinColor);
    if (DICEBEAR_HAIR_COLORS.some((item) => item.id === parsed.hairColor)) setAvatarBuilderHairColor(parsed.hairColor);
    if (DICEBEAR_BG_COLORS.some((item) => item.id === parsed.bgColor)) setAvatarBuilderBgColor(parsed.bgColor);
    if (DICEBEAR_EYES_OPTIONS.some((item) => item.id === parsed.eyes)) setAvatarBuilderEyes(parsed.eyes);
    if (DICEBEAR_EYE_COLORS.some((item) => item.id === parsed.eyeColor)) setAvatarBuilderEyeColor(parsed.eyeColor);
    if (DICEBEAR_MOUTH_OPTIONS.some((item) => item.id === parsed.mouth)) setAvatarBuilderMouth(parsed.mouth);
    setAvatarBuilderGlasses(parsed.glasses && parsed.glasses !== "none" ? parsed.glasses : "none");
    if (DICEBEAR_GLASSES_COLORS.some((item) => item.id === parsed.glassesColor)) setAvatarBuilderGlassesColor(parsed.glassesColor);
    setAvatarBuilderBeard(parsed.beard && parsed.beard !== "none" ? parsed.beard : "none");
    if (DICEBEAR_HAIR_COLORS.some((item) => item.id === parsed.beardColor)) setAvatarBuilderBeardColor(parsed.beardColor);
    if (DICEBEAR_CLOTHING_OPTIONS.some((item) => item.id === parsed.clothing)) setAvatarBuilderClothing(parsed.clothing);
    if (DICEBEAR_CLOTHES_COLORS.some((item) => item.id === parsed.clothesColor)) setAvatarBuilderClothesColor(parsed.clothesColor);
  };
  const [nudgeFeedback, setNudgeFeedback] = useState("");
  const [nudgedUserIds, setNudgedUserIds] = useState([]);
  const [ebookSelection, setEbookSelection] = useState(null);
  const [ebookHighlights, setEbookHighlights] = useState({});
  const [devotionalSelection, setDevotionalSelection] = useState(null);
  const [devotionalHighlights, setDevotionalHighlights] = useState({});
  const [devotionalReflections, setDevotionalReflections] = useState({});
  const [ebookNotes, setEbookNotes] = useState({});
  const [diaryEntries, setDiaryEntries] = useState([]);
  const [reminderEditing, setReminderEditing] = useState(false);
  const [reminderTimeInput, setReminderTimeInput] = useState("07:00");
  const [reminderSaving, setReminderSaving] = useState(false);
  const [reminderFeedback, setReminderFeedback] = useState("");
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const togglePasswordVisibility = (key) => setVisiblePasswords((prev) => ({ ...prev, [key]: !prev[key] }));
  const [ebookTab, setEbookTab] = useState("reading");
  const [ebookPage, setEbookPage] = useState(1);
  const [ebookPageLoading, setEbookPageLoading] = useState(false);
  const [ebookPageError, setEbookPageError] = useState("");
  // Zoom da pagina do e-book (A-/A+) -- pedido da Carla, 25/10/2026: "letra
  // esta pequena e o pessoal nao consegue aumentar e diminuir". Como a
  // pagina e' um PDF desenhado num <canvas> (pdf.js), nao tem "font-size" de
  // verdade pra mexer -- o "aumentar letra" aqui e' re-renderizar a pagina
  // numa resolucao/escala maior, ver efeito do PDF abaixo. Fica salvo no
  // localStorage (preferencia do leitor, nao por e-book) pra nao precisar
  // reajustar toda vez que abre um livro novo.
  const EBOOK_ZOOM_KEY = "mosaico_ebook_zoom";
  // So' 3 tamanhos fixos (pedido do Rodrigo, 25/10: simplificar em vez de
  // passos de 10% -- "limitar 2 volumes ou 3 no maximo"). O primeiro
  // (1 = 100%) e' o "enquadrado": a pagina cabe no visor sem precisar
  // arrastar pro lado. A-/A+ andam por este array, nunca abaixo dele.
  const EBOOK_ZOOM_LEVELS = [1, 1.25, 1.5];
  const EBOOK_ZOOM_MIN = EBOOK_ZOOM_LEVELS[0];
  const EBOOK_ZOOM_MAX = EBOOK_ZOOM_LEVELS[EBOOK_ZOOM_LEVELS.length - 1];
  const closestEbookZoomIndex = (level) => {
    let bestIndex = 0;
    let bestDiff = Infinity;
    EBOOK_ZOOM_LEVELS.forEach((candidate, index) => {
      const diff = Math.abs(candidate - level);
      if (diff < bestDiff) {
        bestDiff = diff;
        bestIndex = index;
      }
    });
    return bestIndex;
  };
  const [ebookZoomLevel, setEbookZoomLevel] = useState(() => {
    try {
      const saved = parseFloat(localStorage.getItem(EBOOK_ZOOM_KEY));
      if (Number.isFinite(saved)) return EBOOK_ZOOM_LEVELS[closestEbookZoomIndex(saved)];
    } catch (error) {
      // noop
    }
    return EBOOK_ZOOM_LEVELS[0];
  });
  const increaseEbookZoom = () => setEbookZoomLevel((level) => {
    const nextIndex = Math.min(EBOOK_ZOOM_LEVELS.length - 1, closestEbookZoomIndex(level) + 1);
    return EBOOK_ZOOM_LEVELS[nextIndex];
  });
  const decreaseEbookZoom = () => setEbookZoomLevel((level) => {
    const prevIndex = Math.max(0, closestEbookZoomIndex(level) - 1);
    return EBOOK_ZOOM_LEVELS[prevIndex];
  });
  useEffect(() => {
    try {
      localStorage.setItem(EBOOK_ZOOM_KEY, String(ebookZoomLevel));
    } catch (error) {
      // noop
    }
  }, [ebookZoomLevel]);
  // "Compartilhar leitura" (aba "Juntos" do ebook) chama setShowInvitePicker,
  // mas esse estado nunca tinha sido declarado aqui -- so a prop era passada
  // pra EbookScreen (linha ~6481). Isso derrubava a tela inteira assim que o
  // leitor de ebook tentava montar (ReferenceError: setShowInvitePicker is
  // not defined), sem boundary pra pegar o erro -- ecra em branco, sem
  // nenhuma mensagem, exatamente o "os ebooks nao abrem". Ainda nao existe
  // um modal de convite implementado; por enquanto so guarda o estado pra
  // parar de quebrar a tela -- o botao "Compartilhar leitura" fica inerte
  // ate esse modal ser construido.
  const [showInvitePicker, setShowInvitePicker] = useState(false);
  const ebookCanvasRef = useRef(null);

  const getProgressStorageKey = (userId) => `mosaico_trail_progress_${userId || "guest"}`;
  const getCompletionStorageKey = (userId) => `mosaico_day_completions_${userId || "guest"}`;
  const getAvatarStorageKey = (userId) => `mosaico_user_avatar_${userId || "guest"}`;
  const readUserTrailProgress = (userId) => {
    if (!userId) return {};
    try {
      const raw = localStorage.getItem(getProgressStorageKey(userId));
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  };
  const applyUserProgressToTrails = (baseTrails, userId) => {
    const progressMap = readUserTrailProgress(userId);
    return baseTrails.map((trail) => {
      const savedProgress = Number(progressMap[trail.id]);
      if (Number.isNaN(savedProgress)) {
        return { ...trail, progress: 0 };
      }
      return {
        ...trail,
        progress: Math.min(Math.max(savedProgress, 0), Math.max(0, trail.duration || 0))
      };
    });
  };
  const getLocalTrailCatalog = () => TRAILS.map((trail) => {
    const devotionals = DAYS_MOCK[trail.id] || [];
    return {
      ...trail,
      slug: trail.slug || trail.id,
      duration: devotionals.length || trail.duration || 0,
      devotionals
    };
  });
  const persistTrailProgress = (trailId, progressValue, userId) => {
    if (!userId) return;
    try {
      const currentMap = readUserTrailProgress(userId);
      const nextMap = { ...currentMap, [trailId]: progressValue };
      localStorage.setItem(getProgressStorageKey(userId), JSON.stringify(nextMap));
    } catch (error) {
      // noop
    }
  };
  const readUserDayCompletions = (userId) => {
    if (!userId) return [];
    try {
      const raw = localStorage.getItem(getCompletionStorageKey(userId));
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  };
  const persistUserDayCompletion = ({ trailId, day }, userId) => {
    if (!userId) return;
    try {
      const today = new Date();
      const dateKey = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      const currentItems = readUserDayCompletions(userId);
      const alreadyLogged = currentItems.some((item) => item.dateKey === dateKey && item.trailId === trailId && item.day === day);
      if (alreadyLogged) return;
      const nextItems = [...currentItems, { trailId, day, dateKey }];
      localStorage.setItem(getCompletionStorageKey(userId), JSON.stringify(nextItems));
    } catch (error) {
      // noop
    }
  };
  // Feedback leve de 1 pergunta ao concluir um dia da trilha (pedido do
  // Rodrigo: o feed "Trilhas novas para aprofundar a semana" na tela
  // /familia/planos e so uma lista fixa no codigo, sem nenhum sinal real de
  // leitura/engajamento -- isso comeca a dar esse sinal pra curadoria, sem
  // virar um formulario no meio da leitura). Local storage so pra nao
  // perguntar de novo ao reabrir um dia ja respondido; o POST pro servidor e
  // best-effort, igual ao resto da sincronizacao de progresso.
  const DEVOTIONAL_FEEDBACK_OPTIONS = [
    { id: "fez_sentido", label: "Fez sentido" },
    { id: "mais_ou_menos", label: "Mais ou menos" },
    { id: "nao_fez_sentido", label: "Não fez sentido" }
  ];
  const getDevotionalFeedbackStorageKey = (userId) => `mosaico_devotional_feedback_${userId || "guest"}`;
  const readDevotionalFeedbackMap = (userId) => {
    if (!userId) return {};
    try {
      const raw = localStorage.getItem(getDevotionalFeedbackStorageKey(userId));
      return raw ? JSON.parse(raw) : {};
    } catch (error) {
      return {};
    }
  };
  const persistDevotionalFeedbackLocally = (trailId, day, response, userId) => {
    if (!userId) return;
    try {
      const currentMap = readDevotionalFeedbackMap(userId);
      const nextMap = { ...currentMap, [`${trailId}_${day}`]: response };
      localStorage.setItem(getDevotionalFeedbackStorageKey(userId), JSON.stringify(nextMap));
    } catch (error) {
      // noop
    }
  };

  const [devotionalFeedbackMap, setDevotionalFeedbackMap] = useState(() => readDevotionalFeedbackMap(viewerUser?.id));
  const [devotionalFeedbackSyncing, setDevotionalFeedbackSyncing] = useState(false);
  // Comentarios reais da mesa sobre o dia aberto (aba "Partilhar" na tela do
  // dia, so' quando ha mesa -- ver GroupDayComment/POST+GET
  // /groups/:id/comments). Pedido da Carla, 20/10/2026: antes essa aba so
  // mostrava um texto fixo de exemplo.
  const [groupDayComments, setGroupDayComments] = useState([]);
  const [groupDayCommentsLoading, setGroupDayCommentsLoading] = useState(false);
  const [groupDayCommentDraft, setGroupDayCommentDraft] = useState("");
  const [groupDayCommentSyncing, setGroupDayCommentSyncing] = useState(false);
  const [groupDayCommentFeedback, setGroupDayCommentFeedback] = useState("");
  // Cadastro (Person) de quem comentou na mesa, por userId -- so' pra quem
  // e' ADMIN de verdade (ve essa tela como qualquer participante, mas com
  // esse extra). Acumula entre dias/trilhas abertas na mesma sessao (nao
  // precisa recarregar quem ja foi resolvido). Ver GET
  // /admin/pessoas-por-user e DayScreen.jsx (bloco "so' admin" na aba
  // Partilhar). Pedido do Rodrigo, 29/10/2026.
  const [adminCommentPersonMap, setAdminCommentPersonMap] = useState({});
  // Resumo das ultimas reflexoes da mesa (qualquer dia), pra visao geral
  // da trilha -- ver useEffect mais abaixo.
  const [recentGroupComments, setRecentGroupComments] = useState([]);
  const [recentGroupCommentsLoading, setRecentGroupCommentsLoading] = useState(false);

  const getWeekDateKeys = () => {
    const now = new Date();
    const start = new Date(now);
    start.setDate(now.getDate() - now.getDay());
    return Array.from({ length: 7 }).map((_, index) => {
      const date = new Date(start);
      date.setDate(start.getDate() + index);
      return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    });
  };
  const getWeeklyCompletionMap = (userId) => {
    const weekKeys = getWeekDateKeys();
    const completions = readUserDayCompletions(userId);
    const completionSet = new Set(completions.map((item) => item.dateKey));
    return weekKeys.map((key) => completionSet.has(key));
  };

  const myBookings = bookings.filter((item) => item.isOwner);
  const fallbackPastoralAvailability = buildPastoralAvailability(pastors);
  const selectedPastorIsLocal = Boolean(selectedPastorId?.startsWith("local-"));
  const pastoralAvailability = pastoralSlots.length > 0 ? pastoralSlots : (selectedPastorIsLocal && pastoralSlotsStatus === "error" ? fallbackPastoralAvailability : []);
  const selectedPastorForBooking = pastors.find((pastor) => pastor.id === selectedPastorId) || null;
  const selectedPastorAvailability = selectedPastorId ? pastoralAvailability.filter((slot) => slot.pastorId === selectedPastorId) : [];
  const selectedPastoralSlot = selectedPastorAvailability.find((slot) => slot.id === selectedPastoralSlotId) || null;
  const MONTH_NAMES_PT = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
  const statusColorMap = {
    OPEN: "#7DD3FC",
    PENDING: "#FCD34D",
    CONFIRMED: "#86EFAC",
    CANCELLED: "#FCA5A5"
  };
  const statusLabelMap = {
    OPEN: "Aberto (disponível)",
    PENDING: "Aguardando resposta",
    CONFIRMED: "Confirmado",
    CANCELLED: "Cancelado"
  };
  // Junta agendamentos (com status real) e horarios abertos (ainda sem dono,
  // status sintetico "OPEN") num feed cronologico unico do mes/acolhedor
  // filtrado -- assim da pra ver num lugar so o que ja foi escolhido e o que
  // ainda esta disponivel, agrupado por dia.
  const calendarFeed = [
    ...(adminOverview?.calendarBookings || []).map((item) => ({ ...item, kind: "booking" })),
    ...(adminOverview?.calendarOpenSlots || []).map((item) => ({ ...item, status: "OPEN", kind: "slot" }))
  ]
    // Descarta registro com startAt que nao vira uma data valida -- sem isso,
    // new Date(...).toLocaleDateString() retorna a string literal "Invalid
    // Date", que vira uma coluna propria no mapa de horarios (bug real visto
    // em producao: coluna "Invalid Date" na frente de outubro/2026). O
    // comparador do sort tambem da NaN nesse caso, que o V8 trata como
    // "iguais" e nao reordena -- por isso o item ruim colava sempre na
    // primeira posicao em vez de ser jogado pro fim.
    .filter((item) => !Number.isNaN(new Date(item.startAt).getTime()))
    .sort((a, b) => new Date(a.startAt) - new Date(b.startAt));
  const calendarFeedVisible = calendarStatusFilter === "all"
    ? calendarFeed
    : calendarStatusFilter === "OVERDUE"
      ? calendarFeed.filter((item) => item.status === "PENDING" && new Date(item.startAt).getTime() < Date.now())
      : calendarFeed.filter((item) => item.status === calendarStatusFilter);
  const calendarFeedByDay = calendarFeedVisible.reduce((acc, item) => {
    const dayKey = new Date(item.startAt).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
    if (!acc[dayKey]) acc[dayKey] = [];
    acc[dayKey].push(item);
    return acc;
  }, {});

  // Grade horário x dia (Agenda de cuidado): pivota o mesmo feed cronológico
  // agrupado por dia acima, mas também por horário, pra dar pra ver de
  // relance a forma da semana/mês -- o que tá livre e o que tá ocupado --
  // em vez de ler célula por célula numa lista comprida. As colunas
  // (calendarGridDays) continuam sendo só os dias que têm pelo menos um
  // horário aberto ou agendado (mesma lógica de "dia sem nada não aparece"
  // que a lista já tinha); as linhas (calendarGridTimes) são a união dos
  // horários realmente usados nesse recorte, ordenada.
  const calendarGridDays = Object.keys(calendarFeedByDay);
  const calendarGridTimes = Array.from(new Set(
    calendarFeedVisible.map((item) => new Date(item.startAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }))
  )).sort();
  const calendarGridCell = (dayKey, time) => (calendarFeedByDay[dayKey] || []).filter(
    (item) => new Date(item.startAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) === time
  );
  // Uma célula da grade: horário aberto (kind "slot") é uma ação direta de
  // apagar (não tem uma "conversa" pra ver, só o(a) acolhedor(a) esperando
  // alguém marcar); agendamento (kind "booking") é 1 toque pra abrir o
  // mesmo modal de detalhe que já existia na lista antiga (acolhedor(a) +
  // pessoa + reagendar/cancelar), tirando as ações que antes ficavam sempre
  // visíveis na linha.
  const renderCalendarGridItem = (item) => {
    const isBooking = item.kind === "booking";
    const overdue = isBooking && isBookingOverdue(item);
    const accent = statusColorMap[item.status] || "#7DD3FC";
    if (isBooking) {
      return (
        <button
          key={item.id}
          onClick={() => setSelectedCalendarBooking(item)}
          style={{ ...buttonReset, position: "relative", flex: "0 0 auto", minHeight: 70, width: "100%", textAlign: "left", borderRadius: 8, padding: "6px 8px", cursor: "pointer", border: `1px solid ${accent}`, background: `${accent}1A`, display: "flex", flexDirection: "column", justifyContent: "center", overflow: "visible" }}
        >
          {overdue && (
            <span style={{ position: "absolute", top: -7, right: 6, background: "#FCA5A5", color: "#3a0d0d", fontSize: 8, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3, padding: "2px 5px", borderRadius: 999 }}>Vencido</span>
          )}
          <span style={{ display: "block", color: accent, fontSize: 8.5, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 2 }}>{statusLabelMap[item.status] || item.status}</span>
          <span style={{ display: "block", color: "#E5E7EB", fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.pastorName}</span>
          <span style={{ display: "block", color: "#9fb0cc", fontSize: 10, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{item.userName}</span>
        </button>
      );
    }
    return (
      <div key={item.id} style={{ position: "relative", flex: "0 0 auto", minHeight: 70, borderRadius: 8, padding: "6px 8px", border: "1.5px dashed rgba(125,211,252,0.5)", background: "rgba(125,211,252,0.07)", display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
        <span style={{ display: "block", color: "#7DD3FC", fontSize: 8.5, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.3, marginBottom: 2 }}>Disponível</span>
        <span style={{ display: "block", color: "#E5E7EB", fontSize: 10.5, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 3 }}>{item.pastorName}</span>
        <span style={{ display: "flex", gap: 8 }}>
          {item.seriesId && (
            <button onClick={() => deleteAdminPastoralSlot(item, "series")} style={{ ...buttonReset, color: "#FCA5A5", fontSize: 9, fontWeight: 800, padding: 0 }}>Apagar recorrência</button>
          )}
          <button onClick={() => deleteAdminPastoralSlot(item, "one")} style={{ ...buttonReset, color: "#94A3B8", fontSize: 9, fontWeight: 800, padding: 0 }}>Apagar</button>
        </span>
      </div>
    );
  };
  const goToAdjacentCalendarMonth = (delta) => {
    const base = new Date(calendarFilterYear, calendarFilterMonth - 1 + delta, 1);
    setCalendarFilterMonth(base.getMonth() + 1);
    setCalendarFilterYear(base.getFullYear());
  };

  const isDesktop = viewportWidth >= 1080;
  const isTablet = viewportWidth >= 768;
  const isMobile = !isTablet;
  const hasCompletedProfile = Boolean(viewerUser?.profileCompleted || viewerUser?.profile_completed || viewerUser?.onboardingCompleted || viewerUser?.onboarding_completed);
  // Default esperto da sub-navegacao do Perfil: "conta" (unica secao
  // desbloqueada) enquanto o cadastro nao foi completado, "overview" depois.
  const effectiveHubSection = profileHubSection || (hasCompletedProfile ? "overview" : "conta");
  const canUseSavedProgress = Boolean(hasCompletedProfile && viewerUser?.id && viewerUser?.sessionToken);
  const currentWeekDayIndex = new Date().getDay();
  const weeklyCompletionMap = canUseSavedProgress ? (weeklyCompletionMapApi || getWeeklyCompletionMap(viewerUser?.id)) : Array.from({ length: 7 }, () => false);
  const weeklyCompletedCount = weeklyCompletionMap.filter(Boolean).length;
  const chromeSurface = "rgba(22, 23, 27, 0.96)";
  const chromeStroke = "rgba(255,255,255,0.10)";
  const userDisplayName = hasCompletedProfile && viewerUser?.name ? viewerUser.name : "Família Mosaico";
  const userHeaderEyebrow = hasCompletedProfile ? "Bem-vindo," : "Bem-vindo à";
  const profilePhotoUrl = hasCompletedProfile ? (viewerUser?.photoUrl || viewerUser?.profilePhotoUrl || viewerUser?.picture || viewerUser?.imageUrl || viewerUser?.avatarUrl || "") : "";
  const profileInitials = userDisplayName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const topBarHeight = 76;
  const bottomBarHeight = 96;
  const fixedTopBarStyle = {
    // Full width edge-a-edge, igual ao topbar institucional e ao do /financeiro
    // (antes ficava centralizado em 1080px no desktop -- uma faixa "flutuando"
    // que terminava no meio da tela em vez de ir até a borda).
    position: "fixed",
    // var(--viewas-offset) e' escrita pelo ViewAsRoleBanner (0px quando
    // ninguem esta simulando um papel) -- sem isso a faixa roxa "Vendo como"
    // (fixed, z-index 9999) ficava por cima deste menu em vez de empurra-lo
    // pra baixo, escondendo logo/nav (reportado pelo Rodrigo).
    top: "var(--viewas-offset, 0px)",
    left: 0,
    right: 0,
    width: "auto",
    // Altura fixa (nao so por padding) igual ao --topbar-h:76px do
    // institucional (institutional.css) -- sem isso a barra renderizava
    // ~71px (padding + conteudo), 5px mais baixa que a do institucional,
    // enquanto o paddingTop do containerStyle abaixo ja assume 76px cheios.
    // Resultado: a borda inferior da barra fixa mudava de posicao ao trocar
    // de rota entre o institucional e /familia, parecendo um "pulo" no menu.
    height: topBarHeight,
    zIndex: 120,
    padding: isMobile ? "0 14px" : "0 20px",
    display: "flex",
    alignItems: "center",
    borderBottom: `1px solid ${chromeStroke}`,
    background: chromeSurface,
    backdropFilter: "blur(14px)",
    boxShadow: "0 10px 26px rgba(0,0,0,0.22), inset 0 -1px 0 rgba(255,255,255,0.035)",
    boxSizing: "border-box"
  };
  // overflowX:"hidden" aqui (sem overflowY) fazia o navegador computar
  // overflowY como "auto" por baixo dos panos (regra do CSS: se um eixo nao
  // e' "visible" e o outro e', o "visible" vira "auto") -- isso transformava
  // esta div num container de rolagem proprio, o que quebra position:sticky
  // de tudo dentro dela (o preview do avatar trava em relacao a ESTA div,
  // que nem chega a rolar de verdade, em vez de travar em relacao a pagina).
  // Removido -- redundante mesmo: html/body/#root (public/index.html) ja'
  // tem overflow-x:hidden proprio, entao o scroll horizontal continua
  // bloqueado sem precisar repetir aqui e sem quebrar sticky. Reportado pelo
  // Rodrigo: preview do avatar continuava cortando ao rolar mesmo depois do
  // sticky ter sido ligado.
  const containerStyle = { width: "100%", maxWidth: "100vw", minHeight: "100vh", backgroundColor: UI.shell, backgroundImage: MOSAIC_BG, backgroundAttachment: isMobile ? "scroll" : "fixed", fontFamily: "'DM Sans', system-ui, sans-serif", color: UI.text, paddingTop: `calc(${topBarHeight}px + var(--viewas-offset, 0px))`, paddingBottom: bottomBarHeight, boxSizing: "border-box" };
  // Padding-top do desktop alinhado ao .hero do institucional (institutional.css:
  // ".hero{margin-top:22px}") -- essa distancia entre a barra fixa e o primeiro
  // bloco de conteudo era 30px aqui contra 22px no institucional, entao o mesmo
  // banner (agora com a mesma altura) ainda comecava 8px mais pra baixo em
  // Devocionais do que em Inicio, dando a impressao de banner desalinhado com a
  // barra de menu ao trocar de pagina.
  // Mesmo motivo do containerStyle logo acima: overflowX sem overflowY vira
  // overflow-y:auto computado (regra do CSS), o que quebra sticky de tudo
  // dentro. Esse aqui especificamente era o que ainda cortava o preview do
  // avatar mesmo depois do containerStyle ser corrigido -- achado inspecionando
  // o site ao vivo de novo apos o primeiro fix nao ter resolvido 100%.
  const pageContentStyle = { width: "100%", maxWidth: isDesktop ? 1180 : isTablet ? 760 : "100%", margin: "0 auto", padding: isDesktop ? "22px 24px 44px" : isTablet ? "26px 16px 32px" : "16px 10px 28px", boxSizing: "border-box" };
  const widthGuardStyle = { width: "100%", maxWidth: "100%", minWidth: 0, boxSizing: "border-box" };
  const overflowGuardStyle = { ...widthGuardStyle, overflow: "hidden" };
  const mobileHeroTitleSize = isDesktop ? 48 : isTablet ? 38 : 30;
  const mobileSectionTitleSize = isDesktop ? 30 : isTablet ? 25 : 22;
  const h1Style = { fontFamily: DS.type.family, fontWeight: 700 };
  const buttonReset = { background: "none", border: "none", cursor: "pointer", fontFamily: "inherit", color: "inherit", padding: 0 };
  const sectionCardStyle = { background: "linear-gradient(160deg, #0f1727, #131f34)", border: "1px solid rgba(120,152,210,0.28)", borderRadius: 16, padding: 18, marginBottom: 18, boxShadow: "0 14px 28px rgba(0,0,0,0.36), inset 0 1px 0 rgba(255,255,255,0.06)" };
  const softPanelStyle = { background: "linear-gradient(160deg, rgba(255,255,255,0.075), rgba(255,255,255,0.026))", border: `1px solid ${UI.stroke}`, borderRadius: 18, boxShadow: "0 16px 32px rgba(0,0,0,0.24), inset 0 1px 0 rgba(255,255,255,0.05)" };
  const warmHeroStyle = { position: "relative", overflow: "hidden", width: "100%", maxWidth: "100%", boxSizing: "border-box", padding: isDesktop ? "34px 38px" : isTablet ? "28px 22px" : "24px 20px", borderRadius: isDesktop ? 34 : isTablet ? 28 : 24, border: "1px solid rgba(255,255,255,0.24)", background: "linear-gradient(135deg, rgba(248,243,232,0.99) 0%, rgba(231,238,239,0.98) 48%, rgba(244,224,194,0.96) 100%)", color: "#182033", boxShadow: "0 22px 54px rgba(0,0,0,0.34)", marginBottom: 20 };
  const eyebrowStyle = { margin: "0 0 10px", fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 900 };
  const profileInputStyle = {
    width: "100%",
    border: "1px solid rgba(226,232,240,0.16)",
    background: "rgba(12,20,34,0.68)",
    color: "#F8FAFC",
    borderRadius: 14,
    padding: "12px 13px",
    fontSize: isMobile ? 16 : 14,
    fontFamily: "inherit",
    boxSizing: "border-box",
    outline: "none",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.04)"
  };
  const profileLabelStyle = { display: "block", margin: "0 0 7px", color: "#cbd5e1", fontSize: 11, letterSpacing: 0.6, textTransform: "uppercase", fontWeight: 800 };
  const profileSurfaceStyle = {
    ...sectionCardStyle,
    borderColor: "rgba(230,177,90,0.28)",
    background: "linear-gradient(145deg, rgba(19,29,48,0.98), rgba(15,23,39,0.98) 54%, rgba(47,37,26,0.82))",
    boxShadow: "0 22px 46px rgba(0,0,0,0.32), inset 0 1px 0 rgba(255,255,255,0.06)"
  };
  const profilePillStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    minHeight: 30,
    padding: "0 10px",
    borderRadius: 999,
    border: "1px solid rgba(230,177,90,0.28)",
    background: "rgba(230,177,90,0.10)",
    color: "#F5D28B",
    fontSize: 11,
    fontWeight: 900,
    textTransform: "uppercase",
    letterSpacing: 0.7
  };

  const openTrail = (trailRef) => {
    const trail = typeof trailRef === "object" && trailRef
      ? (trails.find((item) => item.id === trailRef.id || item.slug === trailRef.slug) || trailRef)
      : trails.find((item) => item.id === trailRef || item.slug === trailRef);
    if (!trail) return;
    setSelectedTrail(trail);
    setScreen("trail");
    window.scrollTo(0,0);
  };

  const requestProfileAccess = (context = "general") => {
    setProfilePromptContext(context);
    setShowProfilePrompt(true);
  };

  const markDayAsCompleted = () => {
    if (!selectedTrail || !selectedDay) return;
    if (!hasCompletedProfile) {
      requestProfileAccess("devotional");
      return;
    }
    if (!canUseSavedProgress) {
      requestProfileAccess("devotional");
      setProgressSyncFeedback("Entre na sua conta para salvar o progresso desta trilha.");
      return;
    }
    if (progressSyncing) return;
    const currentProgress = Math.max(0, selectedTrail.progress || 0);
    const expectedDay = Math.min(currentProgress + 1, selectedTrail.duration || currentProgress + 1);
    if (selectedDay !== expectedDay) {
      setProgressSyncFeedback(`Conclua em ordem: o próximo dia é o Dia ${expectedDay}.`);
      return;
    }
    const nextProgress = expectedDay;
    setTrails((prev) => prev.map((trail) => (trail.id === selectedTrail.id ? { ...trail, progress: nextProgress } : trail)));
    setSelectedTrail((prev) => (prev ? { ...prev, progress: nextProgress } : prev));
    persistTrailProgress(selectedTrail.id, nextProgress, viewerUser?.id);
    persistUserDayCompletion({ trailId: selectedTrail.id, day: selectedDay }, viewerUser?.id);
    setProgressSyncFeedback("Sincronizando progresso...");

    if (viewerUser?.id && viewerUser?.sessionToken && selectedTrail?.slug) {
      setProgressSyncing(true);
      (async () => {
        try {
          await apiPost("/trails/progress/complete", {
            viewer: viewerUser,
            body: { userId: viewerUser.id, trailSlug: selectedTrail.slug, dayNumber: selectedDay }
          });
          const groupTrailSlug = selectedTrailGroup?.trail?.slug || selectedTrailGroup?.trail?.id;
          const trailSlug = selectedTrail?.slug || selectedTrail?.id;
          if (selectedTrailGroup?.id && groupTrailSlug === trailSlug) {
            try {
              const updatedGroup = await apiPatch(`/groups/${encodeURIComponent(selectedTrailGroup.id)}/progress`, {
                viewer: viewerUser,
                body: { userId: viewerUser.id, currentDay: nextProgress }
              });
              setSelectedTrailGroup(updatedGroup);
              setOpenGroups((current) => current.map((item) => item.id === updatedGroup.id ? updatedGroup : item));
            } catch (_) { /* progresso do grupo é best-effort */ }
          }
          setProgressSyncFeedback("Progresso sincronizado com sucesso.");
          await loadTrailsData();
        } catch (error) {
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            clearViewerSession("Sua sessão expirou. Entre novamente para continuar.");
          } else {
            setProgressSyncFeedback("Progresso salvo localmente. Sincronização pendente.");
          }
        } finally {
          setProgressSyncing(false);
        }
      })();
    }
  };

  const submitDevotionalFeedback = (response) => {
    if (!selectedTrail || !selectedDay) return;
    if (!viewerUser?.id) return;
    const feedbackKey = `${selectedTrail.id}_${selectedDay}`;
    if (devotionalFeedbackMap[feedbackKey]) return;
    setDevotionalFeedbackMap((prev) => ({ ...prev, [feedbackKey]: response }));
    persistDevotionalFeedbackLocally(selectedTrail.id, selectedDay, response, viewerUser.id);
    if (viewerUser?.sessionToken && selectedTrail?.slug) {
      setDevotionalFeedbackSyncing(true);
      (async () => {
        try {
          await apiPost("/trails/devotional-feedback", {
            viewer: viewerUser,
            body: { userId: viewerUser.id, trailSlug: selectedTrail.slug, dayNumber: selectedDay, response }
          });
        } catch (error) {
          // best-effort: o registro local ja guardou a resposta pro usuario
          // nao ver a pergunta de novo, mesmo se o envio pro servidor falhar.
        } finally {
          setDevotionalFeedbackSyncing(false);
        }
      })();
    }
  };

  // Carrega os comentarios reais da mesa sobre o dia aberto, so' quando a
  // aba "Partilhar" esta de fato visivel -- evita chamada de API toda vez
  // que alguem so' abre um dia sem nunca olhar essa aba.
  useEffect(() => {
    if (screen !== "day" || dayTab !== "chat" || !coopActive || !selectedTrailGroup?.id || !selectedDay || !viewerUser?.id) {
      return;
    }
    let cancelled = false;
    setGroupDayComments([]);
    setGroupDayCommentDraft("");
    setGroupDayCommentsLoading(true);
    setGroupDayCommentFeedback("");
    (async () => {
      try {
        const data = await apiFetch(
          `/groups/${encodeURIComponent(selectedTrailGroup.id)}/comments?viewerUserId=${encodeURIComponent(viewerUser.id)}&dayNumber=${encodeURIComponent(selectedDay)}`,
          { method: "GET", viewer: viewerUser }
        );
        if (!cancelled) setGroupDayComments(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) setGroupDayCommentFeedback("Não foi possível carregar os comentários da mesa agora.");
      } finally {
        if (!cancelled) setGroupDayCommentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [screen, dayTab, coopActive, selectedTrailGroup?.id, selectedDay, viewerUser?.id]);

  // So' pra ADMIN de verdade: resolve o cadastro (Person) de quem comentou
  // na mesa, pra responder "quem e' essa pessoa?" sem esperar um clique --
  // ver /admin/pessoas-por-user em routes/admin.js. So' busca quem ainda
  // nao esta no mapa acumulado (adminCommentPersonMap), entao trocar de dia
  // nao refaz a mesma consulta pra quem ja comentou em outro dia.
  useEffect(() => {
    const isAdmin = Boolean(viewerUser?.roles?.includes("ADMIN"));
    if (!isAdmin || !groupDayComments.length) return;
    const pendingUserIds = [...new Set(groupDayComments.map((comment) => comment.userId))].filter(
      (userId) => userId && !(userId in adminCommentPersonMap)
    );
    if (!pendingUserIds.length) return;
    let cancelled = false;
    (async () => {
      try {
        const data = await apiFetch(
          `/admin/pessoas-por-user?userIds=${encodeURIComponent(pendingUserIds.join(","))}`,
          { method: "GET", viewer: viewerUser }
        );
        if (cancelled) return;
        setAdminCommentPersonMap((prev) => {
          const next = { ...prev };
          for (const userId of pendingUserIds) next[userId] = null; // marca como "ja' consultado, sem cadastro"
          for (const pessoa of data?.pessoas || []) next[pessoa.userId] = pessoa;
          return next;
        });
      } catch (error) {
        // best-effort: se falhar, a tela so' deixa de mostrar o extra do admin
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [groupDayComments, viewerUser, adminCommentPersonMap]);

  // Resumo das ultimas reflexoes da mesa (qualquer dia), pra tela de visao
  // geral da trilha ("Ritmo compartilhado"). So' carrega quando essa tela
  // esta de fato aberta -- mesmo cuidado do efeito acima, sem chamada de
  // API a toa.
  useEffect(() => {
    if (screen !== "trail" || !coopActive || !selectedTrailGroup?.id || !viewerUser?.id) {
      return;
    }
    let cancelled = false;
    setRecentGroupCommentsLoading(true);
    (async () => {
      try {
        const data = await apiFetch(
          `/groups/${encodeURIComponent(selectedTrailGroup.id)}/comments/recent?viewerUserId=${encodeURIComponent(viewerUser.id)}&limit=4`,
          { method: "GET", viewer: viewerUser }
        );
        if (!cancelled) setRecentGroupComments(Array.isArray(data) ? data : []);
      } catch (error) {
        if (!cancelled) setRecentGroupComments([]);
      } finally {
        if (!cancelled) setRecentGroupCommentsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [screen, coopActive, selectedTrailGroup?.id, viewerUser?.id]);

  const submitGroupDayComment = () => {
    const text = groupDayCommentDraft.trim();
    if (!text || !selectedTrailGroup?.id || !selectedDay || !viewerUser?.id) return;
    setGroupDayCommentSyncing(true);
    setGroupDayCommentFeedback("");
    (async () => {
      try {
        const comment = await apiPost(`/groups/${encodeURIComponent(selectedTrailGroup.id)}/comments`, {
          viewer: viewerUser,
          body: { userId: viewerUser.id, dayNumber: selectedDay, message: text }
        });
        setGroupDayComments((prev) => [...prev, comment]);
        setGroupDayCommentDraft("");
      } catch (error) {
        setGroupDayCommentFeedback(error?.message || "Não foi possível enviar seu comentário agora.");
      } finally {
        setGroupDayCommentSyncing(false);
      }
    })();
  };

  const openDay = (day, trailOverride = null, options = {}) => {
    const { markStarted = true } = options;
    if (!hasCompletedProfile) {
      requestProfileAccess("devotional");
      return;
    }
    const trailForDay = trailOverride || selectedTrail || homeModel?.currentTrail || trails[0] || null;
    const safeDay = Math.min(Math.max(1, day || 1), Math.max(1, trailForDay?.duration || day || 1));
    if (trailForDay && (!selectedTrail || selectedTrail.id !== trailForDay.id)) {
      setSelectedTrail(trailForDay);
    }
    // Marca a trilha como "iniciada" na primeira vez que um dia dela e'
    // aberto, mesmo sem concluir nenhum (POST /trails/progress/start) --
    // sem isso, uma trilha solo so' aparecia em algum lugar (Home, Meus
    // Planos) depois de concluir o Dia 1 (pedido da Carla, 20/10/2026).
    // Best-effort e otimista: atualiza local primeiro, sincroniza depois;
    // se a sincronizacao falhar, so' nao fica "iniciada" ate o proximo
    // carregamento (sem bloquear a leitura por causa disso).
    if (markStarted && trailForDay && canUseSavedProgress && !trailForDay.started && !(Number(trailForDay.progress) > 0) && viewerUser?.id && viewerUser?.sessionToken && trailForDay?.slug) {
      setTrails((prev) => prev.map((trail) => (trail.id === trailForDay.id ? { ...trail, started: true } : trail)));
      setSelectedTrail((prev) => (prev && prev.id === trailForDay.id ? { ...prev, started: true } : prev));
      apiPost("/trails/progress/start", { viewer: viewerUser, body: { userId: viewerUser.id, trailSlug: trailForDay.slug } }).catch(() => {
        // best-effort -- se falhar, so' nao aparece "iniciada" ate a proxima sincronizacao
      });
    }
    trackEvent("devotional_day_open", { day: safeDay, trailId: trailForDay?.slug || trailForDay?.id || null });
    setSelectedDay(safeDay);
    setDayTab("devotional");
    setDayScreenBackTarget("trail");
    setScreen("day");
    window.scrollTo(0,0);
  };

  // Botao "Proximo dia" na propria tela do dia (DayScreen) -- antes,
  // depois de "Concluir este dia", nao tinha nenhum jeito de seguir pro
  // proximo dia direto dali: a pessoa tinha que voltar pra Mesa/Planos na
  // mao e procurar o card "Continuar no Dia X" de novo (reportado ao vivo
  // pelo Rodrigo testando: "como vai para o dia 3? ja salvei o
  // progresso"). getTrailNextDay nao tem nenhuma trava por calendario (so'
  // depende de trail.progress), entao o proximo dia ja fica liberado assim
  // que o dia atual e' concluido.
  const goToNextDayFromDayScreen = () => {
    if (!selectedTrail) return;
    const duration = selectedTrail.duration || 1;
    const progress = selectedTrail.progress || 0;
    if (progress >= duration) {
      // Ultimo dia da trilha ja concluido -- nao ha "proximo dia": volta
      // pra Mesa, onde a trilha deve aparecer como concluida.
      setScreen("main");
      setActiveTab("family");
      window.scrollTo(0, 0);
      return;
    }
    openDay(getTrailNextDay(selectedTrail), selectedTrail);
  };

  const openEbook = (ebookId) => {
    if (!hasCompletedProfile) {
      requestProfileAccess("ebook");
      return;
    }
    const ebook = EBOOKS_MOCK.find(e => e.id === ebookId) || EBOOKS_MOCK[0];
    if (!ebook) return;
    trackEvent("ebook_start", { ebookId: ebook.id });
    setSelectedEbook(ebook);
    setEbookTab("reading");
    try {
      const savedPage = getSavedEbookPage(ebook);
      const totalPages = Math.max(1, ebook?.pages || 1);
      setEbookPage(Number.isNaN(savedPage) ? 1 : Math.min(Math.max(savedPage, 1), totalPages));
    } catch (error) {
      setEbookPage(1);
    }
    setActiveTab("ebooks");
    setScreen("ebook");
    window.scrollTo(0,0);
  };

  // Pula a landing publica do ebook e abre a leitura direto quando a pessoa
  // ja chega logada (cache otimista "mosaico_user" -- ver viewerUser mais
  // acima) -- sem isso, quem clica no banner "E a serie que estamos
  // pregando agora" da Home ja logado(a) via a mesma tela de apresentacao/
  // "Comecar a leitura" de quem nunca abriu o app, em vez de ir direto pro
  // ebook que ja esta acompanhando (pedido da Carla, 25/10/2026: "deveria
  // abrir direto o ebook se ja estiver logado"). So roda uma vez (ref) pra
  // nao brigar com quem volta de proposito pra landing (ex.: "Voltar"
  // depois de abrir a leitura). Continua existindo a landing publica
  // normalmente pra quem nao esta logado -- inclusive pra SEO/robots, que
  // nunca tem "mosaico_user" no localStorage (contexto de crawler limpo).
  const autoSkippedEbookLandingRef = useRef(false);
  useEffect(() => {
    if (autoSkippedEbookLandingRef.current) return;
    if (screen !== "ebook-landing" || !selectedEbook) return;
    if (!viewerUser) return;
    autoSkippedEbookLandingRef.current = true;
    openEbook(selectedEbook.id);
  }, [screen, selectedEbook, viewerUser]);

  const normalizePastoralSlot = (slot) => {
    const startAt = new Date(slot.startAt);
    const endAt = new Date(slot.endAt);
    const pastor = slot.pastor || pastors.find((item) => item.id === slot.pastorId) || {};
    const mode = String(slot.mode || "ONLINE").toLowerCase() === "presential" ? "presencial" : "online";
    return {
      id: slot.id,
      source: "api",
      pastorId: slot.pastorId || pastor.id,
      pastorName: pastor.name || "Pastor",
      startAt,
      endAt,
      duration: Math.max(15, Math.round((endAt.getTime() - startAt.getTime()) / 60000) || 45),
      mode,
      label: mode === "online" ? "Online" : "Presencial",
      helper: mode === "online" ? "Videochamada pelo app" : (slot.locationNote || "Na igreja, com detalhes confirmados pela equipe"),
      locationNote: slot.locationNote || "",
      meetLink: slot.meetLink || ""
    };
  };

  const sendTrailNudge = (member) => {
    if (!member?.id || member.id === "you") return;
    setNudgedUserIds((prev) => (prev.includes(member.id) ? prev : [...prev, member.id]));
    setNudgeFeedback(`${member.name} recebeu um toque carinhoso para retomar a leitura de hoje.`);
  };

  const getEbookHighlightKey = (ebookId, userId = viewerUser?.id) => `mosaico_ebook_highlights_${userId || "guest"}_${ebookId || "default"}`;
  const getEbookNoteKey = (ebookId, userId = viewerUser?.id) => `mosaico_ebook_note_${userId || "guest"}_${ebookId || "default"}`;
  const getEbookPageKey = (ebookId, userId = viewerUser?.id) => `mosaico_ebook_page_${userId || "guest"}_${ebookId || "default"}`;
  const getDevotionalIdentity = (trail = selectedTrail, day = selectedDay) => `${trail?.id || trail?.slug || "trail"}_${day || "day"}`;
  const getDevotionalHighlightKey = (trail = selectedTrail, day = selectedDay) => `mosaico_devotional_highlights_${getDevotionalIdentity(trail, day)}`;
  const getSavedEbookPage = (ebook) => {
    if (!canUseSavedProgress) return 1;
    try {
      const savedPage = parseInt(localStorage.getItem(getEbookPageKey(ebook.id)) || "1", 10);
      return Number.isNaN(savedPage) ? 1 : Math.min(Math.max(savedPage, 1), Math.max(1, ebook.pages || 1));
    } catch (error) {
      return 1;
    }
  };
  // Mesma logica de "concluido" usada no selo dos cards do acervo (overlay
  // + check) -- extraida aqui pra tambem decidir o destaque "Continue sua
  // leitura" do topo (antes ele podia escolher um livro ja concluido e
  // continuava mandando "Continuar leitura" nele, o que nao fazia sentido).
  const isEbookDone = (ebook) => canUseSavedProgress && (ebook.pages || 0) > 0 && getSavedEbookPage(ebook) >= ebook.pages;
  // 1) tem um livro EM ANDAMENTO (progresso, mas nao concluido) -> mostra
  //    ele normalmente, como antes; 2) so tem concluido -> sugere o proximo
  //    ainda nao comecado, com chamada de "comece outro"; 3) ate esse
  //    proximo ja foi lido (acervo inteiro concluido) -> avisa disso; 4) sem
  //    nenhum progresso -> mantem o "Comece por aqui" de sempre.
  const heroInProgressEbook = EBOOKS_MOCK.find((ebook) => getSavedEbookPage(ebook) > 1 && !isEbookDone(ebook));
  const heroCompletedEbook = !heroInProgressEbook ? EBOOKS_MOCK.find((ebook) => isEbookDone(ebook)) : null;
  const heroNextEbook = heroCompletedEbook ? EBOOKS_MOCK.find((ebook) => !isEbookDone(ebook)) : null;
  const continueEbook = heroInProgressEbook || heroNextEbook || heroCompletedEbook || EBOOKS_MOCK[0];
  const continueEbookPage = getSavedEbookPage(continueEbook);
  const hasContinueEbookProgress = canUseSavedProgress && Boolean(heroInProgressEbook);
  const continueEbookProgress = hasContinueEbookProgress ? Math.min(100, Math.round((continueEbookPage / Math.max(1, continueEbook.pages || 1)) * 100)) : 0;
  const heroJustCompletedSuggestingNext = Boolean(heroCompletedEbook) && Boolean(heroNextEbook);
  const heroFinishedWholeCollection = Boolean(heroCompletedEbook) && !heroNextEbook;

  // "Sua mesa agora" / "Pendencias" na Home (Hoje) -- mesmo dado que ja
  // existe em Mesa (openGroups) e nos convites enviados (myInvites), so
  // resumido aqui pra nao depender da pessoa entrar em cada aba pra saber
  // que tem gente esperando ou uma mesa aberta rolando com ela.
  const myActiveMesas = openGroups.filter((group) => {
    const activeMembers = group.members?.filter((member) => ["ACTIVE", "INVITED"].includes(member.status)) || [];
    return activeMembers.some((member) => member.userId && viewerUser?.id && member.userId === viewerUser.id);
  });
  // So PENDING nao basta: ha convites PENDING "orfaos" (enviados em
  // duplicata pra mesma pessoa/mesa antes do /invites/:token/accept passar
  // a resolver os duplicados junto -- ver invites.js) que nunca mudam de
  // status porque a pessoa ja aceitou um OUTRO convite igual. Sem filtrar
  // isso aqui, o card "Convites que voce enviou" ficava dizendo "ainda sem
  // resposta" pra sempre, mesmo com a pessoa ja ativa na mesa (relatado
  // pelo Rodrigo, 20/10/2026: "ja entrei na mesa... e continua sem
  // resposta"). Mesma logica de match (email > whatsapp > nome) usada no
  // accept.
  const pendingSentInvites = myInvites.filter((invite) => {
    if (invite.status !== "PENDING") return false;
    const alreadyActive = invite.group?.members?.some((member) => member.status === "ACTIVE" && (
      (invite.inviteeEmail && member.email === invite.inviteeEmail) ||
      (invite.inviteeWhatsapp && member.whatsapp === invite.inviteeWhatsapp) ||
      member.displayName === invite.inviteeName
    ));
    return !alreadyActive;
  });

  const readEbookHighlights = (ebookId) => {
    if (!canUseSavedProgress) return [];
    try {
      const raw = localStorage.getItem(getEbookHighlightKey(ebookId));
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  };

  const persistEbookHighlights = (ebookId, highlights) => {
    if (!canUseSavedProgress) return;
    try {
      localStorage.setItem(getEbookHighlightKey(ebookId), JSON.stringify(highlights));
    } catch (error) {
      // noop
    }
  };

  const persistEbookNote = (ebookId, value) => {
    if (!canUseSavedProgress) return;
    try {
      localStorage.setItem(getEbookNoteKey(ebookId), value);
    } catch (error) {
      // noop
    }
  };

  const syncEbookState = async (ebookId, patch) => {
    if (!canUseSavedProgress || !ebookId) return;
    try {
      await apiPatch(`/ebooks/${encodeURIComponent(ebookId)}/state`, {
        viewer: viewerUser,
        body: { viewerUserId: viewerUser.id, ...patch }
      });
    } catch (error) {
      // localStorage continua como fallback offline
    }
  };

  const readDevotionalHighlights = (trail, day) => {
    if (!canUseSavedProgress) return [];
    try {
      const raw = localStorage.getItem(getDevotionalHighlightKey(trail, day));
      return raw ? JSON.parse(raw) : [];
    } catch (error) {
      return [];
    }
  };

  const persistDevotionalHighlights = (trail, day, highlights) => {
    if (!canUseSavedProgress) return;
    try {
      localStorage.setItem(getDevotionalHighlightKey(trail, day), JSON.stringify(highlights));
    } catch (error) {
      // noop
    }
  };

  const getDevotionalReflectionKey = (trail = selectedTrail, day = selectedDay) => `mosaico_devotional_reflection_${getDevotionalIdentity(trail, day)}`;

  const readDevotionalReflection = (trail, day) => {
    if (!canUseSavedProgress) return "";
    try {
      return localStorage.getItem(getDevotionalReflectionKey(trail, day)) || "";
    } catch (error) {
      return "";
    }
  };

  const persistDevotionalReflection = (trail, day, value) => {
    if (!canUseSavedProgress) return;
    try {
      localStorage.setItem(getDevotionalReflectionKey(trail, day), value);
    } catch (error) {
      // noop
    }
  };

  const updateDevotionalReflection = (value) => {
    const identity = getDevotionalIdentity();
    setDevotionalReflections((prev) => ({ ...prev, [identity]: value }));
    persistDevotionalReflection(selectedTrail, selectedDay, value);
  };

  const buildDiaryEntries = () => {
    if (!canUseSavedProgress) return [];
    const entries = [];
    trails.forEach((trail) => {
      const trailDevotionals = trail.devotionals || DAYS_MOCK[trail.id] || DAYS_MOCK[trail.slug] || [];
      trailDevotionals.forEach((devotional) => {
        const day = devotional.day;
        const reflection = readDevotionalReflection(trail, day);
        const highlights = readDevotionalHighlights(trail, day);
        if (!reflection && highlights.length === 0) return;
        entries.push({
          trailId: trail.id,
          trailTitle: trail.title,
          trailColor: trail.color,
          day,
          dayTitle: devotional.title,
          reflection,
          highlightCount: highlights.length
        });
      });
    });
    return entries.sort((a, b) => (a.trailTitle || "").localeCompare(b.trailTitle || "") || a.day - b.day);
  };

  const addEbookHighlight = (color) => {
    if (!ebookSelection || !selectedEbook?.id) return;
    const nextHighlights = [
      ...(ebookHighlights[selectedEbook.id] || []),
      {
        paragraphIndex: ebookSelection.paragraphIndex,
        start: ebookSelection.start,
        end: ebookSelection.end,
        color
      }
    ];
    setEbookHighlights((prev) => ({ ...prev, [selectedEbook.id]: nextHighlights }));
    persistEbookHighlights(selectedEbook.id, nextHighlights);
    syncEbookState(selectedEbook.id, { highlights: nextHighlights });
    setEbookSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const removeEbookHighlight = (paragraphIndex, start, end) => {
    if (!selectedEbook?.id) return;
    const nextHighlights = (ebookHighlights[selectedEbook.id] || []).filter((item) => !(item.paragraphIndex === paragraphIndex && item.start === start && item.end === end));
    setEbookHighlights((prev) => ({ ...prev, [selectedEbook.id]: nextHighlights }));
    persistEbookHighlights(selectedEbook.id, nextHighlights);
    syncEbookState(selectedEbook.id, { highlights: nextHighlights });
  };

  const handleEbookTextSelect = () => {
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      let paragraphNode = range.commonAncestorContainer.nodeType === 3 ? range.commonAncestorContainer.parentNode : range.commonAncestorContainer;
      while (paragraphNode && !paragraphNode.hasAttribute?.("data-ebook-p-index")) {
        paragraphNode = paragraphNode.parentNode;
        if (paragraphNode === document.body || !paragraphNode) break;
      }

      if (!paragraphNode?.hasAttribute?.("data-ebook-p-index")) return;
      const paragraphIndex = Number(paragraphNode.getAttribute("data-ebook-p-index"));
      const getOffset = (node, offset) => {
        let absoluteOffset = 0;
        const walker = document.createTreeWalker(paragraphNode, NodeFilter.SHOW_TEXT, null, false);
        let current;
        while ((current = walker.nextNode())) {
          if (current === node) return absoluteOffset + offset;
          absoluteOffset += current.textContent.length;
        }
        return absoluteOffset;
      };

      const startOffset = getOffset(range.startContainer, range.startOffset);
      const endOffset = getOffset(range.endContainer, range.endOffset);
      const start = Math.min(startOffset, endOffset);
      const end = Math.max(startOffset, endOffset);
      if (start === end) return;

      const rect = range.getBoundingClientRect();
      setEbookSelection({
        top: rect.top < 72 ? rect.bottom + 12 : rect.top - 58,
        left: rect.left + rect.width / 2,
        paragraphIndex,
        start,
        end
      });
    }, 50);
  };

  const renderEbookHighlightedText = (text, highlights, paragraphIndex) => {
    if (!highlights?.length) return text;
    const sorted = highlights
      .filter((highlight) => Number.isFinite(highlight.start) && Number.isFinite(highlight.end) && highlight.start >= 0 && highlight.end > highlight.start && highlight.start < text.length)
      .map((highlight) => ({ ...highlight, end: Math.min(highlight.end, text.length) }))
      .sort((a, b) => a.start - b.start);
    if (!sorted.length) return text;
    const merged = [];
    sorted.forEach((highlight) => {
      const last = merged[merged.length - 1];
      if (last && highlight.start <= last.end) {
        last.end = Math.max(last.end, highlight.end);
      } else {
        merged.push({ ...highlight });
      }
    });

    const parts = [];
    let lastIndex = 0;
    merged.forEach((highlight, index) => {
      if (highlight.start > lastIndex) parts.push(<span key={`text-${index}`}>{text.substring(lastIndex, highlight.start)}</span>);
      parts.push(
        <mark
          key={`highlight-${index}`}
          onClick={() => {
            if (window.confirm("Remover marcação?")) removeEbookHighlight(paragraphIndex, highlight.start, highlight.end);
          }}
          style={{ backgroundColor: highlight.color, color: "inherit", padding: "2px 0", borderRadius: 3, cursor: "pointer" }}
        >
          {text.substring(highlight.start, highlight.end)}
        </mark>
      );
      lastIndex = highlight.end;
    });
    if (lastIndex < text.length) parts.push(<span key="text-end">{text.substring(lastIndex)}</span>);
    return parts;
  };

  const addDevotionalHighlight = (color) => {
    if (!devotionalSelection || !selectedTrail || !selectedDay) return;
    const identity = getDevotionalIdentity();
    const nextHighlights = [
      ...(devotionalHighlights[identity] || []),
      {
        paragraphIndex: devotionalSelection.paragraphIndex,
        start: devotionalSelection.start,
        end: devotionalSelection.end,
        color
      }
    ];
    setDevotionalHighlights((prev) => ({ ...prev, [identity]: nextHighlights }));
    persistDevotionalHighlights(selectedTrail, selectedDay, nextHighlights);
    setDevotionalSelection(null);
    window.getSelection()?.removeAllRanges();
  };

  const removeDevotionalHighlight = (paragraphIndex, start, end) => {
    if (!selectedTrail || !selectedDay) return;
    const identity = getDevotionalIdentity();
    const nextHighlights = (devotionalHighlights[identity] || []).filter((item) => !(item.paragraphIndex === paragraphIndex && item.start === start && item.end === end));
    setDevotionalHighlights((prev) => ({ ...prev, [identity]: nextHighlights }));
    persistDevotionalHighlights(selectedTrail, selectedDay, nextHighlights);
  };

  const handleDevotionalTextSelect = () => {
    setTimeout(() => {
      const selection = window.getSelection();
      if (!selection || selection.isCollapsed || selection.rangeCount === 0) return;

      const range = selection.getRangeAt(0);
      let paragraphNode = range.commonAncestorContainer.nodeType === 3 ? range.commonAncestorContainer.parentNode : range.commonAncestorContainer;
      while (paragraphNode && !paragraphNode.hasAttribute?.("data-devotional-p-index")) {
        paragraphNode = paragraphNode.parentNode;
        if (paragraphNode === document.body || !paragraphNode) break;
      }

      if (!paragraphNode?.hasAttribute?.("data-devotional-p-index")) return;
      const paragraphIndex = Number(paragraphNode.getAttribute("data-devotional-p-index"));
      const getOffset = (node, offset) => {
        let absoluteOffset = 0;
        const walker = document.createTreeWalker(paragraphNode, NodeFilter.SHOW_TEXT, null, false);
        let current;
        while ((current = walker.nextNode())) {
          if (current === node) return absoluteOffset + offset;
          absoluteOffset += current.textContent.length;
        }
        return absoluteOffset;
      };

      const startOffset = getOffset(range.startContainer, range.startOffset);
      const endOffset = getOffset(range.endContainer, range.endOffset);
      const start = Math.min(startOffset, endOffset);
      const end = Math.max(startOffset, endOffset);
      if (start === end) return;

      const rect = range.getBoundingClientRect();
      setDevotionalSelection({
        top: rect.top < 72 ? rect.bottom + 12 : rect.top - 58,
        left: rect.left + rect.width / 2,
        paragraphIndex,
        start,
        end
      });
    }, 50);
  };

  const renderDevotionalHighlightedText = (text, highlights, paragraphIndex) => {
    if (!highlights?.length) return text;
    // Bug relatado pela Carla (20/10/2026): marcar um trecho num paragrafo
    // fazia aparecer varios pedacos marcados em OUTROS paragrafos, sem
    // nenhuma relacao com o texto selecionado. Causa: devotionalHighlights
    // guarda TODAS as marcacoes do dia inteiro (de qualquer paragrafo), e
    // essa funcao e chamada uma vez por paragrafo (ver DayScreen.jsx) -- sem
    // filtrar por paragraphIndex, o start/end de uma marcacao feita no
    // paragrafo 0 acabava sendo cortado tambem do TEXTO dos paragrafos 1, 2,
    // 3... (desde que a posicao coubesse dentro do tamanho de cada um),
    // produzindo pedacos de frase aleatorios e sem sentido.
    const sorted = highlights
      .filter((highlight) => highlight.paragraphIndex === paragraphIndex)
      .filter((highlight) => Number.isFinite(highlight.start) && Number.isFinite(highlight.end) && highlight.start >= 0 && highlight.end > highlight.start && highlight.start < text.length)
      .map((highlight) => ({ ...highlight, end: Math.min(highlight.end, text.length) }))
      .sort((a, b) => a.start - b.start);
    if (!sorted.length) return text;

    const merged = [];
    sorted.forEach((highlight) => {
      const last = merged[merged.length - 1];
      if (last && highlight.start <= last.end) {
        last.end = Math.max(last.end, highlight.end);
      } else {
        merged.push({ ...highlight });
      }
    });

    const parts = [];
    let lastIndex = 0;
    merged.forEach((highlight, index) => {
      if (highlight.start > lastIndex) parts.push(<span key={`devotional-text-${index}`}>{text.substring(lastIndex, highlight.start)}</span>);
      parts.push(
        <mark
          key={`devotional-highlight-${index}`}
          onClick={() => {
            if (window.confirm("Remover marcação?")) removeDevotionalHighlight(paragraphIndex, highlight.start, highlight.end);
          }}
          style={{ backgroundColor: highlight.color, color: "inherit", padding: "2px 0", borderRadius: 4, cursor: "pointer", boxDecorationBreak: "clone", WebkitBoxDecorationBreak: "clone" }}
        >
          {text.substring(highlight.start, highlight.end)}
        </mark>
      );
      lastIndex = highlight.end;
    });
    if (lastIndex < text.length) parts.push(<span key="devotional-text-end">{text.substring(lastIndex)}</span>);
    return parts;
  };

  const getNextTrailForJourney = (currentTrail) => {
    if (!currentTrail) return null;
    return trails.find((trail) => trail.id !== currentTrail.id && (trail.progress || 0) < (trail.duration || 0));
  };

  const getRecommendedTrail = () => {
    const inProgress = trails
      .filter((trail) => (trail.progress || 0) > 0 && (trail.progress || 0) < (trail.duration || 0))
      .sort((a, b) => (b.progress || 0) - (a.progress || 0))[0];
    if (inProgress) return inProgress;

    const notStartedOrPartial = trails.find((trail) => (trail.progress || 0) < (trail.duration || 0));
    if (notStartedOrPartial) return notStartedOrPartial;

    return trails[0] || null;
  };

  const getRecommendedEbook = (trail) => {
    const map = {
      plataforma: "casa-ebook",
      "vida-interior": "provacoes",
      "igreja-mesa": "igreja-saudavel",
      proposito: "jardim-evangelho",
      "reino-publico": "verbo-voto",
      "casa-relacoes": "igreja-saudavel"
    };
    const preferred = EBOOKS_MOCK.find((ebook) => ebook.id === map[trail?.id]);
    return preferred || EBOOKS_MOCK[0];
  };

  const getRecommendationReason = (trail) => {
    if (!trail) return "Sugestões para continuar sua jornada espiritual.";
    const progress = trail.progress || 0;
    const duration = trail.duration || 0;
    if (progress > 0 && progress < duration) {
      return `Você está no Dia ${progress} de ${trail.title}. Continue para manter constância.`;
    }
    if (progress >= duration && duration > 0) {
      return `Você concluiu ${trail.title}. Vamos para uma nova trilha para aprofundar sua caminhada.`;
    }
    return `${trail.title} é um ótimo próximo passo para sua caminhada atual.`;
  };

  const getTrailParty = (trail) => {
    const groupTrailSlug = selectedTrailGroup?.trail?.slug || selectedTrailGroup?.trail?.id;
    const trailSlug = trail?.slug || trail?.id;
    if (selectedTrailGroup?.members?.length && groupTrailSlug && trailSlug && groupTrailSlug === trailSlug) {
      return selectedTrailGroup.members
        .filter((member) => ["ACTIVE", "INVITED"].includes(member.status))
        .map((member, index) => {
          const isYou = member.userId && viewerUser?.id && member.userId === viewerUser.id;
          return {
            id: isYou ? "you" : (member.userId || member.id || `member-${index}`),
            name: member.displayName || "Pessoa da mesa",
            color: isYou ? "#f3a952" : ["#7DD3FC", "#A78BFA", "#34D399", "#F472B6"][index % 4],
            day: Math.max(0, Number(member.currentDay) || 0),
            // Usa o avatarId real da conta vinculada (member.user.avatarId,
            // agora que o backend envia isso). So cai pro mock quando o
            // membro nao tem conta vinculada (convite por nome/whatsapp
            // ainda nao aceito) -- antes usava sempre o mock por posicao na
            // lista, entao qualquer pessoa real aparecia com avatar errado
            // pra quem nao fosse ela mesma (bug relatado pelo Rodrigo).
            avatarId: isYou ? selectedAvatarId : (member.user?.avatarId || COMMUNITY_USERS_MOCK[index % COMMUNITY_USERS_MOCK.length]?.avatarId)
          };
        });
    }

    const progress = Math.max(0, trail?.progress || 0);
    const totalDays = Math.max(1, trail?.duration || 1);
    const self = { id: "you", name: getUserDisplayName(), color: "#f3a952", day: progress, avatarId: selectedAvatarId };
    const partnersSeed = JOURNEY_PARTNERS.map((partner, index) => ({ ...partner, avatarId: index === 0 ? "f-06" : index === 1 ? "m-03" : "f-09" }));
    const partners = partnersSeed.map((partner, index) => {
      const drift = index === 0 ? -1 : index === 1 ? 1 : -2;
      return {
        ...partner,
        day: Math.min(totalDays, Math.max(0, progress + drift))
      };
    });
    return [self, ...partners];
  };

  const getAvatarById = (avatarId) => decodeDicebearAvatarId(avatarId) || decodeCustomAvatarId(avatarId) || AVATAR_OPTIONS.find((item) => item.id === avatarId) || AVATAR_OPTIONS[0];

  const getUserDisplayName = () => {
    if (!viewerUser?.name || !viewerUser.name.trim()) return "Você";
    return viewerUser.name.trim().split(" ")[0];
  };

  const availableAvatarOptions = AVATAR_OPTIONS;

  const handleJourneyContinue = () => {
    const currentTrail = trails[0];
    if (!currentTrail) {
      setActiveTab("library");
      setScreen("main");
      window.scrollTo(0, 0);
      return;
    }

    const progress = currentTrail.progress || 0;
    const duration = currentTrail.duration || 0;
    const isCompleted = progress >= duration && duration > 0;

    if (isCompleted) {
      const nextTrail = getNextTrailForJourney(currentTrail);
      if (nextTrail) {
        openTrail(nextTrail.id);
      } else {
        setActiveTab("library");
        setScreen("main");
        window.scrollTo(0, 0);
      }
      return;
    }

    const nextDay = Math.min(progress + 1, Math.max(1, duration));
    openDay(nextDay);
  };

  const recommendedTrail = getRecommendedTrail();
  const recommendedEbook = getRecommendedEbook(recommendedTrail);
  const recommendationReason = getRecommendationReason(recommendedTrail);
  // "currentTrail" sempre mostrava trails[0] (a primeira trilha do
  // catalogo) -- nao tinha nada a ver com o que a pessoa de fato esta
  // lendo, so' dava a impressao de certo por coincidencia quando a
  // primeira trilha do catalogo era a mesma que ela tinha comecado.
  // Achado pelo Rodrigo ("pq aqui n mostra o q eu estou lendo?",
  // 17/10/2026). Agora prioriza: 1) a trilha de uma mesa aberta em que ela
  // esteja (mesmo dado de myActiveMesas, ver "Sua mesa agora" mais abaixo
  // -- assim o hero e o card concordam); 2) senao, uma trilha propria com
  // progresso (comecada, ainda nao concluida); 3) so' cai pra trails[0]
  // (sugestao padrao) quando ela realmente nao tem nenhuma leitura em
  // andamento ainda.
  const homeActiveMesaTrail = myActiveMesas
    .map((group) => trails.find((trail) => trail.id === group.trail?.slug || trail.slug === group.trail?.slug))
    .find(Boolean);
  const homeInProgressTrail = trails.find((trail) => (trail.progress || 0) > 0 && (trail.progress || 0) < (trail.duration || 0));
  const homeModel = {
    currentTrail: homeActiveMesaTrail || homeInProgressTrail || trails[0] || null,
    recommendedTrail,
    recommendedEbook,
    recommendationReason,
    hasTrails: trails.length > 0
  };
  // Pedido do Rodrigo (revisao UX /ux-ui): o hero da Home nao distinguia
  // quem ja esta numa caminhada (mesa aberta ou convite pendente) de quem
  // nunca comecou -- mostrava sempre o mesmo discurso de "escolha um
  // ritmo", mesmo pra quem ja escolheu. hasActiveJourney reusa os mesmos
  // dados que ja alimentam os cards "Sua mesa agora" / "Convite que voce
  // enviou" logo abaixo, so' pra trocar o texto do hero.
  const hasActiveJourney = hasCompletedProfile && (myActiveMesas.length > 0 || pendingSentInvites.length > 0);
  // Enquanto isso ainda carrega, hasActiveJourney pode estar errado (os
  // arrays por tras dele comecam vazios) -- o hero da Home usa isso pra
  // nao mostrar nenhum dos dois textos antes da hora.
  const heroJourneyLoading = hasCompletedProfile && (openGroupsLoading || myInvitesLoading);
  const getTrailMoment = (trail) => (
    Object.entries(TRAIL_MOMENT_BY_ID).find(([, ids]) => ids.includes(trail.id))?.[0] || "comecar"
  );
  const isTrailDone = (trail) => (trail.progress || 0) >= (trail.duration || 0) && (trail.duration || 0) > 0;
  // Pedido do Rodrigo (10/2026): entrar numa mesa (aberta ou por convite
  // aceito) ja deve contar como "comecei a trilha" em Meus Planos, mesmo
  // sem ter marcado nenhum dia como lido ainda -- antes so' progress > 0
  // contava, entao quem so' entrou na mesa nao achava a trilha pra dar
  // sequencia na leitura. myActiveMesas ja e' a lista de mesas (abertas)
  // onde o usuario e' membro ACTIVE/INVITED (mesmo dado usado no card
  // "Sua mesa agora" da Home).
  const mesaStartedTrailSlugs = new Set(
    myActiveMesas
      .filter((group) => group.status === "ACTIVE")
      .map((group) => group.trail?.slug || group.trail?.id)
      .filter(Boolean)
  );
  const isTrailInProgress = (trail) => {
    if (isTrailDone(trail)) return false;
    if ((trail.progress || 0) > 0) return true;
    if (trail.started) return true;
    return mesaStartedTrailSlugs.has(trail.slug || trail.id);
  };
  // Leitura solo em andamento -- progresso pessoal > 0 OU so' ja aberta
  // (trail.started, ver POST /trails/progress/start) -- que NAO e' por
  // conta de uma mesa ativa. Usado no card "Sua leitura em andamento" da
  // Home. Antes a Home so' mostrava "Sua mesa agora" (progresso de mesa);
  // quem comecava uma trilha sozinho(a) no mesmo dia nao via nada ali pra
  // continuar, so' voltando na aba "Planos" (relatado pela Carla,
  // 20/10/2026: "comecei um hoje e n aparece ai"). Depois, a propria
  // Carla confirmou que abrir o Dia 1 (mesmo sem concluir) ja deveria
  // contar como "iniciado", igual ja' funcionava pra mesa -- sem botao
  // separado de "Iniciar plano".
  const soloInProgressTrails = trails.filter((trail) =>
    !isTrailDone(trail) && ((trail.progress || 0) > 0 || trail.started) && !mesaStartedTrailSlugs.has(trail.slug || trail.id)
  );
  // Remove acentos e caixa pra comparar "Fe que pensa" com "fé que pensa"
  // na busca, sem depender de digitar acento certo.
  const normalizeSearchText = (value) => (value || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
  const trailSearchNormalized = normalizeSearchText(trailSearchQuery.trim());
  const visibleTrails = (trailMomentFilter === "todos"
    ? trails
    : trails.filter((trail) => getTrailMoment(trail) === trailMomentFilter)
  ).filter((trail) => (
    !trailSearchNormalized
    || normalizeSearchText(trail.title).includes(trailSearchNormalized)
    || normalizeSearchText(trail.subtitle).includes(trailSearchNormalized)
  ));
  const newlyCuratedTrails = NEWLY_CURATED_TRAIL_IDS.map((id) => trails.find((trail) => trail.id === id)).filter(Boolean).slice(0, NEWLY_CURATED_TRAIL_LIMIT);
  const activeTrailMoment = TRAIL_MOMENT_OPTIONS.find((item) => item.id === trailMomentFilter) || TRAIL_MOMENT_OPTIONS[0];
  const filteredCompletedCount = visibleTrails.filter(isTrailDone).length;
  // "Meus planos": trilhas que o usuario ja comecou (em andamento) ou ja
  // terminou -- separado do grid tematico "Explorar", que volta a ser so'
  // por assunto (pedido do Rodrigo: nao misturar filtro de tema com status
  // pessoal de progresso na mesma fileira de chips).
  const inProgressTrails = trails.filter(isTrailInProgress);
  const completedTrails = trails.filter(isTrailDone);
  const hasAnyTrailActivity = inProgressTrails.length > 0 || completedTrails.length > 0;
  const trailById = (id) => trails.find((trail) => trail.id === id || trail.slug === id);
  const trailCompanionModes = [
    { id: "solo", label: "Sozinho", title: "Um ritmo honesto para você", text: "Comece sem depender da agenda de ninguém. Seu progresso fica guardado para você voltar amanhã sem recomeçar do zero.", action: "Começar sozinho", accentColor: "#e6b15a", icon: <MosaicSeedIcon size={19} color="currentColor" /> },
    { id: "duo", label: "Mesa por convite", title: "Uma mesa para quem você chamar", text: "Escolha uma trilha e envie um convite. Só entra quem recebeu o link, para caminhar em dupla, família ou pequeno grupo.", action: "Criar convite", accentColor: "#64c7d0", icon: <MosaicBridgeIcon size={20} color="currentColor" /> },
    { id: "group", label: "Mesa aberta", title: "Uma mesa para quem quiser chegar", text: "Abra uma trilha disponível no app ou entre em uma mesa já aberta. Bom para quem quer caminhar junto, mas ainda não sabe com quem.", action: "Ver mesas abertas", accentColor: "#93d47b", icon: <MosaicOrbitIcon size={20} color="currentColor" /> }
  ];
  const activeCompanionMode = trailCompanionModes.find((item) => item.id === trailCompanionMode) || trailCompanionModes[0];
  const renderTrailCard = (trail) => {
    const trailProgress = Math.round(((trail.progress || 0) / Math.max(1, trail.duration || 1)) * 100);
    const trailMomentMeta = TRAIL_MOMENT_OPTIONS.find((item) => item.id === getTrailMoment(trail)) || TRAIL_MOMENT_OPTIONS[0];
    const trailDone = isTrailDone(trail);
    const startedViaMesa = !trailDone && !(trail.progress > 0) && mesaStartedTrailSlugs.has(trail.slug || trail.id);
    const trailActionLabel = trailDone ? "Concluída · refazer" : trail.progress > 0 ? "Retomar caminho" : startedViaMesa ? "Começar o Dia 1" : activeCompanionMode.action;
    const trailStatusLabel = trailDone ? "Concluída" : trail.progress > 0 ? `${trailMomentMeta.label} · continuar` : startedViaMesa ? "Na sua mesa · começar" : trailMomentMeta.label;
    return (
      <button key={trail.id} onClick={() => startTrailWithCompanionMode(trail, getTrailMoment(trail))} style={{ ...buttonReset, ...overflowGuardStyle, position: "relative", minHeight: 202, padding: 16, textAlign: "left", border: trailDone ? "1px solid rgba(134,239,172,0.42)" : trail.progress > 0 ? `1px solid ${trail.color}66` : "1px solid rgba(255,255,255,0.10)", background: `linear-gradient(145deg, ${trail.color}24, rgba(255,255,255,0.045))`, color: "#f8fbff", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: "0 16px 30px rgba(0,0,0,0.22)" }}>
        {trailDone && (
          <span style={{ position: "absolute", inset: 0, background: "rgba(4,8,15,0.40)", pointerEvents: "none" }} />
        )}
        <div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
            <span style={{ width: 38, height: 38, borderRadius: 14, background: `${trail.color}22`, color: trail.color, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${trail.color}66`, flexShrink: 0 }}>
              {ICON_MAP[trail.icon] || <Layers size={20} strokeWidth={1.5} />}
            </span>
            {trailDone ? (
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "6px 9px", borderRadius: 999, background: "rgba(134,239,172,0.16)", border: "1px solid rgba(134,239,172,0.5)", color: "#86EFAC", fontSize: 9, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.7, whiteSpace: "nowrap" }}>
                <CheckCircle2 size={12} strokeWidth={2.5} /> Concluída
              </span>
            ) : (
              <span style={{ padding: "6px 8px", borderRadius: 999, background: "rgba(255,255,255,0.07)", border: "1px solid rgba(255,255,255,0.08)", color: "#aebbd1", fontSize: 9, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.7, whiteSpace: "nowrap" }}>{trail.duration} dias</span>
            )}
          </div>
          <p style={{ margin: "14px 0 6px", fontSize: 10, color: trail.color, textTransform: "uppercase", letterSpacing: 1.1, fontWeight: 900 }}>{trailStatusLabel}</p>
          <h3 style={{ margin: 0, fontSize: 17, lineHeight: 1.12, color: "#ffffff", fontWeight: 900 }}>{trail.title}</h3>
          <p style={{ margin: "7px 0 0", fontSize: 12, lineHeight: 1.4, color: "#c7d1e4" }}>{trail.subtitle}</p>
        </div>
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 7 }}>
            <span style={{ fontSize: 11, color: "#9fb0cc", fontWeight: 800 }}>{trail.progress || 0} de {trail.duration} dias</span>
            <span style={{ fontSize: 11, color: "#9fb0cc", fontWeight: 800 }}>{trailProgress}%</span>
          </div>
          <div style={{ height: 7, background: "rgba(255,255,255,0.10)", padding: 2 }}>
            <div style={{ width: `${trailProgress}%`, height: "100%", background: trail.color }} />
          </div>
          <p style={{ margin: "10px 0 0", fontSize: 12, color: "#f8fbff", fontWeight: 900 }}>{trailActionLabel}</p>
        </div>
      </button>
    );
  };
  const guidedJourneyCards = [
    {
      id: "cheguei",
      moment: "comecar",
      trailId: "plataforma",
      icon: <Home size={22} strokeWidth={1.8} />,
      title: "Cheguei agora",
      text: "Para quem quer começar sem se sentir perdido. Uma trilha de fundamento, linguagem simples e direção clara.",
      signal: "Primeiro passo"
    },
    {
      id: "alma",
      moment: "interior",
      trailId: "emocoes-formadas-por-deus",
      icon: <Heart size={22} strokeWidth={1.8} />,
      title: "Preciso cuidar da alma",
      text: "Para quando a fé não sumiu, mas a vida por dentro está pesada. O caminho é tratar desejos, afetos e maturidade diante de Deus.",
      signal: "Vida interior"
    },
    {
      id: "mesa",
      moment: "comunidade",
      trailId: "comunhao-no-caminho",
      icon: <Users size={22} strokeWidth={1.8} />,
      title: "Quero caminhar com alguém",
      text: "Para transformar devocional em conversa, presença e cuidado mútuo. Bom para duplas, famílias e pequenos grupos.",
      signal: "Comunhão"
    },
    {
      id: "semana",
      moment: "missao",
      trailId: "vida-inteira-em-missao",
      icon: <Globe size={22} strokeWidth={1.8} />,
      title: "Fé para a vida real",
      text: "Para ligar oração, trabalho, decisões, vizinhança e testemunho. Uma fé que não fica presa ao domingo.",
      signal: "Missão cotidiana"
    }
  ].map((item) => ({ ...item, trail: trailById(item.trailId) })).filter((item) => item.trail);
  // Antes caia pro guidedJourneyCards[0] mesmo sem a pessoa ter escolhido
  // nada -- fazia o modal "Como esta sua caminhada hoje?" abrir ja com um
  // card marcado como ativo e o bloco "Devocional sugerido" visivel de
  // cara, empurrando o conteudo pra fora da altura do modal e forcando
  // scroll logo na abertura (reportado ao vivo pelo Rodrigo). Agora so'
  // existe selecao depois que a pessoa realmente clica em um card.
  const selectedMomentGuideChoice = guidedJourneyCards.find((item) => item.id === momentGuideChoiceId) || null;
  const getTrailNextDay = (trail) => Math.min(
    Math.max(1, (trail?.progress || 0) + 1),
    Math.max(1, trail?.duration || 1)
  );
  const openGuidedMoment = (card, mode = "day") => {
    if (!card?.trail) return;
    setShowMomentGuide(false);
    setMomentGuideChoiceId(card.id);
    if (card.moment) setTrailMomentFilter(card.moment);
    if (card.id === "mesa") setTrailCompanionMode("duo");
    if (mode === "trail") {
      openTrail(card.trail.id);
      return;
    }
    openDay(getTrailNextDay(card.trail), card.trail);
  };
  const openInviteStart = (trail = null) => {
    if (!hasCompletedProfile) {
      requestProfileAccess("invite");
      return;
    }
    const inviteTrail = trail || trailById(mesaInviteTrailId) || trailById("comunhao-no-caminho") || recommendedTrail || trails[0] || null;
    if (inviteTrail) setMesaInviteTrailId(inviteTrail.id);
    trackEvent("devotional_invite", { channel: "mesa", trailId: inviteTrail?.slug || inviteTrail?.id || null });
    setMesaIntent("duo");
    setTrailCompanionMode("duo");
    setMesaInviteFeedback("");
    setShowDirectInviteModal(true);
  };
  const openProfileSignup = () => {
    setShowProfilePrompt(false);
    setActiveTab("profile");
    setScreen("main");
    window.scrollTo(0, 0);
  };
  const openPastoralCare = () => {
    if (!hasCompletedProfile) {
      requestProfileAccess("pastoral");
      return;
    }
    setBookingFeedback("");
    if (viewerUser?.name) setBookingName(viewerUser.name);
    setShowPastoralAgenda(true);
  };
  const profilePromptCopy = {
    general: {
      eyebrow: "Primeiro acesso",
      title: "Crie seu acesso para personalizar a caminhada.",
      body: "Com seu cadastro, o app consegue guardar seu progresso, liberar e-books, sugerir devocionais com mais sentido e organizar convites para caminhar com outras pessoas."
    },
    ebook: {
      eyebrow: "Leitura no app",
      title: "Crie seu acesso gratuito para ler este e-book.",
      body: "Você pode conhecer todo o acervo antes. Para abrir a leitura, salvar página, marcar trechos e anotar reflexões, precisamos criar seu acesso."
    },
    devotional: {
      eyebrow: "Devocional",
      title: "Crie seu acesso gratuito para começar este devocional.",
      body: "Assim o app guarda seu progresso, suas respostas, suas marcações e consegue sugerir o próximo passo sem você se perder na caminhada."
    },
    invite: {
      eyebrow: "Convite devocional",
      title: "Crie seu acesso gratuito para convidar alguém.",
      body: "O convite precisa sair em seu nome para que a pessoa saiba com quem vai caminhar e para que o app acompanhe a trilha de vocês dois."
    },
    pastoral: {
      eyebrow: "Cuidado pastoral",
      title: "Crie seu acesso gratuito para solicitar uma conversa.",
      body: "Para proteger seu contato e organizar a agenda pastoral, precisamos saber quem está pedindo o horário antes de mostrar a solicitação."
    },
    novo_membro: {
      eyebrow: "Cadastro de membresia",
      title: "Falta só um passo: crie seu acesso ao site da igreja.",
      body: "Recebemos seu cadastro de membresia -- a secretaria confirma em breve. Enquanto isso, crie sua senha aqui pra já acompanhar avisos, devocionais, e-books e os caminhos da igreja pelo site."
    }
  };
  const activeProfilePrompt = profilePromptCopy[profilePromptContext] || profilePromptCopy.general;

  const profilePromptModal = showProfilePrompt && (
    <div style={{ position: "fixed", inset: 0, zIndex: 238, background: "rgba(2,6,23,0.76)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 18 }}>
      <div style={{ ...widthGuardStyle, width: "100%", maxWidth: 520, maxHeight: "calc(100dvh - 32px)", overflowY: "auto", overflowX: "hidden", border: "1px solid rgba(230,177,90,0.46)", background: "linear-gradient(145deg, rgba(19,29,48,0.98), rgba(15,23,39,0.98) 52%, rgba(44,34,24,0.86))", padding: isMobile ? 18 : 24, color: "#f8fbff", boxShadow: "0 24px 60px rgba(0,0,0,0.42)" }}>
        <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 18 }}>
          <div style={{ minWidth: 0 }}>
            <p style={{ margin: "0 0 8px", color: "#F0DCB8", fontSize: 10, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 950 }}>{activeProfilePrompt.eyebrow}</p>
            <h3 style={{ margin: 0, fontSize: isMobile ? 24 : 30, lineHeight: 1.04, color: "#FFFFFF", fontWeight: 950 }}>{activeProfilePrompt.title}</h3>
          </div>
          <button onClick={() => setShowProfilePrompt(false)} aria-label="Fechar cadastro" style={{ ...buttonReset, flexShrink: 0, minWidth: 42, minHeight: 42, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "#DDE6F5", fontSize: 18, fontWeight: 900 }}>
            ×
          </button>
        </div>
        <p style={{ margin: "0 0 16px", color: "#C7D1E4", fontSize: isMobile ? 13 : 14, lineHeight: 1.6 }}>
          {activeProfilePrompt.body}
        </p>
        <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(3, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 9, marginBottom: 18 }}>
          {[
            ["Progresso salvo", "Volte amanhã sem recomeçar do zero."],
            ["Sugestões melhores", "O app entende por onde você pode começar."],
            ["Caminhada junto", "Convites, mesas e cuidado pastoral ficam organizados."]
          ].map(([title, text]) => (
            <div key={title} style={{ padding: 12, border: "1px solid rgba(230,177,90,0.22)", background: "rgba(255,255,255,0.045)" }}>
              <p style={{ margin: "0 0 6px", color: "#FFF8EB", fontSize: 12, fontWeight: 950 }}>{title}</p>
              <p style={{ margin: 0, color: "#9FB0CC", fontSize: 11, lineHeight: 1.45 }}>{text}</p>
            </div>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 9 }}>
          <button onClick={() => setShowProfilePrompt(false)} style={{ ...buttonReset, minHeight: 46, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "#DDE6F5", fontSize: 12, fontWeight: 950 }}>
            Agora não
          </button>
          <button onClick={openProfileSignup} style={{ ...buttonReset, minHeight: 46, border: "1px solid rgba(230,177,90,0.72)", background: "#E6B15A", color: "#111827", fontSize: 12, fontWeight: 950 }}>
            Criar meu acesso
          </button>
        </div>
      </div>
    </div>
  );

  const mesaIntentOptions = [
    {
      id: "duo",
      label: "Caminhar com alguém",
      title: "Chame uma pessoa para uma trilha",
      text: "Bom para quem quer começar pequeno: dois nomes, uma trilha, alguns dias e uma conversa honesta no caminho.",
      action: "Convidar para uma trilha",
      icon: <Users size={22} strokeWidth={1.7} />,
      color: "#E6B15A",
      trailId: "comunhao-no-caminho"
    },
    {
      id: "group",
      label: "Entrar numa mesa",
      title: "Participe de uma caminhada em grupo",
      text: "Para quem precisa de ritmo com outras pessoas, sem expor endereço, sem pressão e com espaço para quem está longe.",
      action: "Ver mesas abertas",
      icon: <Coffee size={22} strokeWidth={1.7} />,
      color: "#2ac37a",
      trailId: "igreja-mesa"
    },
    {
      id: "care",
      label: "Falar com um pastor",
      title: "Marque uma conversa pastoral",
      text: "Quando a caminhada pede escuta, oração e direção pessoal, escolha um amigo e veja os horários que ele disponibilizou para um café.",
      action: "Agendar conversa",
      icon: <MessageSquare size={22} strokeWidth={1.7} />,
      color: "#d946ef",
      trailId: "vida-interior"
    }
  ].map((item) => ({ ...item, trail: trailById(item.trailId) })).filter((item) => item.trail || item.id === "care");
  const activeMesaIntent = mesaIntentOptions.find((item) => item.id === mesaIntent) || mesaIntentOptions[0];
  const mesaInviteTrail = trailById(mesaInviteTrailId) || activeMesaIntent.trail || trails[0] || null;
  const openMesaTrail = trailById(openMesaForm.trailId) || trailById("igreja-mesa") || trails[0] || null;
  const runMesaIntentAction = (item = activeMesaIntent) => {
    if (!item) return;
    setMesaIntent(item.id);
    if (item.id === "duo") {
      openInviteStart();
      return;
    }
    if (item.id === "care") {
      openPastoralCare();
      return;
    }
    if (item.id === "group") {
      setOpenGroupsFeedback("");
      loadOpenGroups();
    }
  };
  const startTrailWithCompanionMode = (trail, moment = "") => {
    if (!trail) return;
    if (moment) setTrailMomentFilter(moment);

    if (trailCompanionMode === "duo") {
      setMesaInviteTrailId(trail.id);
      setMesaIntent("duo");
      setMesaInviteFeedback("");
      openInviteStart(trail);
      return;
    }

    if (trailCompanionMode === "group") {
      setOpenMesaForm((current) => ({
        ...current,
        trailId: trail.id,
        name: current.name || `Mesa aberta: ${trail.title}`
      }));
      setMesaIntent("group");
      setActiveTab("family");
      loadOpenGroups();
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setCoopActive(false);
    setSelectedTrailGroup(null);
    openTrail(trail.id);
  };
  const buildInviteUrl = (invite) => {
    if (!invite?.token || typeof window === "undefined") return "";
    const url = new URL(window.location.origin);
    url.searchParams.set("convite", invite.token);
    return url.toString();
  };
  const buildMesaInviteText = (invite) => {
    const inviteUrl = buildInviteUrl(invite) || (typeof window !== "undefined" ? window.location.origin : "");
    return mesaInviteTrail
      ? `Oi${mesaInviteName.trim() ? `, ${mesaInviteName.trim()}` : ""}! Quero te convidar para fazer comigo a trilha "${mesaInviteTrail.title}" no app Mosaico. A ideia é caminhar alguns dias com calma, lendo, refletindo e conversando sobre o que Deus for formando em nós.${mesaInviteMessage.trim() ? `\n\n${mesaInviteMessage.trim()}` : ""}\n\nEntre por aqui: ${inviteUrl}`
      : "";
  };
  const invitePreviewName = mesaInviteName.trim() || "Pessoa convidada";
  const invitePreviewContact = mesaInviteEmail.trim() || mesaInviteWhatsapp.trim() || "Aguardando contato";
  const profileRequiredFields = ["name", "email", "whatsapp", "state", "city"];
  const profileCompletedFields = profileRequiredFields.filter((field) => String(profileForm[field] || "").trim()).length + (profileForm.privacyAccepted ? 1 : 0);
  const profileCompletion = Math.round((profileCompletedFields / (profileRequiredFields.length + 1)) * 100);
  // Banner "complete/revise seu cadastro de membro" (ver render do shell
  // principal, logo abaixo do topo) -- aparece se a conta nunca teve Member
  // vinculado, ou se o Member existe mas o cadastro tem mais de um ano.
  const MEMBER_STALE_DAYS = 365;
  const memberCreatedDaysAgo = myMember?.createdAt
    ? Math.floor((Date.now() - new Date(myMember.createdAt).getTime()) / (1000 * 60 * 60 * 24))
    : null;
  const showSelfCadastroBanner = Boolean(
    viewerUser?.sessionToken &&
    !viewerUser?.passwordMustChange &&
    !selfCadastroBannerDismissed &&
    !myMemberLoading &&
    (!myMember || (memberCreatedDaysAgo !== null && memberCreatedDaysAgo > MEMBER_STALE_DAYS))
  );
  const profileConnection = PROFILE_CONNECTION_OPTIONS.find((item) => item.id === profileForm.connectionType) || PROFILE_CONNECTION_OPTIONS[0];
  // realIsAdminUser = role de verdade da sessao (nunca simulada) -- usado
  // so nos pontos abaixo que dependem do estado real da conta, nunca pra
  // decidir o que aparece na tela.
  const realIsAdminUser = Boolean(viewerUser?.roles?.includes("ADMIN"));
  // isAdminUser (e tudo que deriva dele) respeita o modo "ver como" de
  // AdminHome: um ADMIN simulando SECRETARIA/ACOLHIMENTO passa a ver
  // exatamente o que esse papel veria aqui dentro (aba Agenda de cuidado,
  // /financeiro).
  const effectiveRoles = getEffectiveRoles(viewerUser);
  const isAdminUser = effectiveRoles.includes("ADMIN");
  // hasMemberRecord = a conta tem cadastro de membresia (Member) vinculado
  // E esse cadastro esta ATIVO (viewerUser.linkedMember.status,
  // selecionado em publicUserSelect/auth.js) -- ter so uma conta de app
  // logada nao basta (pedido: "so pra quem estiver no cadastro de
  // membro"), e um Member INATIVO/TRANSFERIDO/FALECIDO/PENDENTE vinculado
  // tambem nao conta mais (pedido do Rodrigo, 24/10/2026: financeiro so
  // pra membro ATIVO). Quando um ADMIN de verdade esta simulando "Membro
  // (sem papel especial)", assume que o cadastro ativo existe -- e assim
  // que um membro ATIVO de verdade enxergaria a tela, independente do
  // proprio cadastro da conta que esta testando.
  const hasMemberRecord = getSimulatedHasMemberRecord(
    realIsAdminUser,
    viewerUser?.linkedMember?.status === "ATIVO"
  );
  // /financeiro aparece pra ADMIN/FINANCEIRO (sempre) ou pra quem tem
  // cadastro de membresia vinculado -- mesmo gate aplicado no menu do site
  // institucional (InstitutionalLayout.jsx) e na propria pagina estatica.
  const canSeeFinanceiro = Boolean(isAdminUser || effectiveRoles.includes("FINANCEIRO") || hasMemberRecord);
  // Repertorio (catalogo de musicas da equipe de louvor, com link de
  // referencia pra estudar antes do ensaio) -- pedido do Rodrigo, 10/2026.
  // So ADMIN ou quem toca (MUSICO) ve o link -- pedido do Rodrigo, 10/2026.
  const canSeeRepertorio = Boolean(isAdminUser || effectiveRoles.includes("MUSICO"));
  // Escala (agenda de quem toca/serve no culto) -- mesmo gate usado no
  // menu institucional (InstitutionalLayout.jsx, canSeeEscala): ADMIN,
  // MUSICO, ou Person vinculada com isMusician=true (corrige o caso do
  // Gabriel, 20/10/2026, que tinha o papel certo na Person mas nao em
  // User.roles). canSeeRepertorio acima fica mais estrito de proposito --
  // esse gate aqui e' o que o menu de baixo do celular usa agora (pedido
  // do Rodrigo, 21/10/2026: "o menu abaixo ta repertorio, n era para ser
  // escala").
  // Generalizado em 28/10/2026: alem de Musico, qualquer outra flag de
  // ministerio na Person (ESCALA_TIPO_FLAG em lib/person.js, mosaico-api --
  // mesmo mapa, mirrorado aqui so' com os nomes das flags) tambem abre o
  // link -- "quem e' marcado como teens ve a escala de teens, de
  // zeladoria ve a escala de zeladoria e assim por diante". Desde
  // 29/10/2026 todas as flags tem simulador de "Ver como"
  // (getSimulatedMinistryFlag pras 9 outras, getSimulatedIsMusician pra
  // Musico -- ver roleSimulator.js).
  // tipo (Escala.tipo, mosaico-api) <-> flag da Person -- mesmo mapa de
  // ESCALA_TIPO_FLAG em lib/person.js (mosaico-api), mirrorado aqui so'
  // com os ministerios ALEM de Musicos (que continua tratado a parte, com
  // o atalho de role MUSICO e o simulador getSimulatedIsMusician, nenhum
  // dos dois existindo ainda pros outros ministerios).
  const OUTROS_TIPOS_DE_ESCALA = [
    { tipo: "ZELADORIA", flag: "isZelador" },
    { tipo: "KIDS", flag: "isKidsVoluntario" },
    { tipo: "TEENS", flag: "isTeensVoluntario" },
    { tipo: "MIDIA", flag: "isMidiaVoluntario" },
    { tipo: "CAFE", flag: "isCafeVoluntario" },
    { tipo: "RECEPCAO", flag: "isRecepcaoVoluntario" },
    { tipo: "PODCAST", flag: "isPodcastVoluntario" },
    { tipo: "PREGACAO", flag: "isPregacaoVoluntario" },
    { tipo: "TEENS_DEVOCIONAL", flag: "isTeensDevocionalVoluntario" }
  ];
  const canSeeEscala = Boolean(
    isAdminUser
    || effectiveRoles.includes("MUSICO")
    || getSimulatedIsMusician(realIsAdminUser, viewerUser?.person?.isMusician)
    || OUTROS_TIPOS_DE_ESCALA.some(({ flag }) => getSimulatedMinistryFlag(realIsAdminUser, flag, viewerUser?.person?.[flag]))
  );

  // Busca a proxima escala da propria pessoa pro teaser do Perfil (ver
  // nextEscalaSlot acima) -- so quando a aba Perfil esta aberta (nao vale
  // gastar uma chamada em toda carga do app por um dado que so aparece
  // ali) e so pra quem tem Person vinculada (sem isso nao ha o que
  // comparar com musicianPerson.id). GET /escalas ja volta so as datas de
  // hoje em diante, ordenadas por data (ver mosaico-api/src/routes/
  // escalas.js) -- a primeira cujo slot bate com a propria Person e' a
  // proxima.
  useEffect(() => {
    if (activeTab !== "profile" || !canSeeEscala || !viewerUser?.person?.id) return;
    let alive = true;
    setNextEscalaLoading(true);
    // Antes so' consultava /escalas sem `?tipo=` (cai no default MUSICOS do
    // backend) -- so' funcionava pro musico mesmo. Generalizado em
    // 28/10/2026: consulta cada tipo que a propria Person tiver marcado
    // (alem de Musicos, se for o caso) e pega a vaga mais proxima entre
    // todos -- do contrario alguem so' de Zeladoria/Kids/etc. tomava 403
    // silencioso (sem acesso a Musicos) e nunca via o teaser da propria
    // escala no Perfil.
    const temAcessoMusicos = Boolean(
      isAdminUser || effectiveRoles.includes("MUSICO") || getSimulatedIsMusician(realIsAdminUser, viewerUser?.person?.isMusician)
    );
    const tiposProprios = [
      ...(temAcessoMusicos ? ["MUSICOS"] : []),
      ...OUTROS_TIPOS_DE_ESCALA.filter(({ flag }) => getSimulatedMinistryFlag(realIsAdminUser, flag, viewerUser?.person?.[flag])).map(({ tipo }) => tipo)
    ];
    Promise.all(
      tiposProprios.map((tipo) =>
        apiFetch(`/escalas?tipo=${encodeURIComponent(tipo)}`, { method: "GET" }).catch(() => [])
      )
    )
      .then((porTipo) => {
        if (!alive) return;
        const list = porTipo.flat().filter(Boolean);
        const found = list
          .slice()
          .sort((a, b) => String(a.date).localeCompare(String(b.date)))
          .map((escala) => {
            const slot = (escala.slots || []).find((s) => s.musicianPerson?.id === viewerUser.person.id);
            return slot ? { date: escala.date, label: escala.label, funcao: slot.funcao } : null;
          })
          .find(Boolean);
        setNextEscalaSlot(found || false);
      })
      .catch(() => {
        if (alive) setNextEscalaSlot(false);
      })
      .finally(() => {
        if (alive) setNextEscalaLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [activeTab, canSeeEscala, viewerUser?.person?.id]);
  // ACOLHIMENTO (ex-PASTOR, mesmo sem ADMIN) tambem enxerga a Agenda de
  // cuidado -- pedido original: "a agenda de cuidado precisaria ser
  // liberado a quem e so pastor". Um ADMIN simulando "Ver como:
  // Acolhimento" cai aqui do mesmo jeito, ja que effectiveRoles ja
  // reflete o papel simulado.
  const isCareManager = (isAdminUser || effectiveRoles.includes("ACOLHIMENTO")) && !viewerUser?.passwordMustChange;
  const adminPastor = pastors.find((pastor) => pastor.email && viewerUser?.email && pastor.email.toLowerCase() === viewerUser.email.toLowerCase());

  const updateProfileForm = (field, value) => {
    setProfileForm((prev) => ({ ...prev, [field]: value }));
    if (profileFeedback) setProfileFeedback("");
  };

  const updateLoginForm = (field, value) => {
    setLoginForm((prev) => ({ ...prev, [field]: value }));
    if (loginFeedback) setLoginFeedback("");
  };

  const passwordStrengthError = (password) => {
    if (password.length < 8) return "A senha precisa ter pelo menos 8 caracteres.";
    if (!/[A-Z]/.test(password)) return "A senha precisa ter pelo menos uma letra maiúscula.";
    if (!/[^A-Za-z0-9]/.test(password)) return "A senha precisa ter pelo menos um caractere especial.";
    return "";
  };

  const updateAdminSlotForm = (field, value) => {
    setAdminSlotForm((prev) => ({ ...prev, [field]: value }));
    if (adminSlotFeedback) setAdminSlotFeedback("");
  };

  const saveProfileForm = async () => {
    const missingFields = profileRequiredFields.filter((field) => !String(profileForm[field] || "").trim());
    if (missingFields.length > 0) {
      setProfileFeedback("Preencha nome, e-mail, WhatsApp, estado e cidade para liberar a experiência completa.");
      return;
    }
    if (!profileForm.privacyAccepted) {
      setProfileFeedback("Confirme o uso dos seus dados para liberar a experiência completa com segurança.");
      return;
    }
    const shouldCreateAccount = !hasCompletedProfile && !realIsAdminUser;
    if (shouldCreateAccount) {
      const passwordError = passwordStrengthError(loginForm.newPassword);
      if (passwordError) {
        setProfileFeedback(passwordError);
        return;
      }
      if (loginForm.newPassword !== loginForm.confirmPassword) {
        setProfileFeedback("A confirmação precisa ser igual à senha.");
        return;
      }
    }

    setProfileSaving(true);
    setProfileFeedback("");

    try {
      const payload = {
        name: profileForm.name.trim(),
        birthDate: profileForm.birthDate || null,
        email: profileForm.email.trim(),
        whatsapp: profileForm.whatsapp.trim(),
        state: profileForm.state.trim(),
        city: profileForm.city.trim(),
        connectionType: profileForm.connectionType,
        discoveryChannel: profileForm.discoveryChannel,
        invitedBy: profileForm.invitedBy.trim() || null,
        avatarId: selectedAvatarId,
        privacyAccepted: true,
        privacyVersion: "2026-05"
      };

      if (shouldCreateAccount) {
        const created = await apiPost("/auth/register", {
          viewer: viewerUser,
          body: {
            ...payload,
            password: loginForm.newPassword,
            ...(viewerUser?.id ? { viewerUserId: viewerUser.id } : {})
          }
        });
        const user = normalizeUserFromApi(created);
        persistViewerUser(user);
        authConfirmedRef.current = true;
        setLoginForm((prev) => ({ ...prev, password: "", newPassword: "", confirmPassword: "" }));
        // Se e-mail + nascimento + WhatsApp bateram com um cadastro de
        // membresia existente, o backend ja vinculou sozinho (ver
        // tryAutoLinkMember em mosaico-api/src/routes/auth.js) -- avisa
        // aqui em vez de deixar a pessoa descobrir sozinha depois. Pedido
        // do Drigo, 28/10/2026.
        setProfileFeedback(
          user.linkedMember
            ? "Conta criada e já vinculamos ao seu cadastro de membresia, pelos seus dados. Seu acesso aos devocionais, e-books e caminhos em grupo está organizado."
            : "Conta criada. Seu acesso aos devocionais, e-books e caminhos em grupo está organizado."
        );
        // Quem chegou aqui pelo /entrar (aba "profile" logo de cara, ver
        // getInitialActiveTabFromUrl) acabou de criar a conta -- depois de
        // ler a confirmacao acima, leva pra Home (tela "main" ja deixa a
        // URL em /familia sozinha, ver efeito de sincronizacao de URL).
        // Pedido do Drigo, 28/10/2026: "/familia" e' a home logada.
        if (activeTab === "profile") {
          setTimeout(() => setActiveTab("home"), 1400);
        }
        return;
      }

      const sessionUser = await ensureSession();
      payload.viewerUserId = sessionUser.id;

      const savedUser = await apiPatch(`/users/${encodeURIComponent(sessionUser.id)}/profile`, {
        viewer: sessionUser,
        body: payload
      });
      const nextUser = {
        ...(viewerUser || {}),
        ...savedUser,
        birthDate: savedUser.birthDate ? String(savedUser.birthDate).slice(0, 10) : "",
        profileCompleted: true,
        onboarding_completed: true,
        onboardingCompleted: true
      };

      setViewerUser(nextUser);
      authConfirmedRef.current = true;
      setBookingName(nextUser.name);
      try {
        localStorage.setItem("mosaico_user", JSON.stringify(nextUser));
      } catch (error) {
        // noop
      }
      setProfileFeedback("Cadastro salvo. Seu acesso aos devocionais, e-books e caminhos em grupo está organizado.");
    } catch (error) {
      setProfileFeedback(error.message || "Não foi possível salvar agora. Confira sua conexão e tente novamente.");
    } finally {
      setProfileSaving(false);
    }
  };

  const getProfileSessionPayload = () => ({
    name: profileForm.name.trim() || bookingName.trim() || "Visitante Família Mosaico",
    email: profileForm.email.trim() || undefined,
    birthDate: profileForm.birthDate || undefined,
    whatsapp: profileForm.whatsapp.trim() || undefined,
    state: profileForm.state.trim() || undefined,
    city: profileForm.city.trim() || undefined,
    connectionType: profileForm.connectionType || undefined,
    discoveryChannel: profileForm.discoveryChannel || undefined,
    invitedBy: profileForm.invitedBy.trim() || undefined,
    avatarId: selectedAvatarId || undefined,
    privacyAccepted: Boolean(profileForm.privacyAccepted),
    privacyVersion: profileForm.privacyAccepted ? "2026-05" : undefined,
    profileCompleted: profileRequiredFields.every((field) => String(profileForm[field] || "").trim()),
    onboardingCompleted: profileRequiredFields.every((field) => String(profileForm[field] || "").trim())
  });

  const persistViewerUser = (user) => {
    setViewerUser(user);
    if (user?.name && (user.profileCompleted || user.profile_completed || user.onboardingCompleted || user.onboarding_completed)) setBookingName(user.name);
    try {
      localStorage.setItem("mosaico_user", JSON.stringify(user));
    } catch (error) {
      // noop
    }
  };

  const clearViewerSession = (feedback = "") => {
    setViewerUser(null);
    setWeeklyCompletionMapApi(null);
    setLoginForm((prev) => ({ ...prev, password: "", currentPassword: "", newPassword: "", confirmPassword: "" }));
    // Limpa o formulario de perfil e a guarda de sincronizacao -- sem isso, o
    // proximo login (de outra pessoa, no mesmo aparelho) ou o formulario
    // anonimo de visitante herdava os dados de quem saiu.
    setProfileForm(PROFILE_FORM_DEFAULT);
    profileFormSyncedUserIdRef.current = null;
    if (feedback) setLoginFeedback(feedback);
    try {
      localStorage.removeItem("mosaico_user");
    } catch (error) {
      // noop
    }
  };

  // O backend não retorna mais `sessionToken` no JSON — a sessão vive em cookie
  // httpOnly inacessível ao JS. Mantemos um marcador `sessionToken: "cookie"`
  // só para preservar as guardas existentes (`viewerUser?.sessionToken`) que
  // significam "está autenticado".
  const normalizeUserFromApi = (user) => ({
    ...user,
    sessionToken: user?.id ? "cookie" : undefined,
    birthDate: user?.birthDate ? String(user.birthDate).slice(0, 10) : "",
    profileCompleted: Boolean(user?.profileCompleted || user?.profile_completed),
    onboardingCompleted: Boolean(user?.onboardingCompleted || user?.onboarding_completed),
    onboarding_completed: Boolean(user?.onboardingCompleted || user?.onboarding_completed),
    passwordMustChange: Boolean(user?.passwordMustChange || user?.password_must_change)
  });

  const loginWithPassword = async () => {
    if (!loginForm.email.trim() || !loginForm.password) {
      setLoginFeedback("Informe e-mail e senha.");
      return;
    }
    setLoginLoading(true);
    setLoginFeedback("");
    try {
      const loginResult = await apiPost("/auth/login", {
        body: { email: loginForm.email.trim(), password: loginForm.password }
      });
      const user = normalizeUserFromApi(loginResult);
      persistViewerUser(user);
      authConfirmedRef.current = true;
      // profileForm é resincronizado pelo useEffect([viewerUser]) abaixo --
      // ele já sabe distinguir "mesma conta" (mantém o que a pessoa estava
      // digitando) de "conta diferente" (não herda o que sobrou da conta
      // anterior), então não duplicamos essa lógica aqui.
      setLoginForm((prev) => ({ ...prev, password: "" }));
      setLoginFeedback(user.passwordMustChange ? "Acesso liberado. Troque sua senha temporária para continuar com segurança." : "Acesso liberado.");
      // Mesma ideia do registro acima: quem entrou pelo /entrar (aba
      // "profile" inicial) vai pra Home depois do login, em vez de ficar
      // parado na tela de login. Se precisar trocar senha, fica na aba
      // pra nao perder o aviso.
      if (activeTab === "profile" && !user.passwordMustChange) {
        setTimeout(() => setActiveTab("home"), 900);
      }
    } catch (error) {
      setLoginFeedback("Não foi possível entrar. Confira e-mail e senha.");
    } finally {
      setLoginLoading(false);
    }
  };

  const logout = async () => {
    // Tenta encerrar a sessão no servidor primeiro (cookie ainda válido aqui)
    // para deletar a Session do DB e limpar o cookie httpOnly via Set-Cookie.
    try {
      await apiPost("/auth/logout", {});
    } catch (error) {
      // segue mesmo que o servidor falhe — limpamos local de qualquer forma.
    }
    clearViewerSession("Você saiu da sua conta.");
  };

  const changePassword = async () => {
    if (!viewerUser?.id || !viewerUser?.sessionToken) {
      setLoginFeedback("Entre novamente para trocar sua senha.");
      return;
    }
    if (loginForm.newPassword.length < 8) {
      setLoginFeedback(passwordStrengthError(loginForm.newPassword));
      return;
    }
    const passwordError = passwordStrengthError(loginForm.newPassword);
    if (passwordError) {
      setLoginFeedback(passwordError);
      return;
    }
    if (loginForm.newPassword !== loginForm.confirmPassword) {
      setLoginFeedback("A confirmação precisa ser igual à nova senha.");
      return;
    }
    setLoginLoading(true);
    setLoginFeedback("");
    try {
      const result = await apiPost("/auth/change-password", {
        body: {
          currentPassword: loginForm.currentPassword,
          newPassword: loginForm.newPassword
        }
      });
      const user = normalizeUserFromApi(result);
      persistViewerUser(user);
      setLoginForm((prev) => ({ ...prev, currentPassword: "", newPassword: "", confirmPassword: "" }));
      setLoginFeedback("Senha alterada com sucesso.");
    } catch (error) {
      setLoginFeedback(error instanceof ApiError && error.message ? error.message : "Não foi possível trocar a senha. Confira a senha atual.");
    } finally {
      setLoginLoading(false);
    }
  };

  const requestPasswordReset = async () => {
    const email = (loginForm.resetEmail || loginForm.email || profileForm.email).trim();
    if (!email || !email.includes("@")) {
      setLoginFeedback("Informe o e-mail da sua conta.");
      return;
    }
    setLoginLoading(true);
    setLoginFeedback("");
    try {
      const result = await apiPost("/auth/request-password-reset", { body: { email } });
      setLoginForm((prev) => ({ ...prev, resetEmail: email }));
      // Mensagem sempre condicional -- nunca afirma "enviamos" de forma direta,
      // porque o backend intencionalmente responde igual tendo conta ou não
      // (evita revelar quais e-mails têm cadastro). Antes dizia sempre "Enviamos
      // um link de recuperação", mesmo quando a conta não existe ou não tem
      // senha ainda (ninguém recebia nada e ficava sem saber o motivo).
      setLoginFeedback(result?.emailDeliveryConfigured === false
        ? "Pedido registrado. O envio automático de e-mail ainda precisa ser conectado no servidor."
        : "Se esse e-mail já tiver uma conta com senha cadastrada, enviamos um link agora — confira sua caixa de entrada em alguns minutos.");
    } catch (error) {
      setLoginFeedback("Não foi possível iniciar a recuperação agora.");
    } finally {
      setLoginLoading(false);
    }
  };

  const resetPasswordWithToken = async () => {
    if (!loginForm.resetToken.trim()) {
      setLoginFeedback("Informe o código de recuperação recebido por e-mail.");
      return;
    }
    const passwordError = passwordStrengthError(loginForm.newPassword);
    if (passwordError) {
      setLoginFeedback(passwordError);
      return;
    }
    if (loginForm.newPassword !== loginForm.confirmPassword) {
      setLoginFeedback("A confirmação precisa ser igual à nova senha.");
      return;
    }
    setLoginLoading(true);
    setLoginFeedback("");
    try {
      const result = await apiPost("/auth/reset-password", {
        body: { token: loginForm.resetToken.trim(), newPassword: loginForm.newPassword }
      });
      const user = normalizeUserFromApi(result);
      persistViewerUser(user);
      setLoginForm((prev) => ({ ...prev, password: "", currentPassword: "", newPassword: "", confirmPassword: "", resetToken: "" }));
      setLoginFeedback("Senha redefinida. Você já está conectado.");
      window.history.replaceState({}, "", window.location.pathname);
    } catch (error) {
      setLoginFeedback(error?.message || "Não foi possível redefinir a senha.");
    } finally {
      setLoginLoading(false);
    }
  };

  const shareSlimDevotional = () => {
    trackEvent("devotional_invite", { channel: "whatsapp_pao" });
    // Manda direto pra tela de escolher uma trilha (aba Planos), nao so pra
    // home -- assim quem recebe o convite ja escolhe o devocional dela,
    // em vez de cair sem rumo na home do app (pedido do Rodrigo).
    const appUrl = `${window.location.origin}/familia?tab=planos`;
    const text = `O Pão de Hoje (Hebreus 11:1): A fé não é a crença do que Deus fará, ela é a firme convicção do que Deus já fez.\n\nQuer continuar com a gente? Escolha um devocional no app Mosaico e comece por onde fizer sentido: ${appUrl}`;
    const url = `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const createTrailInvite = async (channel) => {
    if (!mesaInviteTrail) {
      setMesaInviteFeedback("Escolha uma trilha para enviar o convite.");
      return null;
    }
    if (!mesaInviteName.trim()) {
      setMesaInviteFeedback("Informe o nome da pessoa convidada.");
      return null;
    }
    if (channel === "EMAIL" && (!mesaInviteEmail.trim() || !mesaInviteEmail.includes("@"))) {
      setMesaInviteFeedback("Informe um e-mail válido para enviar o convite.");
      return null;
    }
    if (channel === "WHATSAPP" && !mesaInviteWhatsapp.replace(/\D/g, "")) {
      setMesaInviteFeedback("Informe o WhatsApp do convidado ou gere um link para enviar por outro canal.");
      return null;
    }

    const user = await ensureSession();
    return apiPost("/invites", {
      viewer: user,
      body: {
        inviterId: user.id,
        trailSlug: mesaInviteTrail.slug || mesaInviteTrail.id,
        inviteeName: mesaInviteName.trim(),
        inviteeEmail: mesaInviteEmail.trim() || null,
        inviteeWhatsapp: mesaInviteWhatsapp.trim() || null,
        message: mesaInviteMessage.trim() || null,
        channel
      }
    });
  };

  const generateMesaInviteLink = async () => {
    try {
      const invite = await createTrailInvite("LINK");
      if (!invite) return;
      if (invite.group) {
        setSelectedTrailGroup({ ...invite.group, trail: invite.trail });
        setCoopActive(true);
      }
      const inviteUrl = buildInviteUrl(invite);
      try {
        await navigator.clipboard.writeText(inviteUrl);
        setMesaInviteFeedback(`Link criado e copiado. Envie para ${mesaInviteName.trim()}: ${inviteUrl}`);
      } catch (error) {
        setMesaInviteFeedback(`Link criado. Envie para ${mesaInviteName.trim()}: ${inviteUrl}`);
      }
    } catch (error) {
      setMesaInviteFeedback("Não foi possível registrar o convite agora. Tente novamente.");
    }
  };

  const shareMesaInviteByWhatsapp = async () => {
    try {
      const invite = await createTrailInvite("WHATSAPP");
      if (!invite) return;
      if (invite.group) {
        setSelectedTrailGroup({ ...invite.group, trail: invite.trail });
        setCoopActive(true);
      }
      const phone = mesaInviteWhatsapp.replace(/\D/g, "");
      setMesaInviteFeedback(`Convite registrado e aberto no WhatsApp para ${mesaInviteName.trim() || "a pessoa convidada"}.`);
      window.open(`https://wa.me/55${phone.replace(/^55/, "")}?text=${encodeURIComponent(buildMesaInviteText(invite))}`, "_blank");
    } catch (error) {
      setMesaInviteFeedback("Não foi possível registrar o convite agora. Tente novamente.");
    }
  };

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    let alive = true;
    const token = new URLSearchParams(window.location.search).get("convite");
    if (!token) return undefined;

    setPendingInviteFeedback("");
    apiFetch(`/invites/public/${encodeURIComponent(token)}`, { method: "GET" })
      .then((invite) => {
        if (!alive) return;
        if (invite) setPendingInvite(invite);
        else setPendingInviteFeedback("Não encontramos este convite. Peça para a pessoa reenviar o link.");
      })
      .catch(() => {
        if (alive) setPendingInviteFeedback("Não foi possível carregar o convite agora.");
      });

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    const resetToken = new URLSearchParams(window.location.search).get("reset");
    if (!resetToken) return;
    setLoginForm((prev) => ({ ...prev, resetToken }));
    setActiveTab("profile");
    setLoginFeedback("Informe sua nova senha para recuperar o acesso.");
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("novo_cadastro") !== "1") return;

    const nome = params.get("nome") || "";
    const email = params.get("email") || "";
    const whatsapp = params.get("whatsapp") || "";
    if (nome || email || whatsapp) {
      setProfileForm((prev) => ({
        ...prev,
        name: nome || prev.name,
        email: email || prev.email,
        whatsapp: whatsapp || prev.whatsapp
      }));
    }
    requestProfileAccess("novo_membro");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const acceptPendingInvite = async () => {
    if (!pendingInvite?.token) return;
    try {
      setPendingInviteFeedback("Entrando na caminhada...");
      const user = await ensureSession();
      const group = await apiPost(`/invites/${encodeURIComponent(pendingInvite.token)}/accept`, {
        viewer: user,
        body: { userId: user.id }
      });
      const trail = trailById(group.trail?.slug) || trails.find((item) => item.slug === group.trail?.slug);
      setPendingInvite(null);
      setPendingInviteFeedback("");
      setSelectedTrailGroup(group);
      setCoopActive(true);
      window.history.replaceState({}, "", window.location.pathname);
      if (trail) openTrail(trail);
      else setActiveTab("library");
    } catch (error) {
      setPendingInviteFeedback("Não foi possível entrar agora. Complete seu cadastro e tente novamente.");
    }
  };

  useEffect(() => {
    let alive = true;

    const loadPastors = async () => {
      setPastorsLoading(true);
      try {
        const data = await apiFetch("/pastors", { method: "GET" });
        if (!alive) return;
        const pastorItems = data.map((item) => ({ id: item.id, name: item.name, email: item.email || "" })).filter((item) => item.id && item.name);
        if (pastorItems.length > 0) setPastors(pastorItems);
      } catch (error) {
        if (alive) setPastors(PASTORAL_TEAM);
      } finally {
        if (alive) setPastorsLoading(false);
      }
    };

    loadPastors();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (showPastoralAgenda) loadBookings();
  }, [showPastoralAgenda]);

  // Carrega o cadastro de membresia e os filhos vinculados a propria conta
  // sempre que a sessao mudar (login, logout, troca de conta) -- alimenta a
  // secao "Meu cadastro na igreja" (aba Perfil) e o banner de lembrete.
  useEffect(() => {
    if (!viewerUser?.sessionToken || viewerUser?.passwordMustChange) {
      setMyMember(null);
      setMyChildren([]);
      return;
    }
    loadMyMember();
    loadMyChildren();
  }, [viewerUser?.id, viewerUser?.sessionToken, viewerUser?.passwordMustChange]);

  useEffect(() => {
    if (activeTab === "conecta" && isCareManager) loadAdminOverview();
  }, [activeTab, isCareManager, viewerUser?.id, viewerUser?.sessionToken, calendarFilterMonth, calendarFilterYear, calendarFilterPastorId]);

  useEffect(() => {
    if (activeTab === "family" || activeTab === "home" || activeTab === "library") loadOpenGroups();
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === "home" && hasCompletedProfile) loadMyInvites();
  }, [activeTab, hasCompletedProfile, viewerUser?.id]);

  useEffect(() => {
    if ((activeTab === "home" || activeTab === "profile") && hasCompletedProfile) loadBadges();
  }, [activeTab, hasCompletedProfile, viewerUser?.id]);

  useEffect(() => {
    if (window.location.pathname === "/conecta") {
      setScreen("main");
      setActiveTab("conecta");
    }
  }, []);

  // Sincroniza a URL/título da aba com a tela atual dentro de /familia (SEO
  // pediu isso pros ebooks; aqui estendemos pra trilha e dia também --
  // pedido do Rodrigo/Carla depois de ver que a aba ficava sem nome). Não
  // mexe no roteamento em si (continua tudo client-state, ver PrototypeApp
  // no topo do arquivo) -- só espelha o estado atual na barra de endereço
  // via history.pushState, sem recarregar a página.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const legacyHost = window.location.hostname.startsWith("familia.") || window.location.hostname === "localhost";
    const basePath = legacyHost ? "" : "/familia";
    const defaultTitle = legacyHost ? "Família Mosaico" : "Igreja Batista Mosaico";

    let path = null;
    let title = null;
    let description = null;
    // Só mexe em canonical/description/OG nas rotas que o robots.txt libera
    // pra indexação (/familia/ebooks/* e /familia/trilhas/*) -- era a causa
    // raiz do "Detectada, mas não indexada" no Search Console: o <title>
    // mudava por tela, mas a canonical/description/OG ficavam sempre
    // apontando pra home (vinham só do public/index.html estático). A tela
    // "main" fica de fora de propósito: /familia/ sozinho é Disallow no
    // robots.txt, então não compensa reescrever o description/OG dela aqui.
    let isIndexableRoute = false;

    if (screen === "ebook" && selectedEbook) {
      path = `${basePath}/ebooks/${selectedEbook.id}`;
      title = `${selectedEbook.title} | Família Mosaico`;
      description = selectedEbook.subtitle || `Ebook gratuito "${selectedEbook.title}", de ${selectedEbook.author}.`;
      isIndexableRoute = true;
    } else if (screen === "ebook-landing" && selectedEbook) {
      path = `${basePath}/ebooks/${selectedEbook.id}`;
      title = `${selectedEbook.title} | Ebook gratuito - Igreja Batista Mosaico`;
      description = selectedEbook.subtitle
        ? `${selectedEbook.subtitle} Ebook gratuito de ${selectedEbook.author}${selectedEbook.pages ? `, ${selectedEbook.pages} páginas` : ""}.`
        : `Baixe gratuitamente "${selectedEbook.title}", de ${selectedEbook.author}.`;
      isIndexableRoute = true;
    } else if (screen === "trail-landing" && selectedTrailLanding) {
      const trailKey = trailUrlSlug(selectedTrailLanding);
      if (trailKey) path = `${basePath}/trilhas/${trailKey}`;
      title = `${selectedTrailLanding.title || "Trilha"} | Trilha gratuita - Igreja Batista Mosaico`;
      description = selectedTrailLanding.subtitle
        ? `${selectedTrailLanding.subtitle} Trilha gratuita${selectedTrailLanding.duration ? ` de ${selectedTrailLanding.duration} dias` : ""}.`
        : `Trilha gratuita "${selectedTrailLanding.title || "Trilha"}" da Família Mosaico.`;
      isIndexableRoute = true;
    } else if (screen === "trail" && selectedTrail) {
      const trailKey = trailUrlSlug(selectedTrail);
      title = `${selectedTrail.title || "Trilha"} | Família Mosaico`;
      if (trailKey) path = `${basePath}/trilhas/${trailKey}`;
      description = selectedTrail.subtitle || `Trilha "${selectedTrail.title || "Trilha"}" da Família Mosaico.`;
      isIndexableRoute = true;
    } else if (screen === "day" && selectedTrail && selectedDay) {
      const trailKey = trailUrlSlug(selectedTrail);
      title = `Dia ${selectedDay} - ${selectedTrail.title || "Trilha"} | Família Mosaico`;
      if (trailKey) path = `${basePath}/trilhas/${trailKey}/dias/${selectedDay}`;
      description = `Dia ${selectedDay} da trilha "${selectedTrail.title || "Trilha"}"${selectedTrail.subtitle ? ` -- ${selectedTrail.subtitle}` : ""}.`;
      isIndexableRoute = true;
    } else if (screen === "main") {
      path = basePath || "/";
      title = defaultTitle;
    }

    try {
      if (path && window.location.pathname !== path) {
        window.history.pushState(null, "", path);
      }
      if (title && document.title !== title) {
        document.title = title;
      }
      if (isIndexableRoute && path && title) {
        const url = SITE_URL + path;
        setCanonical(url);
        if (description) setMeta("name", "description", description);
        setMeta("property", "og:title", title);
        setMeta("property", "og:description", description || "");
        setMeta("property", "og:url", url);
        setMeta("property", "og:type", "website");
        setMeta("property", "og:image", DEFAULT_IMAGE);
        setMeta("name", "twitter:card", "summary_large_image");
        setMeta("name", "twitter:title", title);
        setMeta("name", "twitter:description", description || "");
        setMeta("name", "twitter:image", DEFAULT_IMAGE);
      }
    } catch (error) {
      // Ambiente sem window.history utilizável (ex.: alguns webviews) -- não
      // é crítico, a navegação por estado do app continua funcionando normalmente.
    }
  }, [screen, selectedEbook, selectedTrail, selectedTrailLanding, selectedDay]);

  // Botão "voltar" do navegador: como a navegação aqui é só estado do React
  // (sem router de verdade), o jeito seguro de reagir ao voltar/avançar do
  // navegador é recarregar -- a leitura inicial da URL (getInitialFamiliaEbookFromUrl
  // acima) recoloca a tela certa pra rotas de ebook; outras rotas caem no
  // "main" por enquanto.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handlePopState = () => {
      window.location.reload();
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    let alive = true;
    const loadPastoralSlots = async () => {
      if (!selectedPastorId) {
        setPastoralSlots([]);
        setPastoralSlotsStatus("idle");
        return;
      }

      setPastoralSlotsLoading(true);
      setPastoralSlotsStatus("loading");
      try {
        const data = await apiFetch(`/pastoral/slots?pastorId=${encodeURIComponent(selectedPastorId)}`, { method: "GET" });
        if (alive) {
          setPastoralSlots((Array.isArray(data) ? data : []).map(normalizePastoralSlot));
          setPastoralSlotsStatus("success");
        }
      } catch (error) {
        if (alive) {
          setPastoralSlots([]);
          setPastoralSlotsStatus("error");
        }
      } finally {
        if (alive) setPastoralSlotsLoading(false);
      }
    };

    loadPastoralSlots();
    return () => {
      alive = false;
    };
  }, [selectedPastorId, pastors]);

  useEffect(() => {
    // Quando QUALQUER chamada à API responder 401 ou code SESSION_INVALID,
    // limpa o estado local. O cookie httpOnly já caiu no servidor.
    setSessionExpiredHandler(() => {
      try { localStorage.removeItem("mosaico_user"); } catch (_) {}
      setViewerUser(null);
      setWeeklyCompletionMapApi(null);
    });
    return () => setSessionExpiredHandler(null);
  }, []);

  useEffect(() => {
    // Hidratação otimista a partir de localStorage (perfil só, sem token).
    const raw = localStorage.getItem("mosaico_user");
    if (raw) {
      try {
        const savedUser = normalizeUserFromApi(JSON.parse(raw));
        const isLegacyMockUser = savedUser?.name === "Rodrigo Moraes" && !savedUser?.email && !savedUser?.profileCompleted;
        if (isLegacyMockUser) {
          localStorage.removeItem("mosaico_user");
          setViewerUser(null);
        } else {
          persistViewerUser(savedUser);
        }
      } catch (_) {
        setViewerUser(null);
      }
    }

    // Reconcilia com o servidor: /auth/me confirma se ainda há cookie válido.
    // Se sim, atualiza o perfil. Se 401, limpa o estado local.
    // `skipSessionExpiredHandler` evita que esse 401 (esperado em visita anônima)
    // dispare o handler global de sessão expirada — e os dois callbacks abaixo
    // ignoram o resultado se um login/cadastro já foi confirmado enquanto esta
    // chamada estava em voo (corrida com cold start do Railway, por exemplo).
    apiFetch("/auth/me", { method: "GET", skipSessionExpiredHandler: true })
      .then((freshUser) => {
        if (authConfirmedRef.current) return;
        if (freshUser) persistViewerUser(normalizeUserFromApi(freshUser));
      })
      .catch((error) => {
        if (authConfirmedRef.current) return;
        if (error instanceof ApiError && error.status === 401) {
          // Sem cookie ou cookie expirado — limpa o perfil local em modo silencioso
          // (não mostra mensagem porque pode ser primeira visita).
          try { localStorage.removeItem("mosaico_user"); } catch (_) {}
          setViewerUser(null);
        }
      });
  }, []);

  // Sininho (GET /notifications) -- so faz sentido com sessao; atualiza
  // sozinho de tempos em tempos (mesmo intervalo de 60s do live-status da
  // home institucional) pra pegar avisos novos sem precisar recarregar.
  useEffect(() => {
    if (!viewerUser?.sessionToken) return undefined;
    loadNotifications();
    const intervalId = setInterval(loadNotifications, 60000);
    return () => clearInterval(intervalId);
  }, [viewerUser?.sessionToken]);

  useEffect(() => {
    if (!viewerUser) return;
    // Troca de conta no mesmo aparelho (ex.: sair da Carla e entrar como o
    // Rodrigo) não pode herdar o que sobrou no formulário da conta anterior --
    // por isso, quando o id muda, a base do merge é o PROFILE_FORM_DEFAULT (só
    // servidor + default), nunca o `prev` de outra pessoa. Quando é a MESMA
    // conta (ex.: um refresh do /auth/me em segundo plano), a base continua
    // sendo `prev` pra não sobrescrever uma edição em andamento que a pessoa
    // ainda não salvou.
    const isDifferentAccount = profileFormSyncedUserIdRef.current !== viewerUser.id;
    setProfileForm((prev) => {
      const base = isDifferentAccount ? PROFILE_FORM_DEFAULT : prev;
      return {
        ...base,
        name: viewerUser.name || base.name,
        birthDate: viewerUser.birthDate ? String(viewerUser.birthDate).slice(0, 10) : base.birthDate,
        email: viewerUser.email || base.email,
        whatsapp: viewerUser.whatsapp || base.whatsapp,
        state: viewerUser.state || base.state,
        city: viewerUser.city || base.city,
        connectionType: viewerUser.connectionType || base.connectionType,
        discoveryChannel: viewerUser.discoveryChannel || base.discoveryChannel,
        invitedBy: viewerUser.invitedBy || base.invitedBy,
        privacyAccepted: Boolean(viewerUser.privacyAccepted || base.privacyAccepted)
      };
    });
    profileFormSyncedUserIdRef.current = viewerUser.id;
  }, [viewerUser]);

  useEffect(() => {
    try {
      if (viewerUser?.avatarId && isValidAvatarId(viewerUser.avatarId)) {
        setSelectedAvatarId(viewerUser.avatarId);
        hydrateAvatarBuilderFrom(viewerUser.avatarId);
        return;
      }
      const raw = localStorage.getItem(getAvatarStorageKey(viewerUser?.id));
      if (raw && isValidAvatarId(raw)) {
        setSelectedAvatarId(raw);
        hydrateAvatarBuilderFrom(raw);
        return;
      }
      setSelectedAvatarId(availableAvatarOptions[0]?.id || AVATAR_OPTIONS[0].id);
    } catch (error) {
      setSelectedAvatarId(availableAvatarOptions[0]?.id || AVATAR_OPTIONS[0].id);
    }
  }, [viewerUser?.id, viewerUser?.avatarId, availableAvatarOptions]);

  useEffect(() => {
    if (availableAvatarOptions.length === 0) return;
    if (!isValidAvatarId(selectedAvatarId)) {
      setSelectedAvatarId(availableAvatarOptions[0].id);
    }
  }, [availableAvatarOptions, selectedAvatarId]);

  useEffect(() => {
    try {
      localStorage.setItem(getAvatarStorageKey(viewerUser?.id), selectedAvatarId);
    } catch (error) {
      // noop
    }
  }, [selectedAvatarId, viewerUser?.id]);

  useEffect(() => {
    setTrails((prev) => applyUserProgressToTrails(prev, canUseSavedProgress ? viewerUser?.id : null));
  }, [canUseSavedProgress, viewerUser?.id]);

  useEffect(() => {
    if (!selectedEbook?.id) return;
    setEbookHighlights((prev) => ({ ...prev, [selectedEbook.id]: readEbookHighlights(selectedEbook.id) }));
    try {
      const storedNote = canUseSavedProgress ? localStorage.getItem(getEbookNoteKey(selectedEbook.id)) || "" : "";
      setEbookNotes((prev) => ({ ...prev, [selectedEbook.id]: storedNote }));
    } catch (error) {
      setEbookNotes((prev) => ({ ...prev, [selectedEbook.id]: "" }));
    }
    if (!viewerUser?.id || !viewerUser?.sessionToken) return;
    let alive = true;
    apiFetch(`/ebooks/${encodeURIComponent(selectedEbook.id)}/state?viewerUserId=${encodeURIComponent(viewerUser.id)}`, {
      method: "GET",
      viewer: viewerUser
    })
      .then((state) => {
        if (!alive || !state) return;
        const highlights = Array.isArray(state.highlights) ? state.highlights : [];
        const note = state.note || "";
        const page = Number(state.currentPage || 1);
        setEbookHighlights((prev) => ({ ...prev, [selectedEbook.id]: highlights }));
        setEbookNotes((prev) => ({ ...prev, [selectedEbook.id]: note }));
        persistEbookHighlights(selectedEbook.id, highlights);
        persistEbookNote(selectedEbook.id, note);
        if (Number.isFinite(page)) {
          setEbookPage(Math.min(Math.max(page, 1), Math.max(1, selectedEbook.pages || 1)));
        }
      })
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, [selectedEbook?.id, viewerUser?.id, viewerUser?.sessionToken]);

  useEffect(() => {
    if (!selectedTrail || !selectedDay) return;
    const identity = getDevotionalIdentity(selectedTrail, selectedDay);
    setDevotionalHighlights((prev) => ({
      ...prev,
      [identity]: readDevotionalHighlights(selectedTrail, selectedDay)
    }));
    setDevotionalReflections((prev) => ({
      ...prev,
      [identity]: readDevotionalReflection(selectedTrail, selectedDay)
    }));
  }, [selectedTrail?.id, selectedTrail?.slug, selectedDay]);

  useEffect(() => {
    if (screen !== "diary") return;
    setDiaryEntries(buildDiaryEntries());
  }, [screen]);

  useEffect(() => {
    if (reminderEditing) return;
    setReminderTimeInput(viewerUser?.reminderTime || "07:00");
  }, [viewerUser?.reminderTime, reminderEditing]);

  useEffect(() => {
    if (!selectedEbook?.id) return;
    if (!canUseSavedProgress) return;
    try {
      localStorage.setItem(getEbookPageKey(selectedEbook.id), String(ebookPage));
    } catch (error) {
      // noop
    }
    // completed:true so' na primeira vez que a pagina atual bate com o total
    // de paginas do e-book (badge "Primeiro e-book"/"Leitor dedicado" -- ver
    // PATCH /ebooks/:ebookId/state no backend, que so' grava a conclusao uma
    // vez mesmo recebendo isso de novo em releituras).
    const ebookTotalPages = Math.max(1, selectedEbook.pages || 1);
    syncEbookState(selectedEbook.id, { currentPage: ebookPage, completed: ebookPage >= ebookTotalPages });
  }, [ebookPage, selectedEbook?.id, selectedEbook?.pages, viewerUser?.id, viewerUser?.sessionToken, canUseSavedProgress]);

  useEffect(() => {
    if (!selectedEbook?.pdfUrl || ebookTab !== "reading") return;
    let cancelled = false;
    const renderPdfPage = async () => {
      const canvas = ebookCanvasRef.current;
      if (!canvas) return;
      setEbookPageLoading(true);
      setEbookPageError("");
      try {
        const [pdfModule, workerModule] = await Promise.all([
          import("pdfjs-dist/legacy/build/pdf"),
          import("pdfjs-dist/legacy/build/pdf.worker.entry")
        ]);
        const pdfjsLib = pdfModule.default || pdfModule;
        pdfjsLib.GlobalWorkerOptions.workerSrc = workerModule.default || workerModule;
        // Mitigação CVE-2024-4367: desabilitar eval no parser de fontes.
        // TODO: migrar pdfjs-dist para 4.2.67+ e remover esta flag.
        const loadingTask = pdfjsLib.getDocument({
          url: selectedEbook.pdfUrl,
          isEvalSupported: false
        });
        const pdf = await loadingTask.promise;
        const safePage = Math.min(Math.max(ebookPage, 1), pdf.numPages || selectedEbook.pages || 1);
        const page = await pdf.getPage(safePage);
        const baseViewport = page.getViewport({ scale: 1 });
        const targetWidth = (isDesktop ? 920 : isTablet ? 760 : 560) * ebookZoomLevel;
        const scale = Math.min(2.2 * EBOOK_ZOOM_MAX, Math.max(1.2 * EBOOK_ZOOM_MIN, targetWidth / baseViewport.width));
        const viewport = page.getViewport({ scale });
        const context = canvas.getContext("2d");
        canvas.width = Math.ceil(viewport.width);
        canvas.height = Math.ceil(viewport.height);
        await page.render({ canvasContext: context, viewport }).promise;
        if (!cancelled) {
          if (safePage !== ebookPage) setEbookPage(safePage);
          setEbookPageLoading(false);
        }
      } catch (error) {
        if (!cancelled) {
          setEbookPageError("Não foi possível carregar esta página do livro.");
          setEbookPageLoading(false);
        }
      }
    };
    renderPdfPage();
    return () => {
      cancelled = true;
    };
  }, [ebookPage, ebookTab, isDesktop, isTablet, selectedEbook?.pdfUrl, selectedEbook?.pages, ebookZoomLevel]);

  useEffect(() => {
    const hideEbookMenu = (event) => {
      if (ebookSelection && !event.target.closest?.(".ebook-highlight-menu")) {
        setEbookSelection(null);
      }
    };
    document.addEventListener("mousedown", hideEbookMenu);
    document.addEventListener("touchstart", hideEbookMenu);
    document.addEventListener("scroll", hideEbookMenu);
    return () => {
      document.removeEventListener("mousedown", hideEbookMenu);
      document.removeEventListener("touchstart", hideEbookMenu);
      document.removeEventListener("scroll", hideEbookMenu);
    };
  }, [ebookSelection]);

  useEffect(() => {
    const hideDevotionalMenu = (event) => {
      if (devotionalSelection && !event.target.closest?.(".devotional-highlight-menu")) {
        setDevotionalSelection(null);
      }
    };
    document.addEventListener("mousedown", hideDevotionalMenu);
    document.addEventListener("touchstart", hideDevotionalMenu);
    document.addEventListener("scroll", hideDevotionalMenu);
    return () => {
      document.removeEventListener("mousedown", hideDevotionalMenu);
      document.removeEventListener("touchstart", hideDevotionalMenu);
      document.removeEventListener("scroll", hideDevotionalMenu);
    };
  }, [devotionalSelection]);

  const ensureSession = async () => {
    if (viewerUser?.id && viewerUser?.sessionToken) return viewerUser;
    const createdUser = await apiPost("/auth/session", { body: getProfileSessionPayload() });
    const user = normalizeUserFromApi({
      ...(viewerUser || {}),
      ...createdUser,
      name: createdUser.name || profileForm.name.trim() || bookingName.trim() || "Visitante Família Mosaico"
    });
    persistViewerUser(user);
    authConfirmedRef.current = true;
    return user;
  };

  const loadOpenGroups = async () => {
    setOpenGroupsLoading(true);
    setOpenGroupsFeedback("");
    try {
      const data = await apiFetch("/groups/open", { method: "GET" });
      setOpenGroups(Array.isArray(data) ? data : []);
    } catch (error) {
      setOpenGroups([]);
      setOpenGroupsFeedback("Não foi possível carregar as mesas abertas agora.");
    } finally {
      setOpenGroupsLoading(false);
    }
  };

  const loadMyInvites = async () => {
    if (!viewerUser?.id) return;
    setMyInvitesLoading(true);
    try {
      const data = await apiFetch(`/invites?viewerUserId=${encodeURIComponent(viewerUser.id)}`, { method: "GET", viewer: viewerUser });
      setMyInvites(Array.isArray(data) ? data : []);
    } catch (error) {
      setMyInvites([]);
    } finally {
      setMyInvitesLoading(false);
    }
  };

  const loadBadges = async () => {
    if (!viewerUser?.id) return;
    setBadgesLoading(true);
    try {
      const data = await apiFetch(`/badges/me?viewerUserId=${encodeURIComponent(viewerUser.id)}`, { method: "GET", viewer: viewerUser });
      setBadgesData(data);
    } catch (error) {
      setBadgesData(null);
    } finally {
      setBadgesLoading(false);
    }
  };

  // Dica curta de progresso pra conquista ainda bloqueada, usando os stats
  // crus que ja vem em GET /badges/me (badgesData.stats) -- nunca reimplementa
  // o criterio de elegibilidade aqui (isso mora so' no backend, badges/engine.js).
  const badgeProgressHint = (badge) => {
    const stats = badgesData?.stats;
    if (!stats) return "";
    switch (badge.slug) {
      case "primeiro-devocional":
        return "Conclua seu primeiro dia de devocional.";
      case "cinco-devocionais":
        return `${stats.devotionalsDoneCount} de 5 dias de devocional concluídos.`;
      case "dez-devocionais":
        return `${stats.devotionalsDoneCount} de 10 dias de devocional concluídos.`;
      case "trilha-concluida":
        return "Termine um devocional (trilha) inteiro, do primeiro ao último dia.";
      case "tres-trilhas-concluidas":
        return `${stats.trailsCompletedCount ?? 0} de 3 devocionais concluídos.`;
      case "cinco-trilhas-concluidas":
        return `${stats.trailsCompletedCount ?? 0} de 5 devocionais concluídos.`;
      case "sequencia-7":
        return `Sequência atual: ${stats.streak} de 7 dias.`;
      case "sequencia-30":
        return `Sequência atual: ${stats.streak} de 30 dias.`;
      case "primeiro-ebook":
        return "Conclua a leitura de um e-book.";
      case "tres-ebooks":
        return `${stats.ebooksCompletedCount} de 3 e-books concluídos.`;
      case "primeiro-depoimento":
        return "Responda como um devocional fez sentido pra você.";
      default:
        return "";
    }
  };

  const createOpenMesa = async () => {
    if (!hasCompletedProfile) {
      requestProfileAccess("invite");
      return;
    }
    if (!openMesaTrail) {
      setOpenGroupsFeedback("Escolha uma trilha para abrir a mesa.");
      return;
    }
    const name = openMesaForm.name.trim() || `Mesa aberta: ${openMesaTrail.title}`;

    setOpenMesaSaving(true);
    setOpenGroupsFeedback("");
    try {
      const user = await ensureSession();
      const group = await apiPost("/groups", {
        viewer: user,
        body: {
          ownerId: user.id,
          trailSlug: openMesaTrail.slug || openMesaTrail.id,
          name,
          description: openMesaForm.description.trim() || "Uma mesa aberta para caminhar com calma, leitura e conversa.",
          visibility: "OPEN",
          capacity: Number(openMesaForm.capacity) || 12
        }
      });
      setOpenGroups((current) => [group, ...current.filter((item) => item.id !== group.id)]);
      setSelectedTrailGroup(group);
      setCoopActive(true);
      setOpenMesaForm((current) => ({ ...current, name: "", description: "" }));
      setOpenGroupsFeedback("Mesa aberta criada. Agora ela aparece para quem quiser chegar e sentar.");
      if (openMesaTrail) openTrail(openMesaTrail.id);
    } catch (error) {
      setOpenGroupsFeedback("Não foi possível criar a mesa agora. Tente novamente.");
    } finally {
      setOpenMesaSaving(false);
    }
  };

  const joinOpenMesa = async (group) => {
    if (!group?.id) return;
    if (!hasCompletedProfile) {
      requestProfileAccess("invite");
      return;
    }

    setJoiningOpenGroupId(group.id);
    setOpenGroupsFeedback("");
    try {
      const user = await ensureSession();
      const updatedGroup = await apiPost(`/groups/${group.id}/join`, {
        viewer: user,
        body: { userId: user.id }
      });
      setOpenGroups((current) => current.map((item) => item.id === updatedGroup.id ? updatedGroup : item));
      setSelectedTrailGroup(updatedGroup);
      setOpenGroupsFeedback(`Você entrou na mesa "${updatedGroup.name}".`);
      const localTrail = trailById(updatedGroup.trail?.slug) || trailById(updatedGroup.trail?.id);
      if (localTrail) {
        setCoopActive(true);
        openTrail(localTrail.id);
      }
    } catch (error) {
      setOpenGroupsFeedback(error.message || "Não foi possível entrar nessa mesa agora.");
    } finally {
      setJoiningOpenGroupId("");
    }
  };

  const loadBookings = async () => {
    setBookingsLoading(true);
    try {
      const user = await ensureSession();
      const data = await apiFetch(`/pastoral/bookings?viewerUserId=${encodeURIComponent(user.id)}`, {
        method: "GET",
        viewer: user
      });
      setBookings(data);
    } catch (error) {
      setBookings([]);
    } finally {
      setBookingsLoading(false);
    }
  };

  const loadMyMember = async () => {
    setMyMemberLoading(true);
    try {
      const data = await apiFetch("/me/member", { method: "GET" });
      setMyMember(data || null);
    } catch (error) {
      setMyMember(null);
    } finally {
      setMyMemberLoading(false);
    }
  };

  const loadMyChildren = async () => {
    setMyChildrenLoading(true);
    try {
      const data = await apiFetch("/me/children", { method: "GET" });
      setMyChildren(Array.isArray(data) ? data : []);
    } catch (error) {
      setMyChildren([]);
    } finally {
      setMyChildrenLoading(false);
    }
  };

  const updateMemberEditorForm = (field, value) => {
    setMemberEditorForm((prev) => ({ ...prev, [field]: value }));
    if (memberEditorFeedback) setMemberEditorFeedback("");
  };

  // Abre o formulario de "Meu cadastro" (membresia) -- pre-preenchido com o
  // que ja existe (edicao) ou, se a pessoa ainda nao tem Member vinculado,
  // com o que ja temos do proprio cadastro de acesso (nome/e-mail/WhatsApp),
  // pra nao pedir de novo o que a pessoa ja informou.
  const openMemberEditor = () => {
    if (myMember) {
      setMemberEditorForm({
        fullName: myMember.fullName || "",
        email: myMember.email || "",
        birthDate: myMember.birthDate ? String(myMember.birthDate).slice(0, 10) : "",
        cpf: myMember.cpf || "",
        mobilePhone: myMember.mobilePhone || "",
        homePhone: myMember.homePhone || "",
        emergencyContactName: myMember.emergencyContactName || "",
        emergencyPhone: myMember.emergencyPhone || "",
        address: myMember.address || "",
        addressComplement: myMember.addressComplement || "",
        neighborhood: myMember.neighborhood || "",
        city: myMember.city || "",
        state: myMember.state || "",
        zipCode: myMember.zipCode || "",
        spouseName: myMember.spouseName || "",
        fatherName: myMember.fatherName || "",
        motherName: myMember.motherName || "",
        specialNeeds: myMember.specialNeeds || "",
        baptismDate: myMember.baptismDate ? String(myMember.baptismDate).slice(0, 10) : "",
        notBaptized: Boolean(myMember.notBaptized),
        baptismChurch: myMember.baptismChurch || "",
        churchRole: myMember.churchRole || "",
        howMetChurch: myMember.howMetChurch || "",
        attendingSince: myMember.attendingSince ? String(myMember.attendingSince).slice(0, 10) : "",
        notes: myMember.notes || ""
      });
    } else {
      setMemberEditorForm({
        ...SELF_MEMBER_FORM_DEFAULT,
        fullName: profileForm.name || viewerUser?.name || "",
        email: profileForm.email || viewerUser?.email || "",
        mobilePhone: profileForm.whatsapp || ""
      });
    }
    setMemberEditorFeedback("");
    setShowMemberEditor(true);
  };

  const saveMemberEditor = async () => {
    if (!memberEditorForm.fullName.trim()) {
      setMemberEditorFeedback("Informe o nome completo.");
      return;
    }
    if (!memberEditorForm.notBaptized && !memberEditorForm.baptismDate) {
      setMemberEditorFeedback('Informe a data de batismo ou marque "ainda não fui batizado(a)".');
      return;
    }
    setMemberEditorSaving(true);
    setMemberEditorFeedback("");
    try {
      const payload = {
        ...memberEditorForm,
        fullName: memberEditorForm.fullName.trim(),
        birthDate: memberEditorForm.birthDate || null,
        baptismDate: memberEditorForm.notBaptized ? null : memberEditorForm.baptismDate || null,
        attendingSince: memberEditorForm.attendingSince || null
      };
      const saved = await apiPatch("/me/member", { body: payload });
      setMyMember(saved);
      setMemberEditorFeedback("Cadastro salvo. A secretaria confirma seus dados em breve.");
      setTimeout(() => setShowMemberEditor(false), 900);
    } catch (error) {
      setMemberEditorFeedback(error.message || "Não foi possível salvar agora. Tente novamente.");
    } finally {
      setMemberEditorSaving(false);
    }
  };

  const updateChildEditorForm = (field, value) => {
    setChildEditorForm((prev) => ({ ...prev, [field]: value }));
    if (childEditorFeedback) setChildEditorFeedback("");
  };

  const openAddChildEditor = () => {
    setEditingChildId(null);
    setChildEditorForm(SELF_CHILD_FORM_DEFAULT);
    setChildEditorFeedback("");
    setShowChildEditor(true);
  };

  const openEditChildEditor = (child) => {
    setEditingChildId(child.id);
    setChildEditorForm({
      fullName: child.fullName || "",
      birthDate: child.birthDate ? String(child.birthDate).slice(0, 10) : "",
      address: child.address || "",
      addressComplement: child.addressComplement || "",
      neighborhood: child.neighborhood || "",
      city: child.city || "",
      state: child.state || "",
      zipCode: child.zipCode || "",
      fatherName: child.fatherName || "",
      fatherPhone: child.fatherPhone || "",
      fatherEmail: child.fatherEmail || "",
      motherName: child.motherName || "",
      motherPhone: child.motherPhone || "",
      motherEmail: child.motherEmail || "",
      guardianName: child.guardianName || "",
      guardianPhone: child.guardianPhone || "",
      guardianRelationship: child.guardianRelationship || "",
      livesWith: child.livesWith || "",
      hasAllergies: Boolean(child.hasAllergies),
      allergyDetails: child.allergyDetails || "",
      medications: child.medications || "",
      imageAuthorization: child.imageAuthorization !== false,
      importantNotes: child.importantNotes || ""
    });
    setChildEditorFeedback("");
    setShowChildEditor(true);
  };

  const saveChildEditor = async () => {
    if (!childEditorForm.fullName.trim()) {
      setChildEditorFeedback("Informe o nome completo.");
      return;
    }
    setChildEditorSaving(true);
    setChildEditorFeedback("");
    try {
      const payload = {
        ...childEditorForm,
        fullName: childEditorForm.fullName.trim(),
        birthDate: childEditorForm.birthDate || null
      };
      if (editingChildId) {
        const saved = await apiPatch(`/me/children/${encodeURIComponent(editingChildId)}`, { body: payload });
        setMyChildren((prev) => prev.map((child) => (child.id === saved.id ? { ...saved, addedByMe: true } : child)));
      } else {
        const created = await apiPost("/me/children", { body: payload });
        setMyChildren((prev) => [...prev, { ...created, addedByMe: true }]);
      }
      setChildEditorFeedback("Cadastro salvo. A equipe confirma em breve.");
      setTimeout(() => setShowChildEditor(false), 900);
    } catch (error) {
      setChildEditorFeedback(error.message || "Não foi possível salvar agora. Tente novamente.");
    } finally {
      setChildEditorSaving(false);
    }
  };

  const saveDevotionalReminder = async (enabled) => {
    setReminderSaving(true);
    setReminderFeedback("");
    try {
      if (enabled) {
        await subscribeToPushReminders();
        const updated = await apiPut("/push/reminder", { body: { enabled: true, time: reminderTimeInput } });
        persistViewerUser({ ...viewerUser, ...updated });
        setReminderFeedback(`Lembrete ativado para ${reminderTimeInput}.`);
      } else {
        await unsubscribeFromPushReminders();
        const updated = await apiPut("/push/reminder", { body: { enabled: false } });
        persistViewerUser({ ...viewerUser, ...updated });
        setReminderFeedback("Lembrete desativado.");
      }
      setReminderEditing(false);
    } catch (error) {
      setReminderFeedback(error.message || "Não foi possível salvar agora.");
    } finally {
      setReminderSaving(false);
    }
  };

  const loadAdminOverview = async () => {
    if (!isCareManager || !viewerUser?.id || !viewerUser?.sessionToken) return;
    setAdminLoading(true);
    setAdminFeedback("");
    try {
      const calendarParams = new URLSearchParams({
        calendarMonth: String(calendarFilterMonth),
        calendarYear: String(calendarFilterYear)
      });
      if (calendarFilterPastorId && calendarFilterPastorId !== "all") calendarParams.set("calendarPastorId", calendarFilterPastorId);
      const overview = await apiFetch(`/admin/overview?${calendarParams.toString()}`, { method: "GET", viewer: viewerUser });
      setAdminOverview(overview);
    } catch (error) {
      setAdminFeedback("Não foi possível carregar o painel de cuidado agora.");
    } finally {
      setAdminLoading(false);
    }
  };


  // "YYYY-MM-DD" a partir de um Date local -- toISOString() converte pra UTC
  // e pode empurrar a data pro dia errado perto da meia-noite; aqui mantemos
  // o mesmo dia local que o cursor da recorrencia calculou.
  const formatDateForInput = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const MAX_RECURRING_SLOTS = 52; // ~1 ano de horarios semanais -- trava contra data-fim digitada errada

  const createAdminPastoralSlot = async () => {
    if (!isCareManager || !viewerUser?.id || !viewerUser?.sessionToken) return;
    const pastorId = adminSlotForm.pastorId || adminPastor?.id || pastors[0]?.id || "";
    if (!pastorId || !adminSlotForm.date || !adminSlotForm.time) {
      setAdminSlotFeedback("Escolha acolhedor(a), data e horário.");
      return;
    }
    const firstStartAt = new Date(`${adminSlotForm.date}T${adminSlotForm.time}:00`);
    if (Number.isNaN(firstStartAt.getTime())) {
      setAdminSlotFeedback("Data ou horário inválidos.");
      return;
    }

    // Datas dos horarios a abrir: so a primeira (comportamento de sempre) ou,
    // com "repetir semanalmente" marcado, uma por semana ate a data-fim.
    const occurrenceDates = [new Date(firstStartAt)];
    if (adminSlotForm.repeatWeekly) {
      if (!adminSlotForm.repeatUntil) {
        setAdminSlotFeedback("Escolha até quando repetir.");
        return;
      }
      const untilDate = new Date(`${adminSlotForm.repeatUntil}T23:59:59`);
      if (Number.isNaN(untilDate.getTime()) || untilDate < firstStartAt) {
        setAdminSlotFeedback("A data final da repetição precisa ser depois da primeira data.");
        return;
      }
      let cursor = new Date(firstStartAt);
      cursor.setDate(cursor.getDate() + 7);
      while (cursor <= untilDate && occurrenceDates.length < MAX_RECURRING_SLOTS) {
        occurrenceDates.push(new Date(cursor));
        cursor = new Date(cursor);
        cursor.setDate(cursor.getDate() + 7);
      }
    }

    const durationMinutes = Math.max(15, Number(adminSlotForm.durationMinutes) || 45);
    // Mesmo id em todas as ocorrencias deste lote (so quando repete) -- e o
    // que permite depois apagar "toda a recorrencia" de uma vez (ver
    // deleteAdminPastoralSlot). Horario avulso nao leva seriesId nenhum.
    const seriesId = occurrenceDates.length > 1
      ? (window.crypto?.randomUUID ? window.crypto.randomUUID() : `series_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`)
      : null;
    setAdminSlotLoading(true);
    setAdminSlotFeedback("");

    let opened = 0;
    let conflicted = 0;
    let failed = 0;
    for (const occurrenceDate of occurrenceDates) {
      const occurrenceStartAt = new Date(`${formatDateForInput(occurrenceDate)}T${adminSlotForm.time}:00`);
      try {
        await apiPost("/pastoral/slots", {
          viewer: viewerUser,
          body: {
            viewerUserId: viewerUser.id,
            pastorId,
            startAt: occurrenceStartAt.toISOString(),
            durationMinutes,
            mode: adminSlotForm.mode,
            locationNote: adminSlotForm.mode === "PRESENTIAL" ? adminSlotForm.locationNote.trim() || "Local confirmado pela equipe pastoral." : undefined,
            isAvailable: true,
            ...(seriesId ? { seriesId } : {})
          }
        });
        opened += 1;
      } catch (error) {
        if (error instanceof ApiError && error.status === 409) conflicted += 1;
        else failed += 1;
      }
    }

    if (occurrenceDates.length === 1) {
      setAdminSlotFeedback(opened ? "Horário aberto na agenda de acolhimento." : (conflicted ? "Já existe um horário aberto nesse período." : "Não foi possível abrir esse horário."));
    } else {
      const parts = [`${opened} de ${occurrenceDates.length} horários abertos`];
      if (conflicted) parts.push(`${conflicted} já existiam`);
      if (failed) parts.push(`${failed} falharam`);
      setAdminSlotFeedback(`${parts.join(", ")}.`);
    }

    if (opened > 0) {
      setAdminSlotForm((prev) => ({ ...prev, time: "", locationNote: "", repeatWeekly: false, repeatUntil: "" }));
      if (selectedPastorId === pastorId) setSelectedPastorId("");
      setShowSlotFormModal(false);
      await loadAdminOverview();
    }
    setAdminSlotLoading(false);
  };

  // Apaga um horario aberto da lista "aguardando alguem escolher". scope
  // "series" apaga junto todos os outros horarios em aberto do mesmo lote de
  // "Repetir semanalmente" (mesmo seriesId) -- nunca mexe em horario ja
  // escolhido, o backend recusa isso sozinho.
  const deleteAdminPastoralSlot = async (slot, scope) => {
    if (!viewerUser?.id) return;
    const confirmMessage = scope === "series"
      ? "Apagar este horário e todos os outros já abertos dessa recorrência semanal?"
      : "Apagar este horário aberto?";
    if (!window.confirm(confirmMessage)) return;
    try {
      await apiDelete(`/pastoral/slots/${encodeURIComponent(slot.id)}?viewerUserId=${encodeURIComponent(viewerUser.id)}&scope=${scope}`, {
        viewer: viewerUser
      });
      setAdminSlotFeedback(scope === "series" ? "Recorrência apagada." : "Horário apagado.");
      await loadAdminOverview();
    } catch (error) {
      setAdminSlotFeedback("Não foi possível apagar esse horário agora.");
    }
  };


  const loadNotifications = async () => {
    if (!viewerUser?.sessionToken) return;
    try {
      const user = await ensureSession();
      const data = await apiFetch(`/notifications?viewerUserId=${encodeURIComponent(user.id)}`, { method: "GET", viewer: user });
      setNotifications(Array.isArray(data?.items) ? data.items : []);
      setNotificationsUnreadCount(data?.unreadCount || 0);
    } catch (error) {
      // sininho nao pode quebrar o resto do app -- so nao atualiza desta vez
    }
  };

  const openNotification = async (notification) => {
    setShowNotificationsPanel(false);
    if (!notification.readAt) {
      try {
        const user = await ensureSession();
        await apiPatch(`/notifications/${encodeURIComponent(notification.id)}/read`, { body: { viewerUserId: user.id }, viewer: user });
        setNotifications((prev) => prev.map((item) => (item.id === notification.id ? { ...item, readAt: new Date().toISOString() } : item)));
        setNotificationsUnreadCount((prev) => Math.max(0, prev - 1));
      } catch (error) {
        // segue mesmo se marcar como lido falhar agora
      }
    }
    if (notification.type === "PASTORAL_SLOTS_OPENED" && notification.data?.pastorId) {
      setSelectedPastorId(notification.data.pastorId);
      setShowPastoralAgenda(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const requestPastoralSlotNotice = async () => {
    if (!bookingName.trim()) {
      setSlotRequestFeedback("Por favor, informe seu nome ao lado pra registrar o pedido.");
      return;
    }
    if (!selectedPastorId) {
      setSlotRequestFeedback("Escolha uma pessoa da equipe pastoral.");
      return;
    }
    setSlotRequestLoading(true);
    setSlotRequestFeedback("");
    try {
      const user = await ensureSession();
      await apiPost("/pastoral/slot-requests", {
        viewer: user,
        body: {
          pastorId: selectedPastorId,
          userId: user.id,
          userName: bookingName.trim(),
          memberEmail: profileForm.email || viewerUser?.email || undefined,
          note: bookingNote.trim() || undefined
        }
      });
      setSlotRequestFeedback("Pronto! Avisamos você assim que essa pessoa abrir um novo horário.");
    } catch (error) {
      setSlotRequestFeedback("Não foi possível registrar seu pedido agora. Tente novamente.");
    } finally {
      setSlotRequestLoading(false);
    }
  };

  const createPastoralBooking = async () => {
    if (!bookingName.trim()) {
      setBookingFeedback("Por favor, informe seu nome para solicitar atendimento.");
      return;
    }
    if (!selectedPastoralSlot) {
      setBookingFeedback("Escolha um dos horários disponibilizados pela equipe pastoral.");
      return;
    }

    setBookingLoading(true);
    setBookingFeedback("");
    try {
      const user = await ensureSession();
      const startAt = new Date(selectedPastoralSlot.startAt);
      const endAt = new Date(selectedPastoralSlot.endAt);
      const inviteNote = selectedPastoralSlot.mode === "online"
        ? `${selectedPastoralSlot.label} • Solicitação de conversa online. A equipe pastoral enviará o link após confirmar o horário. • ${bookingNote.trim() || "Sem observação"}`
        : `${selectedPastoralSlot.label} • Solicitação de conversa presencial, sem endereço residencial exposto. A equipe pastoral confirmará local e detalhes. • ${bookingNote.trim() || "Sem observação"}`;

      if (selectedPastoralSlot.pastorId.startsWith("local-")) {
        const localBooking = {
          id: `local-booking-${Date.now()}`,
          pastor: { id: selectedPastoralSlot.pastorId, name: selectedPastoralSlot.pastorName },
          startAt: startAt.toISOString(),
          endAt: endAt.toISOString(),
          status: "PENDING",
          isOwner: true,
          note: inviteNote
        };
        setBookings((prev) => [localBooking, ...prev]);
        setBookingFeedback(selectedPastoralSlot.mode === "online"
          ? "Pedido recebido. A equipe pastoral confirmará o horário e enviará o link da conversa."
          : "Pedido recebido. A equipe pastoral confirmará o horário, local e detalhes da conversa.");
        setSelectedPastoralSlotId("");
        setBookingNote("");
        return;
      }

      try {
        await apiPost("/pastoral/bookings", {
          viewer: user,
          body: {
            pastorId: selectedPastoralSlot.pastorId,
            ...(selectedPastoralSlot.source === "api" ? { slotId: selectedPastoralSlot.id } : {}),
            userId: user.id,
            userName: bookingName.trim(),
            memberEmail: profileForm.email || viewerUser?.email || undefined,
            startAt: startAt.toISOString(),
            endAt: endAt.toISOString(),
            durationMinutes: selectedPastoralSlot.duration,
            mode: selectedPastoralSlot.mode === "presencial" ? "PRESENTIAL" : "ONLINE",
            locationNote: selectedPastoralSlot.locationNote || undefined,
            note: inviteNote
          }
        });
      } catch (bookingError) {
        if (bookingError instanceof ApiError && bookingError.status === 409) {
          setBookingFeedback("Esse horário já foi reservado. Escolha outro horário disponível.");
          return;
        }
        throw bookingError;
      }
      setBookingFeedback(selectedPastoralSlot.mode === "online"
        ? "Pedido enviado. A equipe pastoral confirmará o horário e enviará o link da conversa."
        : "Pedido enviado. A equipe pastoral confirmará o horário, local e detalhes da conversa.");
      setSelectedPastoralSlotId("");
      setBookingNote("");
      await loadBookings();
    } catch (error) {
      setBookingFeedback("Não foi possível concluir o pedido agora. Tente novamente.");
    } finally {
      setBookingLoading(false);
    }
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      if (!viewerUser?.id) {
        setBookingFeedback("Sua sessão expirou. Recarregue a página e tente novamente.");
        return;
      }
      await apiPatch(`/pastoral/bookings/${bookingId}/status`, {
        viewer: viewerUser,
        body: { status, viewerUserId: viewerUser.id }
      });
      await loadBookings();
    } catch (error) {
      setBookingFeedback("Não foi possível atualizar o status neste momento.");
    }
  };

  // Uma conversa de acolhimento "vencida" e uma que continua aguardando
  // resposta (PENDING) com a data/hora ja no passado -- precisa de acao da
  // lideranca (cancelar ou reagendar), ja que ela nunca muda de status
  // sozinha.
  const isBookingOverdue = (item) => item.status === "PENDING" && new Date(item.startAt).getTime() < Date.now();

  const updateAdminBookingStatus = async (bookingId, status) => {
    if (!viewerUser?.id) return;
    if (status === "CANCELLED" && !window.confirm("Cancelar esta conversa de acolhimento?")) return;
    try {
      await apiPatch(`/pastoral/bookings/${encodeURIComponent(bookingId)}/status`, {
        viewer: viewerUser,
        body: { status, viewerUserId: viewerUser.id }
      });
      setAdminBookingActionFeedback(status === "CANCELLED" ? "Conversa cancelada." : "Status atualizado.");
      await loadAdminOverview();
    } catch (error) {
      setAdminBookingActionFeedback("Não foi possível atualizar agora.");
    }
  };

  const openRescheduleBooking = (item) => {
    const startDate = new Date(item.startAt);
    setRescheduleBookingId(item.id);
    setRescheduleDate(formatDateForInput(startDate));
    setRescheduleTime(startDate.toTimeString().slice(0, 5));
  };

  const submitRescheduleBooking = async () => {
    if (!viewerUser?.id || !rescheduleBookingId || !rescheduleDate || !rescheduleTime) return;
    try {
      const startAt = new Date(`${rescheduleDate}T${rescheduleTime}:00`);
      await apiPatch(`/pastoral/bookings/${encodeURIComponent(rescheduleBookingId)}`, {
        viewer: viewerUser,
        body: { viewerUserId: viewerUser.id, startAt: startAt.toISOString() }
      });
      setAdminBookingActionFeedback("Conversa reagendada -- volta a aguardar confirmação.");
      setRescheduleBookingId("");
      setRescheduleDate("");
      setRescheduleTime("");
      await loadAdminOverview();
    } catch (error) {
      setAdminBookingActionFeedback("Não foi possível reagendar agora.");
    }
  };

  const loadTrailsData = async () => {
    setTrailsLoading(true);
    setTrailsError("");
    const localCatalog = getLocalTrailCatalog();
    try {
      const data = await apiFetch("/trails", { method: "GET" });

      const normalized = localCatalog.map((localTrail, index) => {
        const trail = data.find((item) => item.slug === localTrail.slug || item.slug === localTrail.id || item.title === localTrail.title);
        return {
          ...localTrail,
          remoteId: trail?.id || null,
          slug: localTrail.slug || trail?.slug || localTrail.id,
          title: localTrail.title,
          subtitle: localTrail.subtitle || trail?.subtitle || "",
          duration: localTrail.devotionals?.length || localTrail.duration || trail?.devotionals?.length || 0,
          progress: 0,
          started: false,
          color: localTrail.color || TRAILS[index % TRAILS.length]?.color || "#0EA5E9",
          icon: localTrail.icon || TRAILS[index % TRAILS.length]?.icon || "Home"
        };
      });

      let normalizedWithProgress = normalized;
      let loadedRemoteProgress = false;
      let remoteSessionInvalid = false;
      if (canUseSavedProgress) {
        try {
          const progressData = await apiFetch(`/trails/progress?userId=${encodeURIComponent(viewerUser.id)}`, {
            method: "GET",
            viewer: viewerUser
          });
          const trailProgress = progressData?.trailProgress || {};
          const trailStartedSlugs = new Set(Array.isArray(progressData?.trailStarted) ? progressData.trailStarted : []);
          normalizedWithProgress = normalized.map((trail) => ({
            ...trail,
            progress: Math.min(Math.max(Number(trailProgress[trail.slug]) || 0, 0), Math.max(0, trail.duration || 0)),
            started: trailStartedSlugs.has(trail.slug)
          }));
          loadedRemoteProgress = true;
          if (Array.isArray(progressData?.weeklyCompletionMap) && progressData.weeklyCompletionMap.length === 7) {
            setWeeklyCompletionMapApi(progressData.weeklyCompletionMap);
          } else {
            setWeeklyCompletionMapApi(null);
          }
        } catch (error) {
          if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
            remoteSessionInvalid = true;
            clearViewerSession("Sua sessão expirou. Entre novamente para continuar.");
          }
          setWeeklyCompletionMapApi(null);
        }
      } else {
        setWeeklyCompletionMapApi(null);
      }

      if (normalizedWithProgress.length > 0) {
        setTrails(loadedRemoteProgress || remoteSessionInvalid ? normalizedWithProgress : applyUserProgressToTrails(normalizedWithProgress, canUseSavedProgress ? viewerUser?.id : null));
      } else {
        setTrails(remoteSessionInvalid ? localCatalog : applyUserProgressToTrails(localCatalog, canUseSavedProgress ? viewerUser?.id : null));
      }
    } catch (error) {
      setTrails(applyUserProgressToTrails(localCatalog, canUseSavedProgress ? viewerUser?.id : null));
      setTrailsError("Não foi possível atualizar trilhas agora.");
    } finally {
      setTrailsLoading(false);
    }
  };

  // Dispara o carregamento (em segundo plano, sem travar a tela de login) do
  // conteudo pesado das trilhas curadas (~843KB) que antes vinha embutido no
  // bundle principal. Quando termina, incrementa curatedTrailsVersion para
  // que o efeito abaixo recalcule o catalogo de trilhas com o conteudo cheio.
  const [curatedTrailsVersion, setCuratedTrailsVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    ensureCuratedTrailsLoaded().then(() => {
      if (!cancelled) setCuratedTrailsVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    loadTrailsData();
  }, [canUseSavedProgress, viewerUser?.id, curatedTrailsVersion]);

  useEffect(() => {
    if (planosModeAutoSetRef.current || trailsLoading) return;
    planosModeAutoSetRef.current = true;
    if (inProgressTrails.length > 0 || completedTrails.length > 0) setPlanosMode("meus");
  }, [trailsLoading, inProgressTrails.length, completedTrails.length]);

  // Deep-link publico para /familia/trilhas/<slug>: a maioria das trilhas so
  // existe em TRAILS depois que ensureCuratedTrailsLoaded() resolve (ver useEffect
  // acima). Quando isso acontece, resolve de novo -- IMPORTANTE: mesmo para as
  // ~5 trilhas que ja existiam como stub (plataforma, vida-interior,
  // igreja-mesa, proposito, reino-publico) e por isso resolviam sincronamente
  // no primeiro render, o merge troca esse objeto por uma versao mais rica
  // (descricao completa) -- sem reexecutar aqui, a pagina ficava presa no
  // texto curto do stub. Se ainda assim nao encontrar (slug invalido) depois
  // do carregamento, volta pra tela "main" em vez de travar exibindo
  // "Carregando trilha..." pra sempre.
  useEffect(() => {
    if (screen !== "trail-landing") return;
    const slug = getInitialFamiliaTrailSlugFromUrl();
    const found = findTrailBySlug(TRAILS, slug);
    if (found) {
      setSelectedTrailLanding(found);
    } else if (curatedTrailsVersion > 0) {
      setScreen("main");
    }
  }, [curatedTrailsVersion, screen]);

  // --- BOTTOM NAV BAR ---
  const renderBottomNav = () => (
    <>
      {pendingInvite && (
        <div style={{ position: "fixed", inset: 0, zIndex: 260, background: "rgba(2,6,23,0.76)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          <div style={{ width: "100%", maxWidth: 520, border: "1px solid rgba(230,177,90,0.42)", background: "linear-gradient(145deg, rgba(20,27,39,0.98), rgba(11,18,32,0.98))", padding: isTablet ? 28 : 22, color: "#fff", boxShadow: "0 24px 60px rgba(0,0,0,0.38)" }}>
            <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: "#E6B15A", fontWeight: 950 }}>Convite para caminhar junto</p>
            <h3 style={{ margin: 0, fontSize: isTablet ? 28 : 24, lineHeight: 1.08, fontWeight: 950 }}>{pendingInvite.inviter?.name || "Alguém"} te chamou para uma trilha</h3>
            <p style={{ margin: "14px 0 0", color: "#c7d1e4", fontSize: 14, lineHeight: 1.6 }}>
              {pendingInvite.trail?.title || "Trilha devocional"} · {pendingInvite.inviteeName ? `convite para ${pendingInvite.inviteeName}` : "convite aberto"}
            </p>
            {pendingInvite.message && <p style={{ margin: "16px 0 0", padding: 14, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.045)", color: "#E5EAF3", fontSize: 13, lineHeight: 1.55 }}>"{pendingInvite.message}"</p>}
            <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr auto" : "1fr", gap: 10, marginTop: 22 }}>
              <button onClick={acceptPendingInvite} style={{ ...buttonReset, minHeight: 48, padding: "0 18px", border: "1px solid rgba(230,177,90,0.72)", background: "#E6B15A", color: "#111827", fontSize: 13, fontWeight: 950 }}>
                Entrar nessa caminhada
              </button>
              <button onClick={() => setPendingInvite(null)} style={{ ...buttonReset, minHeight: 48, padding: "0 18px", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.045)", color: "#cbd5e1", fontSize: 12, fontWeight: 900 }}>
                Agora não
              </button>
            </div>
            {pendingInviteFeedback && <p style={{ margin: "12px 0 0", color: pendingInviteFeedback.includes("Não") ? "#FCA5A5" : "#A7F3D0", fontSize: 12, lineHeight: 1.45 }}>{pendingInviteFeedback}</p>}
          </div>
        </div>
      )}
      {showPrivacyPolicy && (
        <div style={{ position: "fixed", inset: 0, zIndex: 270, background: "rgba(2,6,23,0.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: 18 }}>
          <div style={{ width: "100%", maxWidth: 620, maxHeight: "84vh", overflowY: "auto", border: "1px solid rgba(125,211,252,0.36)", background: "linear-gradient(145deg, rgba(18,29,48,0.98), rgba(9,17,31,0.98))", padding: isTablet ? 28 : 22, color: "#fff", boxShadow: "0 24px 60px rgba(0,0,0,0.42)" }}>
            <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.5, textTransform: "uppercase", color: "#7DD3FC", fontWeight: 950 }}>Privacidade</p>
            <h3 style={{ margin: 0, fontSize: isTablet ? 28 : 24, lineHeight: 1.08, fontWeight: 950 }}>Como cuidamos dos seus dados</h3>
            <p style={{ margin: "14px 0 0", color: "#c7d1e4", fontSize: 14, lineHeight: 1.65 }}>
              A Família Mosaico usa seu cadastro para liberar acesso ao app, acompanhar sua caminhada devocional, organizar convites, leitura de e-books e solicitações de conversa pastoral.
            </p>
            {/* Pedido do Rodrigo (revisao LGPD, "para por na fila, esta politica
                esta ok? de acordo com a lgpd?"): a versao anterior nao dizia quem
                e o controlador dos dados, nao citava base legal nem prazo de
                guarda, nao listava os direitos do titular (portabilidade,
                revogacao) e -- o ponto mais sensivel -- nunca mencionava que o
                cadastro de crianca (CadastroCrianca.jsx / childEditorForm aqui
                no app) coleta dados de saude (alergia, medicacao, observacoes)
                mesmo a politica tratando so de dados "de cadastro". Isto cobre
                essas lacunas, mas e um rascunho tecnico, nao e parecer juridico
                -- antes de tratar como versao final, revisar com um advogado
                especialista em LGPD, principalmente a parte de dado de saude de
                crianca (Art. 11 e 14 da LGPD tem regra mais rigida pra isso). */}
            <div style={{ display: "grid", gap: 12, marginTop: 18 }}>
              {[
                ["Quem é o responsável (controlador)", "A Igreja Batista Mosaico, em Osasco/SP, é quem decide como seus dados são usados neste app. Para qualquer dúvida ou pedido sobre privacidade, escreva para contato@batistamosaico.com.br."],
                ["Dados coletados", "Nome, e-mail, WhatsApp, cidade, estado, data de nascimento, vínculo com a igreja, avatar escolhido e registros criados por você no app. No cadastro de crianças, também coletamos dados de saúde informados pelo responsável (alergias, medicações, observações importantes) e a autorização de uso de imagem — usados só pela equipe responsável pelo cuidado das crianças nas atividades da igreja."],
                ["Base legal e finalidade", "Usamos seus dados com base no consentimento que você dá ao aceitar esta política, e no interesse legítimo da igreja em organizar o cuidado pastoral e as atividades. Dados de saúde de uma criança só são tratados com o consentimento específico do responsável, e só para a segurança e o cuidado da criança nas atividades da igreja."],
                ["Uso pastoral", "Pedidos marcados como pastorais ficam restritos à liderança responsável pelo cuidado. Pedidos no mural aparecem para a comunidade conforme a visibilidade escolhida."],
                ["Compartilhamento", "Não vendemos dados. Convites por WhatsApp dependem do seu envio pelo aplicativo do WhatsApp. Links de convite podem ser abertos por quem receber o link."],
                ["Por quanto tempo guardamos", "Seus dados ficam guardados enquanto seu cadastro estiver ativo no app. Dados de saúde de uma criança ficam guardados só enquanto ela participa das atividades da igreja, e podem ser apagados a pedido do responsável a qualquer momento."],
                ["Seus direitos", "A qualquer momento, você (ou o responsável por uma criança cadastrada) pode pedir: confirmação dos dados que temos, acesso, correção, anonimização ou exclusão, uma cópia dos seus dados (portabilidade) e a revogação do consentimento dado aqui. É só escrever para contato@batistamosaico.com.br ou falar com a liderança da igreja."],
                ["Versão", "Política em revisão com apoio jurídico, versão 2026-10."]
              ].map(([title, text]) => (
                <div key={title} style={{ border: "1px solid rgba(148,163,184,0.18)", background: "rgba(255,255,255,0.035)", padding: 13 }}>
                  <p style={{ margin: "0 0 5px", color: "#F8FAFC", fontSize: 13, fontWeight: 900 }}>{title}</p>
                  <p style={{ margin: 0, color: "#aebbd1", fontSize: 12, lineHeight: 1.55 }}>{text}</p>
                </div>
              ))}
            </div>
            <button onClick={() => setShowPrivacyPolicy(false)} style={{ ...buttonReset, width: "100%", minHeight: 46, marginTop: 18, border: "1px solid rgba(125,211,252,0.42)", background: "rgba(14,165,233,0.14)", color: "#BAE6FD", fontSize: 13, fontWeight: 950 }}>
              Entendi
            </button>
          </div>
        </div>
      )}
      {/* No mobile, transform ficava "none" -- sem uma layer de compositing
          propria, o WebKit (Safari iOS) as vezes atrasa o repaint desta barra
          fixed+backdrop-filter durante o scroll, fazendo ela "descolar" e
          aparecer no meio da tela por um instante antes de voltar pro lugar
          (bug conhecido de position:fixed + backdrop-filter no iOS). No
          desktop isso nao acontecia por acidente, ja que translateX(-50%)
          pra centralizar ja promovia a barra pra propria layer. Adicionar
          translateZ(0) tambem no mobile forca a mesma promocao. */}
      <div style={{ position: "fixed", bottom: 0, left: isDesktop ? "50%" : 0, transform: isDesktop ? "translateX(-50%) translateZ(0)" : "translateZ(0)", WebkitTransform: isDesktop ? "translateX(-50%) translateZ(0)" : "translateZ(0)", willChange: "transform", right: isDesktop ? "auto" : 0, width: isDesktop ? 1180 : "auto", background: chromeSurface, backdropFilter: "blur(14px)", borderTop: `1px solid ${chromeStroke}`, borderRadius: isDesktop ? "12px 12px 0 0" : 0, display: "flex", paddingBottom: "env(safe-area-inset-bottom)", zIndex: 100, boxShadow: "0 -10px 24px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.035)", boxSizing: "border-box", overflowX: "auto" }}>
        {(() => {
          const bottomNavTabs = [
            { id: "home", label: "Hoje", icon: <Home size={22} strokeWidth={1.5} /> },
            { id: "library", label: "Planos", icon: <BookOpen size={22} strokeWidth={1.5} /> },
            { id: "ebooks", label: "E-books", icon: <BookMarked size={22} strokeWidth={1.5} /> },
            { id: "family", label: "Mesa", icon: <Users size={22} strokeWidth={1.5} /> },
            ...(isCareManager ? [{ id: "conecta", label: "Agenda de cuidado", icon: <MessageSquare size={22} strokeWidth={1.5} /> }] : []),
            // Esse item do menu de baixo (mobile) ficava mostrando "Repertório"
            // enquanto o menu de topo (desktop, toplinks) e o
            // InstitutionalLayout.jsx ja' tinham trocado pra "Escala" em
            // 18-20/10/2026 -- Rodrigo reportou a inconsistencia em
            // 21/10/2026 ("o menu abaixo ta repertorio, n era para ser
            // escala"). Trocado aqui tambem, com o canSeeEscala (mais
            // abrangente que canSeeRepertorio, cobre isMusician da Person)
            // e apontando pra /admin/escalas como as outras duas copias.
            ...(canSeeEscala ? [{ id: "escala", label: "Escala", icon: <CalendarCheck2 size={22} strokeWidth={1.5} />, to: "/admin/escalas" }] : []),
            { id: "profile", label: "Perfil", icon: <User size={22} strokeWidth={1.5} /> }
          ];
          const isDense = isMobile && bottomNavTabs.length > 5;
          const navItemStyle = { ...buttonReset, flex: isDense ? `0 0 ${(100 / bottomNavTabs.length).toFixed(2)}%` : 1, minWidth: isDense ? 58 : 0, display: "flex", flexDirection: "column", alignItems: "center", padding: isDense ? "10px 0" : "12px 0", transition: "color 0.2s ease" };
          const navLabelStyle = { fontSize: isDense ? 9 : 10, marginTop: 4, fontWeight: 600, maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" };
          return bottomNavTabs.map(tab => (
            tab.to ? (
              <NavLink
                key={tab.id}
                to={tab.to}
                onClick={() => window.scrollTo(0, 0)}
                style={({ isActive }) => ({ ...navItemStyle, color: isActive ? "#ffd38f" : "#c7ced9", textDecoration: "none" })}
              >
                {tab.icon}
                <span style={navLabelStyle}>{tab.label}</span>
              </NavLink>
            ) : tab.href ? (
              <a
                key={tab.id}
                href={tab.href}
                style={{ ...navItemStyle, color: "#c7ced9", textDecoration: "none" }}
              >
                {tab.icon}
                <span style={navLabelStyle}>{tab.label}</span>
              </a>
            ) : (
              <button
                key={tab.id}
                onClick={() => { setScreen("main"); setActiveTab(tab.id); window.scrollTo(0,0); }}
                style={{ ...navItemStyle, color: activeTab === tab.id ? "#ffd38f" : "#c7ced9" }}
              >
                {tab.icon}
                <span style={navLabelStyle}>{tab.label}</span>
              </button>
            )
          ));
        })()}
      </div>
    </>
  );

  // --- MAIN SCREEN CONTENT (Depends on activeTab) ---
  if (screen === "main") {
    return (
      <div style={containerStyle}>
        
        {/* TOP HEADER -- logo+nav na esquerda, avatar/saudacao (ou Entrar) na
            direita, mesmo padrao do topbar institucional e do /financeiro. */}
        {/* Reaproveita literalmente as classes .topbar/.toplinks/.navlink do
            institutional.css (bundle unico, CSS ja carregado em qualquer rota)
            em vez de duplicar valores em style inline -- as duas barras
            renderizam a partir da MESMA regra CSS agora, entao ficam 100%
            identicas (inclusive hover e o anel de foco em box-shadow) sem
            risco de as duas copias divergirem de novo no futuro. */}
        <div className="topbar">
          <NavLink to="/" aria-label="Mosaico — início" style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
            <img src="/institucional/logo-mosaico-wordmark.png" alt="Igreja Batista Mosaico — uma igreja, uma família, um propósito" style={{ height: 38, width: "auto", maxWidth: 220, objectFit: "contain", display: "block", flexShrink: 0 }} />
          </NavLink>
          {isDesktop && (
            // Mesmos links do topbar institucional (InstitutionalLayout.jsx) --
            // barra unica de navegacao, acessivel tambem de dentro do app.
            // Fica como filho direto do flex space-between (irmao da logo e do
            // bloco da direita), igual ao .topbar institucional, pra centralizar
            // o menu na barra -- antes estava dentro do mesmo <div> da logo e
            // ficava colado a ela, sem centralizar.
            <nav className="toplinks" aria-label="Navegação do site">
              {[
                { to: "/", label: "Início", end: true },
                { to: "/sobre", label: "Sobre" },
                { to: "/ministerios", label: "Ministérios" },
                { to: "/pregacoes", label: "Vídeos" },
                { to: "/eventos", label: "Eventos" },
                { to: "/familia", label: "Devocionais" },
                { to: "/contato", label: "Contato" }
              ].map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) => "navlink" + (isActive ? " active" : "")}
                >
                  {item.label}
                </NavLink>
              ))}
              {/* /financeiro é um site estático separado (public/financeiro/index.html),
                  fora do roteamento do React Router -- precisa ser um <a> de
                  verdade (navegação de página inteira), não um NavLink.
                  Aparece pra qualquer pessoa logada (ver comentário de
                  canSeeFinanceiro acima). */}
              {canSeeFinanceiro && (
                <a href="/financeiro" className="navlink">
                  Financeiro
                </a>
              )}
              {/* 20/10/2026 (Rodrigo): aqui no topo (desktop) ficava duplicado com
                  o "Repertório" que ja' existe no menu de baixo (bottomNavTabs,
                  pra quem acessa pelo celular). O InstitutionalLayout.jsx ja'
                  tinha trocado esse mesmo lugar por "Escala" em 18/10/2026 --
                  essa copia aqui dentro do app (mesmo CSS .toplinks/.navlink,
                  ver comentario acima) tinha ficado pra tras na troca. Mesmo
                  gate de acesso (canSeeRepertorio == ADMIN/MUSICO) que o
                  InstitutionalLayout usa em canSeeEscala. */}
              {canSeeRepertorio && (
                <NavLink to="/admin/escalas" className={({ isActive }) => "navlink" + (isActive ? " active" : "")}>
                  Escala
                </NavLink>
              )}
            </nav>
          )}
          <div style={{ display: "flex", alignItems: "center", gap: 9, flexShrink: 0, minWidth: 0 }}>
            {viewerUser?.sessionToken ? (
              <div style={{ display: "flex", alignItems: "center", gap: 9, minWidth: 0 }}>
                <div style={{ minWidth: 0, display: isMobile ? "none" : "block" }}>
                  <p style={{ margin: 0, fontSize: 10.5, color: "#b8c0cd", textTransform: "uppercase", letterSpacing: 0.8 }}>{userHeaderEyebrow}</p>
                  <p style={{ margin: 0, fontSize: 13, color: "#f7f9fe", fontWeight: 700, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 160 }}>{userDisplayName}</p>
                </div>
                <div style={{ position: "relative", flexShrink: 0 }}>
                  <button
                    onClick={() => setShowNotificationsPanel((prev) => !prev)}
                    aria-label="Avisos"
                    title="Avisos"
                    style={{ ...buttonReset, width: 36, height: 36, borderRadius: 18, background: "#26282d", border: showNotificationsPanel ? "1px solid rgba(94,234,212,0.72)" : "1px solid rgba(255,255,255,0.14)", display: "flex", alignItems: "center", justifyContent: "center", color: "#C7D1E4", position: "relative" }}
                  >
                    <Bell size={16} />
                    {notificationsUnreadCount > 0 && (
                      <span style={{ position: "absolute", top: -2, right: -2, minWidth: 16, height: 16, borderRadius: 8, background: "#D946EF", color: "#111827", fontSize: 9, fontWeight: 950, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 3px", border: "1px solid #0f172a" }}>
                        {notificationsUnreadCount > 9 ? "9+" : notificationsUnreadCount}
                      </span>
                    )}
                  </button>
                  {showNotificationsPanel && (
                    <div style={{ position: "absolute", top: 44, right: 0, width: 300, maxHeight: 360, overflowY: "auto", border: "1px solid rgba(255,255,255,0.14)", background: "#15171c", borderRadius: 12, boxShadow: "0 18px 40px rgba(0,0,0,0.5)", zIndex: 260, padding: 8 }}>
                      <p style={{ margin: "4px 8px 8px", color: "#9fb0cc", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 900 }}>Avisos</p>
                      {notifications.length === 0 && <p style={{ margin: "8px", color: "#8FA0BF", fontSize: 12 }}>Nenhum aviso por enquanto.</p>}
                      {notifications.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => openNotification(item)}
                          style={{ ...buttonReset, display: "block", width: "100%", textAlign: "left", padding: 10, borderRadius: 8, marginBottom: 4, background: item.readAt ? "transparent" : "rgba(217,70,239,0.10)", border: "1px solid rgba(255,255,255,0.06)" }}
                        >
                          <p style={{ margin: 0, color: "#F8FAFC", fontSize: 12, fontWeight: item.readAt ? 700 : 950 }}>{item.title}</p>
                          {item.body && <p style={{ margin: "3px 0 0", color: "#AEBBD1", fontSize: 11, lineHeight: 1.4 }}>{item.body}</p>}
                          <p style={{ margin: "4px 0 0", color: "#6B7A94", fontSize: 10 }}>{new Date(item.createdAt).toLocaleString("pt-BR")}</p>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setActiveTab("profile");
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                  aria-label={`Perfil de ${userDisplayName}`}
                  title="Perfil"
                  style={{
                    ...buttonReset,
                    width: 36,
                    height: 36,
                    borderRadius: 18,
                    background: "#26282d",
                    border: activeTab === "profile" ? "1px solid rgba(245,210,139,0.72)" : "1px solid rgba(245,210,139,0.28)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    fontSize: 12,
                    color: "#f3d7a8",
                    fontWeight: 900,
                    flexShrink: 0
                  }}
                >
                  {profilePhotoUrl ? (
                    <img src={profilePhotoUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  ) : (
                    profileInitials
                  )}
                </button>
              </div>
            ) : (
              <button
                onClick={() => {
                  setActiveTab("profile");
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                style={{
                  ...buttonReset,
                  minHeight: isMobile ? 40 : 42,
                  padding: isMobile ? "0 12px" : "0 14px",
                  borderRadius: 999,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  border: "1px solid rgba(245,210,139,0.28)",
                  background: "linear-gradient(135deg, #F5D28B, #E6B15A)",
                  color: "#171a22",
                  boxShadow: "0 10px 22px rgba(230,177,90,0.18)",
                  fontSize: 12,
                  fontWeight: 950,
                  whiteSpace: "nowrap"
                }}
              >
                <LockKeyhole size={15} strokeWidth={2.5} />
                Entrar
              </button>
            )}
          </div>
        </div>

        {/* Banner "complete/revise seu cadastro de membro" -- aparece em
            qualquer aba (nao so no Perfil), pra pessoa logada sem Member
            vinculado ou com Member desatualizado (>365 dias). Ver
            showSelfCadastroBanner mais acima. */}
        {showSelfCadastroBanner && (
          <div style={{ margin: "10px 16px 0", padding: "12px 14px", borderRadius: 16, border: "1px solid rgba(230,177,90,0.38)", background: "linear-gradient(145deg, rgba(230,177,90,0.16), rgba(15,23,42,0.92))", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", boxShadow: "0 12px 24px rgba(0,0,0,0.22)" }}>
            <p style={{ margin: 0, fontSize: 12.5, lineHeight: 1.5, color: "#f3e7cf", flex: "1 1 220px", minWidth: 0 }}>
              {myMember ? "Já faz mais de um ano do seu cadastro — reveja e atualize seus dados." : "Complete seu cadastro de membro na Mosaico."}
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
              <button
                onClick={() => { setActiveTab("profile"); window.scrollTo({ top: 0, behavior: "smooth" }); setTimeout(openMemberEditor, 60); }}
                style={{ ...buttonReset, minHeight: 34, padding: "0 14px", borderRadius: 999, background: "#E6B15A", color: "#171a22", fontSize: 12, fontWeight: 950, whiteSpace: "nowrap" }}
              >
                {myMember ? "Revisar cadastro" : "Completar cadastro"}
              </button>
              <button onClick={() => setSelfCadastroBannerDismissed(true)} aria-label="Fechar aviso" style={{ ...buttonReset, width: 28, height: 28, borderRadius: 14, color: "#f3e7cf", fontSize: 15, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>
          </div>
        )}

        <div style={pageContentStyle}>
          
          {/* TAB: HOME */}
          {activeTab === "home" && (
            <div style={{ ...overflowGuardStyle, paddingBottom: 18 }}>
              {/* Padding/border-radius alinhados ao .hero do institucional (institutional.css)
                  -- essa secao usava 36px/38px e cantos de 38px, um pouco diferente dos 34px/28px
                  do institucional, sem motivo (nao e conteudo especifico, e' so o "chrome" do
                  card). O h1 tambem usava um fontSize proprio (54/39/31) em vez da constante
                  mobileHeroTitleSize (48/38/30) ja usada nos outros 5 titulos de hero do app
                  (linhas com "Caminhos para criar constancia...", "Um lugar para ler...", etc.)
                  -- essas duas diferencas juntas faziam esse banner ficar uns 20px mais alto que
                  o hero do Inicio ao navegar entre as duas paginas. */}
              <section style={{ position: "relative", ...overflowGuardStyle, minHeight: isDesktop ? 360 : isTablet ? 430 : "auto", padding: isDesktop ? "34px 28px" : isTablet ? "28px 22px" : "24px 20px", borderRadius: isDesktop ? 28 : isTablet ? 30 : 24, border: "1px solid rgba(255,255,255,0.28)", background: "linear-gradient(135deg, rgba(248,243,232,0.99) 0%, rgba(231,238,239,0.98) 48%, rgba(244,224,194,0.96) 100%), linear-gradient(110deg, rgba(198,107,78,0.10), transparent 42%)", color: "#182033", boxShadow: "0 22px 54px rgba(0,0,0,0.34)", marginBottom: 22 }}>
                <div style={{ position: "relative", display: isDesktop ? "grid" : "block", gridTemplateColumns: "minmax(0, 1.1fr) minmax(300px, 0.9fr)", gap: 28, alignItems: "center" }}>
                  <div>
                    {heroJourneyLoading ? (
                      // Skeleton enquanto ainda nao sabemos se a pessoa tem uma
                      // mesa/convite em andamento -- evita mostrar o texto de
                      // "comecar do zero" e um instante depois trocar pro texto
                      // de "continuar" (ou vice-versa), que e' o "pulo" visual
                      // que a Carla reparou ao entrar em Devocionais (26/10/2026).
                      <div aria-hidden="true">
                        <div style={{ height: 11, width: 120, borderRadius: 6, background: "rgba(21,27,42,0.12)", marginBottom: 16 }} />
                        <div style={{ height: mobileHeroTitleSize, maxWidth: 520, borderRadius: 10, background: "rgba(21,27,42,0.12)", marginBottom: 14 }} />
                        <div style={{ height: 14, maxWidth: 460, borderRadius: 7, background: "rgba(21,27,42,0.09)", marginTop: 18, marginBottom: 8 }} />
                        <div style={{ height: 14, maxWidth: 340, borderRadius: 7, background: "rgba(21,27,42,0.09)" }} />
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 24 }}>
                          <div style={{ height: 50, width: 190, borderRadius: 999, background: "rgba(21,27,42,0.10)" }} />
                          <div style={{ height: 50, width: 150, borderRadius: 999, background: "rgba(21,27,42,0.06)" }} />
                        </div>
                      </div>
                    ) : (
                      <>
                        <p style={{ margin: "0 0 12px", fontSize: 11, color: "#87582b", letterSpacing: 1.6, textTransform: "uppercase", fontWeight: 900 }}>{hasActiveJourney ? "Sua rotina" : "Rotina devocional"}</p>
                        <h1 style={{ margin: 0, maxWidth: 660, fontSize: mobileHeroTitleSize, lineHeight: 1.02, letterSpacing: 0, color: "#151b2a", fontWeight: 900 }}>
                          {hasActiveJourney ? `Continue em "${homeModel.currentTrail?.title || "sua trilha"}".` : "Uma rotina com Deus, perto ou à distância."}
                        </h1>
                        <p style={{ margin: "18px 0 0", maxWidth: 560, fontSize: isMobile ? 15 : 17, lineHeight: 1.58, color: "#425064" }}>
                          {hasActiveJourney
                            ? "Você já tem uma mesa aberta ou um convite em andamento — retome de onde parou, ou comece um novo ritmo quando quiser."
                            : "Escolha um ritmo, separe um tempo e caminhe no secreto, em dupla ou em uma mesa. Um dia de cada vez, com leitura, reflexão e prática."}
                        </p>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, marginTop: 24 }}>
                          <button
                            onClick={() => {
                              trackEvent("devotional_start", { source: "home_hero" });
                              // "Continuar meu momento" tem que RETOMAR a trilha que a pessoa ja
                              // esta lendo, nao abrir de novo o guia de "qual seu momento hoje"
                              // (isso so' faz sentido pra quem ainda nao comecou nada -- botao
                              // "Comecar meu momento"). Antes os dois caminhos abriam o mesmo
                              // modal mesmo com hasActiveJourney=true, o que nao batia com o
                              // texto do botao (Carla reparou, 26/10/2026).
                              if (hasActiveJourney && homeModel.currentTrail?.id) {
                                openTrail(homeModel.currentTrail.id);
                              } else {
                                setShowMomentGuide(true);
                              }
                            }}
                            style={{ ...buttonReset, minHeight: 50, padding: "0 20px", borderRadius: 999, background: "#151b2a", color: "#fff8eb", fontSize: 13, fontWeight: 900, boxShadow: "0 14px 24px rgba(21,27,42,0.24)" }}
                          >
                            {hasActiveJourney ? "Continuar meu momento" : "Começar meu momento"}
                          </button>
                          <button onClick={openInviteStart} style={{ ...buttonReset, minHeight: 50, padding: "0 20px", borderRadius: 999, background: "rgba(255,255,255,0.58)", border: "1px solid rgba(21,27,42,0.18)", color: "#151b2a", fontSize: 13, fontWeight: 900 }}>
                            Convidar alguém
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  <div style={{ marginTop: isDesktop ? 0 : 28, position: "relative" }}>
                    <button onClick={() => hasCompletedProfile ? (selectedMomentGuideChoice ? openGuidedMoment(selectedMomentGuideChoice, "trail") : setShowMomentGuide(true)) : requestProfileAccess("general")} style={{ ...buttonReset, width: "100%", borderRadius: 999, padding: 18, background: "rgba(255,255,255,0.48)", border: "1px solid rgba(21,27,42,0.12)", boxShadow: "0 18px 34px rgba(73,83,100,0.16)", textAlign: "center", cursor: "pointer" }}>
                      <div style={{ borderRadius: 999, minHeight: 230, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: 26, background: "radial-gradient(circle at 50% 0%, rgba(255,255,255,0.92), rgba(255,255,255,0.42))" }}>
                        <p style={{ margin: 0, fontSize: 11, color: "#87582b", textTransform: "uppercase", letterSpacing: 1.4, fontWeight: 900 }}>{hasCompletedProfile ? "Hoje" : "Primeiro passo"}</p>
                        <p style={{ margin: "12px 0 0", fontSize: hasCompletedProfile ? 34 : 31, lineHeight: 1, color: "#151b2a", fontWeight: 900 }}>{hasCompletedProfile ? (selectedMomentGuideChoice?.trail?.title || homeModel.currentTrail?.title || "CASA") : "Comece sua caminhada"}</p>
                        <p style={{ margin: "10px 0 0", fontSize: 13, lineHeight: 1.5, color: "#556171" }}>{hasCompletedProfile ? (selectedMomentGuideChoice?.text || homeModel.currentTrail?.subtitle || "Reaprendendo a ser Igreja") : "Crie seu acesso para guardar leituras, receber sugestões e caminhar com outras pessoas."}</p>
                        {/* Pedido do Rodrigo (revisao UX /ux-ui, 3o problema critico):
                            aqui tinha uma fileira de 3 pilulas "Sozinho/Dupla/Grupo"
                            (so decorativas, sem onClick) que duplicava, palavra por
                            palavra, a secao logo abaixo ("Escolha a forma da
                            caminhada" / "O mesmo devocional, tres ritmos.", com os 3
                            cards cheios de icone+descricao+CTA). Removida daqui --
                            quem completou o perfil ve o resumo do dia (titulo/trilha)
                            e o CTA; os 3 ritmos ja tem lugar proprio, uma rolagem
                            abaixo. */}
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 20, color: "#87582b", fontSize: 11, fontWeight: 950, textTransform: "uppercase", letterSpacing: 1 }}>
                          {hasCompletedProfile ? "Ver caminho sugerido" : "Criar meu acesso"} <ChevronRight size={14} strokeWidth={2.4} />
                        </span>
                      </div>
                    </button>
                  </div>
                </div>
              </section>

              {/* Banner do e-book da serie de pregacao atual -- pedido da
                  Carla, 20/10/2026: "a serie reino dos avessos e a serie q
                  estamos, n pode subir uma CTA na home dela?". Controlado
                  pelo campo featuredHome em ebooks.json (so' um e-book por
                  vez deve ter esse campo) -- quando a serie de pregacao
                  mudar, e so trocar qual e-book tem featuredHome:true, sem
                  mexer aqui. */}
              {(() => {
                const featuredEbook = EBOOKS_MOCK.find((ebook) => ebook.featuredHome);
                if (!featuredEbook) return null;
                return (
                  <button
                    type="button"
                    onClick={() => openEbook(featuredEbook.id)}
                    style={{ ...buttonReset, ...overflowGuardStyle, width: "100%", display: "flex", alignItems: "center", gap: 16, padding: isMobile ? 16 : 18, marginBottom: 18, borderRadius: 18, border: "1px solid rgba(230,177,90,0.38)", background: "linear-gradient(135deg, rgba(230,177,90,0.14), rgba(15,23,42,0.94))", boxShadow: "0 16px 30px rgba(0,0,0,0.24)", textAlign: "left", cursor: "pointer" }}
                  >
                    <div style={{ width: 56, height: 78, borderRadius: 8, overflow: "hidden", flexShrink: 0, background: featuredEbook.coverColor || "#333", boxShadow: "0 8px 18px rgba(0,0,0,0.35)" }}>
                      {featuredEbook.coverImage && (
                        <img src={featuredEbook.coverImage} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                      )}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: "0 0 4px", fontSize: 10.5, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 900, color: "#E6B15A" }}>É a série que estamos pregando agora</p>
                      <p style={{ margin: 0, fontSize: isMobile ? 17 : 19, fontWeight: 900, color: "#f8fbff" }}>{featuredEbook.title}</p>
                      <p style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.5, color: "#c7d1e4", overflowWrap: "anywhere" }}>{featuredEbook.subtitle}</p>
                    </div>
                    <ChevronRight size={18} strokeWidth={2.2} color="#E6B15A" style={{ flexShrink: 0 }} />
                  </button>
                );
              })()}

              {/* Teaser de conquistas (gamificacao leve) -- so' aparece quem
                  ja desbloqueou pelo menos uma, pra nao anunciar "0
                  conquistas" logo de cara pra quem esta comecando agora.
                  Grade completa (com as bloqueadas tambem) mora no Perfil. */}
              {hasCompletedProfile && badgesData && badgesData.earnedCount > 0 && (
                <button
                  type="button"
                  onClick={() => { setActiveTab("profile"); window.scrollTo(0, 0); }}
                  style={{ ...buttonReset, ...overflowGuardStyle, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: isMobile ? 14 : 16, marginBottom: 18, borderRadius: 18, border: "1px solid rgba(230,177,90,0.32)", background: "linear-gradient(145deg, rgba(230,177,90,0.10), rgba(255,255,255,0.045))", color: "#f8fbff", boxShadow: "0 14px 26px rgba(0,0,0,0.20)", cursor: "pointer", textAlign: "left" }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                    <div style={{ display: "flex" }}>
                      {badgesData.badges.filter((badge) => badge.earnedAt).slice(0, 3).map((badge, index) => {
                        const BadgeIcon = BADGE_ICON_MAP[badge.icon] || Award;
                        return (
                          <span key={badge.slug} style={{ width: 34, height: 34, borderRadius: 999, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(230,177,90,0.18)", border: "1px solid rgba(230,177,90,0.5)", color: "#E6B15A", marginLeft: index > 0 ? -10 : 0, boxShadow: "0 0 0 2px rgba(10,14,22,0.92)" }}>
                            <BadgeIcon size={16} strokeWidth={1.75} />
                          </span>
                        );
                      })}
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: 0, fontSize: 10, letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 900, color: "#E6B15A" }}>Conquistas</p>
                      <p style={{ margin: "2px 0 0", fontSize: 13, fontWeight: 800, color: "#f8fbff", overflowWrap: "anywhere" }}>
                        {badgesData.earnedCount} {badgesData.earnedCount === 1 ? "conquista desbloqueada" : "conquistas desbloqueadas"}
                      </p>
                    </div>
                  </div>
                  <ChevronRight size={16} strokeWidth={2.2} color="#c7d1e4" />
                </button>
              )}

              {!hasCompletedProfile && (
                <section style={{ ...overflowGuardStyle, display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) auto" : "minmax(0, 1fr)", gap: 12, alignItems: "center", padding: isMobile ? 16 : 18, marginBottom: 18, border: "1px solid rgba(230,177,90,0.38)", background: "linear-gradient(145deg, rgba(230,177,90,0.16), rgba(255,255,255,0.055))", color: "#f8fbff", boxShadow: "0 16px 30px rgba(0,0,0,0.22)" }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: "0 0 6px", fontSize: 10, letterSpacing: 1.2, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>Primeiro acesso</p>
                    <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, lineHeight: 1.08, color: "#ffffff", fontWeight: 950 }}>Complete seu cadastro para guardar sua caminhada.</h2>
                    <p style={{ margin: "8px 0 0", fontSize: 12, lineHeight: 1.5, color: "#c7d1e4" }}>Leva menos de um minuto e libera progresso, e-books, convites e cuidado pastoral com seus dados certos.</p>
                  </div>
                  <button onClick={() => { setActiveTab("profile"); window.scrollTo(0, 0); }} style={{ ...buttonReset, minHeight: 44, padding: "0 16px", borderRadius: 999, background: "#E6B15A", color: "#111827", fontSize: 12, fontWeight: 950, whiteSpace: "nowrap" }}>
                    Fazer cadastro
                  </button>
                </section>
              )}

              {(myActiveMesas.length > 0 || pendingSentInvites.length > 0 || soloInProgressTrails.length > 0) && (
                /* Empilhado (1 coluna) sempre -- o grid de 2 colunas deixava espaco
                   desigual quando um card tinha bem menos conteudo que o outro
                   (ex.: "Sua mesa agora" com devocional+progresso vs. "Convites"
                   so' com 1 linha). Ver revisao /ux-ui de 18/10/2026. */
                <section style={{ ...overflowGuardStyle, display: "grid", gridTemplateColumns: "minmax(0, 1fr)", gap: 14, marginBottom: 18 }}>
                  {soloInProgressTrails.length > 0 && (() => {
                    const visibleTrails = soloInProgressTrails.slice(0, 2);
                    return (
                      <div style={{ ...overflowGuardStyle, padding: 20, background: "rgba(134,239,172,0.08)", border: "1px solid rgba(134,239,172,0.28)" }}>
                        <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.2, color: "#86EFAC", textTransform: "uppercase", fontWeight: 900 }}>Sua leitura em andamento</p>
                        {visibleTrails.map((trail, index) => (
                          <div
                            key={trail.id}
                            style={{
                              marginBottom: index === visibleTrails.length - 1 ? 0 : 16,
                              paddingBottom: index === visibleTrails.length - 1 ? 0 : 16,
                              borderBottom: index === visibleTrails.length - 1 ? "none" : "1px solid rgba(134,239,172,0.18)"
                            }}
                          >
                            <h3 style={{ margin: 0, fontSize: 18, color: "#ffffff", fontWeight: 900 }}>{trail.title}</h3>
                            <p style={{ margin: "4px 0 10px", fontSize: 12.5, lineHeight: 1.5, color: "#86EFAC", fontWeight: 800 }}>
                              {trail.progress || 0} de {trail.duration} dias
                            </p>
                            {/* Cada devocional em andamento precisa do proprio botao --
                                antes so' existia um unico "Continuar", sempre pro primeiro
                                da lista, mesmo com 2 trilhas mostradas (bug relatado pela
                                Carla, 20/10/2026: "continuar no dia 2 precisa ser pra cada
                                devocional, n da pra escolher qual seguir"). */}
                            <button
                              onClick={() => openDay(getTrailNextDay(trail), trail)}
                              style={{ ...buttonReset, minHeight: 40, padding: "0 14px", borderRadius: 999, background: "rgba(134,239,172,0.16)", border: "1px solid rgba(134,239,172,0.45)", color: "#86EFAC", fontSize: 12, fontWeight: 950 }}
                            >
                              Continuar no Dia {getTrailNextDay(trail)}
                            </button>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                  {myActiveMesas.length > 0 && (
                    <div style={{ ...overflowGuardStyle, padding: 20, background: "rgba(230,177,90,0.10)", border: "1px solid rgba(230,177,90,0.32)" }}>
                      <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.2, color: "#F5D28B", textTransform: "uppercase", fontWeight: 900 }}>Sua mesa agora</p>
                      {myActiveMesas.slice(0, 2).map((group) => {
                        const activeMembers = group.members?.filter((member) => ["ACTIVE", "INVITED"].includes(member.status)) || [];
                        // Qual devocional essa mesa esta seguindo + em que dia -- mesmo
                        // lookup ja usado em homeActiveMesaTrail (mais acima, pro hero da
                        // Home) pra nao inventar uma segunda fonte de verdade. Sem isso o
                        // card so dizia o nome da mesa e quem esta nela, nunca o conteudo
                        // em si (achado pelo Drigo, 18/10/2026: "pq nao traz o devocional
                        // e status de como esta?").
                        const mesaTrail = trails.find((trail) => trail.id === group.trail?.slug || trail.slug === group.trail?.slug);
                        // Mesa cujo devocional ja' chegou no ultimo dia (progress >=
                        // duration) continuava mostrando "Continuar no Dia 5" pra sempre --
                        // o grupo (openGroups) fica "aberto" ate' alguem fechar a mesa, mas
                        // isso nao quer dizer que o CONTEUDO ainda falta. Sem essa distincao
                        // o CTA mentia "ainda falta o Dia 5" pra quem ja' leu tudo (pergunta
                        // da Carla, 25/10/2026: "como fica os que foram concluidos? q estao
                        // no dia 5"). Mesma logica que soloInProgressTrails ja' usa
                        // (isTrailDone) pra sumir da "Sua leitura em andamento".
                        const mesaDone = mesaTrail ? isTrailDone(mesaTrail) : false;
                        // CTA cego ("Abrir mesa", sem dizer pra onde) virou especifico
                        // quando ja' da pra saber o devocional/dia dessa mesa --
                        // refinamento da revisao /ux-ui (18/10/2026, "pode seguir").
                        // Antes esse botao so' existia pra primeira mesa da lista
                        // (myActiveMesas[0]) fora do loop, entao com 2+ mesas abertas
                        // so' uma ganhava botao e a outra ficava sem jeito nenhum de
                        // continuar direto (relatado pela Carla, 21/10/2026: "mesa
                        // aberta tem 2 e so' um botao pra continuar") -- agora cada
                        // mesa da lista tem o seu proprio botao, resolvido com a
                        // trilha dela mesma (nao mais sempre a da primeira).
                        return (
                          <div key={group.id} style={{ marginBottom: 18 }}>
                            <h3 style={{ margin: 0, fontSize: 18, color: "#ffffff", fontWeight: 900 }}>{group.name}</h3>
                            {mesaTrail && (
                              <p style={{ margin: "4px 0 0", fontSize: 12.5, lineHeight: 1.5, color: mesaDone ? "#86EFAC" : "#F5D28B", fontWeight: 800 }}>
                                {mesaDone ? "✓ " : ""}{mesaTrail.title} · {mesaDone ? "Concluída" : `${mesaTrail.progress || 0} de ${mesaTrail.duration} dias`}
                              </p>
                            )}
                            <p style={{ margin: "6px 0 0", fontSize: 12, lineHeight: 1.5, color: "#c7d1e4" }}>
                              {activeMembers.length} pessoa{activeMembers.length === 1 ? "" : "s"}
                              {activeMembers.length > 0 ? ` · ${activeMembers.slice(0, 3).map((member) => member.displayName || "Alguém").join(", ")}${activeMembers.length > 3 ? ` e mais ${activeMembers.length - 3}` : ""}` : ""}
                            </p>
                            <button
                              onClick={() => {
                                if (mesaTrail) {
                                  setSelectedTrailGroup(group);
                                  setCoopActive(true);
                                  openDay(getTrailNextDay(mesaTrail), mesaTrail);
                                } else {
                                  setActiveTab("family");
                                  setMesaIntent("group");
                                  window.scrollTo({ top: 0, behavior: "smooth" });
                                }
                              }}
                              style={{
                                ...buttonReset,
                                minHeight: 40,
                                padding: "0 14px",
                                marginTop: 8,
                                borderRadius: 999,
                                background: mesaDone ? "rgba(134,239,172,0.14)" : "rgba(230,177,90,0.18)",
                                border: mesaDone ? "1px solid rgba(134,239,172,0.45)" : "1px solid rgba(230,177,90,0.5)",
                                color: mesaDone ? "#86EFAC" : "#F5D28B",
                                fontSize: 12,
                                fontWeight: 950
                              }}
                            >
                              {!mesaTrail ? "Abrir mesa" : mesaDone ? `Rever o Dia ${mesaTrail.duration}` : `Continuar no Dia ${getTrailNextDay(mesaTrail)}`}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {pendingSentInvites.length > 0 && (
                    <div style={{ ...overflowGuardStyle, padding: 20, background: "rgba(100,199,208,0.08)", border: "1px solid rgba(100,199,208,0.28)" }}>
                      <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.2, color: "#9fe7ee", textTransform: "uppercase", fontWeight: 900 }}>
                        Convite{pendingSentInvites.length === 1 ? "" : "s"} que você enviou
                      </p>
                      {/* Antes mostrava so "Nome · Trilha", sem dizer de quem partiu o
                          convite nem o que ele significava -- Rodrigo nao entendeu o
                          card ao ver (relatado ao vivo). Agora deixa explicito: e um
                          convite pra fazer uma trilha JUNTO com alguem (ver
                          createTrailInvite/buildMesaInviteText), que essa pessoa ainda
                          nao aceitou. */}
                      {pendingSentInvites.slice(0, 2).map((invite) => (
                        <p key={invite.id} style={{ margin: "6px 0 0", fontSize: 13, lineHeight: 1.5, color: "#c7d1e4" }}>
                          Você chamou <strong style={{ color: "#ffffff" }}>{invite.inviteeName}</strong> pra fazer "{invite.trail?.title || "uma trilha"}" com você — ainda sem resposta.
                        </p>
                      ))}
                      <button onClick={() => { setActiveTab("family"); window.scrollTo({ top: 0, behavior: "smooth" }); }} style={{ ...buttonReset, minHeight: 40, padding: "0 14px", marginTop: 10, borderRadius: 999, background: "rgba(100,199,208,0.16)", border: "1px solid rgba(100,199,208,0.45)", color: "#9fe7ee", fontSize: 12, fontWeight: 950 }}>
                        Ver na Mesa
                      </button>
                    </div>
                  )}
                </section>
              )}

              <section style={{ ...overflowGuardStyle, display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 0.88fr) minmax(0, 1.12fr)" : "minmax(0, 1fr)", gap: 18, marginBottom: 18 }}>
                <div style={{ ...overflowGuardStyle, padding: 22, background: "rgba(255,255,255,0.055)", border: "1px solid rgba(255,255,255,0.10)" }}>
                  <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>Escolha a forma da caminhada</p>
                  <h2 style={{ margin: 0, fontSize: 26, lineHeight: 1.1, color: "#ffffff", fontWeight: 900 }}>O mesmo devocional, três ritmos.</h2>
                </div>
                <div style={{ ...widthGuardStyle, display: "grid", gridTemplateColumns: isTablet ? "repeat(3, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 12 }}>
                  {[
                    { modeId: "solo", text: "Monte uma rotina pessoal de leitura, reflexão e prática.", action: "Criar rotina", onClick: () => { trackEvent("devotional_start", { source: "home_mode_grid" }); setTrailCompanionMode("solo"); setShowMomentGuide(true); } },
                    { modeId: "duo", text: "Crie uma mesa para quem recebeu o link caminhar com você.", action: "Convidar", onClick: () => { setTrailCompanionMode("duo"); openInviteStart(); } },
                    { modeId: "group", text: "Veja mesas disponíveis ou abra uma trilha para quem quiser chegar.", action: "Ver mesas", onClick: () => { setTrailCompanionMode("group"); setMesaIntent("group"); setActiveTab("family"); loadOpenGroups(); window.scrollTo({ top: 0, behavior: "smooth" }); } }
                  ].map((item) => {
                    const mode = trailCompanionModes.find((modeItem) => modeItem.id === item.modeId) || trailCompanionModes[0];
                    return (
                      <button key={mode.id} onClick={item.onClick} style={{ ...buttonReset, minHeight: 194, padding: 20, textAlign: "left", border: `1px solid ${mode.accentColor}44`, background: `linear-gradient(180deg, ${mode.accentColor}25, rgba(255,255,255,0.045))`, color: "#f8fbff", display: "flex", flexDirection: "column", justifyContent: "space-between", boxShadow: `0 14px 28px ${mode.accentColor}12` }}>
                        <div>
                          <span style={{ display: "inline-flex", width: 44, height: 44, borderRadius: 16, background: `radial-gradient(circle at 35% 25%, rgba(255,255,255,0.28), transparent 38%), ${mode.accentColor}`, color: "#121722", alignItems: "center", justifyContent: "center", border: "1px solid rgba(255,255,255,0.22)", boxShadow: `0 12px 22px ${mode.accentColor}20` }}>{mode.icon}</span>
                          <h3 style={{ margin: "16px 0 8px", fontSize: 22, color: "#ffffff", fontWeight: 900 }}>{mode.label}</h3>
                          <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, color: "#c7d1e4" }}>{item.text}</p>
                        </div>
                        <p style={{ margin: "18px 0 0", fontSize: 12, color: mode.accentColor, fontWeight: 900 }}>{item.action}</p>
                      </button>
                    );
                  })}
                </div>
              </section>

              <section style={{ ...overflowGuardStyle, display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 1.05fr) minmax(300px, 0.95fr)" : "minmax(0, 1fr)", gap: 18, marginBottom: 18, alignItems: "stretch" }}>
                <div style={{ position: "relative", ...overflowGuardStyle, padding: isMobile ? 20 : 24, minHeight: 240, background: "linear-gradient(135deg, #fff7e8 0%, #eef2ed 52%, #dceff3 100%)", color: "#151b2a", boxShadow: "0 18px 42px rgba(0,0,0,0.26)" }}>
                  <div style={{ position: "relative" }}>
                    <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.2, color: "#87582b", textTransform: "uppercase", fontWeight: 900 }}>Leituras para a jornada</p>
                    <h2 style={{ margin: 0, maxWidth: 560, fontSize: isMobile ? 29 : 34, lineHeight: 1.02, fontWeight: 900 }}>Livros para ler com calma, marcar e conversar.</h2>
                    <p style={{ margin: "14px 0 0", maxWidth: 560, fontSize: 15, lineHeight: 1.7, color: "#4c596b" }}>
                      Escolha uma leitura, siga no seu ritmo e guarde as frases que precisam amadurecer. Quando fizer sentido, compartilhe um trecho com alguém para caminhar junto.
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 18 }}>
                      {["Ler no app", "Marcar trechos", "Anotar reflexões", "Partilhar"].map((item) => (
                        <span key={item} style={{ padding: "8px 10px", borderRadius: 999, background: "rgba(21,27,42,0.08)", color: "#283247", fontSize: 11, fontWeight: 900 }}>{item}</span>
                      ))}
                    </div>
                    <button onClick={() => { setActiveTab("ebooks"); window.scrollTo(0,0); }} style={{ ...buttonReset, minHeight: 48, padding: "0 18px", marginTop: 24, borderRadius: 999, background: "#151b2a", color: "#fff8eb", fontSize: 13, fontWeight: 900, boxShadow: "0 14px 24px rgba(21,27,42,0.22)" }}>
                      Escolher leitura
                    </button>
                  </div>
                </div>

                <button onClick={() => openEbook(continueEbook.id)} style={{ ...buttonReset, ...overflowGuardStyle, padding: isMobile ? 20 : 24, background: "linear-gradient(160deg, rgba(100,199,208,0.15), rgba(255,255,255,0.055))", border: "1px solid rgba(100,199,208,0.18)", display: "flex", flexDirection: "column", justifyContent: "space-between", minHeight: 240, textAlign: "left", color: "inherit", cursor: "pointer" }}>
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#9fe7ee", textTransform: "uppercase", fontWeight: 900 }}>{hasContinueEbookProgress ? "Continue de onde parou" : "Primeira leitura"}</p>
                    <div style={{ ...widthGuardStyle, display: "grid", gridTemplateColumns: "72px minmax(0, 1fr)", gap: 14, alignItems: "start" }}>
                      <img src={continueEbook.coverImage} alt={continueEbook.title} style={{ width: 72, height: 104, objectFit: "cover", boxShadow: "0 14px 24px rgba(0,0,0,0.28)" }} />
                      <div>
                        <h3 style={{ margin: 0, fontSize: 24, lineHeight: 1.06, color: "#ffffff", fontWeight: 900 }}>{continueEbook.title}</h3>
                        <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, color: "#c7d1e4" }}>
                          {hasContinueEbookProgress
                            ? `Você está na página ${continueEbookPage} de ${continueEbook.pages}. Retome antes que a leitura esfrie.`
                            : "Uma boa porta de entrada para começar sem pressa e criar constância."}
                        </p>
                      </div>
                    </div>
                    {hasContinueEbookProgress ? (
                      <div style={{ marginTop: 18 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                          <span style={{ fontSize: 11, color: "#9fe7ee", fontWeight: 900 }}>{continueEbookPage} de {continueEbook.pages} páginas</span>
                          <span style={{ fontSize: 11, color: "#c7d1e4", fontWeight: 900 }}>{continueEbookProgress}%</span>
                        </div>
                        <div style={{ height: 8, background: "rgba(255,255,255,0.10)", padding: 2 }}>
                          <div style={{ width: `${continueEbookProgress}%`, height: "100%", background: continueEbook.coverColor }} />
                        </div>
                      </div>
                    ) : (
                      <p style={{ margin: "18px 0 0", fontSize: 11, color: "#9fe7ee", fontWeight: 900 }}>{continueEbook.pages} páginas disponíveis no app</p>
                    )}
                  </div>
                  <span style={{ width: "100%", minHeight: 46, marginTop: 22, borderRadius: 999, background: "#64c7d0", color: "#0b1720", fontSize: 12, fontWeight: 900, display: "inline-flex", alignItems: "center", justifyContent: "center", boxShadow: "0 14px 24px rgba(100,199,208,0.28)" }}>
                    {hasContinueEbookProgress ? "Continuar leitura" : "Começar por este livro"}
                  </span>
                </button>
              </section>

              {(() => {
                // Pedido do Rodrigo: esse card mostrava D1-D3 sempre marcados
                // como feitos, fixo, sem relacao com o uso real -- e o
                // contador "Dias na semana" do Perfil (weeklyCompletedCount)
                // usa dado de verdade (weeklyCompletionMap, log de devocional
                // por dia). Os dois precisam contar a MESMA coisa, entao agora
                // o card da Home reusa o mesmo weeklyCompletionMap em vez de
                // um array decorativo.
                const weekDays = weeklyCompletionMap.map((done, index) => [`D${index + 1}`, done]);
                const nextWeekDayIndex = weekDays.findIndex(([, done]) => !done);
                const allWeekDaysDone = nextWeekDayIndex === -1;
                const nextWeekDay = allWeekDaysDone ? weekDays.length : nextWeekDayIndex + 1;
                return (
              <section style={{ ...overflowGuardStyle, display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 1.15fr) minmax(320px, 0.85fr)" : "minmax(0, 1fr)", gap: 18, alignItems: "stretch" }}>
                <div style={{ ...overflowGuardStyle, padding: isMobile ? "22px 18px" : 24, background: "#f7f0e2", color: "#171d2d", boxShadow: "0 18px 42px rgba(0,0,0,0.26)" }}>
                  <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.2, color: "#87582b", textTransform: "uppercase", fontWeight: 900 }}>Caminho da semana</p>
                  <h2 style={{ margin: 0, fontSize: isMobile ? 28 : 31, lineHeight: 1.06, fontWeight: 900 }}>Uma jornada simples para voltar ao essencial todos os dias.</h2>
                  <p style={{ margin: "14px 0 0", fontSize: isMobile ? 14 : 15, lineHeight: 1.65, color: "#4c596b" }}>
                    Entre, leia com calma, responda com honestidade e pratique algo possível. Quem está perto e quem acompanha de longe pode caminhar no mesmo ritmo.
                  </p>
                  {/* D1-D7 aqui sao dias da SEMANA (ritmo de uso, weeklyCompletionMap),
                      nao dias de uma trilha -- mas openDay(dia) sem trilha explicita
                      caia no fallback ambiguo (selectedTrail || homeModel.currentTrail
                      || trails[0]), e o botao abaixo so' dizia "Continuar no dia X" sem
                      nunca contar PRA QUAL leitura isso ia (reparado pela Carla,
                      26/10/2026: "n diz qual é que está lendo"). Agora passa a trilha
                      atual explicitamente (mesma de "Continue em ..." no hero) e nomeia
                      ela no botao, em vez de deixar o destino escondido/adivinhado. */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", alignItems: "center", gap: isMobile ? 5 : 10, marginTop: 24, width: "100%", maxWidth: "100%" }}>
                    {weekDays.map(([label, done], index) => (
                      <button key={label} onClick={() => openDay(index + 1, homeModel.currentTrail)} style={{ ...buttonReset, position: "relative", width: isMobile ? 37 : 44, height: isMobile ? 37 : 44, borderRadius: 999, justifySelf: "center", background: done ? "#151b2a" : "rgba(21,27,42,0.08)", color: done ? "#fff8eb" : "#283247", fontSize: 11, fontWeight: 900 }}>
                        {index > 0 && <span style={{ position: "absolute", right: "100%", top: "50%", width: isMobile ? 7 : 18, height: 1, background: "rgba(21,27,42,0.18)", transform: "translateY(-50%)" }} />}
                        {label}
                      </button>
                    ))}
                  </div>
                  {/* Pedido do Rodrigo (revisao UX /ux-ui): o widget "Caminho da
                      semana" so' mostrava os pontinhos D1-D7, sem nenhum botao --
                      era o unico card da Home sem acao nenhuma. Agora reusa o
                      mesmo openDay() que cada pontinho ja chama, levando direto
                      pro primeiro dia ainda nao feito. */}
                  <button onClick={() => openDay(nextWeekDay, homeModel.currentTrail)} style={{ ...buttonReset, minHeight: 46, padding: "0 20px", marginTop: 22, borderRadius: 999, background: "#151b2a", color: "#fff8eb", fontSize: 12, fontWeight: 900, boxShadow: "0 14px 24px rgba(21,27,42,0.22)" }}>
                    {allWeekDaysDone
                      ? "Revisar a semana"
                      : `Continuar no Dia ${nextWeekDay}${homeModel.currentTrail?.title ? ` · ${homeModel.currentTrail.title}` : ""}`}
                  </button>
                </div>

                <div style={{ ...widthGuardStyle, display: "flex", flexDirection: "column", gap: 14 }}>
                  <div style={{ ...overflowGuardStyle, padding: 22, minHeight: 150, background: "linear-gradient(160deg, rgba(100,199,208,0.18), rgba(255,255,255,0.055))", border: "1px solid rgba(100,199,208,0.18)" }}>
                    <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#9fe7ee", textTransform: "uppercase", fontWeight: 900 }}>Comunidade híbrida</p>
                    <h3 style={{ margin: 0, fontSize: 22, color: "#ffffff", fontWeight: 900 }}>Presencial e virtual na mesma mesa.</h3>
                    <p style={{ margin: "10px 0 0", fontSize: 13, lineHeight: 1.55, color: "#c7d1e4" }}>Acompanhe quem frequenta a igreja e quem caminha de longe sem criar dois mundos separados.</p>
                  </div>
                  <div style={{ ...overflowGuardStyle, padding: 22, minHeight: 150, background: "linear-gradient(160deg, rgba(230,177,90,0.18), rgba(255,255,255,0.055))", border: "1px solid rgba(230,177,90,0.18)" }}>
                    <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>Evangelização</p>
                    <h3 style={{ margin: 0, fontSize: 22, color: "#ffffff", fontWeight: 900 }}>Convite que vira caminhada.</h3>
                    <p style={{ margin: "10px 0 14px", fontSize: 13, lineHeight: 1.55, color: "#c7d1e4" }}>Compartilhar um devocional deve abrir a porta para alguns dias juntos, não apenas para uma visualização.</p>
                    <button onClick={shareSlimDevotional} style={{ ...buttonReset, minHeight: 42, padding: "0 16px", borderRadius: 999, background: "#64c7d0", color: "#0b1720", fontSize: 12, fontWeight: 900, boxShadow: "0 10px 20px rgba(100,199,208,0.28)" }}>
                      Enviar convite devocional
                    </button>
                  </div>
                </div>
              </section>
                );
              })()}
            </div>
           )}

          {/* TAB: LIBRARY */}
          {activeTab === "library" && (
            <div>
              <section style={{ position: "relative", overflow: "hidden", width: "100%", maxWidth: "100%", boxSizing: "border-box", minHeight: isDesktop ? 330 : "auto", padding: isDesktop ? "34px 38px" : isTablet ? "28px 22px" : "24px 20px", borderRadius: isDesktop ? 38 : isTablet ? 30 : 24, border: "1px solid rgba(255,255,255,0.28)", background: "linear-gradient(135deg, rgba(248,243,232,0.99) 0%, rgba(231,238,239,0.98) 48%, rgba(244,224,194,0.96) 100%)", color: "#182033", boxShadow: "0 22px 54px rgba(0,0,0,0.34)", marginBottom: 20 }}>
                <div style={{ position: "relative", display: isDesktop ? "grid" : "block", gridTemplateColumns: "minmax(0, 1.05fr) minmax(300px, 0.95fr)", gap: 30, alignItems: "center" }}>
                  <div>
                    <p style={{ margin: "0 0 12px", fontSize: 11, letterSpacing: 1.6, color: "#87582b", textTransform: "uppercase", fontWeight: 900 }}>Trilhas devocionais</p>
                    <h2 style={{ margin: 0, maxWidth: 640, fontSize: mobileHeroTitleSize, lineHeight: 0.98, color: "#151b2a", fontWeight: 900 }}>Caminhos para criar constância com Deus.</h2>
                    <p style={{ margin: "16px 0 0", maxWidth: 560, fontSize: 16, lineHeight: 1.62, color: "#425064" }}>
                      Escolha uma trilha, avance um dia por vez e caminhe sozinho, em dupla ou com um grupo. O foco é formar ritmo, não correr.
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 20 }}>
                      {trailCompanionModes.map((mode) => {
                        const isActive = trailCompanionMode === mode.id;
                        return (
                          <button
                            key={mode.id}
                            onClick={() => setTrailCompanionMode(mode.id)}
                            style={{
                              ...buttonReset,
                              minHeight: 36,
                              padding: "0 12px",
                              borderRadius: 999,
                              background: isActive ? "#151b2a" : "rgba(21,27,42,0.08)",
                              color: isActive ? "#fff8eb" : "#283247",
                              fontSize: 11,
                              fontWeight: 900,
                              boxShadow: isActive ? "0 12px 20px rgba(21,27,42,0.18)" : "none"
                            }}
                          >
                            {mode.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {homeModel.recommendedTrail ? (
                    <button onClick={() => openTrail(homeModel.recommendedTrail.id)} style={{ ...buttonReset, width: "100%", marginTop: isDesktop ? 0 : 24, padding: 18, borderRadius: 26, background: "rgba(255,255,255,0.52)", border: "1px solid rgba(21,27,42,0.12)", boxShadow: "0 18px 34px rgba(73,83,100,0.16)", textAlign: "left", color: "#151b2a" }}>
                      <p style={{ margin: "0 0 12px", fontSize: 11, letterSpacing: 1.3, color: "#87582b", textTransform: "uppercase", fontWeight: 900 }}>Sua próxima etapa</p>
                      <div style={{ display: "flex", gap: 16, alignItems: "center" }}>
                        <span style={{ width: 64, height: 64, borderRadius: 22, background: `${homeModel.recommendedTrail.color}22`, color: homeModel.recommendedTrail.color, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${homeModel.recommendedTrail.color}55`, flexShrink: 0 }}>
                          {ICON_MAP[homeModel.recommendedTrail.icon] || <Layers size={28} strokeWidth={1.5} />}
                        </span>
                        <div style={{ minWidth: 0 }}>
                          <h3 style={{ margin: 0, fontSize: 24, lineHeight: 1.05, color: "#151b2a", fontWeight: 900 }}>{homeModel.recommendedTrail.title}</h3>
                          <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, color: "#4c596b" }}>{homeModel.recommendationReason}</p>
                        </div>
                      </div>
                      <div style={{ marginTop: 18 }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, marginBottom: 8 }}>
                          <span style={{ fontSize: 11, color: "#87582b", fontWeight: 900 }}>{homeModel.recommendedTrail.progress || 0} de {homeModel.recommendedTrail.duration} dias</span>
                          <span style={{ fontSize: 11, color: "#425064", fontWeight: 900 }}>{Math.round(((homeModel.recommendedTrail.progress || 0) / Math.max(1, homeModel.recommendedTrail.duration || 1)) * 100)}%</span>
                        </div>
                        <div style={{ height: 8, background: "rgba(21,27,42,0.10)", padding: 2 }}>
                          <div style={{ width: `${Math.round(((homeModel.recommendedTrail.progress || 0) / Math.max(1, homeModel.recommendedTrail.duration || 1)) * 100)}%`, height: "100%", background: homeModel.recommendedTrail.color }} />
                        </div>
                        <p style={{ margin: "12px 0 0", fontSize: 12, color: "#151b2a", fontWeight: 900 }}>Entrar na trilha</p>
                      </div>
                    </button>
                  ) : (
                    <div style={{ marginTop: isDesktop ? 0 : 24, padding: 20, borderRadius: 26, background: "rgba(255,255,255,0.52)", border: "1px solid rgba(21,27,42,0.12)", color: "#425064" }}>
                      <p style={{ margin: 0, fontSize: 13, lineHeight: 1.55, fontWeight: 800 }}>Ainda não há uma trilha recomendada para exibir.</p>
                    </div>
                  )}
                </div>
              </section>

              <section style={{ ...overflowGuardStyle, marginBottom: 20, padding: isDesktop ? 26 : 18, border: "1px solid rgba(255,255,255,0.12)", background: "linear-gradient(145deg, rgba(255,255,255,0.07), rgba(15,23,42,0.72))", boxShadow: "0 18px 34px rgba(0,0,0,0.22)" }}>
                <div style={{ marginBottom: 20 }}>
                  {/* Coluna única (eyebrow > H2 > parágrafo), de propósito diferente do
                      cabeçalho de "Mapa das trilhas" logo abaixo (que usa duas colunas) --
                      revisão /ux-ui: os dois cabeçalhos usavam a mesma fórmula
                      (eyebrow + H2 + parágrafo espelhado à direita), fazendo a página
                      parecer o mesmo bloco se repetindo com cor diferente. */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                    <p style={{ ...eyebrowStyle, margin: 0, color: "#f0dcb8" }}>Escolha pelo momento</p>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "4px 10px", borderRadius: 999, background: "rgba(230,177,90,0.14)", border: "1px solid rgba(230,177,90,0.3)", color: "#f0dcb8", fontSize: 11, fontWeight: 900 }}>
                      {activeCompanionMode.icon} Caminhando: {activeCompanionMode.label}
                    </span>
                  </div>
                  <h2 style={{ margin: 0, maxWidth: 560, fontSize: mobileSectionTitleSize, lineHeight: 1.05, color: "#ffffff", fontWeight: 900 }}>Encontre um caminho para o seu momento.</h2>
                  <p style={{ margin: "10px 0 0", maxWidth: 480, fontSize: 13, lineHeight: 1.55, color: "#aebbd1" }}>
                    Comece por uma porta de entrada clara. Para trocar o modo de caminhada, use os botões lá no topo da página.
                  </p>
                </div>

                <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 14 }}>
                  {guidedJourneyCards.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => startTrailWithCompanionMode(item.trail, item.moment)}
                      style={{
                        ...buttonReset,
                        minHeight: 234,
                        padding: 20,
                        textAlign: "left",
                        border: `1px solid ${item.trail.color}55`,
                        background: `linear-gradient(160deg, ${item.trail.color}24, rgba(255,255,255,0.045))`,
                        color: "#f8fbff",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        boxShadow: `0 16px 30px ${item.trail.color}14`
                      }}
                    >
                      <div>
                        <span style={{ width: 50, height: 50, borderRadius: 17, background: `${item.trail.color}22`, color: item.trail.color, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${item.trail.color}66` }}>
                          {item.icon}
                        </span>
                        <p style={{ margin: "18px 0 8px", fontSize: 10, color: item.trail.color, textTransform: "uppercase", letterSpacing: 1.1, fontWeight: 900 }}>{item.signal}</p>
                        <h3 style={{ margin: 0, fontSize: 21, lineHeight: 1.08, color: "#ffffff", fontWeight: 900 }}>{item.title}</h3>
                        <p style={{ margin: "10px 0 0", fontSize: 13, lineHeight: 1.5, color: "#c7d1e4" }}>{item.text}</p>
                      </div>
                      <div style={{ marginTop: 18 }}>
                        <p style={{ margin: "0 0 8px", fontSize: 11, color: "#9fb0cc", fontWeight: 900 }}>{item.trail.title} · {item.trail.duration} dias</p>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: item.trail.color, fontSize: 12, fontWeight: 900 }}>
                          {activeCompanionMode.action} <ChevronRight size={15} strokeWidth={2.4} />
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </section>

              {newlyCuratedTrails.length > 0 && (
                <section style={{ ...overflowGuardStyle, marginBottom: 20, padding: isDesktop ? 22 : 18, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.03)", boxShadow: "0 18px 34px rgba(0,0,0,0.22)" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", padding: "4px 10px", borderRadius: 999, background: "rgba(139,124,255,0.16)", border: "1px solid rgba(139,124,255,0.35)", color: "#b8adff", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 900, whiteSpace: "nowrap" }}>Feed · atualiza toda semana</span>
                    <h2 style={{ margin: 0, fontSize: 22, lineHeight: 1.15, color: "#ffffff", fontWeight: 900 }}>Trilhas novas para aprofundar a semana.</h2>
                  </div>

                  <div style={{ position: "relative" }}>
                    <div style={{ display: "flex", gap: 12, overflowX: "auto", paddingBottom: 4, scrollSnapType: "x mandatory", WebkitOverflowScrolling: "touch" }}>
                      {newlyCuratedTrails.map((trail) => {
                        const trailMomentMeta = TRAIL_MOMENT_OPTIONS.find((item) => item.id === getTrailMoment(trail)) || TRAIL_MOMENT_OPTIONS[1];
                        return (
                          <button key={trail.id} onClick={() => startTrailWithCompanionMode(trail, getTrailMoment(trail))} style={{ ...buttonReset, flex: "0 0 208px", scrollSnapAlign: "start", minHeight: 132, padding: "14px 14px 14px 16px", textAlign: "left", borderTop: "1px solid rgba(255,255,255,0.08)", borderRight: "1px solid rgba(255,255,255,0.08)", borderBottom: "1px solid rgba(255,255,255,0.08)", borderLeft: `3px solid ${trail.color}`, background: "rgba(255,255,255,0.03)", color: "#f8fbff", display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
                            <div>
                              <span style={{ fontSize: 9, color: trail.color, textTransform: "uppercase", letterSpacing: 0.7, fontWeight: 900 }}>{trailMomentMeta.label} · {trail.duration} dias</span>
                              <h3 style={{ margin: "6px 0 5px", fontSize: 15, lineHeight: 1.18, color: "#ffffff", fontWeight: 900 }}>{trail.title}</h3>
                              <p style={{ margin: 0, fontSize: 11, lineHeight: 1.4, color: "#aebbd1", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{FEATURED_CURATED_TRAIL_COPY[trail.id] || trail.subtitle}</p>
                            </div>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, color: trail.color, fontSize: 11, fontWeight: 900, marginTop: 10 }}>
                              {activeCompanionMode.action} <ChevronRight size={12} strokeWidth={2.4} />
                            </span>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ position: "absolute", top: 0, right: 0, bottom: 4, width: 48, pointerEvents: "none", background: "linear-gradient(90deg, rgba(6,11,22,0) 0%, rgba(6,11,22,0.85) 100%)" }} />
                  </div>
                </section>
              )}

<div style={{ borderTop: "1px solid rgba(255,255,255,0.10)", margin: "4px 0 22px" }} />

              <div style={{ margin: "0 0 14px" }}>
                <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>Mapa das trilhas</p>
                <h2 style={{ margin: 0, fontSize: 24, lineHeight: 1.05, color: "#ffffff", fontWeight: 900 }}>Escolha por onde caminhar.</h2>
                {planosMode === "meus" && (
                  <p style={{ margin: "8px 0 0", maxWidth: 460, fontSize: 13, lineHeight: 1.55, color: "#aebbd1" }}>As trilhas que você já começou ou concluiu, num só lugar pra retomar rápido.</p>
                )}
              </div>

              <div style={{ display: "inline-flex", gap: 6, padding: 4, marginBottom: 18, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(15,23,42,0.44)" }}>
                {[{ id: "meus", label: `Meus planos${hasAnyTrailActivity ? ` · ${inProgressTrails.length + completedTrails.length}` : ""}` }, { id: "explorar", label: "Explorar" }].map((mode) => {
                  const isActive = planosMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      onClick={() => setPlanosMode(mode.id)}
                      style={{
                        ...buttonReset,
                        minHeight: 40,
                        padding: "0 16px",
                        border: isActive ? "1px solid #e6b15a" : "1px solid transparent",
                        background: isActive ? "#e6b15a" : "transparent",
                        color: isActive ? "#1a140a" : "#aebbd1",
                        fontSize: 12,
                        fontWeight: 900,
                        whiteSpace: "nowrap"
                      }}
                    >
                      {mode.label}
                    </button>
                  );
                })}
              </div>

              {planosMode === "meus" ? (
                <div>
                  {!hasAnyTrailActivity ? (
                    <div style={{ padding: 22, border: "1px dashed rgba(255,255,255,0.18)", background: "rgba(255,255,255,0.03)", textAlign: "center" }}>
                      <p style={{ margin: "0 0 6px", fontSize: 14, color: "#f8fbff", fontWeight: 900 }}>Você ainda não começou nenhuma trilha.</p>
                      <p style={{ margin: "0 0 16px", fontSize: 12, color: "#aebbd1" }}>Explore o mapa por tema e escolha por onde caminhar hoje.</p>
                      <button onClick={() => setPlanosMode("explorar")} style={{ ...buttonReset, display: "inline-flex", padding: "10px 18px", background: "#e6b15a", color: "#1a140a", fontSize: 12, fontWeight: 900 }}>Ir para Explorar</button>
                    </div>
                  ) : (
                    <>
                      {inProgressTrails.length > 0 && (
                        <div style={{ marginBottom: 22 }}>
                          <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.1, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>Em andamento · {inProgressTrails.length}</p>
                          <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(3, minmax(0, 1fr))" : isTablet ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 12 }}>
                            {inProgressTrails.map(renderTrailCard)}
                          </div>
                        </div>
                      )}
                      {completedTrails.length > 0 && (
                        <div>
                          <p style={{ margin: "0 0 10px", fontSize: 11, letterSpacing: 1.1, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>Concluídas · {completedTrails.length}</p>
                          <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(3, minmax(0, 1fr))" : isTablet ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 12 }}>
                            {completedTrails.map(renderTrailCard)}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
              <>
              {/* Explicacao das categorias movida pra perto dos chips que ela
                  descreve (Todos/Comecar/Vida interior/...) -- antes ficava
                  la em cima, perto do titulo, "voando" longe dos filtros que
                  o texto na verdade explica (pedido do Rodrigo). */}
              <p style={{ margin: "0 0 12px", maxWidth: 640, fontSize: 13, lineHeight: 1.55, color: "#aebbd1" }}>As trilhas estão agrupadas por momento de vida — Começar, Vida interior, Comunidade, Missão. Assim quem chega agora não precisa adivinhar por onde começar.</p>
              {/* Busca e chips de momento viviam em dois blocos separados,
                  com um gap entre eles -- pareciam duas coisas diferentes,
                  mas os dois sao a mesma coisa (formas de filtrar a mesma
                  lista de trilhas). Unificados num so' cartao: busca em
                  cima, chips embaixo. Pedido do Rodrigo. */}
              <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "1fr 280px" : "1fr", gap: 14, alignItems: "stretch", marginBottom: 16 }}>
                <div style={{ padding: 12, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.045)", boxShadow: "0 16px 28px rgba(0,0,0,0.18)" }}>
                  <div style={{ position: "relative", marginBottom: 10 }}>
                    <Search size={16} style={{ position: "absolute", top: "50%", left: 14, transform: "translateY(-50%)", color: "#68758c", pointerEvents: "none" }} />
                    <input
                      type="text"
                      value={trailSearchQuery}
                      onChange={(event) => setTrailSearchQuery(event.target.value)}
                      placeholder="Buscar trilha pelo nome..."
                      style={{ ...profileInputStyle, padding: "12px 38px 12px 40px" }}
                    />
                    {trailSearchQuery && (
                      <button
                        onClick={() => setTrailSearchQuery("")}
                        aria-label="Limpar busca"
                        style={{ ...buttonReset, position: "absolute", top: "50%", right: 10, transform: "translateY(-50%)", width: 28, height: 28, display: "inline-flex", alignItems: "center", justifyContent: "center", color: "#aebbd1" }}
                      >
                        <X size={15} />
                      </button>
                    )}
                  </div>
                  <div style={{ position: "relative" }}>
                    <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 2 }}>
                      {TRAIL_MOMENT_OPTIONS.map((option) => {
                        const isActive = trailMomentFilter === option.id;
                        const count = option.id === "todos"
                          ? trails.length
                          : trails.filter((trail) => getTrailMoment(trail) === option.id).length;
                        return (
                          <button
                            key={option.id}
                            onClick={() => setTrailMomentFilter(option.id)}
                            style={{
                              ...buttonReset,
                              minHeight: 46,
                              flex: "0 0 auto",
                              padding: "0 14px",
                              border: isActive ? "1px solid #e6b15a" : "1px solid rgba(255,255,255,0.10)",
                              background: isActive ? "#e6b15a" : "rgba(15,23,42,0.44)",
                              color: isActive ? "#1a140a" : "#aebbd1",
                              fontSize: 12,
                              fontWeight: 900,
                              boxShadow: isActive ? "0 14px 26px rgba(230,177,90,0.28)" : "none",
                              whiteSpace: "nowrap"
                            }}
                          >
                            {option.label}
                            <span style={{ marginLeft: 8, color: isActive ? "#6b4a17" : "#68758c" }}>{count}</span>
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ position: "absolute", top: 0, right: 0, bottom: 2, width: 34, pointerEvents: "none", background: "linear-gradient(90deg, rgba(6,11,22,0) 0%, rgba(6,11,22,0.92) 100%)" }} />
                  </div>
                </div>

                <div style={{ padding: "16px 18px", border: "1px solid rgba(255,255,255,0.10)", background: "linear-gradient(145deg, rgba(230,177,90,0.14), rgba(255,255,255,0.045))", color: "#f8fbff", boxShadow: "0 16px 28px rgba(0,0,0,0.18)" }}>
                  <p style={{ margin: "0 0 6px", fontSize: 10, letterSpacing: 1.1, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>{visibleTrails.length} trilhas</p>
                  <h3 style={{ margin: 0, fontSize: 18, lineHeight: 1.12, color: "#ffffff", fontWeight: 900 }}>{activeTrailMoment.title}</h3>
                  <p style={{ margin: "8px 0 0", fontSize: 12, lineHeight: 1.45, color: "#aebbd1" }}>{activeTrailMoment.hint}</p>
                  {filteredCompletedCount > 0 && (
                    <p style={{ margin: "10px 0 0", fontSize: 11, color: "#f0dcb8", fontWeight: 900 }}>{filteredCompletedCount} concluída{filteredCompletedCount > 1 ? "s" : ""}</p>
                  )}
                </div>
              </div>

              <div>
                {trailsLoading ? (
                  <p style={{ margin: 0, fontSize: 12, color: UI.textSoft }}>Carregando trilhas disponíveis...</p>
                ) : trails.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 12, color: UI.textSoft }}>Ainda não há trilhas disponíveis neste momento.</p>
                ) : visibleTrails.length === 0 ? (
                  <p style={{ margin: 0, fontSize: 12, color: UI.textSoft }}>{trailSearchQuery.trim() ? `Nenhuma trilha encontrada para "${trailSearchQuery.trim()}".` : "Ainda não há trilhas neste momento da caminhada."}</p>
                ) : (
                  <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(3, minmax(0, 1fr))" : isTablet ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 12 }}>
                    {visibleTrails.map(renderTrailCard)}
                  </div>
                )}
                {trailsError && (
                  <div style={{ marginTop: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(239,68,68,0.08)", borderRadius: 10, padding: "10px 12px" }}>
                    <p style={{ margin: 0, color: "#fca5a5", fontSize: 12 }}>{trailsError}</p>
                    <button onClick={loadTrailsData} style={{ ...buttonReset, color: "#f0dcb8", fontSize: 12, fontWeight: 700 }}>Tentar novamente</button>
                  </div>
)}
              </div>
              </>
              )}

            </div>
          )}

          {/* TAB: EBOOKS */}
          {activeTab === "ebooks" && (
            <div>
              <section style={{ position: "relative", ...overflowGuardStyle, minHeight: isDesktop ? 330 : "auto", padding: isDesktop ? "34px 38px" : isTablet ? "28px 22px" : "24px 20px", borderRadius: isDesktop ? 38 : isTablet ? 30 : 24, border: "1px solid rgba(255,255,255,0.28)", background: "linear-gradient(135deg, rgba(248,243,232,0.99) 0%, rgba(231,238,239,0.98) 48%, rgba(244,224,194,0.96) 100%)", color: "#182033", boxShadow: "0 22px 54px rgba(0,0,0,0.34)", marginBottom: 20 }}>
                <div style={{ position: "relative", display: isDesktop ? "grid" : "block", gridTemplateColumns: "minmax(0, 1.05fr) minmax(300px, 0.95fr)", gap: 30, alignItems: "center" }}>
                  <div>
                    <p style={{ margin: "0 0 12px", fontSize: 11, letterSpacing: 1.6, color: "#87582b", textTransform: "uppercase", fontWeight: 900 }}>Biblioteca de leitura</p>
                    <h2 style={{ margin: 0, maxWidth: 620, fontSize: mobileHeroTitleSize, lineHeight: 1.02, color: "#151b2a", fontWeight: 900 }}>Um lugar para ler, guardar e partilhar.</h2>
                    <p style={{ margin: "16px 0 0", maxWidth: 560, fontSize: isMobile ? 15 : 16, lineHeight: 1.62, color: "#425064" }}>
                      Escolha um livro, avance no seu ritmo e transforme trechos importantes em oração, conversa ou convite para alguém caminhar junto.
                    </p>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 20 }}>
                      {["Leitura guiada", "Marcas", "Reflexões", "Partilha"].map((item) => (
                        <span key={item} style={{ padding: "8px 10px", borderRadius: 999, background: "rgba(21,27,42,0.08)", color: "#283247", fontSize: 11, fontWeight: 900 }}>{item}</span>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => openEbook(continueEbook.id)} style={{ ...buttonReset, width: "100%", maxWidth: "100%", boxSizing: "border-box", marginTop: isDesktop ? 0 : 24, padding: isMobile ? 14 : 18, borderRadius: isMobile ? 20 : 26, background: "rgba(255,255,255,0.52)", border: "1px solid rgba(21,27,42,0.12)", boxShadow: "0 18px 34px rgba(73,83,100,0.16)", textAlign: "left", color: "#151b2a", overflow: "hidden" }}>
                    <p style={{ margin: "0 0 12px", fontSize: 11, letterSpacing: 1.3, color: "#87582b", textTransform: "uppercase", fontWeight: 900 }}>{hasContinueEbookProgress ? "Continue sua leitura" : heroJustCompletedSuggestingNext ? "Você concluiu — comece outro" : heroFinishedWholeCollection ? "Você leu tudo por aqui" : "Comece por aqui"}</p>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "72px minmax(0, 1fr)" : "86px 1fr", gap: isMobile ? 13 : 16, alignItems: "start" }}>
                      <img src={continueEbook.coverImage} alt={continueEbook.title} style={{ width: isMobile ? 72 : 86, height: isMobile ? 104 : 124, objectFit: "cover", objectPosition: "top center", boxShadow: "0 16px 28px rgba(21,27,42,0.22)" }} />
                      <div style={{ minWidth: 0 }}>
                        <h3 style={{ margin: 0, fontSize: isMobile ? 21 : 24, lineHeight: 1.05, color: "#151b2a", fontWeight: 900 }}>{continueEbook.title}</h3>
                        <p style={{ margin: "8px 0 0", fontSize: 13, lineHeight: 1.5, color: "#4c596b" }}>{continueEbook.subtitle}</p>
                        {hasContinueEbookProgress ? (
                          <>
                            <p style={{ margin: "16px 0 8px", fontSize: 11, color: "#87582b", fontWeight: 900 }}>{continueEbookPage} de {continueEbook.pages} páginas</p>
                            <div style={{ height: 8, background: "rgba(21,27,42,0.10)", padding: 2 }}>
                              <div style={{ width: `${continueEbookProgress}%`, height: "100%", background: continueEbook.coverColor }} />
                            </div>
                          </>
                        ) : (
                          <p style={{ margin: "16px 0 0", fontSize: 11, color: "#87582b", fontWeight: 900 }}>{continueEbook.pages} páginas disponíveis</p>
                        )}
                        <p style={{ margin: "12px 0 0", fontSize: 12, color: "#151b2a", fontWeight: 900 }}>{hasContinueEbookProgress ? "Continuar leitura" : heroJustCompletedSuggestingNext ? "Começar este livro" : heroFinishedWholeCollection ? "Reler este livro" : "Abrir este livro"}</p>
                      </div>
                    </div>
                  </button>
                </div>
              </section>

              <div style={{ display: "flex", alignItems: isTablet ? "end" : "start", justifyContent: "space-between", gap: 16, margin: "0 0 14px", flexDirection: isTablet ? "row" : "column" }}>
                <div>
                  <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>Acervo</p>
                  <h2 style={{ margin: 0, fontSize: 28, lineHeight: 1.05, color: "#ffffff", fontWeight: 900 }}>Escolha a próxima leitura.</h2>
                </div>
                <p style={{ margin: 0, maxWidth: 360, fontSize: 13, lineHeight: 1.55, color: "#aebbd1" }}>{EBOOKS_MOCK.length} livros disponíveis para ler dentro do app. O progresso fica salvo depois do cadastro gratuito.</p>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(3, minmax(0, 1fr))" : isTablet ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 14 }}>
                {EBOOKS_MOCK.map((ebook) => {
                  const savedPage = getSavedEbookPage(ebook);
                  const progress = Math.round((savedPage / Math.max(1, ebook.pages || 1)) * 100);
                  const hasEbookProgress = canUseSavedProgress && savedPage > 1;
                  // Mesmo tratamento de "concluido" que os cards de trilha ja
                  // tem (isTrailDone: overlay escurecido + selo com check) --
                  // pedido do Rodrigo pra dar esse mesmo destaque aqui, ja que
                  // sao poucos e-books e da pra notar facil quais ja foram
                  // lidos ate o fim.
                  const ebookDone = isEbookDone(ebook);
                  return (
                    <button key={ebook.id} onClick={() => openEbook(ebook.id)} style={{ ...buttonReset, ...overflowGuardStyle, position: "relative", minHeight: isMobile ? 210 : 238, padding: isMobile ? 12 : 14, textAlign: "left", border: ebookDone ? "1px solid rgba(134,239,172,0.42)" : "1px solid rgba(255,255,255,0.10)", background: `linear-gradient(145deg, ${ebook.coverColor}20, rgba(255,255,255,0.045))`, color: "#f8fbff", display: "grid", gridTemplateColumns: isMobile ? "74px minmax(0, 1fr)" : "92px minmax(0, 1fr)", alignItems: "start", gap: isMobile ? 12 : 14, boxShadow: "0 16px 30px rgba(0,0,0,0.22)" }}>
                      {ebookDone && (
                        <span style={{ position: "absolute", inset: 0, background: "rgba(4,8,15,0.40)", pointerEvents: "none" }} />
                      )}
                      <img src={ebook.coverImage} alt={ebook.title} style={{ width: isMobile ? 74 : 92, height: isMobile ? 108 : 132, objectFit: "cover", objectPosition: "top center", borderRadius: 7, boxShadow: "0 14px 24px rgba(0,0,0,0.38)" }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
                          <p style={{ margin: "0 0 7px", fontSize: 10, color: ebook.coverColor, textTransform: "uppercase", letterSpacing: 1.1, fontWeight: 900 }}>{ebookDone ? "Concluído · reler" : hasEbookProgress ? "Continuar leitura" : "Começar leitura"}</p>
                          {ebookDone && (
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "5px 8px", borderRadius: 999, background: "rgba(134,239,172,0.16)", border: "1px solid rgba(134,239,172,0.5)", color: "#86EFAC", fontSize: 9, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.7, whiteSpace: "nowrap" }}>
                              <CheckCircle2 size={11} strokeWidth={2.5} /> Concluído
                            </span>
                          )}
                        </div>
                        <h3 style={{ margin: 0, fontSize: 18, lineHeight: 1.08, color: "#ffffff", fontWeight: 900 }}>{ebook.title}</h3>
                        <p style={{ margin: "8px 0 0", fontSize: 12, lineHeight: 1.45, color: "#c7d1e4" }}>{ebook.subtitle}</p>
                        {hasEbookProgress ? (
                          <>
                            <p style={{ margin: "14px 0 8px", fontSize: 11, color: "#8fa0bf", fontWeight: 800 }}>{savedPage} de {ebook.pages} páginas</p>
                            <div style={{ height: 8, background: "rgba(255,255,255,0.10)", padding: 2 }}>
                              <div style={{ width: `${progress}%`, height: "100%", background: ebook.coverColor }} />
                            </div>
                            <p style={{ margin: "8px 0 0", fontSize: 10, color: "#9fb0cc", fontWeight: 800 }}>{progress}% lido</p>
                          </>
                        ) : (
                          <p style={{ margin: "14px 0 0", fontSize: 11, color: "#8fa0bf", fontWeight: 800 }}>{ebook.pages} páginas</p>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* TAB: FAMILY */}
          {activeTab === "family" && (
            <div>
              <section style={{ ...warmHeroStyle, ...overflowGuardStyle }}>
                <p style={{ ...eyebrowStyle, color: "#87582b" }}>Mesa e cuidado</p>
                <h2 style={{ margin: 0, maxWidth: 660, fontSize: mobileHeroTitleSize, lineHeight: 1, color: "#151b2a", fontWeight: 900 }}>Um lugar para não caminhar sozinho.</h2>
                <p style={{ margin: "16px 0 0", maxWidth: 610, fontSize: isMobile ? 15 : 16, lineHeight: 1.62, color: "#425064" }}>Entre em uma mesa de devocional ou solicite uma conversa pastoral. O foco aqui é vínculo, cuidado e caminhada possível para quem está perto ou acompanha de longe.</p>
              </section>

              <section style={{ ...overflowGuardStyle, marginBottom: 16, display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 1.15fr) minmax(320px, 0.85fr)" : "minmax(0, 1fr)", gap: 14, alignItems: "stretch" }}>
                <div style={{ ...overflowGuardStyle, padding: isDesktop ? 24 : 18, border: "1px solid rgba(255,255,255,0.12)", background: "linear-gradient(145deg, rgba(255,255,255,0.07), rgba(15,23,42,0.72))", boxShadow: "0 18px 34px rgba(0,0,0,0.22)" }}>
                  <div style={{ display: "flex", alignItems: isTablet ? "end" : "start", justifyContent: "space-between", gap: 16, marginBottom: 18, flexDirection: isTablet ? "row" : "column" }}>
                    <div>
                      <p style={{ ...eyebrowStyle, color: "#f0dcb8" }}>O que você precisa agora?</p>
                      <h3 style={{ margin: 0, fontSize: mobileSectionTitleSize, lineHeight: 1.05, color: "#ffffff", fontWeight: 900 }}>Escolha o jeito de caminhar junto.</h3>
                    </div>
                    <p style={{ margin: 0, maxWidth: 410, fontSize: 13, lineHeight: 1.55, color: "#aebbd1" }}>
                      Você pode convidar uma pessoa, entrar numa mesa devocional ou pedir uma conversa pastoral.
                    </p>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(3, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 12 }}>
                    {mesaIntentOptions.map((item) => {
                      const active = mesaIntent === item.id;
                      return (
                        <button
                          key={item.id}
                          onClick={() => runMesaIntentAction(item)}
                          style={{
                            ...buttonReset,
                            minHeight: 214,
                            padding: 18,
                            textAlign: "left",
                            border: active ? `1px solid ${item.color}` : `1px solid ${item.color}44`,
                            background: active ? `linear-gradient(160deg, ${item.color}28, rgba(255,255,255,0.06))` : `linear-gradient(160deg, ${item.color}16, rgba(255,255,255,0.035))`,
                            color: "#f8fbff",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                            boxShadow: active ? `0 18px 32px ${item.color}18` : "none"
                          }}
                        >
                          <span style={{ width: 46, height: 46, borderRadius: 16, background: `${item.color}22`, color: item.color, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${item.color}66` }}>
                            {item.icon}
                          </span>
                          <span>
                            <span style={{ display: "block", marginBottom: 8, fontSize: 10, color: item.color, textTransform: "uppercase", letterSpacing: 1.1, fontWeight: 900 }}>{item.label}</span>
                            <strong style={{ display: "block", fontSize: 18, lineHeight: 1.08, color: "#ffffff", fontWeight: 950 }}>{item.title}</strong>
                            <span style={{ display: "block", marginTop: 9, color: "#c7d1e4", fontSize: 12, lineHeight: 1.48 }}>{item.text}</span>
                          </span>
                          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: item.color, fontSize: 12, fontWeight: 950 }}>
                            {item.action} <ChevronRight size={15} strokeWidth={2.4} />
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div style={{ ...overflowGuardStyle, padding: isDesktop ? 24 : 18, border: `1px solid ${activeMesaIntent.color}50`, background: `linear-gradient(145deg, ${activeMesaIntent.color}18, rgba(255,255,255,0.045))`, color: "#f8fbff", boxShadow: "0 18px 34px rgba(0,0,0,0.22)", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 18 }}>
                  <div>
                    <p style={{ ...eyebrowStyle, color: activeMesaIntent.color }}>Próximo passo</p>
                    <h3 style={{ margin: 0, fontSize: 25, lineHeight: 1.05, color: "#ffffff", fontWeight: 900 }}>{activeMesaIntent.title}</h3>
                    <p style={{ margin: "12px 0 0", fontSize: 13, lineHeight: 1.58, color: "#c7d1e4" }}>{activeMesaIntent.text}</p>
                  </div>
                  {activeMesaIntent.id === "duo" ? (
                    <div style={{ display: "grid", gap: 10 }}>
                      <label>
                        <span style={{ display: "block", margin: "0 0 6px", color: "#f0dcb8", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 900 }}>Trilha do convite<span style={{ color: "#f0a84e", fontWeight: 700 }}> *</span></span>
                        <select
                          value={mesaInviteTrail?.id || ""}
                          onChange={(event) => {
                            setMesaInviteTrailId(event.target.value);
                            setMesaInviteFeedback("");
                          }}
                          style={{ width: "100%", border: `1px solid ${activeMesaIntent.color}44`, background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                        >
                          {trailsAlphabetical.map((trail) => (
                            <option key={trail.id} value={trail.id}>{trail.title}</option>
                          ))}
                        </select>
                      </label>

                      <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 8 }}>
                        <label>
                          <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Nome<span style={{ color: "#f0a84e", fontWeight: 700 }}> *</span></span>
                          <input
                            value={mesaInviteName}
                            onChange={(event) => {
                              setMesaInviteName(event.target.value);
                              setMesaInviteFeedback("");
                            }}
                            placeholder="Nome do convidado"
                            style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                          />
                        </label>
                        <label>
                          <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>E-mail</span>
                          <input
                            value={mesaInviteEmail}
                            onChange={(event) => {
                              setMesaInviteEmail(event.target.value);
                              setMesaInviteFeedback("");
                            }}
                            placeholder="email@exemplo.com"
                            inputMode="email"
                            style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                          />
                        </label>
                      </div>

                      <label>
                        <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>WhatsApp</span>
                        <input
                          value={mesaInviteWhatsapp}
                          onChange={(event) => {
                            setMesaInviteWhatsapp(event.target.value);
                            setMesaInviteFeedback("");
                          }}
                          placeholder="(00) 00000-0000"
                          inputMode="tel"
                          style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                        />
                      </label>

                      <label>
                        <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Mensagem opcional</span>
                        <textarea
                          value={mesaInviteMessage}
                          onChange={(event) => {
                            setMesaInviteMessage(event.target.value);
                            setMesaInviteFeedback("");
                          }}
                          placeholder="Escreva algo pessoal para esse convite"
                          rows={3}
                          style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, lineHeight: 1.45, fontFamily: "inherit", boxSizing: "border-box", outline: "none", resize: "vertical" }}
                        />
                      </label>

                      <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 8 }}>
                        <button onClick={generateMesaInviteLink} style={{ ...buttonReset, minHeight: 46, padding: "0 14px", border: `1px solid ${activeMesaIntent.color}66`, background: `${activeMesaIntent.color}18`, color: "#fff8eb", fontSize: 12, fontWeight: 950 }}>
                          Gerar link do convite
                        </button>
                        <button onClick={shareMesaInviteByWhatsapp} style={{ ...buttonReset, minHeight: 46, padding: "0 14px", border: "1px solid rgba(42,195,122,0.55)", background: "rgba(42,195,122,0.14)", color: "#baf7d2", fontSize: 12, fontWeight: 950 }}>
                          Compartilhar no WhatsApp
                        </button>
                      </div>
                      {mesaInviteFeedback && <p style={{ margin: 0, color: "#c7d1e4", fontSize: 11, lineHeight: 1.4 }}>{mesaInviteFeedback}</p>}
                    </div>
                  ) : activeMesaIntent.id === "group" ? (
                    <div style={{ display: "grid", gap: 12 }}>
                      <div style={{ display: "grid", gap: 9, padding: 13, borderRadius: 16, border: "1px solid rgba(42,195,122,0.22)", background: "rgba(42,195,122,0.07)" }}>
                        <p style={{ margin: 0, color: "#BFF3D0", fontSize: 12, lineHeight: 1.45, fontWeight: 800 }}>
                          Abra uma mesa para qualquer pessoa do app entrar, ou sente em uma mesa que já está em andamento.
                        </p>
                        <label>
                          <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Trilha da mesa</span>
                          <select
                            value={openMesaForm.trailId}
                            onChange={(event) => setOpenMesaForm((current) => ({ ...current, trailId: event.target.value }))}
                            style={{ width: "100%", border: "1px solid rgba(42,195,122,0.32)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                          >
                            {trailsAlphabetical.map((trail) => (
                              <option key={trail.id} value={trail.id}>{trail.title}</option>
                            ))}
                          </select>
                        </label>
                        <div style={{ display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) 92px" : "1fr", gap: 8 }}>
                          <label>
                            <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Nome da mesa</span>
                            <input
                              value={openMesaForm.name}
                              onChange={(event) => setOpenMesaForm((current) => ({ ...current, name: event.target.value }))}
                              placeholder={openMesaTrail ? `Mesa: ${openMesaTrail.title}` : "Nome da mesa"}
                              style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                            />
                          </label>
                          <label>
                            <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Vagas</span>
                            <input
                              value={openMesaForm.capacity}
                              onChange={(event) => setOpenMesaForm((current) => ({ ...current, capacity: event.target.value }))}
                              inputMode="numeric"
                              style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                            />
                          </label>
                        </div>
                        <textarea
                          value={openMesaForm.description}
                          onChange={(event) => setOpenMesaForm((current) => ({ ...current, description: event.target.value }))}
                          placeholder="Uma frase simples sobre a caminhada dessa mesa"
                          rows={3}
                          style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", borderRadius: 12, padding: "11px 12px", fontSize: isMobile ? 16 : 13, lineHeight: 1.45, fontFamily: "inherit", boxSizing: "border-box", outline: "none", resize: "vertical" }}
                        />
                        <button onClick={createOpenMesa} disabled={openMesaSaving} style={{ ...buttonReset, minHeight: 46, padding: "0 14px", border: "1px solid rgba(42,195,122,0.55)", background: "rgba(42,195,122,0.16)", color: "#BFF3D0", fontSize: 12, fontWeight: 950, opacity: openMesaSaving ? 0.7 : 1 }}>
                          {openMesaSaving ? "Abrindo mesa..." : "Abrir mesa aberta"}
                        </button>
                      </div>

                      <button
                        onClick={loadOpenGroups}
                        disabled={openGroupsLoading}
                        style={{ ...buttonReset, minHeight: 44, padding: "0 14px", border: "1px solid rgba(42,195,122,0.40)", background: "rgba(255,255,255,0.05)", color: "#BFF3D0", fontSize: 12, fontWeight: 950, opacity: openGroupsLoading ? 0.7 : 1 }}
                      >
                        {openGroupsLoading ? "Carregando mesas..." : "Atualizar mesas abertas"}
                      </button>
                      {openGroupsFeedback && <p style={{ margin: 0, color: openGroupsFeedback.includes("Não") ? "#FCA5A5" : "#BFF3D0", fontSize: 11, lineHeight: 1.45 }}>{openGroupsFeedback}</p>}
                    </div>
                  ) : (
                    <button
                      onClick={() => runMesaIntentAction(activeMesaIntent)}
                      style={{ ...buttonReset, minHeight: 48, padding: "0 16px", border: `1px solid ${activeMesaIntent.color}66`, background: `${activeMesaIntent.color}18`, color: "#fff8eb", fontSize: 13, fontWeight: 950, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}
                    >
                      {activeMesaIntent.action}
                      <ChevronRight size={17} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              </section>

              <div style={{ ...sectionCardStyle, padding: isTablet ? 22 : 18, borderRadius: 0, border: "1px solid rgba(42,195,122,0.28)", background: "linear-gradient(145deg, rgba(42,195,122,0.10), rgba(15,23,42,0.78))" }}>
                <div style={{ display: "flex", alignItems: isTablet ? "end" : "start", justifyContent: "space-between", gap: 14, flexDirection: isTablet ? "row" : "column", marginBottom: 14 }}>
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#BFF3D0", textTransform: "uppercase", fontWeight: 900 }}>Mesas abertas</p>
                    <h3 style={{ margin: 0, color: "#FFF", fontSize: 24, lineHeight: 1.05, fontWeight: 950 }}>Chegue e sente em uma caminhada aberta.</h3>
                  </div>
                  <button onClick={loadOpenGroups} disabled={openGroupsLoading} style={{ ...buttonReset, minHeight: 38, padding: "0 13px", border: "1px solid rgba(42,195,122,0.40)", background: "rgba(42,195,122,0.10)", color: "#BFF3D0", fontSize: 11, fontWeight: 950, opacity: openGroupsLoading ? 0.7 : 1, whiteSpace: "nowrap" }}>
                    {openGroupsLoading ? "Atualizando" : "Atualizar"}
                  </button>
                </div>
                {openGroupsFeedback && <p style={{ margin: "0 0 12px", color: openGroupsFeedback.includes("Não") ? "#FCA5A5" : "#BFF3D0", fontSize: 12, lineHeight: 1.45 }}>{openGroupsFeedback}</p>}
                {openGroupsLoading && openGroups.length === 0 ? (
                  <p style={{ margin: 0, color: "#aebbd1", fontSize: 13 }}>Carregando mesas abertas...</p>
                ) : openGroups.length > 0 ? (
                  <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(3, minmax(0, 1fr))" : isTablet ? "repeat(2, minmax(0, 1fr))" : "1fr", gap: 10 }}>
                    {openGroups.map((group) => {
                      const activeMembers = group.members?.filter((member) => ["ACTIVE", "INVITED"].includes(member.status)) || [];
                      const remainingSeats = group.capacity ? Math.max(0, group.capacity - activeMembers.length) : null;
                      const isJoining = joiningOpenGroupId === group.id;
                      // Quando a pessoa ja esta sentada nessa mesa, "Sentar a
                      // mesa" de novo era o unico jeito de abrir -- nada no
                      // card avisava isso, entao parecia que nao dava pra
                      // abrir (relatado pelo Rodrigo). Detecta a membresia e
                      // troca o rotulo/():dispensa a trava de "mesa cheia"
                      // pra quem ja esta dentro.
                      const isMember = activeMembers.some((member) => member.userId && viewerUser?.id && member.userId === viewerUser.id);
                      return (
                        <div key={group.id} style={{ minHeight: 220, padding: 15, border: `1px solid ${isMember ? "rgba(230,177,90,0.5)" : "rgba(42,195,122,0.22)"}`, background: "rgba(255,255,255,0.055)", display: "flex", flexDirection: "column", justifyContent: "space-between", gap: 14 }}>
                          <div>
                            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 8 }}>
                              <p style={{ margin: 0, color: "#BFF3D0", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 950 }}>{group.trail?.title || "Trilha devocional"}</p>
                              {isMember && (
                                <span style={{ padding: "3px 8px", borderRadius: 999, background: "rgba(230,177,90,0.18)", border: "1px solid rgba(230,177,90,0.5)", color: "#F5D28B", fontSize: 9, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.6, whiteSpace: "nowrap" }}>Você está aqui</span>
                              )}
                            </div>
                            <h4 style={{ margin: 0, color: "#FFFFFF", fontSize: 17, lineHeight: 1.12, fontWeight: 950 }}>{group.name}</h4>
                            <p style={{ margin: "9px 0 0", color: "#aebbd1", fontSize: 12, lineHeight: 1.45 }}>{group.description || group.trail?.subtitle || "Uma mesa aberta para caminhar com calma e conversa."}</p>
                          </div>
                          <div>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8, marginBottom: 12 }}>
                              <div style={{ padding: 9, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(15,23,42,0.36)" }}>
                                <p style={{ margin: 0, color: "#738198", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 900 }}>Pessoas</p>
                                <p style={{ margin: "5px 0 0", color: "#F8FAFC", fontSize: 13, fontWeight: 950 }}>{activeMembers.length}{group.capacity ? `/${group.capacity}` : ""}</p>
                              </div>
                              <div style={{ padding: 9, border: "1px solid rgba(255,255,255,0.08)", background: "rgba(15,23,42,0.36)" }}>
                                <p style={{ margin: 0, color: "#738198", fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 900 }}>Anfitrião</p>
                                <p style={{ margin: "5px 0 0", color: "#F8FAFC", fontSize: 13, fontWeight: 950, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{group.owner?.name || "Mosaico"}</p>
                              </div>
                            </div>
                            {activeMembers.length > 0 && (
                              <p style={{ margin: "0 0 12px", color: "#aebbd1", fontSize: 11, lineHeight: 1.5 }}>
                                <span style={{ display: "block", color: "#738198", textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 900, fontSize: 10, marginBottom: 3 }}>Quem já está aqui</span>
                                {activeMembers.slice(0, 5).map((member) => member.displayName || "Alguém").join(", ")}
                                {activeMembers.length > 5 ? ` e mais ${activeMembers.length - 5}` : ""}
                              </p>
                            )}
                            <button onClick={() => joinOpenMesa(group)} disabled={isJoining || (remainingSeats === 0 && !isMember)} style={{ ...buttonReset, width: "100%", minHeight: 44, padding: "0 13px", border: isMember ? "1px solid rgba(230,177,90,0.55)" : "1px solid rgba(42,195,122,0.50)", background: isMember ? "rgba(230,177,90,0.18)" : remainingSeats === 0 ? "rgba(148,163,184,0.10)" : "rgba(42,195,122,0.16)", color: isMember ? "#F5D28B" : remainingSeats === 0 ? "#94A3B8" : "#BFF3D0", fontSize: 12, fontWeight: 950, opacity: isJoining ? 0.7 : 1 }}>
                              {isJoining ? "Abrindo..." : isMember ? "Abrir mesa" : remainingSeats === 0 ? "Mesa cheia" : "Sentar à mesa"}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ padding: 16, border: "1px solid rgba(42,195,122,0.18)", background: "rgba(255,255,255,0.045)" }}>
                    <p style={{ margin: 0, color: "#c7d1e4", fontSize: 13, lineHeight: 1.5 }}>Ainda não há mesas abertas. Você pode abrir a primeira e deixar disponível para quem quiser caminhar junto.</p>
                  </div>
                )}
              </div>

              <div style={{ ...sectionCardStyle, padding: isTablet ? 22 : 18, borderRadius: 0, border: "1px solid rgba(230,177,90,0.30)", backgroundColor: "rgba(13,23,37,0.76)", backgroundImage: "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='180' height='180' viewBox='0 0 180 180'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.82' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='180' height='180' filter='url(%23n)' opacity='.16'/%3E%3C/svg%3E\"), linear-gradient(160deg, rgba(15,23,39,0.76), rgba(22,34,55,0.50))", backgroundSize: "180px 180px, auto", backgroundBlendMode: "screen, normal" }}>
                <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 0.85fr) minmax(0, 1.15fr)" : "1fr", gap: 16, alignItems: "stretch" }}>
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#f0dcb8", textTransform: "uppercase", fontWeight: 900 }}>Convites em andamento</p>
                    <h3 style={{ margin: 0, fontSize: 24, lineHeight: 1.05, color: "#FFF", fontWeight: 950 }}>Acompanhe quem você chamou.</h3>
                    <p style={{ margin: "10px 0 0", fontSize: 13, lineHeight: 1.55, color: "#aebbd1" }}>
                      Depois de enviar um convite, o próximo passo não é esquecer. É acompanhar a resposta e começar a trilha junto.
                    </p>
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    <div style={{ padding: 14, border: "1px solid rgba(230,177,90,0.46)", background: "linear-gradient(145deg, rgba(230,177,90,0.22), rgba(255,255,255,0.055))", display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) auto" : "1fr", gap: 12, alignItems: "center" }}>
                      <div>
                        <p style={{ margin: "0 0 6px", color: "#f0dcb8", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 900 }}>Próximo convite</p>
                        <h4 style={{ margin: 0, color: "#fff", fontSize: 17, fontWeight: 950 }}>{invitePreviewName}</h4>
                        <p style={{ margin: "5px 0 0", color: "#aebbd1", fontSize: 12, lineHeight: 1.45 }}>{invitePreviewContact} · {mesaInviteTrail?.title || "Escolha uma trilha"}</p>
                      </div>
                      <span style={{ padding: "8px 10px", border: "1px solid rgba(230,177,90,0.55)", background: "rgba(230,177,90,0.18)", color: "#F8DFA8", fontSize: 11, fontWeight: 950, whiteSpace: "nowrap" }}>
                        {mesaInviteFeedback ? "Convite preparado" : "Aguardando envio"}
                      </span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(3, 1fr)" : "1fr", gap: 8 }}>
                      {[
                        ["Trilha", mesaInviteTrail?.duration ? `${mesaInviteTrail.duration} dias` : "Selecionar"],
                        ["Ritmo", "Vocês dois"],
                        ["Retorno", mesaInviteFeedback ? "Acompanhar" : "Pendente"]
                      ].map(([label, value]) => (
                        <div key={label} style={{ minHeight: 72, padding: 12, border: "1px solid rgba(230,177,90,0.20)", background: "rgba(255,255,255,0.055)" }}>
                          <p style={{ margin: "0 0 7px", color: "#738198", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>{label}</p>
                          <p style={{ margin: 0, color: "#f8fbff", fontSize: 13, fontWeight: 900 }}>{value}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          )}

          {/* TAB: CONECTA */}
          {activeTab === "conecta" && (
            <div style={widthGuardStyle}>
              <section style={{ ...sectionCardStyle, ...overflowGuardStyle, padding: isDesktop ? 28 : 18, borderRadius: isDesktop ? 18 : 12, borderColor: "rgba(20,184,166,0.38)", background: "linear-gradient(145deg, rgba(13,47,54,0.86), rgba(15,23,42,0.98))" }}>
                <p style={{ margin: "0 0 8px", color: "#5EEAD4", fontSize: 11, letterSpacing: 1.4, textTransform: "uppercase", fontWeight: 950 }}>Agenda de cuidado</p>
                <h2 style={{ margin: 0, color: "#F8FAFC", fontSize: isDesktop ? 36 : isTablet ? 30 : 24, lineHeight: 1.05, fontWeight: 950, overflowWrap: "anywhere" }}>
                  O que precisa de atenção agora.
                </h2>
                <p style={{ margin: "14px 0 0", maxWidth: 720, color: "#B6C5D8", fontSize: 14, lineHeight: 1.6 }}>
                  Horários abertos, conversas de acolhimento e o calendário pastoral da semana — tudo num só lugar.
                </p>
              </section>

              {!isCareManager && (
                <div style={{ ...sectionCardStyle, ...overflowGuardStyle, padding: isMobile ? 16 : 20, borderRadius: isDesktop ? 18 : 12, borderColor: "rgba(252,211,77,0.34)", background: "rgba(30,41,59,0.92)" }}>
                  <h3 style={{ margin: "0 0 8px", color: "#FFF", fontSize: 22 }}>Acesso restrito</h3>
                  <p style={{ margin: "0 0 14px", color: "#B6C5D8", fontSize: 13, lineHeight: 1.55 }}>A Agenda de cuidado é uma área de liderança. Entre com uma conta admin para acessar.</p>
                  {!viewerUser?.sessionToken ? (
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr auto" : "1fr", gap: 9, alignItems: "end" }}>
                      <label>
                        <span style={profileLabelStyle}>E-mail</span>
                        <input value={loginForm.email} onChange={(event) => updateLoginForm("email", event.target.value)} placeholder="email@batistamosaico.com.br" inputMode="email" style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Senha</span>
                        <PasswordField value={loginForm.password} onChange={(event) => updateLoginForm("password", event.target.value)} placeholder="Senha" style={profileInputStyle} visible={!!visiblePasswords.conectaLogin} onToggleVisibility={() => togglePasswordVisibility("conectaLogin")} />
                      </label>
                      <button onClick={loginWithPassword} disabled={loginLoading} style={{ ...buttonReset, minHeight: 46, padding: "0 16px", border: "1px solid rgba(252,211,77,0.45)", background: "rgba(252,211,77,0.14)", color: "#FDE68A", fontSize: 12, fontWeight: 950, opacity: loginLoading ? 0.7 : 1 }}>
                        {loginLoading ? "Entrando..." : "Entrar"}
                      </button>
                    </div>
                  ) : (
                    <p style={{ margin: 0, color: "#FDE68A", fontSize: 12 }}>Sua conta atual não tem permissão de admin para acessar esta área.</p>
                  )}
                  {loginFeedback && <p style={{ margin: "10px 0 0", color: loginFeedback.includes("liberado") ? "#A7F3D0" : "#F8DFA8", fontSize: 12 }}>{loginFeedback}</p>}
                </div>
              )}

              {isCareManager && (
                <div style={{ ...sectionCardStyle, padding: isDesktop ? 22 : 16, borderColor: "rgba(125,211,252,0.35)", background: "linear-gradient(145deg, rgba(8,47,73,0.64), rgba(15,23,42,0.96))" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 15 }}>
                    <div>
                      <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.35, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Painel de cuidado</p>
                      <h3 style={{ margin: 0, fontSize: isDesktop ? 24 : 20, color: "#FFF", fontWeight: 950 }}>Agenda de acolhimento pastoral</h3>
                      <p style={{ margin: "8px 0 0", maxWidth: 620, fontSize: 12, color: "#aebbd1", lineHeight: 1.5 }}>Visão restrita para liderança acompanhar horários abertos, conversas de acolhimento e o calendário da semana.</p>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexShrink: 0 }}>
                      <button onClick={() => setShowSlotFormModal(true)} style={{ ...buttonReset, display: "inline-flex", alignItems: "center", gap: 6, minHeight: 38, padding: "0 14px", border: "1px solid rgba(74,222,128,0.4)", background: "rgba(34,197,94,0.16)", color: "#86EFAC", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" }}>
                        <Plus size={13} />
                        Novo horário
                      </button>
                      <button onClick={loadAdminOverview} disabled={adminLoading} style={{ ...buttonReset, minHeight: 38, padding: "0 13px", border: "1px solid rgba(125,211,252,0.38)", background: "rgba(14,165,233,0.12)", color: "#BAE6FD", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap", opacity: adminLoading ? 0.7 : 1 }}>
                        {adminLoading ? "Atualizando" : "Atualizar"}
                      </button>
                    </div>
                  </div>
                  {adminFeedback && <p style={{ margin: "0 0 12px", color: "#FCA5A5", fontSize: 12 }}>{adminFeedback}</p>}
                  {Array.isArray(adminOverview?.pendingSlotRequestsList) && adminOverview.pendingSlotRequestsList.length > 0 && (
                    <div style={{ marginTop: 10, marginBottom: 10, border: "1px solid rgba(94,234,212,0.28)", background: "rgba(20,184,166,0.08)", padding: 12 }}>
                      <p style={{ margin: "0 0 8px", color: "#99F6E4", fontSize: 11, letterSpacing: 1, textTransform: "uppercase", fontWeight: 950 }}>
                        Pedidos em aberto ({adminOverview.pendingSlotRequestsList.length})
                      </p>
                      <p style={{ margin: "0 0 10px", color: "#aebbd1", fontSize: 11, lineHeight: 1.5 }}>Pessoas esperando algum acolhedor abrir horário. Avisamos sozinhos (push, e-mail e sininho) assim que um horário novo for aberto pra essa pessoa.</p>
                      <div style={{ display: "grid", gap: 8 }}>
                        {adminOverview.pendingSlotRequestsList.map((item) => (
                          <div key={item.id} style={{ padding: 10, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.035)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                            <div style={{ minWidth: 0 }}>
                              <p style={{ margin: 0, color: "#FFF", fontSize: 12, fontWeight: 900 }}>{item.userName} <span style={{ color: "#9fb0cc", fontWeight: 700 }}>→ {item.pastorName}</span></p>
                              {item.note && <p style={{ margin: "3px 0 0", color: "#9FB0CC", fontSize: 11, lineHeight: 1.4 }}>{item.note}</p>}
                              <p style={{ margin: "3px 0 0", color: "#6B7A94", fontSize: 10 }}>Pedido em {new Date(item.createdAt).toLocaleDateString("pt-BR")}</p>
                            </div>
                            <button
                              onClick={() => {
                                setAdminSlotForm((prev) => ({ ...prev, pastorId: item.pastorId }));
                                setShowSlotFormModal(true);
                              }}
                              style={{ ...buttonReset, minHeight: 32, padding: "0 10px", border: "1px solid rgba(74,222,128,0.4)", background: "rgba(34,197,94,0.14)", color: "#86EFAC", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap" }}
                            >
                              Abrir horário
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: 10, border: "1px solid rgba(125,211,252,0.20)", background: "rgba(255,255,255,0.035)", padding: 12 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 9 }}>
                      <p style={{ margin: 0, color: "#F8FAFC", fontSize: 13, fontWeight: 900 }}>Mapa de horários</p>
                      {adminOverview?.metrics?.pendingBookings > 0 && (
                        // Esse numero e' global (qualquer mes, qualquer acolhedor) --
                        // clicavel pra sempre levar ate o item, em vez de so avisar
                        // que existe e deixar a pessoa procurando no mes/filtro
                        // errado (achado do Rodrigo: "o filtro aqui diz q tem
                        // aguardando resposta mas n carrega nada", 10/2026).
                        <button
                          type="button"
                          onClick={() => {
                            // O numero do badge e' uma contagem global (qualquer
                            // mes/acolhedor), mas o calendario so' busca um mes
                            // por vez -- so' trocar pro mes atual nao garante
                            // achar o item (ele pode estar marcado pra outro
                            // mes). pendingBookingsList vem pronto do backend
                            // (todas as pendentes, sem recorte de mes nem limite
                            // por "mais recente criado" -- recentBookings e' por
                            // data de criacao e pode nao conter a pendencia se
                            // outras conversas mais novas foram criadas depois
                            // dela, que foi exatamente o bug visto em producao).
                            const pendingBooking = (adminOverview?.pendingBookingsList || [])[0];
                            if (pendingBooking) {
                              const startDate = new Date(pendingBooking.startAt);
                              setCalendarFilterMonth(startDate.getMonth() + 1);
                              setCalendarFilterYear(startDate.getFullYear());
                              setCalendarFilterPastorId("all");
                              setCalendarStatusFilter("PENDING");
                              setAdminBookingActionFeedback("");
                            } else {
                              // So' acontece se houver mais de 20 pendencias ao
                              // mesmo tempo (limite da lista) -- avisa em vez de
                              // levar pra um mes qualquer sem garantia nenhuma.
                              setCalendarStatusFilter("PENDING");
                              setAdminBookingActionFeedback("Tem mais conversas pendentes do que consigo listar de uma vez -- clique em \"Atualizar\" e tente de novo.");
                            }
                          }}
                          style={{ ...buttonReset, padding: "3px 9px", borderRadius: 999, background: "rgba(248,223,168,0.16)", border: "1px solid rgba(248,223,168,0.4)", color: "#F8DFA8", fontSize: 10, fontWeight: 900, whiteSpace: "nowrap", cursor: "pointer" }}
                        >
                          {adminOverview.metrics.pendingBookings} aguardando resposta →
                        </button>
                      )}
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 8, marginBottom: 10 }}>
                      <button onClick={() => goToAdjacentCalendarMonth(-1)} style={{ ...buttonReset, minHeight: 30, padding: "0 10px", border: "1px solid rgba(125,211,252,0.3)", background: "rgba(14,165,233,0.10)", color: "#BAE6FD", fontSize: 13, fontWeight: 900 }}>‹</button>
                      <p style={{ margin: 0, color: "#F8FAFC", fontSize: 12, fontWeight: 900, minWidth: 130, textAlign: "center" }}>{MONTH_NAMES_PT[calendarFilterMonth - 1]} / {calendarFilterYear}</p>
                      <button onClick={() => goToAdjacentCalendarMonth(1)} style={{ ...buttonReset, minHeight: 30, padding: "0 10px", border: "1px solid rgba(125,211,252,0.3)", background: "rgba(14,165,233,0.10)", color: "#BAE6FD", fontSize: 13, fontWeight: 900 }}>›</button>
                      <select
                        value={calendarFilterPastorId}
                        onChange={(event) => setCalendarFilterPastorId(event.target.value)}
                        style={{ minHeight: 30, marginLeft: 6, border: "1px solid rgba(125,211,252,0.28)", background: "rgba(12,20,34,0.68)", color: "#E5E7EB", fontSize: 11, fontWeight: 800, borderRadius: 8, padding: "0 8px" }}
                      >
                        <option value="all">Todos os acolhedores</option>
                        {pastors.map((pastor) => (
                          <option key={pastor.id} value={pastor.id}>{pastor.name}</option>
                        ))}
                      </select>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 9 }}>
                      {[
                        { status: "all", label: "Todos" },
                        { status: "OPEN", label: "Aberto (disponível)" },
                        { status: "PENDING", label: "Aguardando resposta" },
                        { status: "CONFIRMED", label: "Confirmado" },
                        { status: "CANCELLED", label: "Cancelado" },
                        { status: "OVERDUE", label: "Vencidos" }
                      ].map((item) => {
                        const isActive = calendarStatusFilter === item.status;
                        return (
                          <button
                            key={item.status}
                            onClick={() => setCalendarStatusFilter(item.status)}
                            style={{
                              ...buttonReset,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 5,
                              padding: "5px 10px",
                              border: isActive ? "1px solid rgba(94,234,212,0.6)" : "1px solid rgba(125,211,252,0.2)",
                              background: isActive ? "rgba(20,184,166,0.16)" : "rgba(15,23,42,0.4)",
                              color: isActive ? "#99F6E4" : "#9fb0cc",
                              fontSize: 10,
                              fontWeight: 800,
                              whiteSpace: "nowrap"
                            }}
                          >
                            {statusColorMap[item.status] && (
                              <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: "50%", background: statusColorMap[item.status] }} />
                            )}
                            {item.label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: `52px repeat(${calendarGridDays.length}, 168px)`, columnGap: 8, rowGap: 6, maxHeight: 380, overflow: "auto", paddingBottom: 6 }}>
                      <div style={{ position: "sticky", left: 0, zIndex: 2, background: "#141b2b" }} />
                      {calendarGridDays.map((dayKey) => (
                        <p key={dayKey} style={{ margin: 0, height: 28, display: "flex", alignItems: "center", justifyContent: "center", color: "#8fb5cb", fontSize: 10, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.4, background: "rgba(125,211,252,0.06)", borderRadius: 8 }}>{dayKey}</p>
                      ))}
                      {calendarGridTimes.map((time) => (
                        <React.Fragment key={time}>
                          <div style={{ position: "sticky", left: 0, zIndex: 2, background: "#141b2b", minHeight: 78, display: "flex", alignItems: "center", justifyContent: "flex-end", paddingRight: 6, color: "#7DD3FC", fontSize: 11, fontWeight: 900 }}>{time}</div>
                          {calendarGridDays.map((dayKey) => {
                            const cellItems = calendarGridCell(dayKey, time);
                            if (cellItems.length === 0) {
                              return (
                                <div key={dayKey} style={{ minHeight: 78, border: "1px dashed rgba(255,255,255,0.06)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center" }}>
                                  <span style={{ color: "#3a4150", fontSize: 12 }}>—</span>
                                </div>
                              );
                            }
                            return (
                              <div key={dayKey} style={{ minHeight: 78, display: "flex", flexDirection: "column", gap: 4 }}>
                                {cellItems.map((item) => renderCalendarGridItem(item))}
                              </div>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </div>
                    {calendarGridDays.length === 0 && <p style={{ margin: "10px 0 0", color: "#9fb0cc", fontSize: 11 }}>Nenhum horário aberto nem agendamento neste mês{calendarFilterPastorId !== "all" ? " para esse acolhedor" : ""}{calendarStatusFilter !== "all" ? " com esse filtro" : ""}.</p>}
                    <p style={{ margin: "8px 0 0", color: "#68758c", fontSize: 10 }}>Toque em um horário marcado (agendado) pra ver quem é a pessoa e o(a) acolhedor(a). Horários "Aberto" ainda não têm ninguém marcado.</p>
                    {adminBookingActionFeedback && <p style={{ margin: "6px 0 0", color: "#9fb0cc", fontSize: 11 }}>{adminBookingActionFeedback}</p>}
                  </div>
                  {showSlotFormModal && (
                    <div style={{ position: "fixed", inset: 0, background: "rgba(4,8,15,0.72)", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: 20, overflowY: "auto", zIndex: 60 }} onClick={() => !adminSlotLoading && setShowSlotFormModal(false)}>
                      <div onClick={(event) => event.stopPropagation()} style={{ width: "100%", maxWidth: 480, margin: "3vh auto", position: "relative", border: "1px solid rgba(125,211,252,0.3)", background: "#0F172A", borderRadius: 12, padding: 18 }}>
                        <button onClick={() => !adminSlotLoading && setShowSlotFormModal(false)} disabled={adminSlotLoading} style={{ ...buttonReset, position: "absolute", top: 14, right: 14, width: 28, height: 28, display: "inline-flex", alignItems: "center", justifyContent: "center", borderRadius: 8, border: "1px solid rgba(148,163,184,0.28)", background: "rgba(255,255,255,0.03)", color: "#c7ced9", fontSize: 15, lineHeight: 1 }}>×</button>
                        <p style={{ margin: "0 0 12px", color: "#F8FAFC", fontSize: 13, fontWeight: 900 }}>Abrir horário de acolhimento</p>
                        <div style={{ display: "grid", gap: 8 }}>
                        <label>
                          <span style={profileLabelStyle}>Acolhedor(a)</span>
                          <select value={adminSlotForm.pastorId || adminPastor?.id || ""} onChange={(event) => updateAdminSlotForm("pastorId", event.target.value)} style={profileInputStyle}>
                            <option value="">Escolha</option>
                            {pastors.map((pastor) => <option key={pastor.id} value={pastor.id}>{pastor.name}</option>)}
                          </select>
                        </label>
                        <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr 0.8fr" : "1fr", gap: 8 }}>
                          <label>
                            <span style={profileLabelStyle}>Data</span>
                            <input type="date" value={adminSlotForm.date} onChange={(event) => updateAdminSlotForm("date", event.target.value)} style={{ ...profileInputStyle, colorScheme: "dark" }} />
                          </label>
                          <label>
                            <span style={profileLabelStyle}>Horário</span>
                            <input type="time" value={adminSlotForm.time} onChange={(event) => updateAdminSlotForm("time", event.target.value)} style={{ ...profileInputStyle, colorScheme: "dark" }} />
                          </label>
                          <label>
                            <span style={profileLabelStyle}>Minutos</span>
                            <input value={adminSlotForm.durationMinutes} onChange={(event) => updateAdminSlotForm("durationMinutes", event.target.value)} inputMode="numeric" style={profileInputStyle} />
                          </label>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: isTablet ? "0.8fr 1.2fr" : "1fr", gap: 8 }}>
                          <label>
                            <span style={profileLabelStyle}>Formato</span>
                            <select value={adminSlotForm.mode} onChange={(event) => updateAdminSlotForm("mode", event.target.value)} style={profileInputStyle}>
                              <option value="ONLINE">Online</option>
                              <option value="PRESENTIAL">Presencial</option>
                            </select>
                          </label>
                          <label>
                            <span style={profileLabelStyle}>Local presencial</span>
                            <input value={adminSlotForm.locationNote} onChange={(event) => updateAdminSlotForm("locationNote", event.target.value)} placeholder="Opcional" disabled={adminSlotForm.mode !== "PRESENTIAL"} style={{ ...profileInputStyle, opacity: adminSlotForm.mode === "PRESENTIAL" ? 1 : 0.55 }} />
                          </label>
                        </div>
                        <div style={{ display: "grid", gridTemplateColumns: adminSlotForm.repeatWeekly && isTablet ? "1fr 1fr" : "1fr", gap: 8 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                            <input type="checkbox" checked={adminSlotForm.repeatWeekly} onChange={(event) => updateAdminSlotForm("repeatWeekly", event.target.checked)} />
                            <span style={profileLabelStyle}>Repetir semanalmente</span>
                          </label>
                          {adminSlotForm.repeatWeekly && (
                            <label>
                              <span style={profileLabelStyle}>Repetir até</span>
                              <input type="date" value={adminSlotForm.repeatUntil} onChange={(event) => updateAdminSlotForm("repeatUntil", event.target.value)} style={{ ...profileInputStyle, colorScheme: "dark" }} />
                            </label>
                          )}
                        </div>
                        <button onClick={createAdminPastoralSlot} disabled={adminSlotLoading} style={{ ...buttonReset, minHeight: 44, border: "1px solid rgba(125,211,252,0.42)", background: "rgba(14,165,233,0.18)", color: "#BAE6FD", fontSize: 12, fontWeight: 950, opacity: adminSlotLoading ? 0.7 : 1 }}>
                          {adminSlotLoading ? "Abrindo..." : adminSlotForm.repeatWeekly ? "Abrir horários na agenda" : "Abrir horário na agenda"}
                        </button>
                        {adminSlotFeedback && <p style={{ margin: 0, color: adminSlotFeedback.includes("aberto") ? "#A7F3D0" : "#F8DFA8", fontSize: 12 }}>{adminSlotFeedback}</p>}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedCalendarBooking && (
                    <div style={{ position: "fixed", inset: 0, background: "rgba(4,8,15,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 16, zIndex: 60 }} onClick={() => setSelectedCalendarBooking(null)}>
                      <div onClick={(event) => event.stopPropagation()} style={{ width: "100%", maxWidth: 360, border: "1px solid rgba(125,211,252,0.3)", background: "#0F172A", borderRadius: 12, padding: 18 }}>
                        <p style={{ margin: "0 0 4px", color: "#7DD3FC", fontSize: 10, textTransform: "uppercase", letterSpacing: 1, fontWeight: 900 }}>Conversa de acolhimento</p>
                        <p style={{ margin: "0 0 10px", color: "#F8FAFC", fontSize: 15, fontWeight: 900 }}>{new Date(selectedCalendarBooking.startAt).toLocaleString("pt-BR")}</p>
                        <p style={{ margin: "0 0 4px", color: "#CBD5E1", fontSize: 13 }}>Pessoa: {selectedCalendarBooking.userName}</p>
                        <p style={{ margin: "0 0 4px", color: "#CBD5E1", fontSize: 13 }}>Acolhedor(a): {selectedCalendarBooking.pastorName}</p>
                        <p style={{ margin: "0 0 14px", color: statusColorMap[selectedCalendarBooking.status] || "#9fb0cc", fontSize: 12, fontWeight: 900 }}>{statusLabelMap[selectedCalendarBooking.status] || selectedCalendarBooking.status}</p>
                        {rescheduleBookingId === selectedCalendarBooking.id ? (
                          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginBottom: 14 }}>
                            <input type="date" value={rescheduleDate} onChange={(event) => setRescheduleDate(event.target.value)} style={{ ...profileInputStyle, colorScheme: "dark", padding: "6px 8px", fontSize: 11, minHeight: 30 }} />
                            <input type="time" value={rescheduleTime} onChange={(event) => setRescheduleTime(event.target.value)} style={{ ...profileInputStyle, colorScheme: "dark", padding: "6px 8px", fontSize: 11, minHeight: 30 }} />
                            <button onClick={async () => { await submitRescheduleBooking(); setSelectedCalendarBooking(null); }} style={{ ...buttonReset, color: "#A7F3D0", fontSize: 11, fontWeight: 900 }}>Confirmar novo horário</button>
                            <button onClick={() => setRescheduleBookingId("")} style={{ ...buttonReset, color: "#94A3B8", fontSize: 11, fontWeight: 900 }}>Cancelar edição</button>
                          </div>
                        ) : (
                          selectedCalendarBooking.status !== "CANCELLED" && (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, marginBottom: 14 }}>
                              {selectedCalendarBooking.status === "PENDING" && (
                                <button onClick={async () => { await updateAdminBookingStatus(selectedCalendarBooking.id, "CONFIRMED"); setSelectedCalendarBooking(null); }} style={{ ...buttonReset, color: "#86EFAC", fontSize: 11, fontWeight: 900 }}>Confirmar</button>
                              )}
                              <button onClick={() => openRescheduleBooking(selectedCalendarBooking)} style={{ ...buttonReset, color: "#7DD3FC", fontSize: 11, fontWeight: 900 }}>Reagendar</button>
                              <button onClick={async () => { await updateAdminBookingStatus(selectedCalendarBooking.id, "CANCELLED"); setSelectedCalendarBooking(null); }} style={{ ...buttonReset, color: "#FCA5A5", fontSize: 11, fontWeight: 900 }}>Cancelar</button>
                            </div>
                          )
                        )}
                        <button onClick={() => { setSelectedCalendarBooking(null); setRescheduleBookingId(""); }} style={{ ...buttonReset, minHeight: 42, width: "100%", border: "1px solid rgba(125,211,252,0.38)", background: "rgba(14,165,233,0.14)", color: "#BAE6FD", fontSize: 12, fontWeight: 900 }}>Fechar</button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* TAB: PROFILE */}
          {activeTab === "profile" && (
            <div>
              <section style={{ ...warmHeroStyle, ...overflowGuardStyle, padding: isDesktop ? "32px 36px" : isTablet ? "28px 22px" : "22px 18px" }}>
                <div style={{ position: "absolute", inset: "auto -70px -130px auto", width: 250, height: 250, borderRadius: "50%", background: "rgba(230,177,90,0.18)", filter: "blur(6px)", pointerEvents: "none" }} />
                <div style={{ position: "relative", display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1.1fr) minmax(260px, 0.72fr)" : "minmax(0, 1fr)", gap: isTablet ? 26 : 18, alignItems: "center" }}>
                  <div>
                    <p style={{ ...eyebrowStyle, color: "#87582b" }}>{hasCompletedProfile ? "Meu caminho" : "Cadastro"}</p>
                    <h2 style={{ margin: 0, maxWidth: 660, fontSize: mobileHeroTitleSize, lineHeight: 1, color: "#151b2a", fontWeight: 900 }}>
                      {hasCompletedProfile ? "Sua rotina, seus registros, sua caminhada." : "Crie seu acesso para caminhar com a comunidade."}
                    </h2>
                    <p style={{ margin: "16px 0 0", maxWidth: 610, fontSize: isMobile ? 15 : 16, lineHeight: 1.62, color: "#425064" }}>
                      {hasCompletedProfile
                        ? "Aqui ficam seus dados, seu acesso e pequenos sinais do caminho que você vem fazendo com a Mosaico."
                        : "Um cadastro simples, sem burocracia: quem é você, de onde vem e como podemos manter contato quando a caminhada pedir proximidade."}
                    </p>
                  </div>

                  <div style={{ borderRadius: 24, padding: 16, border: "1px solid rgba(24,32,51,0.12)", background: "rgba(255,255,255,0.48)", boxShadow: "0 18px 38px rgba(82,61,34,0.15)" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                      <div style={{ width: 58, height: 58, flex: "0 0 auto", borderRadius: 19, display: "grid", placeItems: "center", overflow: "hidden", background: "linear-gradient(145deg, #182033, #2d3748)", color: "#F5D28B", fontWeight: 950, fontSize: 18, boxShadow: "0 12px 24px rgba(0,0,0,0.18)" }}>
                        {profilePhotoUrl ? <img src={profilePhotoUrl} alt={userDisplayName} style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : profileInitials || <UserRound size={24} />}
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: 0, color: "#151b2a", fontSize: 16, fontWeight: 950, overflowWrap: "anywhere" }}>{userDisplayName}</p>
                        <p style={{ margin: "4px 0 0", color: "#5d6776", fontSize: 12, fontWeight: 800 }}>{profileConnection.label}</p>
                      </div>
                    </div>
                    <div style={{ display: "grid", gap: 9 }}>
                      {[
                        { icon: ShieldCheck, label: "Acesso", value: viewerUser?.sessionToken ? "Conectado" : "Pendente" },
                        { icon: MapPinned, label: "Cidade", value: profileForm.city || "Sem cidade" },
                        { icon: CheckCircle2, label: "Perfil", value: `${profileCompletion}% preenchido` }
                      ].map((item) => {
                        const Icon = item.icon;
                        return (
                          <div key={item.label} style={{ display: "grid", gridTemplateColumns: "28px 1fr auto", alignItems: "center", gap: 8, minHeight: 38, padding: "7px 9px", borderRadius: 14, background: "rgba(255,255,255,0.56)", border: "1px solid rgba(24,32,51,0.08)" }}>
                            <Icon size={16} color="#87582b" strokeWidth={2.4} />
                            <span style={{ color: "#667085", fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 0.7 }}>{item.label}</span>
                            <span style={{ color: "#182033", fontSize: 12, fontWeight: 950, textAlign: "right", overflowWrap: "anywhere" }}>{item.value}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </section>

              {/* CORRECAO findability (10/2026, revisao UX): o "Sair" ficava
                  so' depois do card de Conquistas (e do card de login/senha,
                  quando aparece) -- quem tivesse muitas conquistas podia
                  precisar rolar 2-3 telas pra achar o botao de sair, logo
                  abaixo das Conquistas e antes do card de cadastro. Agora
                  essa linha fica logo no topo da aba Perfil, a primeira coisa
                  depois do cabecalho, pra ser achada sem rolar -- pedido do
                  Rodrigo apos receio de que o botao "nao fosse encontrado". */}
              {viewerUser?.sessionToken && !viewerUser?.passwordMustChange && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", padding: "2px 2px 14px", marginBottom: 4 }}>
                  <p style={{ margin: 0, fontSize: 12.5, color: "#9AA7BD" }}>
                    Conectado como <strong style={{ color: "#E5EAF3", fontWeight: 800 }}>{viewerUser?.name || "você"}</strong>
                  </p>
                  <button
                    onClick={logout}
                    style={{
                      ...buttonReset,
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      minHeight: 44,
                      padding: "0 16px",
                      borderRadius: 10,
                      border: "1px solid rgba(248,113,113,0.38)",
                      background: "rgba(248,113,113,0.12)",
                      color: "#FCA5A5",
                      fontSize: 12.5,
                      fontWeight: 800,
                      cursor: "pointer",
                    }}
                  >
                    <LogOut size={14} />
                    Sair
                  </button>
                </div>
              )}

              {/* Teaser "Escala" -- so pra quem e' musico (mesmo gate do link
                  "Escala" no menu, canSeeEscala). Nao duplica a lista inteira
                  de /admin/escalas aqui dentro (sugestao dada ao Drigo,
                  23/10/2026, "pode seguir"): so a proxima data + funcao, com
                  atalho pra tela de Escalas pra ver o resto -- mesmo padrao
                  resumo+link dos cards "Sua mesa agora" da Home. */}
              {canSeeEscala && viewerUser?.sessionToken && !viewerUser?.passwordMustChange && (
                <div style={{ ...profileSurfaceStyle, padding: isDesktop ? 22 : 16, marginBottom: 18, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                    <div style={{ width: 42, height: 42, flexShrink: 0, borderRadius: 14, display: "grid", placeItems: "center", background: "rgba(230,177,90,0.14)", border: "1px solid rgba(230,177,90,0.32)" }}>
                      <Music size={20} color="#E6B15A" strokeWidth={2.2} />
                    </div>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: "0 0 5px", fontSize: 11, letterSpacing: 1.25, color: "#F5D28B", textTransform: "uppercase", fontWeight: 900 }}>Escala</p>
                      {nextEscalaLoading ? (
                        <h3 style={{ margin: 0, color: "#FFF", fontSize: 15, fontWeight: 800 }}>Verificando...</h3>
                      ) : nextEscalaSlot ? (
                        <h3 style={{ margin: 0, color: "#FFF", fontSize: isDesktop ? 18 : 16, fontWeight: 950, overflowWrap: "anywhere" }}>
                          Você está escalado(a){" "}
                          {new Date(nextEscalaSlot.date).toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "2-digit" })}
                          {nextEscalaSlot.label ? ` (${nextEscalaSlot.label})` : ""} · {nextEscalaSlot.funcao}
                        </h3>
                      ) : (
                        <h3 style={{ margin: 0, color: "#FFF", fontSize: isDesktop ? 18 : 16, fontWeight: 950 }}>Você não está escalado(a) nos próximos dias.</h3>
                      )}
                    </div>
                  </div>
                  <Link
                    to="/admin/escalas"
                    style={{ ...buttonReset, flexShrink: 0, display: "inline-flex", alignItems: "center", minHeight: 40, padding: "0 14px", borderRadius: 999, background: "rgba(230,177,90,0.18)", border: "1px solid rgba(230,177,90,0.5)", color: "#F5D28B", fontSize: 12, fontWeight: 950, textDecoration: "none" }}
                  >
                    Ver escalas
                  </Link>
                </div>
              )}

              {/* Grade completa de conquistas -- conquistadas em destaque
                  (cor/icone dourado), bloqueadas em cinza com uma dica curta
                  de como desbloquear (badgeProgressHint). So' aparece pra
                  quem completou o cadastro, mesmo criterio do teaser na Home.
                  Comeca FECHADA (so' o resumo, igual ao teaser da Home) --
                  toca no resumo pra abrir a grade inteira (badgesExpanded). */}
              {(() => {
                // Split "Meus dados" em duas abas (pedido do Drigo, 31/10/2026):
                // "dados" (conta -- User: nome, nascimento, whatsapp, email,
                // estado, cidade, relacao com a Mosaico, como chegou, quem
                // convidou) separada de "cadastro-membro" (cadastro oficial
                // na igreja -- Member/Child, ja' era uma secao a parte no
                // codigo mesmo quando as duas viviam na mesma aba -- ver
                // comentario "Separado do cadastro de acesso" mais abaixo).
                // "Identidade" virou "Avatar" (e so' mesmo o montador de
                // avatar das trilhas, nada mais vive nessa aba).
                const hubSections = [
                  { id: "overview", label: "Visão geral" },
                  { id: "conta", label: "Conta" },
                  { id: "dados", label: "Dados de usuário" },
                  { id: "cadastro-membro", label: "Cadastro de membros" },
                  { id: "identidade", label: "Avatar" },
                  { id: "agenda", label: "Agenda" }
                ];
                return (
                  <div style={{ display: "flex", gap: isDesktop ? 8 : 7, marginBottom: 18, flexWrap: isDesktop ? "wrap" : "nowrap", overflowX: isDesktop ? "visible" : "auto", paddingBottom: isDesktop ? 0 : 2 }}>
                    {hubSections.map((sec) => {
                      const locked = sec.id !== "conta" && !hasCompletedProfile;
                      const active = effectiveHubSection === sec.id;
                      return (
                        <button
                          key={sec.id}
                          type="button"
                          onClick={() => { if (!locked) setProfileHubSection(sec.id); }}
                          disabled={locked}
                          style={{
                            ...buttonReset,
                            flexShrink: 0,
                            display: "flex",
                            alignItems: "center",
                            gap: 6,
                            padding: "9px 14px",
                            borderRadius: 999,
                            border: active ? "1px solid rgba(125,211,252,0.5)" : "1px solid rgba(255,255,255,0.12)",
                            background: active ? "rgba(125,211,252,0.18)" : "rgba(255,255,255,0.04)",
                            color: active ? "#BAE6FD" : "#9fb0c9",
                            fontSize: 12.5,
                            fontWeight: 800,
                            opacity: locked ? 0.4 : 1,
                            cursor: locked ? "default" : "pointer",
                            whiteSpace: "nowrap"
                          }}
                        >
                          {sec.label}
                          {locked && <LockKeyhole size={11} />}
                        </button>
                      );
                    })}
                  </div>
                );
              })()}

              {effectiveHubSection === "overview" && hasCompletedProfile && badgesData && (
                <div style={{ ...profileSurfaceStyle, padding: isDesktop ? 22 : 16, marginBottom: 18 }}>
                  <button
                    type="button"
                    onClick={() => setBadgesExpanded((v) => !v)}
                    style={{ ...buttonReset, width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, cursor: "pointer", textAlign: "left" }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 14, display: "grid", placeItems: "center", background: "rgba(230,177,90,0.14)", border: "1px solid rgba(230,177,90,0.32)" }}>
                        <Trophy size={20} color="#E6B15A" strokeWidth={2.2} />
                      </div>
                      <div>
                        <p style={{ margin: "0 0 5px", fontSize: 11, letterSpacing: 1.25, color: "#F5D28B", textTransform: "uppercase", fontWeight: 900 }}>Conquistas</p>
                        <h3 style={{ margin: 0, color: "#FFF", fontSize: isDesktop ? 20 : 17, fontWeight: 950 }}>
                          {badgesData.earnedCount} de {badgesData.badges.length} desbloqueadas
                        </h3>
                      </div>
                    </div>
                    {badgesExpanded ? (
                      <ChevronRight size={18} strokeWidth={2.4} color="#c7d1e4" style={{ transform: "rotate(90deg)", flexShrink: 0 }} />
                    ) : (
                      <ChevronRight size={18} strokeWidth={2.4} color="#c7d1e4" style={{ flexShrink: 0 }} />
                    )}
                  </button>
                  {/* flex-basis reproduz as mesmas 3/2/1 colunas do grid antigo;
                      flex:"0 0 <basis>" (sem grow) + justifyContent:"center" no
                      container e' o que deixa uma ultima linha incompleta (ex.: 7
                      conquistas em 3 colunas, sobra 1 sozinha) centralizada em vez
                      de colada na esquerda com a linha toda vazia do lado --
                      linhas completas nao mudam em nada, so preenchem igual antes. */}
                  {badgesExpanded && (() => {
                    const badgeBasis = isDesktop ? "calc((100% - 20px) / 3)" : isTablet ? "calc((100% - 10px) / 2)" : "100%";
                    return (
                      <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 10, marginTop: 16 }}>
                        {badgesData.badges.map((badge) => {
                          const earned = Boolean(badge.earnedAt);
                          const BadgeIcon = BADGE_ICON_MAP[badge.icon] || Award;
                          return (
                            <div key={badge.slug} style={{ ...overflowGuardStyle, flex: `0 0 ${badgeBasis}`, maxWidth: badgeBasis, padding: 14, borderRadius: 16, border: earned ? "1px solid rgba(230,177,90,0.42)" : "1px solid rgba(148,163,184,0.16)", background: earned ? "linear-gradient(145deg, rgba(230,177,90,0.14), rgba(255,255,255,0.03))" : "rgba(255,255,255,0.025)", opacity: earned ? 1 : 0.72 }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                                <span style={{ width: 36, height: 36, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, background: earned ? "rgba(230,177,90,0.18)" : "rgba(148,163,184,0.10)", color: earned ? "#E6B15A" : "#8089a0" }}>
                                  <BadgeIcon size={18} strokeWidth={1.8} />
                                </span>
                                <p style={{ margin: 0, fontSize: 13, fontWeight: 900, color: earned ? "#FFF" : "#aebbd1", overflowWrap: "anywhere" }}>{badge.title}</p>
                              </div>
                              <p style={{ margin: 0, fontSize: 11.5, lineHeight: 1.5, color: earned ? "#d8c8a8" : "#8089a0", overflowWrap: "anywhere" }}>
                                {earned ? badge.description : badgeProgressHint(badge)}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                </div>
              )}
              {/* So mostra o card cheio (login / troca de senha obrigatoria) quando
                  faz sentido pedir atencao pra isso. Pra quem ja esta conectado
                  normalmente, um card inteiro so pra dizer "voce esta conectado" nao
                  informa nada que a pessoa nao ja sabe (esta vendo e usando a tela) --
                  isso so empurrava pra baixo o card de cadastro logo abaixo, que e o
                  conteudo que realmente importa aqui no Perfil (pedido do Rodrigo,
                  10/2026: "precisa estar ai ou poderia ser so o botao sair"). */}
              {effectiveHubSection === "conta" && (!viewerUser?.sessionToken || viewerUser?.passwordMustChange) && (

              <div style={{ ...profileSurfaceStyle, padding: isDesktop ? 22 : 16, borderColor: viewerUser?.passwordMustChange ? "rgba(252,211,77,0.42)" : "rgba(125,211,252,0.22)", background: viewerUser?.passwordMustChange ? "linear-gradient(145deg, rgba(80,53,12,0.50), rgba(15,23,42,0.98))" : "linear-gradient(145deg, rgba(14,38,58,0.70), rgba(15,23,42,0.98) 58%, rgba(31,28,24,0.82))" }}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", marginBottom: viewerUser?.sessionToken && !viewerUser?.passwordMustChange ? 0 : 14 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ width: 42, height: 42, borderRadius: 14, display: "grid", placeItems: "center", background: viewerUser?.passwordMustChange ? "rgba(252,211,77,0.16)" : "rgba(125,211,252,0.12)", border: viewerUser?.passwordMustChange ? "1px solid rgba(252,211,77,0.34)" : "1px solid rgba(125,211,252,0.24)" }}>
                      <LockKeyhole size={20} color={viewerUser?.passwordMustChange ? "#FCD34D" : "#7DD3FC"} strokeWidth={2.4} />
                    </div>
                    <div>
                      <p style={{ margin: "0 0 5px", fontSize: 11, letterSpacing: 1.25, color: viewerUser?.passwordMustChange ? "#FCD34D" : "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>{viewerUser?.passwordMustChange ? "Senha temporária" : "Acesso"}</p>
                      <h3 style={{ margin: 0, color: "#FFF", fontSize: isDesktop ? 22 : 19, fontWeight: 950 }}>
                        {viewerUser?.passwordMustChange ? "Troque sua senha para continuar." : viewerUser?.sessionToken ? "Você está conectado." : "Entre na sua conta"}
                      </h3>
                    </div>
                  </div>
                  {/* Bloco "sessao ativa + Sair" que existia aqui foi removido (10/2026):
                      era codigo morto -- so renderizava se sessionToken=true E
                      passwordMustChange=false, mas o card inteiro so aparece quando
                      !sessionToken OU passwordMustChange=true, condicoes que nunca
                      sao compativeis com essa. O botao real de Sair pra quem esta
                      logado normalmente fica no topo da aba Perfil (ver acima). */}
                </div>
                {!viewerUser?.sessionToken && (
                  <div role="tablist" aria-label="Formas de acesso" style={{ display: "flex", gap: 6, background: "rgba(255,255,255,0.05)", padding: 4, borderRadius: 14, marginBottom: 14, maxWidth: isDesktop ? 420 : "100%" }}>
                    {[
                      { id: "login", label: "Entrar" },
                      { id: "cadastro", label: "Criar conta" },
                      { id: "recuperar", label: "Esqueci a senha" }
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setAcessoCardTab(tab.id)}
                        style={{
                          ...buttonReset,
                          flex: 1,
                          textAlign: "center",
                          padding: "9px 6px",
                          borderRadius: 10,
                          border: acessoCardTab === tab.id ? "1px solid rgba(125,211,252,0.42)" : "1px solid transparent",
                          background: acessoCardTab === tab.id ? "rgba(125,211,252,0.16)" : "transparent",
                          color: acessoCardTab === tab.id ? "#BAE6FD" : "#9fb0c9",
                          fontSize: 12,
                          fontWeight: 800
                        }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>
                )}
                {!viewerUser?.sessionToken && acessoCardTab === "login" && (
                  <div style={{ display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) minmax(0, 1fr) auto" : "minmax(0, 1fr)", gap: 9, alignItems: "end" }}>
                    <label>
                      <span style={profileLabelStyle}>E-mail</span>
                      <input value={loginForm.email} onChange={(event) => updateLoginForm("email", event.target.value)} placeholder="voce@email.com" inputMode="email" style={profileInputStyle} />
                    </label>
                    <label>
                      <span style={profileLabelStyle}>Senha</span>
                      <PasswordField value={loginForm.password} onChange={(event) => updateLoginForm("password", event.target.value)} placeholder="Senha" style={profileInputStyle} visible={!!visiblePasswords.profileLogin} onToggleVisibility={() => togglePasswordVisibility("profileLogin")} />
                    </label>
                    <button onClick={loginWithPassword} disabled={loginLoading} style={{ ...buttonReset, minHeight: 46, padding: "0 16px", borderRadius: 14, border: "1px solid rgba(125,211,252,0.46)", background: "rgba(14,165,233,0.16)", color: "#BAE6FD", fontSize: 12, fontWeight: 950, opacity: loginLoading ? 0.7 : 1 }}>
                      {loginLoading ? "Entrando..." : "Entrar"}
                    </button>
                  </div>
                )}
                {!viewerUser?.sessionToken && acessoCardTab === "recuperar" && (
                  <div style={{ display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) auto" : "minmax(0, 1fr)", gap: 9, alignItems: "end", marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.10)" }}>
                    <label>
                      <span style={profileLabelStyle}>Recuperar senha</span>
                      <input value={loginForm.resetEmail} onChange={(event) => updateLoginForm("resetEmail", event.target.value)} placeholder="E-mail cadastrado" inputMode="email" style={profileInputStyle} />
                    </label>
                    <button onClick={requestPasswordReset} disabled={loginLoading} style={{ ...buttonReset, minHeight: 46, padding: "0 16px", borderRadius: 14, border: "1px solid rgba(148,163,184,0.34)", background: "rgba(255,255,255,0.06)", color: "#DDE6F5", fontSize: 12, fontWeight: 950, opacity: loginLoading ? 0.7 : 1 }}>
                      Enviar recuperação
                    </button>
                  </div>
                )}
                {loginForm.resetToken && (
                  <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr auto" : "1fr", gap: 9, alignItems: "end", marginTop: 12, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.10)" }}>
                    <label>
                      <span style={profileLabelStyle}>Nova senha</span>
                      <PasswordField value={loginForm.newPassword} onChange={(event) => updateLoginForm("newPassword", event.target.value)} placeholder="8 caracteres, maiúscula e especial" style={profileInputStyle} visible={!!visiblePasswords.resetNew} onToggleVisibility={() => togglePasswordVisibility("resetNew")} />
                    </label>
                    <label>
                      <span style={profileLabelStyle}>Confirmar</span>
                      <PasswordField value={loginForm.confirmPassword} onChange={(event) => updateLoginForm("confirmPassword", event.target.value)} placeholder="Repita a nova senha" style={profileInputStyle} visible={!!visiblePasswords.resetConfirm} onToggleVisibility={() => togglePasswordVisibility("resetConfirm")} />
                    </label>
                    <button onClick={resetPasswordWithToken} disabled={loginLoading} style={{ ...buttonReset, minHeight: 46, padding: "0 16px", borderRadius: 14, border: "1px solid rgba(125,211,252,0.46)", background: "rgba(14,165,233,0.16)", color: "#BAE6FD", fontSize: 12, fontWeight: 950, opacity: loginLoading ? 0.7 : 1 }}>
                      Redefinir
                    </button>
                  </div>
                )}
                {viewerUser?.passwordMustChange && (
                  <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(3, minmax(0, 1fr)) auto" : "minmax(0, 1fr)", gap: 9, alignItems: "end" }}>
                    <label>
                      <span style={profileLabelStyle}>Senha atual</span>
                      <PasswordField value={loginForm.currentPassword} onChange={(event) => updateLoginForm("currentPassword", event.target.value)} placeholder="@mudar123" style={profileInputStyle} visible={!!visiblePasswords.changeCurrent} onToggleVisibility={() => togglePasswordVisibility("changeCurrent")} />
                    </label>
                    <label>
                      <span style={profileLabelStyle}>Nova senha</span>
                      <PasswordField value={loginForm.newPassword} onChange={(event) => updateLoginForm("newPassword", event.target.value)} placeholder="8 caracteres, maiúscula e especial" style={profileInputStyle} visible={!!visiblePasswords.changeNew} onToggleVisibility={() => togglePasswordVisibility("changeNew")} />
                    </label>
                    <label>
                      <span style={profileLabelStyle}>Confirmar</span>
                      <PasswordField value={loginForm.confirmPassword} onChange={(event) => updateLoginForm("confirmPassword", event.target.value)} placeholder="Repita a nova senha" style={profileInputStyle} visible={!!visiblePasswords.changeConfirm} onToggleVisibility={() => togglePasswordVisibility("changeConfirm")} />
                    </label>
                    <button onClick={changePassword} disabled={loginLoading} style={{ ...buttonReset, minHeight: 46, padding: "0 16px", borderRadius: 14, border: "1px solid rgba(252,211,77,0.58)", background: "#FCD34D", color: "#1F2937", fontSize: 12, fontWeight: 950, opacity: loginLoading ? 0.7 : 1 }}>
                      Alterar senha
                    </button>
                  </div>
                )}
                {loginFeedback && <p style={{ margin: "10px 0 0", color: loginFeedback.includes("sucesso") || loginFeedback.includes("liberado") ? "#A7F3D0" : "#F8DFA8", fontSize: 12, lineHeight: 1.45 }}>{loginFeedback}</p>}
              </div>
              )}

              {effectiveHubSection === "conta" && hasCompletedProfile && viewerUser?.sessionToken && !viewerUser?.passwordMustChange && (
              <div style={{ ...profileSurfaceStyle, padding: isDesktop ? 22 : 16 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
                  <div style={{ width: 42, height: 42, borderRadius: 14, display: "grid", placeItems: "center", background: "rgba(125,211,252,0.12)", border: "1px solid rgba(125,211,252,0.24)" }}>
                    <LockKeyhole size={20} color="#7DD3FC" strokeWidth={2.4} />
                  </div>
                  <div>
                    <p style={{ margin: "0 0 5px", fontSize: 11, letterSpacing: 1.25, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Acesso</p>
                    <h3 style={{ margin: 0, color: "#FFF", fontSize: isDesktop ? 22 : 19, fontWeight: 950 }}>Sua conta</h3>
                  </div>
                </div>
                <p style={{ margin: "0 0 14px", color: "#9fb0c9", fontSize: 13 }}>
                  E-mail cadastrado: <strong style={{ color: "#E5EAF3" }}>{viewerUser?.email || profileForm.email || "—"}</strong>
                </p>
                {!voluntaryPasswordChangeOpen && (
                  <button onClick={() => setVoluntaryPasswordChangeOpen(true)} style={{ ...buttonReset, minHeight: 44, padding: "0 16px", borderRadius: 12, border: "1px solid rgba(125,211,252,0.34)", background: "rgba(125,211,252,0.08)", color: "#BAE6FD", fontSize: 12.5, fontWeight: 900 }}>
                    Alterar senha
                  </button>
                )}
                {voluntaryPasswordChangeOpen && (
                  <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(3, minmax(0, 1fr)) auto" : "minmax(0, 1fr)", gap: 9, alignItems: "end" }}>
                    <label>
                      <span style={profileLabelStyle}>Senha atual</span>
                      <PasswordField value={loginForm.currentPassword} onChange={(event) => updateLoginForm("currentPassword", event.target.value)} placeholder="Sua senha atual" style={profileInputStyle} visible={!!visiblePasswords.changeCurrent} onToggleVisibility={() => togglePasswordVisibility("changeCurrent")} />
                    </label>
                    <label>
                      <span style={profileLabelStyle}>Nova senha</span>
                      <PasswordField value={loginForm.newPassword} onChange={(event) => updateLoginForm("newPassword", event.target.value)} placeholder="8 caracteres, maiúscula e especial" style={profileInputStyle} visible={!!visiblePasswords.changeNew} onToggleVisibility={() => togglePasswordVisibility("changeNew")} />
                    </label>
                    <label>
                      <span style={profileLabelStyle}>Confirmar</span>
                      <PasswordField value={loginForm.confirmPassword} onChange={(event) => updateLoginForm("confirmPassword", event.target.value)} placeholder="Repita a nova senha" style={profileInputStyle} visible={!!visiblePasswords.changeConfirm} onToggleVisibility={() => togglePasswordVisibility("changeConfirm")} />
                    </label>
                    <button onClick={changePassword} disabled={loginLoading} style={{ ...buttonReset, minHeight: 46, padding: "0 16px", borderRadius: 14, border: "1px solid rgba(125,211,252,0.46)", background: "rgba(14,165,233,0.16)", color: "#BAE6FD", fontSize: 12, fontWeight: 950, opacity: loginLoading ? 0.7 : 1 }}>
                      Alterar senha
                    </button>
                  </div>
                )}
                {loginFeedback && <p style={{ margin: "10px 0 0", color: loginFeedback.includes("sucesso") || loginFeedback.includes("liberado") ? "#A7F3D0" : "#F8DFA8", fontSize: 12, lineHeight: 1.45 }}>{loginFeedback}</p>}
              </div>
              )}

              {(
                (effectiveHubSection === "conta" && !hasCompletedProfile && acessoCardTab === "cadastro") ||
                (effectiveHubSection === "dados" && hasCompletedProfile)
              ) && (
              <div style={{ ...profileSurfaceStyle, padding: isDesktop ? 24 : 18 }}>
                <div style={{ display: "grid", gridTemplateColumns: isTablet ? "minmax(250px, 0.85fr) minmax(0, 1.45fr)" : "1fr", gap: isTablet ? 24 : 18, alignItems: "start" }}>
                  <div>
                    <span style={profilePillStyle}>{hasCompletedProfile ? "Acesso completo" : "Primeiro acesso"}</span>
                    <h3 style={{ margin: "0 0 12px", fontSize: isDesktop ? 28 : 23, lineHeight: 1.08, color: "#FFF", fontWeight: 950 }}>{hasCompletedProfile ? "Entre do seu jeito. Caminhe com a comunidade." : "Conte pra gente quem está chegando."}</h3>
                    <p style={{ margin: "0 0 16px", color: "#b8c3d6", fontSize: 14, lineHeight: 1.58 }}>
                      Esses dados ajudam a Mosaico acolher quem está perto, quem acompanha de outra cidade e quem chegou por um convite.
                    </p>
                    <div style={{ padding: 15, borderRadius: 18, background: "rgba(230,177,90,0.10)", border: "1px solid rgba(230,177,90,0.26)", marginBottom: 12 }}>
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                        <span style={{ color: "#F8FAFC", fontSize: 12, fontWeight: 900 }}>{profileCompletion}% preenchido</span>
                        <span style={{ color: "#E6B15A", fontSize: 11, fontWeight: 800 }}>{profileConnection.label}</span>
                      </div>
                      <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,0.11)", overflow: "hidden" }}>
                        <div style={{ width: `${profileCompletion}%`, height: "100%", borderRadius: 999, background: "linear-gradient(90deg, #E6B15A, #F5D28B)", transition: "width 220ms ease" }} />
                      </div>
                    </div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {[
                        { icon: Mail, text: profileForm.email || "E-mail ainda não informado" },
                        { icon: Phone, text: profileForm.whatsapp || "WhatsApp ainda não informado" },
                        { icon: MapPin, text: [profileForm.city, profileForm.state].filter(Boolean).join(", ") || "Localização ainda não informada" }
                      ].map((item) => {
                        const Icon = item.icon;
                        return (
                          <div key={item.text} style={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: 9, alignItems: "center", color: "#aebbd1", fontSize: 12, lineHeight: 1.35, overflowWrap: "anywhere" }}>
                            <Icon size={15} color="#E6B15A" strokeWidth={2.3} />
                            <span>{item.text}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <span style={profileLabelStyle}>Qual é a sua relação com a Mosaico?</span>
                    <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(3, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 9, marginBottom: 16 }}>
                      {PROFILE_CONNECTION_OPTIONS.filter((option) => !option.legacy).map((option) => {
                        const active = profileForm.connectionType === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => updateProfileForm("connectionType", option.id)}
                            style={{
                              ...buttonReset,
                              minHeight: 94,
                              textAlign: "left",
                              padding: "13px 13px",
                              borderRadius: 16,
                              border: active ? "1px solid rgba(230,177,90,0.72)" : "1px solid rgba(148,163,184,0.24)",
                              background: active ? "rgba(230,177,90,0.14)" : "rgba(15,23,42,0.48)",
                              color: "#FFF",
                              boxShadow: active ? "0 12px 24px rgba(0,0,0,0.22)" : "none"
                            }}
                          >
                            <span style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 900 }}>{option.label}</span>
                            <span style={{ display: "block", color: "#9fb0cc", fontSize: 11, lineHeight: 1.38 }}>{option.description}</span>
                          </button>
                        );
                      })}
                    </div>

                    {/* Segunda pergunta, separada da relacao acima -- antes so existia
                        "Cheguei por convite" misturado ali, sem opcao nenhuma pra quem
                        achou a Mosaico sozinho (Google, redes, passando na frente).
                        Pedido do Rodrigo, 10/2026. */}
                    <span style={profileLabelStyle}>Como você chegou até a gente?</span>
                    <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "repeat(4, minmax(0, 1fr))" : isTablet ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 9, marginBottom: 16 }}>
                      {DISCOVERY_CHANNEL_OPTIONS.map((option) => {
                        const active = profileForm.discoveryChannel === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => updateProfileForm("discoveryChannel", option.id)}
                            style={{
                              ...buttonReset,
                              minHeight: 86,
                              textAlign: "left",
                              padding: "13px 13px",
                              borderRadius: 16,
                              border: active ? "1px solid rgba(230,177,90,0.72)" : "1px solid rgba(148,163,184,0.24)",
                              background: active ? "rgba(230,177,90,0.14)" : "rgba(15,23,42,0.48)",
                              color: "#FFF",
                              boxShadow: active ? "0 12px 24px rgba(0,0,0,0.22)" : "none"
                            }}
                          >
                            <span style={{ display: "block", marginBottom: 6, fontSize: 13, fontWeight: 900 }}>{option.label}</span>
                            <span style={{ display: "block", color: "#9fb0cc", fontSize: 11, lineHeight: 1.38 }}>{option.description}</span>
                          </button>
                        );
                      })}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1.4fr 0.6fr" : "1fr", gap: 11, marginBottom: 11 }}>
                      <label style={{ minWidth: 0 }}>
                        <span style={profileLabelStyle}>Nome completo <span style={{ color: "#FCA5A5" }}>*</span></span>
                        <input value={profileForm.name} onChange={(event) => updateProfileForm("name", event.target.value)} placeholder="Seu nome" style={profileInputStyle} />
                      </label>
                      <label style={{ minWidth: 0 }}>
                        <span style={profileLabelStyle}>Data de nascimento</span>
                        {/* input[type=date] tem largura minima intrinseca propria (calendario
                            nativo do iOS/Safari) que ignora width:100% dentro de um item de
                            grid -- sem minWidth:0 no <label> pai, o item nao encolhe pra caber
                            na coluna e o formulario "estoura" pra fora da tela no mobile. */}
                        <input type="date" value={profileForm.birthDate} onChange={(event) => updateProfileForm("birthDate", event.target.value)} max={new Date().toISOString().slice(0, 10)} style={{ ...profileInputStyle, colorScheme: "dark", minWidth: 0 }} />
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 11, marginBottom: 11 }}>
                      <label>
                        <span style={profileLabelStyle}>WhatsApp <span style={{ color: "#FCA5A5" }}>*</span></span>
                        <input value={profileForm.whatsapp} onChange={(event) => updateProfileForm("whatsapp", event.target.value)} placeholder="(00) 00000-0000" inputMode="tel" style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>E-mail <span style={{ color: "#FCA5A5" }}>*</span></span>
                        <input value={profileForm.email} onChange={(event) => updateProfileForm("email", event.target.value)} placeholder="voce@email.com" inputMode="email" style={profileInputStyle} />
                      </label>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "0.55fr 1fr" : "1fr", gap: 11, marginBottom: 11 }}>
                      <label>
                        <span style={profileLabelStyle}>Estado <span style={{ color: "#FCA5A5" }}>*</span></span>
                        <input value={profileForm.state} onChange={(event) => updateProfileForm("state", event.target.value)} placeholder="UF" maxLength={32} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Cidade <span style={{ color: "#FCA5A5" }}>*</span></span>
                        <input value={profileForm.city} onChange={(event) => updateProfileForm("city", event.target.value)} placeholder="Sua cidade" style={profileInputStyle} />
                      </label>
                    </div>

                    <label style={{ display: "block", marginBottom: 14 }}>
                      <span style={profileLabelStyle}>Quem te convidou?</span>
                      <input value={profileForm.invitedBy} onChange={(event) => updateProfileForm("invitedBy", event.target.value)} placeholder="Opcional" style={profileInputStyle} />
                    </label>

                    {!hasCompletedProfile && !realIsAdminUser && (
                      <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 11, marginBottom: 14, padding: 13, borderRadius: 16, border: "1px solid rgba(125,211,252,0.28)", background: "rgba(14,165,233,0.07)" }}>
                        <label>
                          <span style={profileLabelStyle}>Criar senha</span>
                          <PasswordField value={loginForm.newPassword} onChange={(event) => updateLoginForm("newPassword", event.target.value)} placeholder="8 caracteres, maiúscula e especial" style={profileInputStyle} visible={!!visiblePasswords.cadastroNew} onToggleVisibility={() => togglePasswordVisibility("cadastroNew")} />
                        </label>
                        <label>
                          <span style={profileLabelStyle}>Confirmar senha</span>
                          <PasswordField value={loginForm.confirmPassword} onChange={(event) => updateLoginForm("confirmPassword", event.target.value)} placeholder="Repita sua senha" style={profileInputStyle} visible={!!visiblePasswords.cadastroConfirm} onToggleVisibility={() => togglePasswordVisibility("cadastroConfirm")} />
                        </label>
                        <p style={{ gridColumn: isTablet ? "1 / -1" : "auto", margin: 0, color: "#9fb0cc", fontSize: 11, lineHeight: 1.45 }}>
                          Use pelo menos 8 caracteres, uma letra maiúscula e um caractere especial.
                        </p>
                      </div>
                    )}

                    <label
                      style={{
                        display: "grid",
                        gridTemplateColumns: "22px 1fr",
                        gap: 10,
                        alignItems: "flex-start",
                        padding: 13,
                        marginBottom: 14,
                        borderRadius: 16,
                        border: "1px solid rgba(125,211,252,0.30)",
                        background: "rgba(14,165,233,0.08)",
                        color: "#dbeafe",
                        cursor: "pointer"
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={profileForm.privacyAccepted}
                        onChange={(event) => updateProfileForm("privacyAccepted", event.target.checked)}
                        style={{ width: 18, height: 18, marginTop: 2, accentColor: "#0EA5E9" }}
                      />
                      <span style={{ fontSize: 12, lineHeight: 1.45, color: "#b9c7dc" }}>
                        Autorizo a Família Mosaico a usar meus dados de cadastro para liberar meu acesso, acompanhar minha caminhada no app e entrar em contato quando eu pedir uma trilha ou conversa pastoral.
                        <button type="button" onClick={(event) => { event.preventDefault(); setShowPrivacyPolicy(true); }} style={{ ...buttonReset, display: "inline", marginLeft: 6, color: "#7DD3FC", fontSize: 12, fontWeight: 900, textDecoration: "underline", textUnderlineOffset: 3 }}>
                          Ler política de privacidade
                        </button>
                      </span>
                    </label>

                    <button
                      onClick={saveProfileForm}
                      disabled={profileSaving}
                      style={{
                        ...buttonReset,
                        width: "100%",
                        minHeight: 48,
                        borderRadius: 14,
                        background: "linear-gradient(135deg, #F5D28B, #E6B15A)",
                        color: "#171a22",
                        fontSize: 14,
                        fontWeight: 950,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                        boxShadow: "0 16px 28px rgba(230,177,90,0.18)",
                        opacity: profileSaving ? 0.72 : 1,
                        cursor: profileSaving ? "wait" : "pointer"
                      }}
                    >
                      <User size={17} strokeWidth={2.4} /> {profileSaving ? "Salvando..." : "Salvar cadastro"}
                    </button>
                    {profileFeedback && <p style={{ margin: "10px 0 0", color: profileFeedback.includes("Preencha") || profileFeedback.includes("Confirme") ? "#FCA5A5" : "#A7F3D0", fontSize: 12, lineHeight: 1.45 }}>{profileFeedback}</p>}
                  </div>
                </div>
              </div>
              )}

              {/* "Meu cadastro na igreja" -- cadastro de membresia (Member) e
                  filhos (Child) vinculados a propria conta, ver /me/member e
                  /me/children (routes/selfService.js). Separado do cadastro
                  de acesso (User/profileForm) acima: aqui e o cadastro
                  "oficial" da secretaria/ministerio infantil. Vive na aba
                  "Cadastro de membros", separada de "Dados de usuário"
                  (pedido do Drigo, 31/10/2026). */}
              {effectiveHubSection === "cadastro-membro" && (
              <div style={profileSurfaceStyle}>
                <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#E6B15A", textTransform: "uppercase", fontWeight: 800 }}>Cadastro na igreja</p>
                <h3 style={{ margin: "0 0 14px", fontSize: 20, color: "#FFF", fontWeight: 900 }}>Meu cadastro na igreja</h3>

                {myMemberLoading && <p style={{ margin: "0 0 16px", fontSize: 12, color: "#9fb0cc" }}>Carregando seu cadastro...</p>}

                {!myMemberLoading && myMember && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: 14, marginBottom: 18, borderRadius: 16, border: "1px solid rgba(148,163,184,0.22)", background: "rgba(15,23,42,0.5)" }}>
                    <div style={{ minWidth: 0 }}>
                      <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 900, color: "#F8FAFC", overflowWrap: "anywhere" }}>{myMember.fullName}</p>
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        {myMember.status === "PENDENTE" && (
                          <span style={{ ...profilePillStyle, borderColor: "rgba(252,211,77,0.4)", background: "rgba(252,211,77,0.12)", color: "#FCD34D" }}>Aguardando confirmação</span>
                        )}
                        {myMember.status && myMember.status !== "PENDENTE" && (
                          <span style={{ ...profilePillStyle, borderColor: "rgba(134,239,172,0.28)", background: "rgba(34,197,94,0.10)", color: "#BBF7D0" }}>{myMember.status === "ATIVO" ? "Ativo" : myMember.status}</span>
                        )}
                      </div>
                    </div>
                    <button onClick={openMemberEditor} style={{ ...buttonReset, minHeight: 38, padding: "0 14px", borderRadius: 999, border: "1px solid rgba(230,177,90,0.42)", background: "rgba(230,177,90,0.12)", color: "#F5D28B", fontSize: 12, fontWeight: 900, whiteSpace: "nowrap" }}>
                      Editar meu cadastro
                    </button>
                  </div>
                )}

                {!myMemberLoading && !myMember && (
                  <div style={{ padding: 15, marginBottom: 18, borderRadius: 16, border: "1px solid rgba(230,177,90,0.30)", background: "rgba(230,177,90,0.08)" }}>
                    <p style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 900, color: "#F8FAFC" }}>Você é membro da Mosaico?</p>
                    <p style={{ margin: "0 0 12px", fontSize: 12.5, lineHeight: 1.5, color: "#c7d1e4" }}>Complete seu cadastro de membresia -- leva menos de um minuto.</p>
                    <button onClick={openMemberEditor} style={{ ...buttonReset, minHeight: 40, padding: "0 16px", borderRadius: 999, background: "linear-gradient(135deg, #F5D28B, #E6B15A)", color: "#171a22", fontSize: 12, fontWeight: 950 }}>
                      Completar cadastro
                    </button>
                  </div>
                )}

                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 10 }}>
                  <h4 style={{ margin: 0, fontSize: 15, color: "#F8FAFC", fontWeight: 900 }}>Meus filhos</h4>
                  <button onClick={openAddChildEditor} style={{ ...buttonReset, minHeight: 32, padding: "0 12px", borderRadius: 999, border: "1px solid rgba(125,211,252,0.34)", background: "rgba(14,165,233,0.10)", color: "#BAE6FD", fontSize: 11.5, fontWeight: 900, whiteSpace: "nowrap" }}>
                    + Adicionar filho(a)
                  </button>
                </div>

                {myChildrenLoading && <p style={{ margin: 0, fontSize: 12, color: "#9fb0cc" }}>Carregando...</p>}
                {!myChildrenLoading && myChildren.length === 0 && (
                  <p style={{ margin: 0, fontSize: 12.5, color: "#9fb0cc", lineHeight: 1.5 }}>Nenhum filho(a) cadastrado(a) ainda.</p>
                )}
                {!myChildrenLoading && myChildren.length > 0 && (
                  <div style={{ display: "grid", gap: 8 }}>
                    {myChildren.map((child) => (
                      <div key={child.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap", padding: "10px 13px", borderRadius: 14, border: "1px solid rgba(148,163,184,0.18)", background: "rgba(15,23,42,0.42)" }}>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: "0 0 3px", fontSize: 13, fontWeight: 900, color: "#F8FAFC", overflowWrap: "anywhere" }}>{child.fullName}</p>
                          <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                            {child.status === "PENDENTE" && (
                              <span style={{ ...profilePillStyle, minHeight: 22, padding: "0 8px", fontSize: 10, borderColor: "rgba(252,211,77,0.4)", background: "rgba(252,211,77,0.12)", color: "#FCD34D" }}>Aguardando confirmação</span>
                            )}
                            {!child.addedByMe && (
                              <span style={{ fontSize: 11, color: "#9fb0cc" }}>
                                cadastrado por {child.guardianUser?.name || "outro responsável"} · mesma família
                              </span>
                            )}
                          </div>
                        </div>
                        {child.addedByMe && (
                          <button onClick={() => openEditChildEditor(child)} style={{ ...buttonReset, minHeight: 32, padding: "0 12px", borderRadius: 999, border: "1px solid rgba(230,177,90,0.34)", background: "rgba(230,177,90,0.10)", color: "#F5D28B", fontSize: 11, fontWeight: 900, whiteSpace: "nowrap" }}>
                            Editar
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              )}

              {showMemberEditor && (
                <div style={{ position: "fixed", inset: 0, zIndex: 260, background: "rgba(2,6,23,0.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 18 }}>
                  <div style={{ ...overflowGuardStyle, width: "100%", maxWidth: 640, maxHeight: "88vh", overflowY: "auto", borderRadius: 22, border: "1px solid rgba(230,177,90,0.42)", background: "linear-gradient(145deg, rgba(19,29,48,0.98), rgba(15,23,39,0.98) 52%, rgba(44,34,24,0.86))", padding: isMobile ? 18 : 24, color: "#f8fbff", boxShadow: "0 24px 60px rgba(0,0,0,0.42)" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 16 }}>
                      <div>
                        <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.2, color: "#E6B15A", textTransform: "uppercase", fontWeight: 900 }}>Cadastro de membresia</p>
                        <h3 style={{ margin: 0, fontSize: isTablet ? 24 : 20, color: "#FFF", fontWeight: 950 }}>{myMember ? "Editar meu cadastro" : "Completar meu cadastro"}</h3>
                      </div>
                      <button onClick={() => setShowMemberEditor(false)} aria-label="Fechar" style={{ ...buttonReset, width: 32, height: 32, borderRadius: 16, color: "#c7d1e4", fontSize: 18, lineHeight: 1, flexShrink: 0 }}>×</button>
                    </div>

                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6 }}>Dados pessoais</p>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Nome completo <span style={{ color: "#FCA5A5" }}>*</span></span>
                      <input value={memberEditorForm.fullName} onChange={(e) => updateMemberEditorForm("fullName", e.target.value)} style={profileInputStyle} />
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label style={{ minWidth: 0 }}>
                        <span style={profileLabelStyle}>Data de nascimento <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input type="date" value={memberEditorForm.birthDate} onChange={(e) => updateMemberEditorForm("birthDate", e.target.value)} style={{ ...profileInputStyle, colorScheme: "dark", minWidth: 0 }} />
                      </label>
                      <label style={{ minWidth: 0 }}>
                        <span style={profileLabelStyle}>CPF <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.cpf} onChange={(e) => updateMemberEditorForm("cpf", e.target.value)} placeholder="000.000.000-00" style={profileInputStyle} />
                      </label>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 16 }}>
                      <label>
                        <span style={profileLabelStyle}>E-mail <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input type="email" value={memberEditorForm.email} onChange={(e) => updateMemberEditorForm("email", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>WhatsApp <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.mobilePhone} onChange={(e) => updateMemberEditorForm("mobilePhone", e.target.value)} placeholder="(11) 90000-0000" style={profileInputStyle} />
                      </label>
                    </div>

                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6 }}>Endereço</p>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Endereço <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={memberEditorForm.address} onChange={(e) => updateMemberEditorForm("address", e.target.value)} placeholder="Rua, número" style={profileInputStyle} />
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Complemento <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.addressComplement} onChange={(e) => updateMemberEditorForm("addressComplement", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Bairro <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.neighborhood} onChange={(e) => updateMemberEditorForm("neighborhood", e.target.value)} style={profileInputStyle} />
                      </label>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Cidade <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.city} onChange={(e) => updateMemberEditorForm("city", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Estado (UF) <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.state} onChange={(e) => updateMemberEditorForm("state", e.target.value.toUpperCase())} maxLength={2} placeholder="SP" style={profileInputStyle} />
                      </label>
                    </div>
                    <label style={{ display: "block", marginBottom: 16 }}>
                      <span style={profileLabelStyle}>CEP <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={memberEditorForm.zipCode} onChange={(e) => updateMemberEditorForm("zipCode", e.target.value)} placeholder="00000-000" style={profileInputStyle} />
                    </label>

                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6 }}>Contato de emergência e família</p>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Contato de emergência <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.emergencyContactName} onChange={(e) => updateMemberEditorForm("emergencyContactName", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Telefone de emergência <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.emergencyPhone} onChange={(e) => updateMemberEditorForm("emergencyPhone", e.target.value)} style={profileInputStyle} />
                      </label>
                    </div>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Cônjuge <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={memberEditorForm.spouseName} onChange={(e) => updateMemberEditorForm("spouseName", e.target.value)} placeholder="Nome do cônjuge, se casado(a)" style={profileInputStyle} />
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Nome do pai <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.fatherName} onChange={(e) => updateMemberEditorForm("fatherName", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Nome da mãe <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.motherName} onChange={(e) => updateMemberEditorForm("motherName", e.target.value)} style={profileInputStyle} />
                      </label>
                    </div>
                    <label style={{ display: "block", marginBottom: 16 }}>
                      <span style={profileLabelStyle}>Necessidade especial de acessibilidade <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={memberEditorForm.specialNeeds} onChange={(e) => updateMemberEditorForm("specialNeeds", e.target.value)} style={profileInputStyle} />
                    </label>

                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6 }}>Vida na igreja</p>
                    <label style={{ display: "block", marginBottom: 8 }}>
                      <span style={profileLabelStyle}>Data de batismo <span style={{ color: "#FCA5A5" }}>*</span></span>
                      <input type="date" value={memberEditorForm.baptismDate} onChange={(e) => updateMemberEditorForm("baptismDate", e.target.value)} disabled={memberEditorForm.notBaptized} style={{ ...profileInputStyle, colorScheme: "dark", opacity: memberEditorForm.notBaptized ? 0.55 : 1 }} />
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16, fontSize: 12.5, color: "#c7d1e4" }}>
                      <input
                        type="checkbox"
                        checked={memberEditorForm.notBaptized}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setMemberEditorForm((prev) => ({ ...prev, notBaptized: checked, baptismDate: checked ? "" : prev.baptismDate, baptismChurch: checked ? "" : prev.baptismChurch }));
                        }}
                        style={{ width: 16, height: 16 }}
                      />
                      Ainda não fui batizado(a)
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Igreja do batismo <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={memberEditorForm.baptismChurch} onChange={(e) => updateMemberEditorForm("baptismChurch", e.target.value)} disabled={memberEditorForm.notBaptized} style={{ ...profileInputStyle, opacity: memberEditorForm.notBaptized ? 0.55 : 1 }} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Membro/participante desde <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input type="date" value={memberEditorForm.attendingSince} onChange={(e) => updateMemberEditorForm("attendingSince", e.target.value)} style={{ ...profileInputStyle, colorScheme: "dark" }} />
                      </label>
                    </div>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Já exerceu função em outra igreja? <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={memberEditorForm.churchRole} onChange={(e) => updateMemberEditorForm("churchRole", e.target.value)} style={profileInputStyle} />
                    </label>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Como conheceu a Mosaico? <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={memberEditorForm.howMetChurch} onChange={(e) => updateMemberEditorForm("howMetChurch", e.target.value)} style={profileInputStyle} />
                    </label>
                    <label style={{ display: "block", marginBottom: 18 }}>
                      <span style={profileLabelStyle}>Observação <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <textarea rows="3" value={memberEditorForm.notes} onChange={(e) => updateMemberEditorForm("notes", e.target.value)} style={{ ...profileInputStyle, resize: "vertical" }} />
                    </label>

                    <button onClick={saveMemberEditor} disabled={memberEditorSaving} style={{ ...buttonReset, width: "100%", minHeight: 48, borderRadius: 14, background: "linear-gradient(135deg, #F5D28B, #E6B15A)", color: "#171a22", fontSize: 14, fontWeight: 950, opacity: memberEditorSaving ? 0.72 : 1 }}>
                      {memberEditorSaving ? "Salvando..." : "Salvar cadastro"}
                    </button>
                    {memberEditorFeedback && <p style={{ margin: "10px 0 0", color: memberEditorFeedback.includes("Informe") ? "#FCA5A5" : "#A7F3D0", fontSize: 12, lineHeight: 1.45 }}>{memberEditorFeedback}</p>}
                  </div>
                </div>
              )}

              {showChildEditor && (
                <div style={{ position: "fixed", inset: 0, zIndex: 260, background: "rgba(2,6,23,0.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 18 }}>
                  <div style={{ ...overflowGuardStyle, width: "100%", maxWidth: 640, maxHeight: "88vh", overflowY: "auto", borderRadius: 22, border: "1px solid rgba(125,211,252,0.36)", background: "linear-gradient(145deg, rgba(19,29,48,0.98), rgba(15,23,39,0.98))", padding: isMobile ? 18 : 24, color: "#f8fbff", boxShadow: "0 24px 60px rgba(0,0,0,0.42)" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 16 }}>
                      <div>
                        <p style={{ margin: "0 0 6px", fontSize: 11, letterSpacing: 1.2, color: "#7DD3FC", textTransform: "uppercase", fontWeight: 900 }}>Ministério infantil</p>
                        <h3 style={{ margin: 0, fontSize: isTablet ? 24 : 20, color: "#FFF", fontWeight: 950 }}>{editingChildId ? "Editar cadastro" : "Adicionar filho(a)"}</h3>
                      </div>
                      <button onClick={() => setShowChildEditor(false)} aria-label="Fechar" style={{ ...buttonReset, width: 32, height: 32, borderRadius: 16, color: "#c7d1e4", fontSize: 18, lineHeight: 1, flexShrink: 0 }}>×</button>
                    </div>

                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6 }}>Dados da criança/adolescente</p>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Nome completo <span style={{ color: "#FCA5A5" }}>*</span></span>
                      <input value={childEditorForm.fullName} onChange={(e) => updateChildEditorForm("fullName", e.target.value)} style={profileInputStyle} />
                    </label>
                    <label style={{ display: "block", marginBottom: 16 }}>
                      <span style={profileLabelStyle}>Data de nascimento <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input type="date" value={childEditorForm.birthDate} onChange={(e) => updateChildEditorForm("birthDate", e.target.value)} style={{ ...profileInputStyle, colorScheme: "dark" }} />
                    </label>

                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6 }}>Endereço</p>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Endereço <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={childEditorForm.address} onChange={(e) => updateChildEditorForm("address", e.target.value)} placeholder="Rua, número" style={profileInputStyle} />
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Complemento <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.addressComplement} onChange={(e) => updateChildEditorForm("addressComplement", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Bairro <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.neighborhood} onChange={(e) => updateChildEditorForm("neighborhood", e.target.value)} style={profileInputStyle} />
                      </label>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Cidade <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.city} onChange={(e) => updateChildEditorForm("city", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Estado (UF) <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.state} onChange={(e) => updateChildEditorForm("state", e.target.value.toUpperCase())} maxLength={2} placeholder="SP" style={profileInputStyle} />
                      </label>
                    </div>
                    <label style={{ display: "block", marginBottom: 16 }}>
                      <span style={profileLabelStyle}>CEP <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={childEditorForm.zipCode} onChange={(e) => updateChildEditorForm("zipCode", e.target.value)} placeholder="00000-000" style={profileInputStyle} />
                    </label>

                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6 }}>Pais/responsáveis</p>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Nome do pai <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.fatherName} onChange={(e) => updateChildEditorForm("fatherName", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Telefone do pai <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.fatherPhone} onChange={(e) => updateChildEditorForm("fatherPhone", e.target.value)} style={profileInputStyle} />
                      </label>
                    </div>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>E-mail do pai <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input type="email" value={childEditorForm.fatherEmail} onChange={(e) => updateChildEditorForm("fatherEmail", e.target.value)} style={profileInputStyle} />
                    </label>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Nome da mãe <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.motherName} onChange={(e) => updateChildEditorForm("motherName", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Telefone da mãe <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.motherPhone} onChange={(e) => updateChildEditorForm("motherPhone", e.target.value)} style={profileInputStyle} />
                      </label>
                    </div>
                    <label style={{ display: "block", marginBottom: 12 }}>
                      <span style={profileLabelStyle}>E-mail da mãe <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input type="email" value={childEditorForm.motherEmail} onChange={(e) => updateChildEditorForm("motherEmail", e.target.value)} style={profileInputStyle} />
                    </label>
                    <p style={{ margin: "0 0 10px", fontSize: 11.5, color: "#8592ab" }}>Se houver um terceiro responsável (avó, tio, padrasto etc.), preencha abaixo.</p>
                    <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10, marginBottom: 10 }}>
                      <label>
                        <span style={profileLabelStyle}>Nome do responsável <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.guardianName} onChange={(e) => updateChildEditorForm("guardianName", e.target.value)} style={profileInputStyle} />
                      </label>
                      <label>
                        <span style={profileLabelStyle}>Telefone do responsável <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.guardianPhone} onChange={(e) => updateChildEditorForm("guardianPhone", e.target.value)} style={profileInputStyle} />
                      </label>
                    </div>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Parentesco <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={childEditorForm.guardianRelationship} onChange={(e) => updateChildEditorForm("guardianRelationship", e.target.value)} placeholder="Ex.: avó materna" style={profileInputStyle} />
                    </label>
                    <label style={{ display: "block", marginBottom: 16 }}>
                      <span style={profileLabelStyle}>Mora com <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={childEditorForm.livesWith} onChange={(e) => updateChildEditorForm("livesWith", e.target.value)} placeholder="Ex.: com os pais, mãe e padrasto..." style={profileInputStyle} />
                    </label>

                    <p style={{ margin: "0 0 6px", fontSize: 12, fontWeight: 900, color: "#7DD3FC", textTransform: "uppercase", letterSpacing: 0.6 }}>Saúde e autorização</p>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10, fontSize: 12.5, color: "#c7d1e4" }}>
                      <input type="checkbox" checked={childEditorForm.hasAllergies} onChange={(e) => updateChildEditorForm("hasAllergies", e.target.checked)} style={{ width: 16, height: 16 }} />
                      Tem alguma alergia
                    </label>
                    {childEditorForm.hasAllergies && (
                      <label style={{ display: "block", marginBottom: 10 }}>
                        <span style={profileLabelStyle}>Detalhes da alergia <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                        <input value={childEditorForm.allergyDetails} onChange={(e) => updateChildEditorForm("allergyDetails", e.target.value)} style={profileInputStyle} />
                      </label>
                    )}
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={profileLabelStyle}>Usa alguma medicação? <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <input value={childEditorForm.medications} onChange={(e) => updateChildEditorForm("medications", e.target.value)} style={profileInputStyle} />
                    </label>
                    <label style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12, fontSize: 12.5, color: "#c7d1e4" }}>
                      <input type="checkbox" checked={childEditorForm.imageAuthorization} onChange={(e) => updateChildEditorForm("imageAuthorization", e.target.checked)} style={{ width: 16, height: 16 }} />
                      Autorizo o uso de imagem em fotos e vídeos das atividades da igreja
                    </label>
                    <label style={{ display: "block", marginBottom: 18 }}>
                      <span style={profileLabelStyle}>Observação importante <span style={{ color: "#8592ab" }}>(opcional)</span></span>
                      <textarea rows="3" value={childEditorForm.importantNotes} onChange={(e) => updateChildEditorForm("importantNotes", e.target.value)} placeholder="Ex.: timidez inicial, TDAH, alguma condição que a equipe deva saber..." style={{ ...profileInputStyle, resize: "vertical" }} />
                    </label>

                    <button onClick={saveChildEditor} disabled={childEditorSaving} style={{ ...buttonReset, width: "100%", minHeight: 48, borderRadius: 14, background: "linear-gradient(135deg, #7DD3FC, #38BDF8)", color: "#0b1420", fontSize: 14, fontWeight: 950, opacity: childEditorSaving ? 0.72 : 1 }}>
                      {childEditorSaving ? "Salvando..." : "Salvar cadastro"}
                    </button>
                    {childEditorFeedback && <p style={{ margin: "10px 0 0", color: childEditorFeedback.includes("Informe") ? "#FCA5A5" : "#A7F3D0", fontSize: 12, lineHeight: 1.45 }}>{childEditorFeedback}</p>}
                  </div>
                </div>
              )}

              {effectiveHubSection === "overview" && hasCompletedProfile && (
              <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(3, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 12, marginBottom: 16 }}>
                {[
                  { label: "Dias na semana", value: weeklyCompletedCount, icon: BookOpen, color: "#F59E0B" },
                  { label: "Compromissos", value: myBookings.length, icon: Clock, color: "#0EA5E9" },
                  { label: "Perfil", value: `${profileCompletion}%`, icon: UserRound, color: "#E6B15A" }
                ].map((item) => {
                  const Icon = item.icon;
                  return (
                    <div key={item.label} style={{ ...sectionCardStyle, marginBottom: 0, padding: 16, display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                      <div style={{ width: 42, height: 42, flex: "0 0 auto", borderRadius: 14, display: "grid", placeItems: "center", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.08)" }}>
                        <Icon size={19} color={item.color} strokeWidth={2.4} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <p style={{ margin: "0 0 4px", fontSize: 22, fontWeight: 950, color: "#F8FAFC" }}>{item.value}</p>
                        <p style={{ margin: 0, fontSize: 11, color: "#9CA3AF", textTransform: "uppercase", letterSpacing: 1, fontWeight: 800 }}>{item.label}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
              )}

              {effectiveHubSection === "identidade" && (
              <div style={sectionCardStyle}>
                <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#c7ced9", textTransform: "uppercase", fontWeight: 700 }}>Identidade da jornada</p>
                <h3 style={{ margin: "0 0 12px", fontSize: 20, color: "#FFF", fontWeight: 900 }}>Como você aparece nas trilhas</h3>
                <p style={{ margin: "0 0 14px", fontSize: 12, color: "#9fb0cc", lineHeight: 1.45 }}>Monte seu avatar: escolha o formato do cabelo, a cor da pele, a cor do cabelo e os acessórios.</p>

                <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "3fr 7fr" : "1fr", gap: isDesktop ? 26 : 18, alignItems: "stretch" }}>
                  {/* Esse wrapper externo e' quem vira o item do grid e estica
                      (alignItems:stretch no pai) ate' a altura da coluna de
                      opcoes, que e' bem mais alta. Se o proprio elemento
                      sticky fosse esse item esticado, ele mesmo ficaria
                      "alto" (ocupando a fileira inteira desde o topo) e o
                      conteudo (avatar) la dentro, fixado no topo dele via
                      flex-start, rolaria pra fora de vista igual antes --
                      sticky precisa de um container ALTO ao redor de um
                      elemento CURTO pra ter espaco de "andar" durante a
                      rolagem. Por isso o position:sticky fica no filho de
                      dentro, nao nesse wrapper. */}
                  <div style={{ height: "100%" }}>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: isDesktop ? "column" : "row",
                        alignItems: "center",
                        gap: 14,
                        // Fixo (sticky) em QUALQUER largura, nao so' desktop --
                        // antes so' isDesktop tinha sticky, entao abaixo de
                        // 1080px (telas "nao-desktop" nessa definicao, o que
                        // inclui varias janelas largas tambem) o preview rolava
                        // junto com o resto e sumia assim que voce ia pras
                        // cores/roupa -- exatamente o que o Rodrigo reportou.
                        // topBarHeight + 12 pra nao grudar atras da topbar fixa.
                        position: "sticky",
                        top: topBarHeight + 12
                      }}
                    >
                    <span
                      style={{
                        width: isDesktop ? 220 : 66,
                        height: isDesktop ? 220 : 66,
                        borderRadius: 999,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flex: "0 0 auto",
                        background: "rgba(15,23,42,0.46)",
                        border: "1px solid rgba(148,163,184,0.22)"
                      }}
                    >
                      <AvatarSvg avatar={getAvatarById(selectedAvatarId)} size={isDesktop ? 200 : 60} />
                    </span>
                    <p style={{ margin: 0, fontSize: 12, color: UI.textSoft, lineHeight: 1.5, textAlign: isDesktop ? "center" : "left" }}>É assim que você vai aparecer no mapa da trilha e nos grupos.</p>
                    {selectedAvatarId !== viewerUser?.avatarId && (
                      // Unico botao de salvar do avatar agora -- colado na
                      // previa, que com o sticky consertado de verdade fica
                      // visivel o tempo todo durante a rolagem. Antes havia
                      // um segundo botao "Salvar avatar" repetido la embaixo
                      // de todas as opcoes (cor/roupa/acessorios), criado
                      // quando o sticky ainda nao funcionava direito e esse
                      // botao de cima sumia de vista. Virou duplicado e
                      // confuso (2 botoes fazendo a mesma coisa na mesma
                      // tela) -- removido a pedido do Rodrigo assim que o
                      // sticky passou a funcionar de verdade.
                      <button
                        onClick={saveProfileForm}
                        disabled={profileSaving}
                        style={{ ...buttonReset, width: "100%", minHeight: 38, padding: "0 14px", borderRadius: 999, background: "linear-gradient(135deg, #F5D28B, #E6B15A)", color: "#171a22", fontSize: 12, fontWeight: 950, opacity: profileSaving ? 0.72 : 1, cursor: profileSaving ? "wait" : "pointer" }}
                      >
                        {profileSaving ? "Salvando..." : "Salvar avatar"}
                      </button>
                    )}
                    {profileFeedback && (
                      <p style={{ margin: 0, fontSize: 11, lineHeight: 1.4, textAlign: isDesktop ? "center" : "left", color: profileFeedback.includes("Preencha") || profileFeedback.includes("Confirme") ? "#FCA5A5" : "#A7F3D0" }}>{profileFeedback}</p>
                    )}
                    </div>
                  </div>

                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Cabelo</p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
                      {AVATAR_HAIR_LENGTH_OPTIONS.map((option) => {
                        const isActive = avatarHairLengthFilter === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => setAvatarHairLengthFilter(option.id)}
                            style={{
                              ...buttonReset,
                              minHeight: 30,
                              padding: "0 11px",
                              borderRadius: 999,
                              border: isActive ? "1px solid #e6b15a" : "1px solid rgba(255,255,255,0.14)",
                              background: isActive ? "#e6b15a" : "rgba(15,23,42,0.46)",
                              color: isActive ? "#1a140a" : "#aebbd1",
                              fontSize: 11.5,
                              fontWeight: 800,
                              whiteSpace: "nowrap"
                            }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(60px, 1fr))", gap: 8, marginBottom: 16 }}>
                      {DICEBEAR_TOP_OPTIONS.filter((topOption) => avatarHairLengthFilter === "todos" || topOption.length === avatarHairLengthFilter).map((topOption) => {
                        const active = avatarBuilderTop === topOption.id;
                        const preview = getAvatarById(encodeDicebearAvatarId({
                          top: topOption.id,
                          skinColor: avatarBuilderSkinColor,
                          hairColor: avatarBuilderHairColor,
                          bgColor: avatarBuilderBgColor,
                          eyes: avatarBuilderEyes,
                          eyeColor: avatarBuilderEyeColor,
                          mouth: avatarBuilderMouth,
                          glasses: "none",
                          beard: "none",
                          clothing: avatarBuilderClothing,
                          clothesColor: avatarBuilderClothesColor
                        }));
                        return (
                          <button
                            key={topOption.id}
                            title={topOption.label}
                            onClick={() => applyAvatarBuilderChange({ top: topOption.id })}
                            style={{
                              ...buttonReset,
                              padding: "8px 6px",
                              borderRadius: 14,
                              border: active ? "1px solid rgba(230,177,90,0.82)" : "1px solid rgba(148,163,184,0.22)",
                              background: active ? "linear-gradient(160deg, rgba(230,177,90,0.18), rgba(230,177,90,0.06))" : "rgba(15,23,42,0.46)",
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "center",
                              justifyContent: "center",
                              minHeight: 64
                            }}
                          >
                            <AvatarSvg avatar={preview} size={40} />
                          </button>
                        );
                      })}
                    </div>

                    <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Cor da pele</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {DICEBEAR_SKIN_COLORS.map((tone) => {
                        const active = avatarBuilderSkinColor === tone.id;
                        return (
                          <button
                            key={tone.id}
                            title={tone.label}
                            onClick={() => applyAvatarBuilderChange({ skinColor: tone.id })}
                            style={{ ...buttonReset, width: 34, height: 34, borderRadius: 999, background: tone.hex, border: active ? "2px solid rgba(230,177,90,0.9)" : "2px solid transparent", boxShadow: active ? "0 0 0 2px rgba(230,177,90,0.35)" : "none" }}
                          />
                        );
                      })}
                    </div>

                    {avatarBuilderTop !== "bald" && (
                      <>
                        <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Cor do cabelo</p>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                          {DICEBEAR_HAIR_COLORS.map((color) => {
                            const active = avatarBuilderHairColor === color.id;
                            return (
                              <button
                                key={color.id}
                                title={color.label}
                                onClick={() => applyAvatarBuilderChange({ hairColor: color.id })}
                                style={{ ...buttonReset, width: 34, height: 34, borderRadius: 999, background: color.hex, border: active ? "2px solid rgba(230,177,90,0.9)" : "2px solid transparent", boxShadow: active ? "0 0 0 2px rgba(230,177,90,0.35)" : "none" }}
                              />
                            );
                          })}
                        </div>
                      </>
                    )}

                    <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Cor de fundo</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {DICEBEAR_BG_COLORS.map((color) => {
                        const active = avatarBuilderBgColor === color.id;
                        return (
                          <button
                            key={color.id}
                            title={color.label}
                            onClick={() => applyAvatarBuilderChange({ bgColor: color.id })}
                            style={{ ...buttonReset, width: 34, height: 34, borderRadius: 999, background: color.hex, border: active ? "2px solid rgba(230,177,90,0.9)" : "2px solid transparent", boxShadow: active ? "0 0 0 2px rgba(230,177,90,0.35)" : "none" }}
                          />
                        );
                      })}
                    </div>

                    <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Roupa</p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
                      {DICEBEAR_CLOTHING_OPTIONS.map((option) => {
                        const active = avatarBuilderClothing === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => applyAvatarBuilderChange({ clothing: option.id })}
                            style={{ ...buttonReset, padding: "7px 14px", borderRadius: 999, border: active ? "1px solid rgba(230,177,90,0.82)" : "1px solid rgba(148,163,184,0.22)", background: active ? "rgba(230,177,90,0.16)" : "transparent", color: active ? "#E6B15A" : "#c7ced9", fontSize: 12, fontWeight: 700 }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {DICEBEAR_CLOTHES_COLORS.map((color) => {
                        const active = avatarBuilderClothesColor === color.id;
                        return (
                          <button
                            key={color.id}
                            title={color.label}
                            onClick={() => applyAvatarBuilderChange({ clothesColor: color.id })}
                            style={{ ...buttonReset, width: 34, height: 34, borderRadius: 999, background: color.hex, border: active ? "2px solid rgba(230,177,90,0.9)" : "2px solid transparent", boxShadow: active ? "0 0 0 2px rgba(230,177,90,0.35)" : "none" }}
                          />
                        );
                      })}
                    </div>

                    <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Expressão</p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
                      {DICEBEAR_EYES_OPTIONS.map((option) => {
                        const active = avatarBuilderEyes === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => applyAvatarBuilderChange({ eyes: option.id })}
                            style={{ ...buttonReset, padding: "7px 14px", borderRadius: 999, border: active ? "1px solid rgba(230,177,90,0.82)" : "1px solid rgba(148,163,184,0.22)", background: active ? "rgba(230,177,90,0.16)" : "transparent", color: active ? "#E6B15A" : "#c7ced9", fontSize: 12, fontWeight: 700 }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>
                    <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Cor dos olhos</p>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
                      {DICEBEAR_EYE_COLORS.map((color) => {
                        const active = avatarBuilderEyeColor === color.id;
                        return (
                          <button
                            key={color.id}
                            title={color.label}
                            onClick={() => applyAvatarBuilderChange({ eyeColor: color.id })}
                            style={{ ...buttonReset, width: 28, height: 28, borderRadius: 999, background: color.hex, border: active ? "2px solid rgba(230,177,90,0.9)" : "2px solid transparent", boxShadow: active ? "0 0 0 2px rgba(230,177,90,0.35)" : "none" }}
                          />
                        );
                      })}
                    </div>

                    <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Boca</p>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", marginBottom: 16 }}>
                      {DICEBEAR_MOUTH_OPTIONS.map((option) => {
                        const active = avatarBuilderMouth === option.id;
                        return (
                          <button
                            key={option.id}
                            onClick={() => applyAvatarBuilderChange({ mouth: option.id })}
                            style={{ ...buttonReset, padding: "7px 14px", borderRadius: 999, border: active ? "1px solid rgba(230,177,90,0.82)" : "1px solid rgba(148,163,184,0.22)", background: active ? "rgba(230,177,90,0.16)" : "transparent", color: active ? "#E6B15A" : "#c7ced9", fontSize: 12, fontWeight: 700 }}
                          >
                            {option.label}
                          </button>
                        );
                      })}
                    </div>

                    <p style={{ margin: "0 0 8px", fontSize: 10, letterSpacing: 1, color: "#7c8db0", textTransform: "uppercase", fontWeight: 800 }}>Acessórios</p>

                    <div style={{ marginBottom: 14 }}>
                      <p style={{ margin: "0 0 6px", fontSize: 11, color: "#9fb0cc", fontWeight: 700 }}>Óculos</p>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        <button
                          onClick={() => applyAvatarBuilderChange({ glasses: "none" })}
                          style={{ ...buttonReset, padding: "7px 14px", borderRadius: 999, border: avatarBuilderGlasses === "none" ? "1px solid rgba(230,177,90,0.82)" : "1px solid rgba(148,163,184,0.22)", background: avatarBuilderGlasses === "none" ? "rgba(230,177,90,0.16)" : "transparent", color: avatarBuilderGlasses === "none" ? "#E6B15A" : "#c7ced9", fontSize: 12, fontWeight: 700 }}
                        >
                          Sem óculos
                        </button>
                        <button
                          onClick={() => applyAvatarBuilderChange({ glasses: avatarBuilderGlasses === "none" ? DICEBEAR_GLASSES_OPTIONS[1].id : avatarBuilderGlasses })}
                          style={{ ...buttonReset, padding: "7px 14px", borderRadius: 999, border: avatarBuilderGlasses !== "none" ? "1px solid rgba(230,177,90,0.82)" : "1px solid rgba(148,163,184,0.22)", background: avatarBuilderGlasses !== "none" ? "rgba(230,177,90,0.16)" : "transparent", color: avatarBuilderGlasses !== "none" ? "#E6B15A" : "#c7ced9", fontSize: 12, fontWeight: 700 }}
                        >
                          Com óculos
                        </button>
                        {avatarBuilderGlasses !== "none" && (
                          <span style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingLeft: 4, borderLeft: "1px solid rgba(148,163,184,0.22)", marginLeft: 2 }}>
                            {DICEBEAR_GLASSES_OPTIONS.filter((option) => option.id !== "none").map((option) => {
                              const active = avatarBuilderGlasses === option.id;
                              return (
                                <button
                                  key={option.id}
                                  onClick={() => applyAvatarBuilderChange({ glasses: option.id })}
                                  style={{ ...buttonReset, padding: "6px 12px", borderRadius: 999, border: active ? "1px solid rgba(125,211,252,0.7)" : "1px solid rgba(148,163,184,0.22)", background: active ? "rgba(125,211,252,0.14)" : "transparent", color: active ? "#7DD3FC" : "#9fb0cc", fontSize: 11, fontWeight: 700 }}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </span>
                        )}
                      </div>
                      {avatarBuilderGlasses !== "none" && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          {DICEBEAR_GLASSES_COLORS.map((color) => {
                            const active = avatarBuilderGlassesColor === color.id;
                            return (
                              <button
                                key={color.id}
                                title={color.label}
                                onClick={() => applyAvatarBuilderChange({ glassesColor: color.id })}
                                style={{ ...buttonReset, width: 28, height: 28, borderRadius: 999, background: color.hex, border: active ? "2px solid rgba(230,177,90,0.9)" : "2px solid transparent", boxShadow: active ? "0 0 0 2px rgba(230,177,90,0.35)" : "none" }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>

                    <div>
                      <p style={{ margin: "0 0 6px", fontSize: 11, color: "#9fb0cc", fontWeight: 700 }}>Barba</p>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        <button
                          onClick={() => applyAvatarBuilderChange({ beard: "none" })}
                          style={{ ...buttonReset, padding: "7px 14px", borderRadius: 999, border: avatarBuilderBeard === "none" ? "1px solid rgba(230,177,90,0.82)" : "1px solid rgba(148,163,184,0.22)", background: avatarBuilderBeard === "none" ? "rgba(230,177,90,0.16)" : "transparent", color: avatarBuilderBeard === "none" ? "#E6B15A" : "#c7ced9", fontSize: 12, fontWeight: 700 }}
                        >
                          Sem barba
                        </button>
                        <button
                          onClick={() => applyAvatarBuilderChange({ beard: avatarBuilderBeard === "none" ? DICEBEAR_BEARD_OPTIONS[1].id : avatarBuilderBeard })}
                          style={{ ...buttonReset, padding: "7px 14px", borderRadius: 999, border: avatarBuilderBeard !== "none" ? "1px solid rgba(230,177,90,0.82)" : "1px solid rgba(148,163,184,0.22)", background: avatarBuilderBeard !== "none" ? "rgba(230,177,90,0.16)" : "transparent", color: avatarBuilderBeard !== "none" ? "#E6B15A" : "#c7ced9", fontSize: 12, fontWeight: 700 }}
                        >
                          Com barba
                        </button>
                        {avatarBuilderBeard !== "none" && (
                          <span style={{ display: "flex", gap: 6, flexWrap: "wrap", paddingLeft: 4, borderLeft: "1px solid rgba(148,163,184,0.22)", marginLeft: 2 }}>
                            {DICEBEAR_BEARD_OPTIONS.filter((option) => option.id !== "none").map((option) => {
                              const active = avatarBuilderBeard === option.id;
                              return (
                                <button
                                  key={option.id}
                                  onClick={() => applyAvatarBuilderChange({ beard: option.id })}
                                  style={{ ...buttonReset, padding: "6px 12px", borderRadius: 999, border: active ? "1px solid rgba(125,211,252,0.7)" : "1px solid rgba(148,163,184,0.22)", background: active ? "rgba(125,211,252,0.14)" : "transparent", color: active ? "#7DD3FC" : "#9fb0cc", fontSize: 11, fontWeight: 700 }}
                                >
                                  {option.label}
                                </button>
                              );
                            })}
                          </span>
                        )}
                      </div>
                      {avatarBuilderBeard !== "none" && (
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                          {DICEBEAR_HAIR_COLORS.map((color) => {
                            const active = avatarBuilderBeardColor === color.id;
                            return (
                              <button
                                key={color.id}
                                title={color.label}
                                onClick={() => applyAvatarBuilderChange({ beardColor: color.id })}
                                style={{ ...buttonReset, width: 28, height: 28, borderRadius: 999, background: color.hex, border: active ? "2px solid rgba(230,177,90,0.9)" : "2px solid transparent", boxShadow: active ? "0 0 0 2px rgba(230,177,90,0.35)" : "none" }}
                              />
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

              </div>
              )}

              {effectiveHubSection === "agenda" && (
              <>
              <div style={sectionCardStyle}>
                <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#c7ced9", textTransform: "uppercase", fontWeight: 700 }}>Jornada pessoal</p>
                <h3 style={{ margin: "0 0 12px", fontSize: 20, color: "#FFF", fontWeight: 900 }}>Meu diário</h3>
                <p style={{ fontSize: 13, color: "#9CA3AF", margin: "0 0 16px", lineHeight: 1.55 }}>Um lugar para voltar às anotações, orações, marcas de leitura e decisões que nasceram nos devocionais.</p>
                <button onClick={() => { setScreen("diary"); window.scrollTo(0, 0); }} style={{ ...buttonReset, background: "#1c2639", border: `1px solid ${UI.stroke}`, padding: "10px 16px", borderRadius: 12, color: "#D1D5DB", fontSize: 13, fontWeight: 700, width: "100%" }}>Abrir diário</button>
              </div>

              <div style={sectionCardStyle}>
                <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#c7ced9", textTransform: "uppercase", fontWeight: 700 }}>Agenda pastoral</p>
                <h3 style={{ margin: "0 0 10px", fontSize: 20, color: "#FFF", fontWeight: 700 }}>Seus compromissos</h3>
                <button onClick={loadBookings} style={{ ...buttonReset, background: "#1c2639", border: `1px solid ${UI.stroke}`, padding: "8px 12px", borderRadius: 10, color: UI.textSoft, fontSize: 12, fontWeight: 700, marginBottom: 10 }}>
                  Atualizar agenda
                </button>
                {myBookings.length === 0 && <p style={{ margin: 0, fontSize: 13, color: UI.textSoft }}>Nenhum agendamento seu por enquanto.</p>}
                {myBookings.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {myBookings.slice(0, 4).map((item) => (
                      <div key={item.id} style={{ padding: "10px 12px", borderRadius: UI.radiusSm, background: UI.panelSoft, border: `1px solid ${UI.stroke}` }}>
                        <p style={{ margin: 0, fontSize: 12, color: UI.text }}>{item.pastor?.name || "Pastor"}</p>
                        <p style={{ margin: "2px 0 0", fontSize: 11, color: UI.textSoft }}>{new Date(item.startAt).toLocaleString("pt-BR")} • {item.status}</p>
                      </div>
                    ))}
                  </div>
                )}
                <p style={{ margin: "12px 0 0", fontSize: 11, color: "#c7ced9" }}>Semana: {weeklyCompletedCount}/7 dias concluídos</p>
              </div>

              <div style={sectionCardStyle}>
                <p style={{ margin: "0 0 8px", fontSize: 11, letterSpacing: 1.2, color: "#c7ced9", textTransform: "uppercase", fontWeight: 700 }}>Disciplina espiritual</p>
                <h3 style={{ margin: "0 0 16px", fontSize: 20, color: "#FFF", fontWeight: 900 }}>Lembrete devocional</h3>
                {!isPushSupported() && (
                  <p style={{ margin: 0, fontSize: 13, color: "#9CA3AF" }}>Notificações não são suportadas neste navegador.</p>
                )}
                {isPushSupported() && !reminderEditing && (
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <p style={{ margin: 0, fontSize: 13, color: "#9CA3AF" }}>
                      {viewerUser?.reminderEnabled ? `Ativado às ${viewerUser?.reminderTime || reminderTimeInput}.` : "Um toque simples para separar tempo com Deus."}
                    </p>
                    <button onClick={() => { setReminderFeedback(""); setReminderEditing(true); }} style={{ ...buttonReset, color: "#F59E0B", fontSize: 13, fontWeight: 700, whiteSpace: "nowrap" }}>Alterar</button>
                  </div>
                )}
                {isPushSupported() && reminderEditing && (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                      <input
                        type="time"
                        value={reminderTimeInput}
                        onChange={(event) => setReminderTimeInput(event.target.value)}
                        style={{ ...profileInputStyle, colorScheme: "dark", width: "auto" }}
                      />
                      <button
                        onClick={() => saveDevotionalReminder(true)}
                        disabled={reminderSaving}
                        style={{ ...buttonReset, background: "rgba(245,158,11,0.13)", border: "1px solid #F59E0B", padding: "8px 14px", borderRadius: 10, color: "#F8E7C4", fontSize: 12, fontWeight: 800, opacity: reminderSaving ? 0.6 : 1 }}
                      >
                        {viewerUser?.reminderEnabled ? "Salvar horário" : "Ativar"}
                      </button>
                    </div>
                    <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
                      {viewerUser?.reminderEnabled && (
                        <button onClick={() => saveDevotionalReminder(false)} disabled={reminderSaving} style={{ ...buttonReset, color: "#9CA3AF", fontSize: 12, fontWeight: 700 }}>Desativar</button>
                      )}
                      <button onClick={() => { setReminderEditing(false); setReminderFeedback(""); }} disabled={reminderSaving} style={{ ...buttonReset, color: "#9CA3AF", fontSize: 12, fontWeight: 700 }}>Cancelar</button>
                    </div>
                  </div>
                )}
                {reminderFeedback && (
                  <p style={{ margin: "10px 0 0", fontSize: 12, color: "#c7ced9" }}>{reminderFeedback}</p>
                )}
              </div>
              </>
              )}

            </div>
          )}

        </div>
        {showMomentGuide && (
          <div style={{ position: "fixed", inset: 0, zIndex: 230, background: "rgba(2,6,23,0.74)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 18 }}>
            <div style={{ ...overflowGuardStyle, width: "100%", maxWidth: 720, maxHeight: "86vh", overflowY: "auto", border: "1px solid rgba(230,177,90,0.42)", background: "linear-gradient(145deg, rgba(19,29,48,0.98), rgba(15,23,39,0.98) 52%, rgba(44,34,24,0.86))", padding: isMobile ? 18 : 24, color: "#f8fbff", boxShadow: "0 24px 60px rgba(0,0,0,0.42)" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 18 }}>
                <div>
                  <p style={{ margin: "0 0 8px", color: "#F0DCB8", fontSize: 10, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 950 }}>Começar meu momento</p>
                  <h3 style={{ margin: 0, fontSize: isMobile ? 24 : 30, lineHeight: 1.04, color: "#FFFFFF", fontWeight: 950 }}>Como está sua caminhada hoje?</h3>
                  <p style={{ margin: "10px 0 0", maxWidth: 560, color: "#C7D1E4", fontSize: isMobile ? 13 : 14, lineHeight: 1.55 }}>
                    Escolha um contexto simples. A partir disso, o app sugere uma trilha e abre o próximo devocional com mais sentido para o seu momento.
                  </p>
                </div>
                <button onClick={() => setShowMomentGuide(false)} style={{ ...buttonReset, flexShrink: 0, minWidth: 42, minHeight: 42, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "#DDE6F5", fontSize: 18, fontWeight: 900 }}>
                  ×
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(2, minmax(0, 1fr))" : "minmax(0, 1fr)", gap: 10, marginBottom: 16 }}>
                {guidedJourneyCards.map((card) => {
                  const active = selectedMomentGuideChoice?.id === card.id;
                  const nextDay = getTrailNextDay(card.trail);
                  return (
                    <button
                      key={card.id}
                      onClick={() => setMomentGuideChoiceId(card.id)}
                      style={{
                        ...buttonReset,
                        ...overflowGuardStyle,
                        minHeight: 146,
                        padding: 16,
                        textAlign: "left",
                        border: active ? `1px solid ${card.trail.color}` : "1px solid rgba(255,255,255,0.12)",
                        background: active ? `linear-gradient(145deg, ${card.trail.color}24, rgba(255,255,255,0.06))` : "rgba(255,255,255,0.045)",
                        color: "#f8fbff",
                        boxShadow: active ? `0 16px 30px ${card.trail.color}16` : "none"
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                        <span style={{ width: 42, height: 42, borderRadius: 14, flexShrink: 0, background: `${card.trail.color}22`, color: card.trail.color, display: "inline-flex", alignItems: "center", justifyContent: "center", border: `1px solid ${card.trail.color}66` }}>
                          {card.icon}
                        </span>
                        <span style={{ minWidth: 0 }}>
                          <span style={{ display: "block", marginBottom: 5, color: card.trail.color, fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 950 }}>{card.signal}</span>
                          <strong style={{ display: "block", color: "#FFFFFF", fontSize: 17, lineHeight: 1.1, fontWeight: 950 }}>{card.title}</strong>
                          <span style={{ display: "block", marginTop: 8, color: "#C7D1E4", fontSize: 12, lineHeight: 1.45 }}>{card.text}</span>
                          <span style={{ display: "block", marginTop: 10, color: "#8FA0BF", fontSize: 11, fontWeight: 900 }}>
                            Sugestão: {card.trail.title} · dia {nextDay}
                          </span>
                        </span>
                      </div>
                    </button>
                  );
                })}
              </div>

              {selectedMomentGuideChoice && (
                <div style={{ display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) auto auto" : "minmax(0, 1fr)", gap: 10, alignItems: "center", padding: 14, border: `1px solid ${selectedMomentGuideChoice.trail.color}55`, background: `linear-gradient(145deg, ${selectedMomentGuideChoice.trail.color}18, rgba(255,255,255,0.045))` }}>
                  <div style={{ minWidth: 0 }}>
                    <p style={{ margin: "0 0 5px", color: "#F0DCB8", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 950 }}>Devocional sugerido</p>
                    <h4 style={{ margin: 0, color: "#FFFFFF", fontSize: 18, lineHeight: 1.12, fontWeight: 950 }}>{selectedMomentGuideChoice.trail.title}</h4>
                    <p style={{ margin: "6px 0 0", color: "#AEBBD1", fontSize: 12, lineHeight: 1.45 }}>
                      Dia {getTrailNextDay(selectedMomentGuideChoice.trail)} de {selectedMomentGuideChoice.trail.duration}. Você pode começar agora ou ver a trilha antes.
                    </p>
                  </div>
                  <button onClick={() => openGuidedMoment(selectedMomentGuideChoice, "trail")} style={{ ...buttonReset, minHeight: 44, padding: "0 14px", border: "1px solid rgba(255,255,255,0.14)", background: "rgba(255,255,255,0.06)", color: "#E5EAF3", fontSize: 12, fontWeight: 950, whiteSpace: "nowrap" }}>
                    Ver trilha
                  </button>
                  <button onClick={() => openGuidedMoment(selectedMomentGuideChoice, "day")} style={{ ...buttonReset, minHeight: 44, padding: "0 16px", border: `1px solid ${selectedMomentGuideChoice.trail.color}`, background: selectedMomentGuideChoice.trail.color, color: "#111827", fontSize: 12, fontWeight: 950, whiteSpace: "nowrap" }}>
                    Começar agora
                  </button>
                </div>
              )}
            </div>
          </div>
        )}
        {profilePromptModal}
        {showPastoralAgenda && (
          <div style={{ position: "fixed", inset: 0, zIndex: 239, background: "rgba(2,6,23,0.78)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 18 }}>
            <div style={{ ...widthGuardStyle, width: "100%", maxWidth: 760, maxHeight: "calc(100dvh - 32px)", overflowY: "auto", overflowX: "hidden", border: "1px solid rgba(217,70,239,0.46)", background: "linear-gradient(145deg, rgba(19,29,48,0.98), rgba(15,23,39,0.98) 54%, rgba(42,23,54,0.86))", padding: isMobile ? 18 : 24, color: "#f8fbff", boxShadow: "0 24px 60px rgba(0,0,0,0.42)" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 18 }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: "0 0 8px", color: "#F0DCB8", fontSize: 10, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 950 }}>Cuidado pastoral</p>
                  <h3 style={{ margin: 0, fontSize: isMobile ? 24 : 30, lineHeight: 1.04, color: "#FFFFFF", fontWeight: 950 }}>Escolha um pastor e um horário.</h3>
                  <p style={{ margin: "10px 0 0", maxWidth: 620, color: "#C7D1E4", fontSize: isMobile ? 13 : 14, lineHeight: 1.58 }}>
                    Os horários abaixo são os que a equipe pastoral abriu para atendimento. Quando for online, o link da conversa será enviado na confirmação.
                  </p>
                </div>
                <button onClick={() => setShowPastoralAgenda(false)} aria-label="Fechar agendamento pastoral" style={{ ...buttonReset, flexShrink: 0, minWidth: 42, minHeight: 42, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "#DDE6F5", fontSize: 18, fontWeight: 900 }}>
                  ×
                </button>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 0.9fr) minmax(0, 1.1fr)" : "minmax(0, 1fr)", gap: 14 }}>
                <div style={{ display: "grid", gap: 12, alignContent: "start" }}>
                  <div>
                    <p style={{ margin: "0 0 8px", color: "#E9D5FF", fontSize: 10, letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 950 }}>1. Pessoa</p>
                    <div style={{ display: "grid", gap: 8 }}>
                      {pastorsLoading && <p style={{ margin: 0, color: "#AEBBD1", fontSize: 12 }}>Carregando equipe pastoral...</p>}
                      {!pastorsLoading && pastors.map((pastor) => {
                        const active = selectedPastorId === pastor.id;
                        return (
                          <button
                            key={pastor.id}
                            onClick={() => {
                              setSelectedPastorId(pastor.id);
                              setSelectedPastoralSlotId("");
                              setBookingFeedback("");
                            }}
                            style={{ ...buttonReset, minHeight: 54, padding: "12px 13px", border: active ? "1px solid rgba(217,70,239,0.78)" : "1px solid rgba(255,255,255,0.12)", background: active ? "rgba(217,70,239,0.18)" : "rgba(255,255,255,0.045)", color: "#F8FAFC", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, textAlign: "left" }}
                          >
                            <span>
                              <strong style={{ display: "block", color: "#FFFFFF", fontSize: 14, fontWeight: 950 }}>{pastor.name}</strong>
                              <span style={{ display: "block", marginTop: 3, color: "#9FB0CC", fontSize: 11 }}>Ver horários disponíveis</span>
                            </span>
                            <ChevronRight size={16} strokeWidth={2.5} style={{ color: active ? "#F0ABFC" : "#64748B" }} />
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  <div>
                    <p style={{ margin: "0 0 8px", color: "#E9D5FF", fontSize: 10, letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 950 }}>2. Horário</p>
                    {!selectedPastorId && <p style={{ margin: 0, color: "#AEBBD1", fontSize: 12, lineHeight: 1.5 }}>Escolha primeiro uma pessoa da equipe pastoral para ver a agenda dela.</p>}
                    {selectedPastorId && pastoralSlotsLoading && <p style={{ margin: 0, color: "#AEBBD1", fontSize: 12 }}>Buscando horários de {selectedPastorForBooking?.name || "pastor"}...</p>}
                    {selectedPastorId && !pastoralSlotsLoading && selectedPastorAvailability.length === 0 && (
                      <div style={{ padding: 13, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.045)", color: "#C7D1E4" }}>
                        <p style={{ margin: "0 0 10px", fontSize: 13, lineHeight: 1.5 }}>Esse pastor ainda não tem horários abertos agora. Preencha seu nome ao lado e peça pra ser avisado(a) assim que um horário abrir.</p>
                        <button
                          onClick={requestPastoralSlotNotice}
                          disabled={slotRequestLoading}
                          style={{ ...buttonReset, width: "100%", minHeight: 42, border: "1px solid rgba(94,234,212,0.5)", background: "rgba(20,184,166,0.16)", color: "#99F6E4", fontSize: 12, fontWeight: 950, opacity: slotRequestLoading ? 0.7 : 1 }}
                        >
                          {slotRequestLoading ? "Enviando..." : "Avisar quando abrir horário"}
                        </button>
                        {slotRequestFeedback && <p style={{ margin: "10px 0 0", color: slotRequestFeedback.includes("Pronto") ? "#BAF7D2" : "#F8DFA8", fontSize: 12, lineHeight: 1.45 }}>{slotRequestFeedback}</p>}
                      </div>
                    )}
                    <div style={{ display: "grid", gap: 8 }}>
                      {selectedPastorAvailability.map((slot) => {
                        const active = selectedPastoralSlotId === slot.id;
                        const dateText = new Date(slot.startAt).toLocaleDateString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit" });
                        const timeText = new Date(slot.startAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
                        return (
                          <button
                            key={slot.id}
                            onClick={() => {
                              setSelectedPastoralSlotId(slot.id);
                              setBookingFeedback("");
                            }}
                            style={{ ...buttonReset, padding: 12, border: active ? "1px solid rgba(230,177,90,0.80)" : "1px solid rgba(255,255,255,0.12)", background: active ? "rgba(230,177,90,0.16)" : "rgba(255,255,255,0.045)", color: "#F8FAFC", display: "grid", gridTemplateColumns: "minmax(0, 1fr) auto", gap: 10, alignItems: "center", textAlign: "left" }}
                          >
                            <span style={{ minWidth: 0 }}>
                              <strong style={{ display: "block", color: "#FFFFFF", fontSize: 14, fontWeight: 950 }}>{dateText} às {timeText}</strong>
                              <span style={{ display: "block", marginTop: 4, color: "#9FB0CC", fontSize: 11, lineHeight: 1.4 }}>{slot.helper}</span>
                            </span>
                            <span style={{ padding: "7px 9px", border: `1px solid ${slot.mode === "online" ? "rgba(56,189,248,0.45)" : "rgba(230,177,90,0.45)"}`, background: slot.mode === "online" ? "rgba(56,189,248,0.14)" : "rgba(230,177,90,0.14)", color: slot.mode === "online" ? "#BAE6FD" : "#F8DFA8", fontSize: 10, fontWeight: 950, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                              {slot.label}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div style={{ display: "grid", gap: 12, alignContent: "start" }}>
                  <div style={{ padding: 14, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.045)" }}>
                    <p style={{ margin: "0 0 8px", color: "#E9D5FF", fontSize: 10, letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 950 }}>3. Solicitação</p>
                    <label style={{ display: "block", marginBottom: 10 }}>
                      <span style={{ display: "block", margin: "0 0 6px", color: "#CBD5E1", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Seu nome</span>
                      <input
                        value={bookingName}
                        onChange={(event) => setBookingName(event.target.value)}
                        placeholder="Seu nome"
                        style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", padding: "12px 13px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                      />
                    </label>
                    <label style={{ display: "block" }}>
                      <span style={{ display: "block", margin: "0 0 6px", color: "#CBD5E1", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Como podemos te ajudar?</span>
                      <textarea
                        value={bookingNote}
                        onChange={(event) => setBookingNote(event.target.value)}
                        placeholder="Escreva só o necessário. A conversa continua com o pastor."
                        rows={5}
                        style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.62)", color: "#F8FAFC", padding: "12px 13px", fontSize: isMobile ? 16 : 13, lineHeight: 1.45, fontFamily: "inherit", boxSizing: "border-box", outline: "none", resize: "vertical" }}
                      />
                    </label>
                    {selectedPastoralSlot && (
                      <p style={{ margin: "10px 0 0", color: "#C7D1E4", fontSize: 12, lineHeight: 1.5 }}>
                        {selectedPastorForBooking?.name || "Pastor"} • {new Date(selectedPastoralSlot.startAt).toLocaleString("pt-BR", { weekday: "short", day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })} • {selectedPastoralSlot.label}
                      </p>
                    )}
                    <button
                      onClick={createPastoralBooking}
                      disabled={bookingLoading || !selectedPastoralSlot}
                      style={{ ...buttonReset, width: "100%", minHeight: 48, marginTop: 12, border: "1px solid rgba(217,70,239,0.72)", background: bookingLoading || !selectedPastoralSlot ? "rgba(148,163,184,0.18)" : "#D946EF", color: bookingLoading || !selectedPastoralSlot ? "#94A3B8" : "#111827", fontSize: 13, fontWeight: 950, cursor: bookingLoading || !selectedPastoralSlot ? "not-allowed" : "pointer" }}
                    >
                      {bookingLoading ? "Enviando solicitação..." : "Solicitar conversa"}
                    </button>
                    {bookingFeedback && <p style={{ margin: "10px 0 0", color: bookingFeedback.includes("enviado") || bookingFeedback.includes("recebido") ? "#BAF7D2" : "#F8DFA8", fontSize: 12, lineHeight: 1.45 }}>{bookingFeedback}</p>}
                  </div>

                  <div style={{ padding: 14, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(15,23,42,0.48)" }}>
                    <p style={{ margin: "0 0 8px", color: "#E9D5FF", fontSize: 10, letterSpacing: 1.1, textTransform: "uppercase", fontWeight: 950 }}>Suas conversas</p>
                    {bookingsLoading && <p style={{ margin: 0, color: "#AEBBD1", fontSize: 12 }}>Atualizando agenda...</p>}
                    {!bookingsLoading && myBookings.length === 0 && <p style={{ margin: 0, color: "#AEBBD1", fontSize: 12, lineHeight: 1.5 }}>Quando você solicitar um horário, ele aparece aqui.</p>}
                    {!bookingsLoading && myBookings.length > 0 && (
                      <div style={{ display: "grid", gap: 8 }}>
                        {myBookings.slice(0, 3).map((item) => (
                          <div key={item.id} style={{ padding: 10, border: "1px solid rgba(255,255,255,0.10)", background: "rgba(255,255,255,0.035)" }}>
                            <p style={{ margin: 0, color: "#FFFFFF", fontSize: 12, fontWeight: 900 }}>{item.pastor?.name || "Pastor"}</p>
                            <p style={{ margin: "4px 0 0", color: "#9FB0CC", fontSize: 11 }}>{new Date(item.startAt).toLocaleString("pt-BR")} • {item.status === "PENDING" ? "Aguardando confirmação" : item.status}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {showDirectInviteModal && (
          <div style={{ position: "fixed", inset: 0, zIndex: 240, background: "rgba(2,6,23,0.76)", display: "flex", alignItems: "center", justifyContent: "center", padding: isMobile ? 12 : 18 }}>
            <div style={{ ...overflowGuardStyle, width: "100%", maxWidth: 620, maxHeight: "86vh", overflowY: "auto", border: "1px solid rgba(230,177,90,0.46)", background: "linear-gradient(145deg, rgba(19,29,48,0.98), rgba(15,23,39,0.98) 52%, rgba(44,34,24,0.86))", padding: isMobile ? 18 : 24, color: "#f8fbff", boxShadow: "0 24px 60px rgba(0,0,0,0.42)" }}>
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 14, marginBottom: 18 }}>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: "0 0 8px", color: "#F0DCB8", fontSize: 10, letterSpacing: 1.3, textTransform: "uppercase", fontWeight: 950 }}>Convite devocional</p>
                  <h3 style={{ margin: 0, fontSize: isMobile ? 24 : 30, lineHeight: 1.04, color: "#FFFFFF", fontWeight: 950 }}>Chame alguém para caminhar com você.</h3>
                  <p style={{ margin: "10px 0 0", maxWidth: 520, color: "#C7D1E4", fontSize: isMobile ? 13 : 14, lineHeight: 1.55 }}>
                    Escolha uma trilha, informe os dados da pessoa e envie um convite simples. A ideia é abrir uma caminhada, não apenas mandar um link.
                  </p>
                </div>
                <button onClick={() => setShowDirectInviteModal(false)} aria-label="Fechar convite" style={{ ...buttonReset, flexShrink: 0, minWidth: 42, minHeight: 42, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.06)", color: "#DDE6F5", fontSize: 18, fontWeight: 900 }}>
                  ×
                </button>
              </div>

              <div style={{ display: "grid", gap: 11 }}>
                <label>
                  <span style={{ display: "block", margin: "0 0 6px", color: "#f0dcb8", fontSize: 10, letterSpacing: 1, textTransform: "uppercase", fontWeight: 900 }}>Trilha do convite<span style={{ color: "#f0a84e", fontWeight: 700 }}> *</span></span>
                  <select
                    value={mesaInviteTrail?.id || ""}
                    onChange={(event) => {
                      setMesaInviteTrailId(event.target.value);
                      setMesaInviteFeedback("");
                    }}
                    style={{ width: "100%", border: "1px solid rgba(230,177,90,0.38)", background: "rgba(15,23,42,0.72)", color: "#F8FAFC", borderRadius: 12, padding: "12px 13px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                  >
                    {trailsAlphabetical.map((trail) => (
                      <option key={trail.id} value={trail.id}>{trail.title}</option>
                    ))}
                  </select>
                </label>

                <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 10 }}>
                  <label>
                    <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Nome<span style={{ color: "#f0a84e", fontWeight: 700 }}> *</span></span>
                    <input
                      value={mesaInviteName}
                      onChange={(event) => {
                        setMesaInviteName(event.target.value);
                        setMesaInviteFeedback("");
                      }}
                      placeholder="Nome do convidado"
                      style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.72)", color: "#F8FAFC", borderRadius: 12, padding: "12px 13px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                    />
                  </label>
                  <label>
                    <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>E-mail</span>
                    <input
                      value={mesaInviteEmail}
                      onChange={(event) => {
                        setMesaInviteEmail(event.target.value);
                        setMesaInviteFeedback("");
                      }}
                      placeholder="email@exemplo.com"
                      inputMode="email"
                      style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.72)", color: "#F8FAFC", borderRadius: 12, padding: "12px 13px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                    />
                  </label>
                </div>

                <label>
                  <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>WhatsApp</span>
                  <input
                    value={mesaInviteWhatsapp}
                    onChange={(event) => {
                      setMesaInviteWhatsapp(event.target.value);
                      setMesaInviteFeedback("");
                    }}
                    placeholder="(00) 00000-0000"
                    inputMode="tel"
                    style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.72)", color: "#F8FAFC", borderRadius: 12, padding: "12px 13px", fontSize: isMobile ? 16 : 13, fontFamily: "inherit", boxSizing: "border-box", outline: "none" }}
                  />
                </label>

                <label>
                  <span style={{ display: "block", margin: "0 0 6px", color: "#c7d1e4", fontSize: 10, letterSpacing: 0.8, textTransform: "uppercase", fontWeight: 900 }}>Mensagem opcional</span>
                  <textarea
                    value={mesaInviteMessage}
                    onChange={(event) => {
                      setMesaInviteMessage(event.target.value);
                      setMesaInviteFeedback("");
                    }}
                    placeholder="Escreva algo pessoal para esse convite"
                    rows={3}
                    style={{ width: "100%", border: "1px solid rgba(255,255,255,0.13)", background: "rgba(15,23,42,0.72)", color: "#F8FAFC", borderRadius: 12, padding: "12px 13px", fontSize: isMobile ? 16 : 13, lineHeight: 1.45, fontFamily: "inherit", boxSizing: "border-box", outline: "none", resize: "vertical" }}
                  />
                </label>

                <div style={{ display: "grid", gridTemplateColumns: isTablet ? "1fr 1fr" : "1fr", gap: 9, marginTop: 2 }}>
                  <button onClick={generateMesaInviteLink} style={{ ...buttonReset, minHeight: 48, padding: "0 14px", border: "1px solid rgba(230,177,90,0.62)", background: "rgba(230,177,90,0.16)", color: "#fff8eb", fontSize: 12, fontWeight: 950 }}>
                    Gerar link do convite
                  </button>
                  <button onClick={shareMesaInviteByWhatsapp} style={{ ...buttonReset, minHeight: 48, padding: "0 14px", border: "1px solid rgba(42,195,122,0.55)", background: "rgba(42,195,122,0.14)", color: "#baf7d2", fontSize: 12, fontWeight: 950 }}>
                    Compartilhar no WhatsApp
                  </button>
                </div>
                {mesaInviteFeedback && <p style={{ margin: "2px 0 0", color: "#c7d1e4", fontSize: 11, lineHeight: 1.45 }}>{mesaInviteFeedback}</p>}
              </div>
            </div>
          </div>
        )}
        {renderBottomNav()}
      </div>
    );
  }

  // --- TRAIL DETAILS SCREEN ---
  if (screen === "trail" && selectedTrail) {
    return (
      <div style={containerStyle}>
        <div style={{ ...fixedTopBarStyle }}>
          <button onClick={() => setScreen("main")} style={{ ...buttonReset, fontSize: 24, color: "#9CA3AF", marginRight: 16 }}>←</button>
          <span style={{ fontSize: 12, fontWeight: 700, color: "#FFF", textTransform: "uppercase", letterSpacing: 2 }}>Visão Geral</span>
          <a href="/" aria-label="Voltar para o site institucional" title="Início do site" style={{ marginLeft: "auto", display: "inline-flex", alignItems: "center", justifyContent: "center", width: 34, height: 34, borderRadius: 999, color: "#9CA3AF", textDecoration: "none", flexShrink: 0 }}>
            <Home size={18} />
          </a>
        </div>

        <section style={{ position: "relative", ...overflowGuardStyle, margin: isDesktop ? "18px auto 0" : "10px 10px 0", maxWidth: isDesktop ? 1180 : "none", padding: isDesktop ? "34px 38px" : isTablet ? "28px 22px" : "24px 18px", borderRadius: isDesktop ? 38 : isTablet ? 30 : 24, border: "1px solid rgba(255,255,255,0.22)", background: `linear-gradient(135deg, rgba(248,243,232,0.98) 0%, rgba(232,238,239,0.96) 52%, ${selectedTrail.color}24 100%)`, color: "#151b2a", boxShadow: "0 22px 54px rgba(0,0,0,0.34)" }}>
          <div aria-hidden="true" style={{ position: "absolute", inset: 0, background: `radial-gradient(circle at 12% 18%, ${selectedTrail.color}26, transparent 30%), radial-gradient(circle at 92% 12%, rgba(255,255,255,0.65), transparent 26%)`, pointerEvents: "none" }} />
          <div style={{ position: "relative", display: "grid", gridTemplateColumns: isDesktop ? "minmax(0, 1fr) 260px" : "1fr", gap: 24, alignItems: "end" }}>
            <div>
              <p style={{ margin: "0 0 12px", fontSize: 11, letterSpacing: 1.5, color: "#87582b", textTransform: "uppercase", fontWeight: 950 }}>Trilha devocional</p>
              <h1 style={{ margin: 0, maxWidth: 720, fontSize: mobileHeroTitleSize, lineHeight: 0.98, color: "#151b2a", fontWeight: 950 }}>{selectedTrail.title}</h1>
              <p style={{ margin: "15px 0 0", maxWidth: 620, color: "#425064", fontSize: isMobile ? 15 : 16, lineHeight: 1.6 }}>{selectedTrail.subtitle}</p>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 20 }}>
                {[
                  `${selectedTrail.duration} dias`,
                  selectedTrail.progress > 0 ? `${selectedTrail.progress} concluído${selectedTrail.progress > 1 ? "s" : ""}` : "Começar agora",
                  coopActive ? "Em grupo" : "Pode ser em grupo"
                ].map((item) => (
                  <span key={item} style={{ padding: "8px 10px", borderRadius: 999, background: "rgba(21,27,42,0.08)", color: "#283247", fontSize: 11, fontWeight: 900 }}>{item}</span>
                ))}
              </div>
            </div>

            <div style={{ padding: 18, borderRadius: 24, background: "rgba(255,255,255,0.58)", border: "1px solid rgba(21,27,42,0.12)", boxShadow: "0 18px 34px rgba(73,83,100,0.16)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "baseline", marginBottom: 10 }}>
                <span style={{ fontSize: 11, color: "#87582b", fontWeight: 950, textTransform: "uppercase", letterSpacing: 1.1 }}>Progresso</span>
                <strong style={{ color: "#151b2a", fontSize: 16, fontWeight: 950 }}>{selectedTrail.progress || 0}/{selectedTrail.duration}</strong>
              </div>
              <div style={{ display: "flex", gap: 3, height: 10, overflow: "hidden", background: "rgba(21,27,42,0.10)", padding: 2 }}>
                {Array.from({ length: selectedTrail.duration }).map((_, i) => (
                  <div key={i} style={{ flex: 1, minWidth: 4, background: i < (selectedTrail.progress || 0) ? selectedTrail.color : "rgba(21,27,42,0.14)" }} />
                ))}
              </div>
              {(!selectedTrail.started && !((selectedTrail.progress || 0) > 0)) ? (
                // Trilha solo ainda nao iniciada: oferece "Iniciar Plano" (compromete,
                // marca started) separado de "Ver previa" (so' le o Dia 1, sem marcar
                // progresso) -- pedido da Carla 20/10/2026, pra nao lotar "Meus Planos"
                // com trilhas que a pessoa so' abriu pra espiar do que se tratava.
                <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
                  <button onClick={() => openDay(1)} style={{ ...buttonReset, flex: 1, minHeight: 44, borderRadius: 999, background: "#151b2a", color: "#fff8eb", fontSize: 12, fontWeight: 950 }}>
                    Iniciar Plano
                  </button>
                  <button onClick={() => openDay(1, null, { markStarted: false })} style={{ ...buttonReset, flex: 1, minHeight: 44, borderRadius: 999, background: "rgba(21,27,42,0.08)", border: "1px solid rgba(21,27,42,0.22)", color: "#151b2a", fontSize: 12, fontWeight: 950 }}>
                    Ver prévia
                  </button>
                </div>
              ) : (
                <button onClick={() => openDay(Math.min(Math.max(1, selectedTrail.duration || 1), (selectedTrail.progress || 0) + 1))} style={{ ...buttonReset, width: "100%", minHeight: 44, marginTop: 16, borderRadius: 999, background: "#151b2a", color: "#fff8eb", fontSize: 12, fontWeight: 950 }}>
                  Abrir próximo dia
                </button>
              )}
            </div>
          </div>
        </section>

        <div style={pageContentStyle}>
          {!coopActive ? (
            <button onClick={() => { openInviteStart(selectedTrail); setScreen("main"); }} style={{ ...buttonReset, ...overflowGuardStyle, width: "100%", background: `linear-gradient(135deg, rgba(214,168,92,0.16), rgba(255,255,255,0.055) 48%, rgba(15,23,42,0.86))`, border: `1px solid ${selectedTrail.color}`, borderRadius: 22, padding: isTablet ? "20px 22px" : "18px", textAlign: "left", display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) auto" : "minmax(0, 1fr)", gap: 18, alignItems: "center", marginBottom: 24, boxShadow: "0 18px 36px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.06)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ width: 52, height: 52, borderRadius: 18, background: selectedTrail.color, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 14px 26px ${selectedTrail.color}32` }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#111" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                </div>
                <div style={{ minWidth: 0 }}>
                  <p style={{ margin: "0 0 6px", fontSize: 10, color: selectedTrail.color, fontWeight: 950, textTransform: "uppercase", letterSpacing: 1.5 }}>Mesa de jornada</p>
                  <h3 style={{ margin: "0 0 6px", fontSize: isTablet ? 22 : 18, color: "#FFFFFF", fontWeight: 950, letterSpacing: 0.2 }}>Comece esta trilha com alguém.</h3>
                  <p style={{ margin: 0, fontSize: 13, color: "#AEB9CB", lineHeight: 1.45 }}>Convide uma pessoa ou um pequeno grupo para manter ritmo, conversa e cuidado durante os devocionais.</p>
                </div>
              </div>
              <span style={{ justifySelf: isTablet ? "end" : "stretch", minHeight: 42, padding: "0 16px", borderRadius: 999, background: `${selectedTrail.color}22`, border: `1px solid ${selectedTrail.color}66`, color: "#F8E7C4", fontSize: 12, fontWeight: 950, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, whiteSpace: "nowrap" }}>Criar grupo <span style={{ fontSize: 18, lineHeight: 1 }}>+</span></span>
            </button>
          ) : (
            <div style={{ background: `linear-gradient(160deg, ${UI.panel}, ${UI.panelSoft})`, border: `1px solid ${UI.stroke}`, borderLeft: `4px solid ${selectedTrail.color}`, borderRadius: UI.radiusMd, padding: "20px 24px", marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div>
                <h3 style={{ margin: "0 0 4px", fontSize: 13, color: "#9CA3AF", fontWeight: 700, textTransform: "uppercase", letterSpacing: 1 }}>Grupo da Trilha</h3>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 8 }}>
                  <div style={{ width: 36, height: 36, borderRadius: 18, background: "#333", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 700 }}>F</div>
                  <p style={{ margin: 0, fontSize: 16, color: "#FFF", fontWeight: 600 }}>{(() => { const count = getTrailParty(selectedTrail).length; return count > 1 ? `${count} participantes` : "Você + parceiros"; })()}</p>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <p style={{ margin: "0 0 4px", fontSize: 11, color: "#6B7280", textTransform: "uppercase", letterSpacing: 1 }}>Convites</p>
                <button onClick={() => { openInviteStart(selectedTrail); setScreen("main"); }} style={{ ...buttonReset, color: selectedTrail.color, fontSize: 14, fontWeight: 700 }}>Gerenciar +</button>
              </div>
            </div>
          )}

          {(() => {
            const trailDevotionals = selectedTrail.devotionals || DAYS_MOCK[selectedTrail.id] || DAYS_MOCK[selectedTrail.slug] || [];
            const trailDuration = Math.max(1, selectedTrail.duration || trailDevotionals.length || 1);
            const trailProgress = Math.min(Math.max(0, selectedTrail.progress || 0), trailDuration);
            const nextDay = Math.min(trailDuration, trailProgress + 1);
            const nextDevotional = trailDevotionals.find((item) => item.day === nextDay) || trailDevotionals[0] || null;
            const party = coopActive ? getTrailParty(selectedTrail) : [];
            const sortedParty = [...party].sort((a, b) => {
              if ((a.id === "you") !== (b.id === "you")) return a.id === "you" ? -1 : 1;
              return (b.day || 0) - (a.day || 0);
            });
            const groupLeadDay = sortedParty.length ? Math.max(...sortedParty.map((member) => member.day || 0), 0) : trailProgress;
            const behindMembers = groupLeadDay > 0 ? sortedParty.filter((member) => member.id !== "you" && (member.day || 0) < groupLeadDay) : [];

            return (
              <div style={{ display: "grid", gap: 16 }}>
                <section style={{ ...sectionCardStyle, padding: isTablet ? 22 : 18, display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) auto" : "1fr", gap: 18, alignItems: "center", background: "linear-gradient(145deg, rgba(255,255,255,0.08), rgba(255,255,255,0.028))" }}>
                  <div>
                    <p style={{ margin: "0 0 8px", fontSize: 11, color: selectedTrail.color, fontWeight: 950, letterSpacing: 1.4, textTransform: "uppercase" }}>Próximo passo</p>
                    <h3 style={{ margin: "0 0 8px", color: UI.text, fontSize: isTablet ? 28 : 22, lineHeight: 1.1, fontWeight: 950 }}>
                      {nextDevotional ? nextDevotional.title : "Começar a trilha"}
                    </h3>
                    <p style={{ margin: 0, color: "#AEB9CB", fontSize: 13, lineHeight: 1.5 }}>
                      Dia {nextDay} de {trailDuration}
                      {nextDevotional?.objective ? ` • ${nextDevotional.objective}` : " • Retome a caminhada de hoje com calma e presença."}
                    </p>
                  </div>
                  <button
                    onClick={() => openDay(nextDay, null, nextDay === 1 ? { markStarted: false } : undefined)}
                    style={{ ...buttonReset, minHeight: 46, padding: "0 18px", borderRadius: 14, background: selectedTrail.color, color: "#111827", fontSize: 13, fontWeight: 950, boxShadow: `0 16px 28px ${selectedTrail.color}24`, whiteSpace: "nowrap" }}
                  >
                    Abrir dia {nextDay}
                  </button>
                </section>

                <section style={{ ...sectionCardStyle, padding: isTablet ? 20 : 16 }}>
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 14 }}>
                    <div>
                      <p style={{ margin: 0, fontSize: 11, color: "#8F9BAD", fontWeight: 900, letterSpacing: 1.2, textTransform: "uppercase" }}>Progresso</p>
                      <h3 style={{ margin: "5px 0 0", color: UI.text, fontSize: 18, fontWeight: 950 }}>Sua caminhada nesta trilha</h3>
                    </div>
                    <strong style={{ color: selectedTrail.color, fontSize: 18, fontWeight: 950 }}>{trailProgress}/{trailDuration}</strong>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: isMobile ? "repeat(5, minmax(0, 1fr))" : "repeat(auto-fit, minmax(54px, 1fr))", gap: isMobile ? 6 : 8 }}>
                    {Array.from({ length: trailDuration }).map((_, index) => {
                      const dayNumber = index + 1;
                      const isDone = dayNumber <= trailProgress;
                      const isCurrent = dayNumber === nextDay;
                      const isAvailable = dayNumber <= nextDay;
                      return (
                        <button
                          key={`practical-day-${dayNumber}`}
                          onClick={() => openDay(dayNumber, null, dayNumber === 1 ? { markStarted: false } : undefined)}
                          disabled={!isAvailable}
                          style={{
                            ...buttonReset,
                            minHeight: isMobile ? 52 : 56,
                            borderRadius: isMobile ? 12 : 14,
                            border: isCurrent ? `1px solid ${selectedTrail.color}` : `1px solid ${isDone ? "rgba(42,195,122,0.34)" : UI.stroke}`,
                            background: isDone ? "rgba(42,195,122,0.13)" : isCurrent ? `${selectedTrail.color}24` : "rgba(255,255,255,0.035)",
                            color: isDone ? "#9EF0BA" : isCurrent ? "#F8E7C4" : "#778196",
                            fontSize: 12,
                            fontWeight: 950,
                            cursor: isAvailable ? "pointer" : "not-allowed",
                            opacity: isAvailable ? 1 : 0.48
                          }}
                        >
                          D{dayNumber}
                          <span style={{ display: "block", marginTop: 4, fontSize: 9, color: "inherit", opacity: 0.82, textTransform: "uppercase", letterSpacing: 0.6 }}>
                            {isDone ? "feito" : isCurrent ? "hoje" : "depois"}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>

                {coopActive && (
                  <section style={{ ...sectionCardStyle, padding: isTablet ? 20 : 16 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", gap: 14, alignItems: "flex-start", marginBottom: 14 }}>
                      <div>
                        <p style={{ margin: 0, fontSize: 11, color: selectedTrail.color, fontWeight: 950, letterSpacing: 1.2, textTransform: "uppercase" }}>Grupo</p>
                        <h3 style={{ margin: "5px 0 0", color: UI.text, fontSize: 18, fontWeight: 950 }}>Ritmo compartilhado</h3>
                      </div>
                      <span style={{ padding: "7px 10px", borderRadius: 999, background: "rgba(255,255,255,0.06)", color: "#D9E2F0", fontSize: 11, fontWeight: 900 }}>
                        {sortedParty.length} pessoas
                      </span>
                    </div>

                    {behindMembers.length > 0 ? (
                      <div style={{ marginBottom: 12, padding: 12, borderRadius: 12, background: "rgba(214,168,92,0.12)", border: "1px solid rgba(214,168,92,0.28)", color: "#F8DFA8", fontSize: 12, lineHeight: 1.45, fontWeight: 800 }}>
                        {behindMembers.length} pessoa{behindMembers.length > 1 ? "s" : ""} {behindMembers.length > 1 ? "estão" : "está"} atrás do ritmo do grupo. Um toque breve pode ajudar sem virar cobrança pesada.
                      </div>
                    ) : (
                      <div style={{ marginBottom: 12, padding: 12, borderRadius: 12, background: "rgba(42,195,122,0.10)", border: "1px solid rgba(42,195,122,0.22)", color: "#BFF3D0", fontSize: 12, lineHeight: 1.45, fontWeight: 800 }}>
                        O grupo está caminhando junto. Bom momento para comentar uma reflexão do dia.
                      </div>
                    )}

                    {nudgeFeedback && (
                      <div style={{ marginBottom: 12, padding: "10px 12px", borderRadius: 10, background: "rgba(255,255,255,0.06)", color: "#F8DFA8", fontSize: 12, fontWeight: 800 }}>
                        {nudgeFeedback}
                      </div>
                    )}

                    <div style={{ display: "grid", gap: 10 }}>
                      {sortedParty.map((member) => {
                        const isYou = member.id === "you";
                        const memberDay = Math.min(trailDuration, Math.max(0, member.day || 0));
                        const percent = Math.min(100, Math.max(0, (memberDay / trailDuration) * 100));
                        const canNudge = !isYou && memberDay < groupLeadDay;
                        const alreadyNudged = nudgedUserIds.includes(member.id);
                        return (
                          <div key={member.id} style={{ display: "grid", gridTemplateColumns: isTablet ? "minmax(0, 1fr) 140px" : "1fr", gap: 12, alignItems: "center", padding: 12, borderRadius: 14, border: `1px solid ${isYou ? selectedTrail.color : UI.stroke}`, background: isYou ? "rgba(214,168,92,0.10)" : UI.panelSoft }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                              <span style={{ width: 44, height: 44, borderRadius: 999, padding: 2, background: member.color || selectedTrail.color, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <AvatarSvg avatar={getAvatarById(member.avatarId)} size={40} />
                              </span>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 }}>
                                  <strong style={{ color: UI.text, fontSize: 14, fontWeight: 950, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                    {member.name}{isYou ? " (você)" : ""}
                                  </strong>
                                  <span style={{ color: memberDay > 0 && memberDay < groupLeadDay ? "#F8DFA8" : "#BFF3D0", fontSize: 12, fontWeight: 900 }}>{memberDay > 0 ? `Dia ${memberDay}` : "Não iniciou"}</span>
                                </div>
                                <div style={{ height: 7, background: "#202838", borderRadius: 999, overflow: "hidden", marginTop: 8 }}>
                                  <div style={{ width: `${percent}%`, height: "100%", borderRadius: 999, background: memberDay < groupLeadDay ? selectedTrail.color : "#2AC37A" }} />
                                </div>
                              </div>
                            </div>
                            {canNudge && (
                              <button
                                onClick={() => sendTrailNudge(member)}
                                style={{ ...buttonReset, minHeight: 38, borderRadius: 12, border: `1px solid ${alreadyNudged ? "rgba(255,255,255,0.12)" : "rgba(214,168,92,0.45)"}`, background: alreadyNudged ? "rgba(255,255,255,0.05)" : "rgba(214,168,92,0.13)", color: alreadyNudged ? "#9CA3AF" : "#F8DFA8", fontSize: 12, fontWeight: 950 }}
                              >
                                {alreadyNudged ? "Toque enviado" : "Dar um toque"}
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {(recentGroupCommentsLoading || recentGroupComments.length > 0) && (
                      <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${UI.stroke}` }}>
                        <p style={{ margin: "0 0 10px", fontSize: 11, color: "#8F9BAD", fontWeight: 900, letterSpacing: 1.2, textTransform: "uppercase" }}>Últimas reflexões da mesa</p>
                        {recentGroupCommentsLoading ? (
                          <p style={{ margin: 0, fontSize: 12, color: "#6B7280" }}>Carregando...</p>
                        ) : (
                          <div style={{ display: "grid", gap: 10 }}>
                            {recentGroupComments.map((comment) => {
                              const commentMember = selectedTrailGroup?.members?.find((item) => item.userId === comment.userId);
                              const commentIsYou = Boolean(viewerUser?.id) && comment.userId === viewerUser.id;
                              const commentName = commentIsYou ? "Você" : (commentMember?.displayName || "Alguém da mesa");
                              const commentAvatarId = commentIsYou ? (viewerUser?.avatarId || selectedAvatarId) : commentMember?.user?.avatarId;
                              const commentAvatar = commentAvatarId ? getAvatarById(commentAvatarId) : null;
                              const commentDayNumber = comment.devotional?.dayNumber || 1;
                              return (
                                <button
                                  key={comment.id}
                                  onClick={() => { setDayTab("chat"); openDay(commentDayNumber, null, { markStarted: false }); }}
                                  style={{ ...buttonReset, display: "flex", gap: 10, alignItems: "flex-start", textAlign: "left", padding: 10, borderRadius: 12, background: "rgba(255,255,255,0.035)", border: `1px solid ${UI.stroke}` }}
                                >
                                  <span style={{ width: 26, height: 26, borderRadius: 13, overflow: "hidden", flexShrink: 0, background: "#333", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 900, color: "#FFF" }}>
                                    {commentAvatar ? <AvatarSvg avatar={commentAvatar} size={26} /> : (commentName || "?").trim().charAt(0).toUpperCase()}
                                  </span>
                                  <span style={{ minWidth: 0, flex: 1 }}>
                                    <span style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                                      <strong style={{ fontSize: 12, color: UI.text, fontWeight: 900 }}>{commentName}</strong>
                                      <span style={{ fontSize: 10, color: "#6B7280", fontWeight: 800, flexShrink: 0 }}>Dia {commentDayNumber}</span>
                                    </span>
                                    <span style={{ display: "block", marginTop: 3, fontSize: 12.5, color: "#AEB9CB", lineHeight: 1.4, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{comment.message}</span>
                                  </span>
                                </button>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                  </section>
                )}

                <section style={{ ...sectionCardStyle, padding: isTablet ? 20 : 16 }}>
                  <p style={{ margin: "0 0 7px", fontSize: 11, letterSpacing: 1.3, color: selectedTrail.color, textTransform: "uppercase", fontWeight: 950 }}>Dias da trilha</p>
                  <h3 style={{ margin: "0 0 14px", fontSize: 18, color: UI.text, fontWeight: 950 }}>Escolha onde continuar</h3>
                  <div style={{ display: "grid", gridTemplateColumns: isTablet ? "repeat(2, minmax(0, 1fr))" : "1fr", gap: 10 }}>
                    {trailDevotionals.map((devotional) => {
                      const isAvailable = devotional.day <= nextDay;
                      const isDone = devotional.day <= trailProgress;
                      return (
                        <button
                          key={`${selectedTrail.id}-simple-devotional-${devotional.day}`}
                          onClick={() => openDay(devotional.day, null, devotional.day === 1 ? { markStarted: false } : undefined)}
                          disabled={!isAvailable}
                          style={{ ...buttonReset, padding: 14, borderRadius: 14, border: `1px solid ${isDone ? "rgba(42,195,122,0.28)" : isAvailable ? "rgba(214,168,92,0.30)" : "rgba(148,163,184,0.15)"}`, background: isAvailable ? "rgba(255,255,255,0.052)" : "rgba(15,23,42,0.22)", textAlign: "left", opacity: isAvailable ? 1 : 0.5, cursor: isAvailable ? "pointer" : "not-allowed" }}
                        >
                          <span style={{ display: "flex", alignItems: "flex-start", gap: 11 }}>
                            <span style={{ width: 34, height: 34, borderRadius: 11, flexShrink: 0, background: isDone ? "rgba(42,195,122,0.16)" : `${selectedTrail.color}20`, border: `1px solid ${isDone ? "rgba(42,195,122,0.30)" : `${selectedTrail.color}45`}`, color: isDone ? "#9EF0BA" : selectedTrail.color, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 950 }}>D{devotional.day}</span>
                            <span style={{ minWidth: 0 }}>
                              <span style={{ display: "block", color: "#F8FAFC", fontSize: 14, fontWeight: 950, lineHeight: 1.25 }}>{devotional.title}</span>
                              <span style={{ display: "block", marginTop: 5, color: "#9fb0cc", fontSize: 12, lineHeight: 1.35 }}>{devotional.objective || devotional.macroTheme || devotional.verse}</span>
                              <span style={{ display: "block", marginTop: 8, color: isDone ? "#9EF0BA" : isAvailable ? selectedTrail.color : "#778196", fontSize: 10, fontWeight: 950, textTransform: "uppercase", letterSpacing: 0.7 }}>
                                {isDone ? "Concluído" : isAvailable ? "Abrir agora" : "Libera depois"}
                              </span>
                            </span>
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </section>
              </div>
            );
          })()}

        </div>
        {renderBottomNav()}
      </div>
    );
  }

  // --- EBOOK VIEWER SCREEN ---
  // --- EBOOK SCREEN ---
  if (screen === "ebook" && selectedEbook) {
    return (
      <EbookScreen
        selectedEbook={selectedEbook}
        ebookNotes={ebookNotes}
        setEbookNotes={setEbookNotes}
        ebookTab={ebookTab}
        setEbookTab={setEbookTab}
        ebookPage={ebookPage}
        setEbookPage={setEbookPage}
        ebookPageLoading={ebookPageLoading}
        ebookPageError={ebookPageError}
        ebookZoomLevel={ebookZoomLevel}
        onIncreaseEbookZoom={increaseEbookZoom}
        onDecreaseEbookZoom={decreaseEbookZoom}
        ebookZoomMin={EBOOK_ZOOM_MIN}
        ebookZoomMax={EBOOK_ZOOM_MAX}
        ebookCanvasRef={ebookCanvasRef}
        persistEbookNote={persistEbookNote}
        syncEbookState={syncEbookState}
        setScreen={setScreen}
        setShowInvitePicker={setShowInvitePicker}
        renderBottomNav={renderBottomNav}
        isMobile={isMobile}
        isTablet={isTablet}
        isDesktop={isDesktop}
        styles={{
          containerStyle,
          fixedTopBarStyle,
          buttonReset,
          pageContentStyle,
          h1Style,
          overflowGuardStyle
        }}
      />
    );
  }

  // --- DAY SCREEN ---
  if (screen === "day" && selectedDay) {
    return (
      <DayScreen
        selectedDay={selectedDay}
        selectedTrail={selectedTrail}
        devotionalHighlights={devotionalHighlights}
        devotionalReflections={devotionalReflections}
        onChangeDevotionalReflection={updateDevotionalReflection}
        getDevotionalIdentity={getDevotionalIdentity}
        coopActive={coopActive}
        dayTab={dayTab}
        setDayTab={setDayTab}
        handleDevotionalTextSelect={handleDevotionalTextSelect}
        renderDevotionalHighlightedText={renderDevotionalHighlightedText}
        devotionalSelection={devotionalSelection}
        addDevotionalHighlight={addDevotionalHighlight}
        markDayAsCompleted={markDayAsCompleted}
        onGoToNextDay={goToNextDayFromDayScreen}
        progressSyncing={progressSyncing}
        progressSyncFeedback={progressSyncFeedback}
        devotionalFeedbackOptions={DEVOTIONAL_FEEDBACK_OPTIONS}
        devotionalFeedbackGiven={selectedTrail ? devotionalFeedbackMap[`${selectedTrail.id}_${selectedDay}`] : null}
        devotionalFeedbackSyncing={devotionalFeedbackSyncing}
        onSubmitDevotionalFeedback={submitDevotionalFeedback}
        selectedTrailGroup={selectedTrailGroup}
        viewerUserId={viewerUser?.id}
        viewerAvatarId={viewerUser?.avatarId || selectedAvatarId}
        AvatarSvg={AvatarSvg}
        getAvatarById={getAvatarById}
        groupDayComments={groupDayComments}
        groupDayCommentsLoading={groupDayCommentsLoading}
        isRealAdmin={realIsAdminUser}
        adminCommentPersonMap={adminCommentPersonMap}
        groupDayCommentDraft={groupDayCommentDraft}
        onChangeGroupDayCommentDraft={setGroupDayCommentDraft}
        groupDayCommentSyncing={groupDayCommentSyncing}
        groupDayCommentFeedback={groupDayCommentFeedback}
        onSubmitGroupDayComment={submitGroupDayComment}
        onBack={() => setScreen(dayScreenBackTarget)}
        onGoToDay={(day) => openDay(day, selectedTrail)}
        renderBottomNav={renderBottomNav}
        isMobile={isMobile}
        isTablet={isTablet}
        isDesktop={isDesktop}
        devotionalsByTrail={DAYS_MOCK}
        styles={{
          containerStyle,
          fixedTopBarStyle,
          pageContentStyle,
          warmHeroStyle,
          overflowGuardStyle,
          eyebrowStyle,
          h1Style,
          softPanelStyle,
          buttonReset
        }}
      />
    );
  }

  // --- DIARY SCREEN ("Meu diário": reflexões + marcações, agregadas) ---
  if (screen === "diary") {
    return (
      <DiaryScreen
        entries={diaryEntries}
        onOpenEntry={(entry) => {
          const trailForEntry = trails.find((t) => t.id === entry.trailId) || null;
          if (trailForEntry) setSelectedTrail(trailForEntry);
          setSelectedDay(entry.day);
          setDayTab("reflection");
          setDayScreenBackTarget("diary");
          setScreen("day");
          window.scrollTo(0, 0);
        }}
        setScreen={setScreen}
        renderBottomNav={renderBottomNav}
        isMobile={isMobile}
        isTablet={isTablet}
        isDesktop={isDesktop}
        styles={{
          containerStyle,
          fixedTopBarStyle,
          pageContentStyle,
          eyebrowStyle,
          h1Style,
          softPanelStyle,
          buttonReset
        }}
      />
    );
  }

  // --- EBOOK LANDING SCREEN (publica, sem login: /familia/ebooks/<slug>) ---
  if (screen === "ebook-landing" && selectedEbook) {
    return (
      <>
        <EbookLanding
          ebook={selectedEbook}
          onStartReading={() => openEbook(selectedEbook.id)}
          onBack={() => {
            setScreen("main");
            setActiveTab("ebooks");
            window.scrollTo(0, 0);
          }}
          renderBottomNav={renderBottomNav}
          isMobile={isMobile}
          isTablet={isTablet}
          isDesktop={isDesktop}
          styles={{
            containerStyle,
            fixedTopBarStyle,
            buttonReset,
            pageContentStyle,
            h1Style,
            overflowGuardStyle
          }}
        />
        {profilePromptModal}
      </>
    );
  }

  // --- TRILHA LANDING SCREEN (publica, sem login: /familia/trilhas/<slug>) ---
  if (screen === "trail-landing") {
    if (!selectedTrailLanding) {
      // Maioria das trilhas só existe depois que ensureCuratedTrailsLoaded()
      // resolve (chunk separado de ~843KB) -- ver useEffect que reage a
      // curatedTrailsVersion. Enquanto isso, mostra um estado simples de
      // carregamento em vez de piscar a tela "main".
      return (
        <div style={containerStyle}>
          <div style={pageContentStyle}>
            <p style={{ color: "#c7d1e4", padding: 24 }}>Carregando trilha...</p>
          </div>
        </div>
      );
    }
    const trailLandingDevotionals = selectedTrailLanding.devotionals
      || DAYS_MOCK[selectedTrailLanding.id]
      || DAYS_MOCK[selectedTrailLanding.slug]
      || [];
    return (
      <>
        <TrailLanding
          trail={selectedTrailLanding}
          devotionals={trailLandingDevotionals}
          onStartDay={() => openDay(1, selectedTrailLanding)}
          onBack={() => {
            setScreen("main");
            setActiveTab("home");
            window.scrollTo(0, 0);
          }}
          renderBottomNav={renderBottomNav}
          isMobile={isMobile}
          isTablet={isTablet}
          isDesktop={isDesktop}
          styles={{
            containerStyle,
            fixedTopBarStyle,
            buttonReset,
            pageContentStyle,
            h1Style,
            overflowGuardStyle
          }}
        />
        {profilePromptModal}
      </>
    );
  }

  return null;
}
